import React, { useState, useRef, useEffect, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import { StaffingView } from "./StaffingView";
import { MarketingView } from "./MarketingView";
import { ScenarioView } from "./ScenarioView";
import { IntegrationsView } from "./IntegrationsView";
import { InvoicesView } from "./InvoicesView";
import { GripVertical, Bell, Settings, LogOut, ChevronDown, Home, LayoutDashboard, Target, Beaker, Users, FileText, Plug, Menu, Boxes, UtensilsCrossed, BarChart3, Megaphone } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

// Vite env type augmentation
declare global {
  interface ImportMeta {
    env: Record<string, string>;
  }
}

// Supabase Configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

// Initialize Supabase only if keys are present to prevent top-level crashes
let supabaseInstance: any = null;
try {
  if (supabaseUrl && supabaseAnonKey) {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey);
  }
} catch (e) {
  console.error("Critical error: Failed to initialize Supabase client", e);
}

export const supabase = supabaseInstance;

if (!supabase) {
  console.warn("Supabase credentials missing or invalid. Check your VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.");
}

/**
 * 1) File usage:
 * - Files are reliably read from localStorage.
 * - Relevant files per agent are logged and truncated before being added to the prompt.
 */

// Agent personas and their capabilities
const AGENT_PERSONAS = {
  chef: {
    name: "Chef / Inventory Agent",
    instructions: `You are a seasoned head chef who also runs inventory and prep for busy food vendors. You’re practical, fast, and you speak like a real person in a busy kitchen.

Voice & style rules:
- Speak in first person ("I", "we") and use natural contractions ("I’d", "you’re").
- Be concise, honest, and slightly opinionated — give a clear recommendation and why.
- Avoid formal, textbook headings; use plain-language bullets when needed.
- If a number is missing, ask 1–2 quick clarifying questions instead of guessing.
- Show simple, readable maths when pricing, then give a practical next step.
- End with: "Next steps:" and 2–4 short bullets.

What you do well:
- Improve recipes for consistency, speed, and profit
- Portion control without hurting the customer experience
- Swap ingredients to reduce cost while keeping taste
- Price menu items to hit food-cost targets (typically 25–35%)
- Reduce waste and tighten prep/inventory routines

What you don't do:
- Tax, payroll, or legal compliance details (route to Accountant/Manager)
- Full marketing strategies (route to Marketing)

Example reply (use this tone and structure):
Honestly, I’d price chicken madras at about £8.50 to £9. Looking at your ingredients, you’re probably in the £2.50–£3.00 per portion range, depending on how generous you are and whether you always use coconut milk. If it’s nearer £3 a portion, anything under £9 starts squeezing your margin. If it’s closer to £2.50, £8.50 still leaves you comfortable. I’d avoid £8 flat unless you’re pushing volume or using it as a value dish. My advice? Start at £9, see how it moves for a couple of weeks, and adjust from there.

Use this structure for answers: one-sentence recommendation, short reason/estimate with simple math, practical advice about testing or serving, then "Next steps:" bullets.
`,
    allowedTags: ["Menu/Recipes", "Ingredient Costs", "Supplier Invoices", "Sales"],
    scope: "chef",
  },

  manager: {
    name: "General Manager / Ops Agent",
    instructions: `I’m a hands-on ops lead who helps busy kitchens run smoother. I speak plainly, give practical priorities, and focus on small changes that make shifts less chaotic.

Voice & style rules:
- Speak in first person ("I", "we") and be direct.
- Give a clear recommendation up front, then a short practical explanation.
- Use checklists or quick diagnostics when helpful. Keep it actionable.
- Ask 1–2 clarifying questions only if they change your recommendation.
- End with: "Next steps:" and 2–4 short bullets.

What I help with:
- Staffing coverage, rotas, and shift structure
- Service flow, SOPs, and reducing bottlenecks
- Basic compliance checklists (food safety, permits) — not legal advice
- Customer service standards and handling peak periods
- Maintenance routines and operational reliability

What I don’t do:
- Deep recipe formulation (route to Chef)
- Detailed tax calculations (route to Accountant)
- Full marketing campaign strategy (route to Marketing)

Example reply (tone):
Honestly, I’d move that garnish prep to the early shift — it’ll shave about 15–20% off peak ticket times. Try it for two services and compare ticket times.\n\nNext steps: - Shift test for 2 services - Measure ticket times - Reassign one prep role if improvement is steady
`,
    allowedTags: ["Staffing/Ops", "Compliance Notes", "Sales", "Supplier Invoices"],
    scope: "manager",
  },

  accountant: {
    name: "Accountant / Finance Agent",
    instructions: `I’m a practical accountant for small food businesses — I focus on protecting cash flow and making margins understandable. I keep explanations simple and give clear next steps.
    
    Data Source Note: For Square integrations, I extract sales trends and P&L data directly from https://app.squareup.com/dashboard/sales/reports/sales-trends.`,
    allowedTags: ["Supplier Invoices", "Sales", "Ingredient Costs", "Staffing/Ops"],
    scope: "accountant",
  },

  marketing: {
    name: "Marketing / Growth Agent",
    instructions: `I’m a hands-on marketer for local food vendors — I give simple, low-cost ideas that actually get people through the door.

Voice & style rules:
- Speak in first person, upbeat and practical.
- Give 2–4 concrete options, not one perfect answer.
- Make suggestions that are easy to test in a week.
- End with: "Next steps:" and 2–4 short bullets.

What I help with:
- Menu item naming that sells
- Social content ideas that are realistic to execute
- Review response templates and reputation building
- Promotions, bundles, and local community tactics
- Positioning: why someone chooses YOU over the stall next door

What I don’t do:
- Detailed finance maths (route to Accountant)
- Kitchen ops or food safety (route to Chef/Manager)

When the user mentions reviews, complaints, bad feedback, or reputation, follow these rules for a public reply:
- Write a concise 3–6 sentence public reply suitable for Google Reviews.
- Match the selected tone exactly: calm = apologetic, steady, professional; warm = empathetic, friendly, reassuring; firm = polite, confident, sets expectations.
- End the reply by asking: “Do you want me to post this on Google for you?”
- After the reply, append this exact plain-text block (used by the UI to enable actions):

ACTION:
type: review_reply
platform: google
confidence_check: required

Example reply (tone):
I’d call it the ‘Market-Fresh Smash Burger’ and push it as a premium add-on — shout out the local supplier and price it slightly higher.\nSupport: Try 3 posts and a weekend promo; measure clicks and sales.\n\nNext steps: - Create 3 short posts - Run weekend promo - Track uplift in sales
`,
    allowedTags: ["Marketing/Reviews", "Sales", "Menu/Recipes"],
    scope: "marketing",
  },
} as const;

type ValidAgentId = keyof typeof AGENT_PERSONAS;
type AgentId = ValidAgentId | null;

// ---- Helpers ----
function normalizeAgentId(value: any): AgentId {
  if (value === null) return null;
  const v = String(value || "").toLowerCase().trim();
  if (v === "chef" || v === "manager" || v === "accountant" || v === "marketing") {
    return v as AgentId;
  }
  throw new Error(
    `Invalid agentType "${value}". Expected one of: chef | manager | accountant | marketing.`
  );
}

// Global file storage - using a more persistent approach
const FILE_STORAGE_KEY = "findsy_uploaded_files";

// Clean model output: remove markdown bold, headings, and backticks
function sanitizeOutput(text: any) {
  const s = String(text || "");
  return s
    .replace(/\*\*(.*?)\*\*/gs, "$1") // **bold** -> bold
    .replace(/(^|\n)#+\s*/g, "$1") // remove markdown headings
    .replace(/`+/g, ""); // remove backticks
}

function getStoredFiles() {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(FILE_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error reading stored files:", error);
      return [];
    }
  }
  return [];
}

// ------------------ Targets: data models & storage ------------------
type Target = {
  id: string;
  userId: string;
  name: string;
  metric: string;
  threshold: number;
  direction: "above" | "below";
  tolerance?: number;
  agentType: AgentId;
  fileScopeTags?: string[];
  createdAt: string;
  lastCheckedAt?: string;
  lastValue?: number;
  status: "ok" | "alerting";
  isChecking?: boolean;
};

type TargetNotification = {
  id: string;
  userId: string;
  targetId: string;
  targetName: string;
  metric: string;
  message: string;
  agentType: AgentId;
  createdAt: string;
  read: boolean;
  value?: number;
  threshold?: number;
  direction?: "above" | "below";
};


// ------------------ Inventory Types ------------------

type InventoryStatus = "OK" | "Low" | "Critical" | "Overstocked";

type InventoryItem = {
  id: string;
  name: string;
  category: "Protein" | "Produce" | "Dairy" | "Dry Goods" | "Alcohol" | "Beverage";
  currentQty: number;
  unit: string;
  minThreshold: number;
  avgDailyUsage: number;
  costPerUnit: number;
  supplierId: string;
  lastDelivered: string; // ISO Date
  expiryDate?: string; // ISO Date
  status: InventoryStatus;
};

type WasteRecord = {
  id: string;
  itemId: string;
  itemName: string;
  quantity: number;
  reason: "Expired" | "Damaged" | "Prep Error" | "Spillage" | "Theft";
  value: number;
  date: string; // ISO Date
};

type Supplier = {
  id: string;
  name: string;
  reliability: number; // 0-100
  leadTimeDays: number;
};

// ------------------ Inventory Mock Data Generators ------------------

function generateMockInventory(): { items: InventoryItem[]; waste: WasteRecord[]; suppliers: Supplier[] } {
  const suppliers: Supplier[] = [
    { id: "s1", name: "FreshFarm Local", reliability: 95, leadTimeDays: 1 },
    { id: "s2", name: "Metro Wholesale", reliability: 98, leadTimeDays: 2 },
    { id: "s3", name: "Coastal Seafoods", reliability: 88, leadTimeDays: 1 },
    { id: "s4", name: "Vintners Co", reliability: 92, leadTimeDays: 3 },
  ];

  const items: InventoryItem[] = [
    { id: "i1", name: "Chicken Breast", category: "Protein", currentQty: 12, unit: "kg", minThreshold: 15, avgDailyUsage: 4.5, costPerUnit: 6.5, supplierId: "s1", lastDelivered: "2023-10-25", status: "Critical" },
    { id: "i2", name: "Basmati Rice", category: "Dry Goods", currentQty: 45, unit: "kg", minThreshold: 10, avgDailyUsage: 2.1, costPerUnit: 1.2, supplierId: "s2", lastDelivered: "2023-10-10", status: "OK" },
    { id: "i3", name: "Tomatoes", category: "Produce", currentQty: 5, unit: "kg", minThreshold: 8, avgDailyUsage: 3.0, costPerUnit: 2.5, supplierId: "s1", lastDelivered: "2023-10-26", status: "Low" },
    { id: "i4", name: "Cheddar Cheese", category: "Dairy", currentQty: 18, unit: "kg", minThreshold: 5, avgDailyUsage: 1.2, costPerUnit: 8.9, supplierId: "s2", lastDelivered: "2023-10-20", status: "Overstocked" },
    { id: "i5", name: "Salmon Fillet", category: "Protein", currentQty: 3, unit: "kg", minThreshold: 5, avgDailyUsage: 1.8, costPerUnit: 14.5, supplierId: "s3", lastDelivered: "2023-10-27", status: "Critical" },
    { id: "i6", name: "House Red Wine", category: "Alcohol", currentQty: 14, unit: "btl", minThreshold: 12, avgDailyUsage: 3, costPerUnit: 5.5, supplierId: "s4", lastDelivered: "2023-10-15", status: "OK" },
  ];

  const waste: WasteRecord[] = [
    { id: "w1", itemId: "i3", itemName: "Tomatoes", quantity: 2, reason: "Expired", value: 5.0, date: "2023-10-26" },
    { id: "w2", itemId: "i1", itemName: "Chicken Breast", quantity: 0.5, reason: "Prep Error", value: 3.25, date: "2023-10-25" },
    { id: "w3", itemId: "i5", itemName: "Salmon Fillet", quantity: 0.2, reason: "Spillage", value: 2.9, date: "2023-10-27" },
  ];

  return { items, waste, suppliers };
}


// ------------------ Menu Intelligence Types ------------------

type MenuRole = "Core Seller" | "Opportunity" | "Underperforming" | "Support Item";

type MenuItem = {
  id: string;
  name: string;
  category: "Appetizer" | "Main" | "Dessert" | "Beverage";
  price: number;
  cost: number;
  margin: number; // calculated as price - cost
  marginPercent: number; // (margin / price) * 100
  ordersPerWeek: number; // units sold last 7 days
  revenueContribution: number; // % of total revenue
  menuRole: MenuRole;
  prepTime: "Low" | "Medium" | "High";
  operationalSignals: string[];
  recommendation: string;
  estimatedImpact: string;
  trend: "up" | "down" | "stable";
};

type MenuItemInput = {
  id: string;
  name: string;
  category: "Appetizer" | "Main" | "Dessert" | "Beverage";
  price: number;
  cost: number;
  ordersPerWeek: number;
  prepTime: "Low" | "Medium" | "High";
  operationalSignals: string[];
  trend: "up" | "down" | "stable";
};

// ------------------ Sales Demand Types ------------------

type DailyForecast = {
  date: string; // "YYYY-MM-DD"
  dayName: string;
  predictedRevenue: number;
  predictedCovers: number;
  weather: {
    condition: "Sunny" | "Cloudy" | "Rain" | "Snow";
    temp: number;
    impact?: string; // e.g. "Reduced walk-ins likely"
  };
  events?: { name: string; impact: string }[];
  staffingNeeds: {
    foh: number;
    kitchen: number;
    note: string;
  };
  demandDrivers: string[];
  hourlyTrend: HourlyTrend[];
};

type HourlyTrend = {
  hour: string; // "17:00"
  demandLevel: number; // 0-100 scale
  projectedRevenue: number; // £
};

// ------------------ Menu & Sales Mock Generators ------------------

function normalizeMenuItemInput(item: any): MenuItemInput | null {
  if (!item || !item.name) return null;
  const signalsRaw = item.operationalSignals || item.signals || [];
  const signals = Array.isArray(signalsRaw)
    ? signalsRaw.filter(Boolean)
    : typeof signalsRaw === "string"
      ? signalsRaw.split(",").map((s: string) => s.trim()).filter(Boolean)
      : [];

  return {
    id: String(item.id || Math.random().toString()),
    name: String(item.name),
    category: (item.category || "Main") as MenuItemInput["category"],
    price: Number(item.price) || 0,
    cost: Number(item.cost) || 0,
    ordersPerWeek: Number(item.ordersPerWeek) || 0,
    prepTime: (item.prepTime || "Medium") as MenuItemInput["prepTime"],
    operationalSignals: signals,
    trend: (item.trend || "stable") as MenuItemInput["trend"],
  };
}

function computeMenuRole(meta: { name: string; ordersPerWeek: number; marginPercent: number }) {
  let menuRole: MenuRole = "Underperforming";
  let recommendation = "";
  let estimatedImpact = "";

  if (meta.ordersPerWeek > 100 && meta.marginPercent > 60) {
    menuRole = "Core Seller";
    recommendation = "Keep quality • Monitor supply";
    estimatedImpact = "Maintain £2k+ profit";
  } else if (meta.ordersPerWeek <= 50 && meta.marginPercent > 70) {
    menuRole = "Opportunity";
    recommendation = "Promote on social / menu highlights";
    estimatedImpact = "+£400/wk potential";
  } else if (meta.ordersPerWeek < 20 || (meta.ordersPerWeek < 50 && meta.marginPercent < 40)) {
    menuRole = "Underperforming";
    recommendation = "Consider removal or recipe rework";
    estimatedImpact = "Reduce £200/wk waste potential";
  } else {
    menuRole = "Support Item";
    recommendation = "Operational necessity";
    estimatedImpact = "Drives attach rate";
  }

  if (meta.name === "Lobster Ravioli") {
    recommendation = "Consider removing";
    estimatedImpact = "Reduce margin drag";
  }
  if (meta.name === "Choc Lava Cake") {
    recommendation = "Promote heavily";
    estimatedImpact = "+£300 profit potential";
  }

  return { menuRole, recommendation, estimatedImpact };
}

function enrichMenuItems(items: MenuItemInput[]): MenuItem[] {
  const safeItems = (items || []).map(normalizeMenuItemInput).filter(Boolean) as MenuItemInput[];
  const totalRev = safeItems.reduce((acc, item) => acc + (item.price * item.ordersPerWeek), 0);
  const safeTotal = totalRev > 0 ? totalRev : 1;

  return safeItems.map((item) => {
    const price = Number(item.price) || 0;
    const cost = Number(item.cost) || 0;
    const ordersPerWeek = Number(item.ordersPerWeek) || 0;
    const margin = price - cost;
    const marginPercent = price ? (margin / price) * 100 : 0;
    const revenueContribution = ((price * ordersPerWeek) / safeTotal) * 100;
    const { menuRole, recommendation, estimatedImpact } = computeMenuRole({ name: item.name, ordersPerWeek, marginPercent });

    return {
      ...item,
      margin,
      marginPercent,
      revenueContribution,
      menuRole,
      recommendation,
      estimatedImpact,
    } as MenuItem;
  });
}

function generateMockMenu(): MenuItem[] {
  const items: MenuItemInput[] = [
    { id: "m1", name: "Double Smash Burger", category: "Main", price: 16.50, cost: 4.80, ordersPerWeek: 110, trend: "up", prepTime: "Medium", operationalSignals: [] },
    { id: "m2", name: "Truffle Fries", category: "Appetizer", price: 8.50, cost: 1.80, ordersPerWeek: 210, trend: "up", prepTime: "Low", operationalSignals: [] },
    { id: "m3", name: "Duck Confit", category: "Main", price: 28.00, cost: 14.50, ordersPerWeek: 8, trend: "down", prepTime: "High", operationalSignals: ["High labor cost", "20 min prep"] },
    { id: "m4", name: "House Fries", category: "Appetizer", price: 4.50, cost: 0.60, ordersPerWeek: 420, trend: "stable", prepTime: "Low", operationalSignals: [] },
    { id: "m5", name: "Ice Cream", category: "Dessert", price: 6.50, cost: 1.20, ordersPerWeek: 185, trend: "up", prepTime: "Low", operationalSignals: ["90% attach rate"] },
    { id: "m6", name: "Vegan Lasagna", category: "Main", price: 18.00, cost: 5.50, ordersPerWeek: 4, trend: "down", prepTime: "High", operationalSignals: ["High waste risk"] },
    { id: "m7", name: "Signature Cocktail", category: "Beverage", price: 14.00, cost: 2.50, ordersPerWeek: 125, trend: "up", prepTime: "Medium", operationalSignals: [] },
    { id: "m8", name: "Garlic Bread", category: "Appetizer", price: 5.50, cost: 0.80, ordersPerWeek: 280, trend: "stable", prepTime: "Low", operationalSignals: ["Quick prep"] },
  ];
  return enrichMenuItems(items);
}

function generateMockSalesForecast(): { weekly: DailyForecast[] } {
  const today = new Date();
  const weekly: DailyForecast[] = [];

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const dayName = days[d.getDay()];
    const isWeekend = d.getDay() === 5 || d.getDay() === 6 || d.getDay() === 0;

    const weatherCondition = Math.random() > 0.7 ? "Rain" : "Sunny";
    const weatherImpact = weatherCondition === "Rain" ? "Rain expected → reduced garden seating / walk-ins" : "Clear skies → higher outdoor demand";

    const events: { name: string; impact: string }[] = [];
    if (dayName === "Sat") events.push({ name: "Local Football Match", impact: "Elevated pre-match drinks & burger volume" });
    if (d.getDate() > 25) events.push({ name: "Post-Payday Period", impact: "Higher AOV expected across all shifts" });
    if (i === 4) events.push({ name: "Ramadan Ends", impact: "Increased evening family bookings expected" });

    const totalRevenue = isWeekend ? 8500 + Math.random() * 2000 : 4500 + Math.random() * 1000;
    const covers = isWeekend ? 180 + Math.random() * 40 : 80 + Math.random() * 20;

    const hourlyTrend: HourlyTrend[] = [
      { hour: "12:00", demandLevel: 40 + Math.random() * 40, projectedRevenue: totalRevenue * 0.15 },
      { hour: "17:00", demandLevel: 30 + Math.random() * 30, projectedRevenue: totalRevenue * 0.10 },
      { hour: "18:00", demandLevel: 50 + Math.random() * 30, projectedRevenue: totalRevenue * 0.20 },
      { hour: "19:00", demandLevel: 70 + Math.random() * 30, projectedRevenue: totalRevenue * 0.25 },
      { hour: "20:00", demandLevel: 60 + Math.random() * 40, projectedRevenue: totalRevenue * 0.20 },
      { hour: "21:00", demandLevel: 40 + Math.random() * 40, projectedRevenue: totalRevenue * 0.10 },
    ];

    weekly.push({
      date: d.toISOString().split('T')[0],
      dayName,
      predictedRevenue: totalRevenue,
      predictedCovers: covers,
      weather: {
        condition: weatherCondition as any,
        temp: 22 - Math.random() * 5,
        impact: weatherImpact
      },
      events,
      staffingNeeds: {
        foh: isWeekend ? 5 : 3,
        kitchen: isWeekend ? 4 : 2,
        note: isWeekend ? "Pressure peak 7-9pm: +2 FOH needed" : "Standard weekday coverage"
      },
      demandDrivers: [
        dayName === "Fri" ? "Historical Friday rush" : "Routine weekday",
        weatherCondition === "Rain" ? "Weather suppressed" : "Weather supported"
      ],
      hourlyTrend
    });
  }

  return { weekly };
}

const defaultSalesForecast = generateMockSalesForecast();
const DEFAULT_REVENUE_PROGRESS_POINTS = [
  { time: "9am", actual: 420, expected: 420 },
  { time: "11am", actual: 1180, expected: 1000 },
  { time: "1pm", actual: 2050, expected: 1650 },
  { time: "3pm", actual: 2780, expected: 2350 },
  { time: "5pm", actual: 3240, expected: 2850 },
  { time: "7pm", expected: 3600 },
  { time: "9pm", expected: 4400 }
];

const DEFAULT_REVENUE_VS_COST = {
  labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
  revenue: [4200, 4350, 4520, 4480],
  cost: [3200, 3400, 3650, 3800]
};

const DEFAULT_DEMAND_HEATMAP = {
  days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  hours: ["6am", "9am", "12pm", "3pm", "6pm", "9pm"],
  data: [
    [10, 15, 45, 30, 60, 55], [12, 18, 42, 28, 58, 52], [11, 16, 48, 32, 65, 58],
    [13, 20, 50, 35, 70, 62], [15, 25, 55, 40, 80, 75], [20, 35, 65, 50, 90, 85],
    [18, 30, 60, 45, 75, 70]
  ]
};

const DEFAULT_PEAK_HOURS = [
  { day: "Mon", hours: "12-2pm", intensity: 45, level: "Med", color: "bg-yellow-500" },
  { day: "Tue", hours: "12-2pm", intensity: 40, level: "Med", color: "bg-yellow-500" },
  { day: "Wed", hours: "12-2pm, 6-8pm", intensity: 55, level: "High", color: "bg-orange-500" },
  { day: "Thu", hours: "6-9pm", intensity: 65, level: "High", color: "bg-orange-500" },
  { day: "Fri", hours: "6-10pm", intensity: 85, level: "Peak", color: "bg-red-500" },
  { day: "Sat", hours: "12-3pm, 6-10pm", intensity: 95, level: "Peak", color: "bg-red-500" },
  { day: "Sun", hours: "12-4pm", intensity: 70, level: "High", color: "bg-orange-500" }
];

const DEFAULT_STAFFING_COVERAGE = [
  { hour: "11am-12pm", demand: 30, staff: 3, status: "ok", detail: "Perfect coverage for morning prep and early lunch." },
  { hour: "12pm-1pm", demand: 65, staff: 4, status: "ok", detail: "Comfortable coverage for the lunch rush." },
  { hour: "1pm-2pm", demand: 70, staff: 4, status: "ok", detail: "Kitchen at high capacity, but fully covered by current rota." },
  { hour: "2pm-3pm", demand: 45, staff: 3, status: "ok", detail: "Standard afternoon coverage." },
  { hour: "5pm-6pm", demand: 50, staff: 4, status: "ok", detail: "Dinner setup and early bird service is well-staffed." },
  { hour: "6pm-7pm", demand: 75, staff: 5, status: "ok", detail: "Maximum primary capacity reached. Service remains efficient." },
  { hour: "7pm-8pm", demand: 95, staff: 5, status: "under", detail: "CRITICAL: Demand exceeds staffing capacity by 15%. Wait times will increase." },
  { hour: "8pm-9pm", demand: 90, staff: 5, status: "under", detail: "HIGH PRESSURE: Peak dinner wave still exceeding comfortable service levels." },
  { hour: "9pm-10pm", demand: 60, staff: 4, status: "ok", detail: "Late service winding down. Staffing levels are appropriate." }
];

const DEFAULT_ROTA = [
  { name: "Sarah Johnson", role: "Head Chef", shift: "11:00 - 22:00", cost: 120 },
  { name: "Mike Chen", role: "Sous Chef", shift: "11:00 - 22:00", cost: 95 },
  { name: "Emma Wilson", role: "Line Cook", shift: "17:00 - 22:00", cost: 45 },
  { name: "James Brown", role: "FOH Manager", shift: "11:00 - 22:00", cost: 110 },
  { name: "Lisa Garcia", role: "Server", shift: "11:00 - 15:00", cost: 40 },
  { name: "Tom Anderson", role: "Server", shift: "17:00 - 22:00", cost: 50 },
  { name: "Rachel Lee", role: "Server", shift: "17:00 - 22:00", cost: 50 },
  { name: "David Kim", role: "Bartender", shift: "17:00 - 23:00", cost: 60 },
  { name: "Sophie Martin", role: "Host", shift: "17:00 - 22:00", cost: 45 },
  { name: "Alex Turner", role: "Dishwasher", shift: "17:00 - 23:00", cost: 48 }
];

const DEFAULT_HOURLY_TEMPLATE: HourlyTrend[] = [
  { hour: "12:00", demandLevel: 40, projectedRevenue: 650 },
  { hour: "17:00", demandLevel: 30, projectedRevenue: 450 },
  { hour: "18:00", demandLevel: 55, projectedRevenue: 850 },
  { hour: "19:00", demandLevel: 75, projectedRevenue: 1050 },
  { hour: "20:00", demandLevel: 65, projectedRevenue: 900 },
  { hour: "21:00", demandLevel: 45, projectedRevenue: 600 },
];

function normalizeHourlyTrend(list: any[] = []): HourlyTrend[] {
  const raw = Array.isArray(list) && list.length > 0 ? list : DEFAULT_HOURLY_TEMPLATE;
  const cleaned = raw.map((h: any, idx: number) => ({
    hour: h.hour || DEFAULT_HOURLY_TEMPLATE[idx]?.hour || `Hour ${idx + 1}`,
    projectedRevenue: Number(h.projectedRevenue ?? h.revenue ?? DEFAULT_HOURLY_TEMPLATE[idx]?.projectedRevenue ?? 0),
    demandLevel: Number(h.demandLevel ?? 0)
  }));
  const maxRevenue = Math.max(...cleaned.map(c => c.projectedRevenue), 1);
  return cleaned.map(c => ({
    ...c,
    demandLevel: c.demandLevel || Math.round((c.projectedRevenue / maxRevenue) * 100)
  }));
}

function normalizeManualSalesData(input?: any) {
  const salesInput = input?.sales || {};
  const demandInput = input?.demand || {};
  const staffingInput = input?.staffing || {};

  const weeklySrc = Array.isArray(demandInput.weekly) && demandInput.weekly.length > 0
    ? demandInput.weekly
    : defaultSalesForecast.weekly;

  const hourlyTemplate = normalizeHourlyTrend(demandInput.hourlyTemplate);

  const weekly = weeklySrc.map((day: any, idx: number) => {
    const baseDay = defaultSalesForecast.weekly[idx] || defaultSalesForecast.weekly[0];
    const normalizedHourly = Array.isArray(day?.hourlyTrend) && day.hourlyTrend.length > 0
      ? normalizeHourlyTrend(day.hourlyTrend)
      : hourlyTemplate;

    return {
      date: day?.date || baseDay?.date,
      dayName: day?.dayName || baseDay?.dayName || `Day ${idx + 1}`,
      predictedRevenue: Number(day?.predictedRevenue ?? baseDay?.predictedRevenue ?? 0),
      predictedCovers: Number(day?.predictedCovers ?? baseDay?.predictedCovers ?? 0),
      weather: {
        condition: day?.weather?.condition || baseDay?.weather?.condition || "Cloudy",
        temp: Number(day?.weather?.temp ?? baseDay?.weather?.temp ?? 18),
        impact: day?.weather?.impact || baseDay?.weather?.impact
      },
      events: day?.events || baseDay?.events,
      staffingNeeds: day?.staffingNeeds || baseDay?.staffingNeeds,
      demandDrivers: day?.demandDrivers || baseDay?.demandDrivers,
      hourlyTrend: normalizedHourly
    } as DailyForecast;
  });

  const revenueProgressPoints = Array.isArray(salesInput?.revenueProgress?.points) && salesInput.revenueProgress.points.length > 0
    ? salesInput.revenueProgress.points
    : DEFAULT_REVENUE_PROGRESS_POINTS;

  const revenueVsCost = salesInput?.revenueVsCost || DEFAULT_REVENUE_VS_COST;
  const revenueVsCostLabels = Array.isArray(revenueVsCost.labels) && revenueVsCost.labels.length > 0
    ? revenueVsCost.labels
    : DEFAULT_REVENUE_VS_COST.labels;
  const revenueVsCostRevenue = Array.isArray(revenueVsCost.revenue) && revenueVsCost.revenue.length > 0
    ? revenueVsCost.revenue.map((v: any) => Number(v))
    : DEFAULT_REVENUE_VS_COST.revenue;
  const revenueVsCostCost = Array.isArray(revenueVsCost.cost) && revenueVsCost.cost.length > 0
    ? revenueVsCost.cost.map((v: any) => Number(v))
    : DEFAULT_REVENUE_VS_COST.cost;

  const heatmap = demandInput.heatmap || DEFAULT_DEMAND_HEATMAP;
  const peakHours = Array.isArray(demandInput.peakHours) && demandInput.peakHours.length > 0 ? demandInput.peakHours : DEFAULT_PEAK_HOURS;

  const coverage = Array.isArray(staffingInput.coverage) && staffingInput.coverage.length > 0 ? staffingInput.coverage : DEFAULT_STAFFING_COVERAGE;
  const rota = Array.isArray(staffingInput.rota) && staffingInput.rota.length > 0 ? staffingInput.rota : DEFAULT_ROTA;

  return {
    sales: {
      today: {
        revenueSoFar: Number(salesInput?.today?.revenueSoFar ?? 3240),
        expectedByNow: Number(salesInput?.today?.expectedByNow ?? 2850),
        fullDayTarget: Number(salesInput?.today?.fullDayTarget ?? 5700),
        note: salesInput?.today?.note || ""
      },
      health: {
        transactions: Number(salesInput?.health?.transactions ?? 175),
        transactionsDelta: Number(salesInput?.health?.transactionsDelta ?? 12),
        pacePercent: Number(salesInput?.health?.pacePercent ?? 114),
        paceLabel: salesInput?.health?.paceLabel || "",
        busiestHour: salesInput?.health?.busiestHour || "1pm - 2pm",
        busiestHourRevenue: Number(salesInput?.health?.busiestHourRevenue ?? 485)
      },
      aov: {
        value: Number(salesInput?.aov?.value ?? 18.5),
        rangeLow: Number(salesInput?.aov?.rangeLow ?? 17),
        rangeHigh: Number(salesInput?.aov?.rangeHigh ?? 21),
        status: salesInput?.aov?.status || "",
        note: salesInput?.aov?.note || ""
      },
      salesMix: {
        netSales: Number(salesInput?.salesMix?.netSales ?? 4860),
        promoSales: Number(salesInput?.salesMix?.promoSales ?? 620),
        refundsComps: Number(salesInput?.salesMix?.refundsComps ?? 85)
      },
      revenueProgress: {
        points: revenueProgressPoints
      },
      revenueVsCost: {
        labels: revenueVsCostLabels,
        revenue: revenueVsCostRevenue,
        cost: revenueVsCostCost
      }
    },
    demand: {
      weekly,
      heatmap,
      peakHours,
      hourlyTemplate
    },
    staffing: {
      recommendation: staffingInput.recommendation || "You're slightly understaffed during the 7-9pm dinner rush. Consider adding 1 FOH staff member for those hours to maintain service quality during peak demand.",
      costVsSales: {
        staffingCost: Number(staffingInput?.costVsSales?.staffingCost ?? 680),
        expectedRevenue: Number(staffingInput?.costVsSales?.expectedRevenue ?? 5200),
        staffCostPercent: Number(staffingInput?.costVsSales?.staffCostPercent ?? 13.1),
        rangeLow: Number(staffingInput?.costVsSales?.rangeLow ?? 12),
        rangeHigh: Number(staffingInput?.costVsSales?.rangeHigh ?? 18),
        statusNote: staffingInput?.costVsSales?.statusNote || "Your staffing costs are within the healthy range. You're efficiently staffed for today's expected demand."
      },
      coverage,
      rota,
      totalStaffingCost: Number(staffingInput?.totalStaffingCost ?? 0)
    }
  };
}

const DEFAULT_MARKETING_DATA = {
  kpis: {
    reputationScore: 4.7,
    reputationMax: 5,
    reviewVelocity: 42,
    reviewVelocityDelta: 8,
    totalReach: 120000,
    totalReachDelta: 14.2,
    engagementRate: 6.1,
    engagementDelta: 0.8
  },
  alertSummary: {
    title: "Damage Control: Reputation Alert",
    subtitle: "2 Negative signals detected on Social Media",
    priority: "High Priority",
    insight: "A personalized response to the TikToker could convert the 12k reach into a PR win. Draft a response and recovery plan."
  },
  alerts: [
    { platform: "TikTok", user: "@foodie_hater", rating: 1, text: "Wait time was 45 mins after booking. Terrible management.", date: "4h ago", avatar: "FH", impact: "High Reach (12k views)" },
    { platform: "Google", user: "John D.", rating: 2, text: "Burger was cold. Service was slow today.", date: "6h ago", avatar: "JD", impact: "Profile Visibility" }
  ],
  contentFocus: {
    defaultFocus: "Awareness",
    copy: {
      Awareness: {
        headline: "Boost weekend reach",
        sub: "Short-form video + local hashtags around dinner rush.",
        channel: "Instagram Reels",
        window: "Fri 18:00–20:00",
        metricLabel: "Reach Lift",
        metricValue: "+18%",
        cta: "Build 7-day plan"
      },
      Reputation: {
        headline: "Reduce negative sentiment",
        sub: "Prioritize replies to 1–2 star reviews within 2 hours.",
        channel: "Google Reviews",
        window: "Daily 09:00–11:00",
        metricLabel: "Risk Level",
        metricValue: "Moderate",
        cta: "Open response queue"
      },
      Offers: {
        headline: "Drive weekday traffic",
        sub: "Limited lunch offer with upsell bundle to lift covers.",
        channel: "Meta + In-store",
        window: "Mon–Wed 11:00–13:00",
        metricLabel: "Projected Uplift",
        metricValue: "+12%",
        cta: "Launch offer"
      }
    }
  },
  platforms: [
    { name: "Instagram", icon: "IG", status: "Connected", followers: "12.4k", engagement: "4.8%" },
    { name: "TikTok", icon: "TT", status: "Connected", followers: "8.2k", engagement: "12.1%" },
    { name: "Facebook", icon: "FB", status: "Connected", followers: "5.1k", engagement: "2.3%" },
    { name: "Google", icon: "GG", status: "Connected", rating: "4.7", reviews: "842" },
  ],
  reach: {
    series: [40, 55, 45, 70, 65, 80, 75, 90, 85, 100, 95, 110, 105, 120],
    labels: ["1 Jan", "15 Jan", "30 Jan"]
  },
  topPosts: [
    { id: 1, platform: "Instagram", type: "Reel", reach: "24.5k", engagement: "1.2k", sentiment: "Positive", date: "2d ago", thumbnail: "R" },
    { id: 2, platform: "TikTok", type: "Video", reach: "48.1k", engagement: "5.4k", sentiment: "Highly Positive", date: "1d ago", thumbnail: "V" },
    { id: 3, platform: "Instagram", type: "Photo", reach: "8.2k", engagement: "420", sentiment: "Neutral", date: "4d ago", thumbnail: "P" },
    { id: 4, platform: "Facebook", type: "Post", reach: "3.1k", engagement: "156", sentiment: "Positive", date: "1w ago", thumbnail: "P" }
  ],
  reviews: [
    { platform: "Google", user: "Alex M.", rating: 5, text: "The truffle fries are a must-try! Best service in the area.", date: "1d ago", avatar: "AM" },
    { platform: "TripAdvisor", user: "Sarah J.", rating: 4, text: "Great atmosphere, though it was quite busy on Friday night.", date: "3d ago", avatar: "SJ" },
    { platform: "Google", user: "Michael R.", rating: 5, text: "Excellent burgers and the accurate wait times were spot on.", date: "5d ago", avatar: "MR" }
  ]
};

function normalizeManualMarketingData(input?: any) {
  const base = DEFAULT_MARKETING_DATA;
  const kpis = input?.kpis || {};
  const contentFocus = input?.contentFocus || {};
  const focusCopy = contentFocus.copy || contentFocus.focusCopy || base.contentFocus.copy;

  const normalizedFocus = {
    Awareness: { ...base.contentFocus.copy.Awareness, ...(focusCopy?.Awareness || {}) },
    Reputation: { ...base.contentFocus.copy.Reputation, ...(focusCopy?.Reputation || {}) },
    Offers: { ...base.contentFocus.copy.Offers, ...(focusCopy?.Offers || {}) }
  };

  const validFocus = ["Awareness", "Reputation", "Offers"];
  const defaultFocus = validFocus.includes(contentFocus.defaultFocus) ? contentFocus.defaultFocus : base.contentFocus.defaultFocus;

  const normalizedAlerts = Array.isArray(input?.alerts) && input.alerts.length > 0 ? input.alerts : base.alerts;
  const alerts = normalizedAlerts.map((alert: any, idx: number) => ({
    platform: alert.platform || base.alerts[idx]?.platform || "Platform",
    user: alert.user || base.alerts[idx]?.user || "User",
    rating: Number(alert.rating ?? base.alerts[idx]?.rating ?? 0),
    text: alert.text || base.alerts[idx]?.text || "",
    date: alert.date || base.alerts[idx]?.date || "",
    avatar: alert.avatar || base.alerts[idx]?.avatar || "NA",
    impact: alert.impact || base.alerts[idx]?.impact || ""
  }));

  const normalizedReviews = Array.isArray(input?.reviews) && input.reviews.length > 0 ? input.reviews : base.reviews;
  const reviews = normalizedReviews.map((rev: any, idx: number) => ({
    platform: rev.platform || base.reviews[idx]?.platform || "Platform",
    user: rev.user || base.reviews[idx]?.user || "Guest",
    rating: Number(rev.rating ?? base.reviews[idx]?.rating ?? 0),
    text: rev.text || base.reviews[idx]?.text || "",
    date: rev.date || base.reviews[idx]?.date || "",
    avatar: rev.avatar || base.reviews[idx]?.avatar || "NA"
  }));

  const reachSeriesRaw = Array.isArray(input?.reach?.series) && input.reach.series.length > 0
    ? input.reach.series
    : Array.isArray(input?.reachSeries) && input.reachSeries.length > 0
      ? input.reachSeries
      : base.reach.series;

  const reach = {
    series: reachSeriesRaw.map((v: any, idx: number) => Number.isFinite(Number(v)) ? Number(v) : Number(base.reach.series[idx] ?? 0)),
    labels: Array.isArray(input?.reach?.labels) && input.reach.labels.length > 0 ? input.reach.labels : base.reach.labels
  };

  return {
    kpis: {
      reputationScore: Number(kpis.reputationScore ?? base.kpis.reputationScore),
      reputationMax: Number(kpis.reputationMax ?? base.kpis.reputationMax),
      reviewVelocity: Number(kpis.reviewVelocity ?? base.kpis.reviewVelocity),
      reviewVelocityDelta: Number(kpis.reviewVelocityDelta ?? base.kpis.reviewVelocityDelta),
      totalReach: Number(kpis.totalReach ?? base.kpis.totalReach),
      totalReachDelta: Number(kpis.totalReachDelta ?? base.kpis.totalReachDelta),
      engagementRate: Number(kpis.engagementRate ?? base.kpis.engagementRate),
      engagementDelta: Number(kpis.engagementDelta ?? base.kpis.engagementDelta),
      reviewCount: kpis.reviewCount ?? undefined
    },
    alertSummary: {
      title: input?.alertSummary?.title || base.alertSummary.title,
      subtitle: input?.alertSummary?.subtitle || base.alertSummary.subtitle,
      priority: input?.alertSummary?.priority || base.alertSummary.priority,
      insight: input?.alertSummary?.insight || base.alertSummary.insight
    },
    alerts,
    contentFocus: {
      defaultFocus,
      copy: normalizedFocus
    },
    platforms: Array.isArray(input?.platforms) && input.platforms.length > 0 ? input.platforms : base.platforms,
    reach,
    topPosts: Array.isArray(input?.topPosts) && input.topPosts.length > 0 ? input.topPosts : base.topPosts,
    reviews
  };
}

// ------------------ Supabase Helpers ------------------

async function supaFetchTargets(): Promise<Target[]> {
  try {
    const { data, error } = await supabase
      .from('targets')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Convert DB fields to camelCase if necessary, or just use as is
    return (data || []).map((t: any) => ({
      id: t.id,
      userId: t.user_id,
      name: t.name,
      metric: t.metric,
      threshold: t.threshold,
      direction: t.direction,
      agentType: t.agent_type,
      fileScopeTags: t.file_scope_tags,
      createdAt: t.created_at,
      lastCheckedAt: t.last_checked_at,
      lastValue: t.last_value,
      status: t.status,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching targets", e);
    return getStoredTargets(); // Fallback to local
  }
}

async function supaSaveTarget(t: Target) {
  try {
    const { error } = await supabase
      .from('targets')
      .upsert({
        id: t.id,
        user_id: t.userId,
        name: t.name,
        metric: t.metric,
        threshold: t.threshold,
        direction: t.direction,
        agent_type: t.agentType,
        file_scope_tags: t.fileScopeTags,
        created_at: t.createdAt,
        last_checked_at: t.lastCheckedAt,
        last_value: t.lastValue,
        status: t.status,
      });
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error saving target", e);
    storeTargets([...getStoredTargets(), t]); // Fallback
  }
}

async function supaFetchNotifications(): Promise<TargetNotification[]> {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((n: any) => ({
      id: n.id,
      userId: n.user_id,
      targetId: n.target_id,
      targetName: n.target_name,
      metric: n.metric,
      message: n.message,
      agentType: n.agent_type,
      createdAt: n.created_at,
      read: n.read,
      value: n.value,
      threshold: n.threshold,
      direction: n.direction,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching notifications", e);
    return getStoredTargetNotifications();
  }
}

async function supaSaveNotification(n: TargetNotification) {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert({
        id: n.id,
        user_id: n.userId,
        target_id: n.targetId,
        target_name: n.targetName,
        metric: n.metric,
        message: n.message,
        agent_type: n.agentType,
        created_at: n.createdAt,
        read: n.read,
        value: n.value,
        threshold: n.threshold,
        direction: n.direction,
      });
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error saving notification", e);
    const current = getStoredTargetNotifications();
    storeTargetNotifications([n, ...current]);
  }
}

async function supaMarkNotificationRead(id: string) {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id);
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error marking notification read", e);
  }
}

async function supaFetchDecisions(): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('decisions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((d: any) => ({
      id: d.id,
      userId: d.user_id,
      decision: d.decision,
      context: d.context,
      expectedImpact: d.expected_impact,
      agentType: d.agent_type,
      timestamp: d.created_at,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching decisions", e);
    return [];
  }
}

async function supaSaveDecision(d: any) {
  try {
    const { error } = await supabase
      .from('decisions')
      .insert({
        id: d.id,
        user_id: d.userId,
        decision: d.decision,
        context: d.context,
        expected_impact: d.expectedImpact,
        agent_type: d.agentType,
        created_at: d.timestamp,
      });
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error saving decision", e);
  }
}

async function supaFetchFilesMetadata(userId: string): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((f: any) => ({
      id: f.id,
      userId: f.user_id,
      fileName: f.file_name,
      content: f.content,
      tags: f.tags,
      scope: Array.isArray(f.tags)
        ? (f.tags.find((t: any) => typeof t === "string" && t.toLowerCase().startsWith("scope:")) || "")
          .toString()
          .replace(/scope:/i, "")
          .trim()
        : null,
      uploadedAt: f.created_at,
      size: f.size,
      summary: f.summary,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching files", e);
    return getStoredFiles();
  }
}

async function supaSaveFileMetadata(f: any) {
  try {
    const { data, error } = await supabase
      .from('file_metadata')
      .upsert({
        user_id: f.userId,
        venue_id: f.venueId,
        file_name: f.fileName,
        storage_path: 'local_text',
        content: f.content,
        tags: f.tags,
        summary: f.summary,
        size: f.size,
      })
      .select()
      .single();

    if (error) {
      console.error("Supabase Error:", error);
      throw error;
    }
    return data;
  } catch (e: any) {
    console.error("Supabase: Error saving file metadata", e);
    throw e;
  }
}

async function supaDeleteFileRecord(fileId: number) {
  try {
    const { error } = await supabase
      .from('file_metadata')
      .delete()
      .eq('id', fileId);
    if (error) throw error;
    return true;
  } catch (e: any) {
    console.error("Supabase: Error deleting file record", e);
    return false;
  }
}

async function supaFetchManualInventory(userId: string) {
  try {
    const { data: files, error } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .eq('file_name', 'manual_inventory.json');
    if (error) throw error;
    if (files && files.length > 0) {
      return JSON.parse(files[0].content || "{}");
    }
    return null;
  } catch (e) {
    console.error("Error fetching manual inventory:", e);
    return null;
  }
}

async function supaSaveManualInventory(userId: string, venueId: string, content: any) {
  try {
    const { data: files } = await supabase
      .from('file_metadata')
      .select('id')
      .eq('user_id', userId)
      .eq('file_name', 'manual_inventory.json');

    const existingId = files?.[0]?.id;

    const { error: saveError } = await supabase
      .from('file_metadata')
      .upsert({
        ...(existingId ? { id: existingId } : {}),
        user_id: userId,
        venue_id: (venueId && venueId !== "" && venueId !== "null") ? venueId : null,
        file_name: 'manual_inventory.json',
        storage_path: 'local/manual_inventory.json',
        file_type: 'application/json',
        content: JSON.stringify(content),
        tags: ['Manual Data', 'Inventory'],
        size: JSON.stringify(content).length,
        created_at: new Date().toISOString()
      });

    if (saveError) throw saveError;
    return true;
  } catch (e) {
    console.error("Error saving manual inventory:", e);
    return false;
  }
}

async function supaFetchManualMenu(userId: string) {
  try {
    const { data: files, error } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .eq('file_name', 'manual_menu.json');
    if (error) throw error;
    if (files && files.length > 0) {
      return JSON.parse(files[0].content || "{}");
    }
    return null;
  } catch (e) {
    console.error("Error fetching manual menu data:", e);
    return null;
  }
}

async function supaSaveManualMenu(userId: string, venueId: string, content: any) {
  try {
    const { data: files } = await supabase
      .from('file_metadata')
      .select('id')
      .eq('user_id', userId)
      .eq('file_name', 'manual_menu.json');

    const existingId = files?.[0]?.id;

    const { error: saveError } = await supabase
      .from('file_metadata')
      .upsert({
        ...(existingId ? { id: existingId } : {}),
        user_id: userId,
        venue_id: (venueId && venueId !== "" && venueId !== "null") ? venueId : null,
        file_name: 'manual_menu.json',
        storage_path: 'local/manual_menu.json',
        file_type: 'application/json',
        content: JSON.stringify(content),
        tags: ['Manual Data', 'Menu'],
        size: JSON.stringify(content).length,
        created_at: new Date().toISOString()
      });

    if (saveError) throw saveError;
    return true;
  } catch (e) {
    console.error("Error saving manual menu data:", e);
    return false;
  }
}

async function supaFetchManualSales(userId: string) {
  try {
    const { data: files, error } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .eq('file_name', 'manual_sales.json');
    if (error) throw error;
    if (files && files.length > 0) {
      return JSON.parse(files[0].content || "{}");
    }
    return null;
  } catch (e) {
    console.error("Error fetching manual sales data:", e);
    return null;
  }
}

async function supaSaveManualSales(userId: string, venueId: string, content: any) {
  try {
    const { data: files } = await supabase
      .from('file_metadata')
      .select('id')
      .eq('user_id', userId)
      .eq('file_name', 'manual_sales.json');

    const existingId = files?.[0]?.id;

    const { error: saveError } = await supabase
      .from('file_metadata')
      .upsert({
        ...(existingId ? { id: existingId } : {}),
        user_id: userId,
        venue_id: (venueId && venueId !== "" && venueId !== "null") ? venueId : null,
        file_name: 'manual_sales.json',
        storage_path: 'local/manual_sales.json',
        file_type: 'application/json',
        content: JSON.stringify(content),
        tags: ['Manual Data', 'Sales'],
        size: JSON.stringify(content).length,
        created_at: new Date().toISOString()
      });

    if (saveError) throw saveError;
    return true;
  } catch (e) {
    console.error("Error saving manual sales data:", e);
    return false;
  }
}

async function supaFetchManualMarketing(userId: string) {
  try {
    const { data: files, error } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .eq('file_name', 'manual_marketing.json');
    if (error) throw error;
    if (files && files.length > 0) {
      return JSON.parse(files[0].content || "{}");
    }
    return null;
  } catch (e) {
    console.error("Error fetching manual marketing data:", e);
    return null;
  }
}

async function supaSaveManualMarketing(userId: string, venueId: string, content: any) {
  try {
    const { data: files } = await supabase
      .from('file_metadata')
      .select('id')
      .eq('user_id', userId)
      .eq('file_name', 'manual_marketing.json');

    const existingId = files?.[0]?.id;

    const { error: saveError } = await supabase
      .from('file_metadata')
      .upsert({
        ...(existingId ? { id: existingId } : {}),
        user_id: userId,
        venue_id: (venueId && venueId !== "" && venueId !== "null") ? venueId : null,
        file_name: 'manual_marketing.json',
        storage_path: 'local/manual_marketing.json',
        file_type: 'application/json',
        content: JSON.stringify(content),
        tags: ['Manual Data', 'Marketing'],
        size: JSON.stringify(content).length,
        created_at: new Date().toISOString()
      });

    if (saveError) throw saveError;
    return true;
  } catch (e) {
    console.error("Error saving manual marketing data:", e);
    return false;
  }
}

async function supaUpdateManualMetric(userId: string, venueId: string, metric: string, value: any) {
  try {
    const { data: files, error: fetchError } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .eq('file_name', 'manual_data.json');

    if (fetchError) throw fetchError;

    let contentObj: any = {};
    let existingFileId: string | null = null;

    if (files && files.length > 0) {
      existingFileId = files[0].id;
      try {
        contentObj = JSON.parse(files[0].content || "{}");
      } catch (e) { contentObj = {}; }
    }

    contentObj[metric] = value;
    contentObj.updatedAt = new Date().toISOString();

    const { error: saveError } = await supabase
      .from('file_metadata')
      .upsert({
        ...(existingFileId ? { id: existingFileId } : {}),
        user_id: userId,
        venue_id: venueId,
        file_name: 'manual_data.json',
        storage_path: 'manual_data',
        content: JSON.stringify(contentObj, null, 2),
        tags: ['Manual Entry', 'Dashboard Data'],
        size: JSON.stringify(contentObj).length,
      });

    if (saveError) throw saveError;
    console.log(`Supabase: Manual metric ${metric} updated to ${value}`);
  } catch (e: any) {
    console.error("Supabase: Error updating manual metric", e);
  }
}

async function supaUpsertManualMetrics(userId: string, venueId: string, patch: Record<string, any>) {
  if (!patch || Object.keys(patch).length === 0) return;
  try {
    const { data: files, error: fetchError } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .eq('file_name', 'manual_data.json');

    if (fetchError) throw fetchError;

    let contentObj: any = {};
    let existingFileId: any = null;

    if (files && files.length > 0) {
      existingFileId = files[0].id;
      try {
        contentObj = JSON.parse(files[0].content || "{}");
      } catch {
        contentObj = {};
      }
    }

    const nextObj = { ...contentObj, ...patch, updatedAt: new Date().toISOString() };

    const { error: saveError } = await supabase
      .from('file_metadata')
      .upsert({
        ...(existingFileId ? { id: existingFileId } : {}),
        user_id: userId,
        venue_id: venueId,
        file_name: 'manual_data.json',
        storage_path: 'manual_data',
        content: JSON.stringify(nextObj, null, 2),
        tags: ['Manual Entry', 'Dashboard Data'],
        size: JSON.stringify(nextObj).length,
      });

    if (saveError) throw saveError;
    console.log(`Supabase: Manual metrics batch updated (${Object.keys(patch).length})`);
  } catch (e: any) {
    console.error("Supabase: Error batch-updating manual metrics", e);
  }
}

async function supaFetchVenueProfile(userId: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('full_name, role, kpi_layout, widget_layout, venues(*)')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data;
  } catch (e: any) {
    console.error("Supabase: Error fetching venue/profile", e);
    return null;
  }
}

async function supaUpdateKpiLayout(userId: string, layout: string[]) {
  if (!supabase) return;
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ kpi_layout: layout })
      .eq('id', userId);
    if (error) throw error;
    console.log("[Findsy] KPI Layout saved:", layout);
  } catch (e) {
    console.error("[Findsy] Error saving layout:", e);
  }
}

async function supaUpdateVenueProfile(userId: string, venueId: string, updates: { fullName?: string; venueName?: string; businessType?: string }) {
  try {
    if (updates.fullName) {
      await supabase.from('profiles').update({ full_name: updates.fullName }).eq('id', userId);
    }
    if (venueId && (updates.venueName || updates.businessType)) {
      const vUpdates: any = {};
      if (updates.venueName) vUpdates.name = updates.venueName;
      if (updates.businessType) vUpdates.business_type = updates.businessType;
      await supabase.from('venues').update(vUpdates).eq('id', venueId);
    }
  } catch (e: any) {
    console.error("Supabase: Error updating venue/profile", e);
    throw e;
  }
}

const TARGETS_STORAGE_KEY = "findsy_targets";
const TARGET_NOTIF_STORAGE_KEY = "findsy_target_notifications";
const SQUARE_AUTH_STORAGE_KEY = "findsy_square_auth";
const SQUARE_SYNC_STORAGE_KEY = "findsy_square_last_sync";

type SquareAuth = {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: string;
  merchantId?: string;
  updatedAt?: string;
};

function getStoredTargets(): Target[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(TARGETS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e: any) {
    console.error("Error reading targets:", e);
    return [];
  }
}

function storeTargets(targets: Target[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(TARGETS_STORAGE_KEY, JSON.stringify(targets));
  } catch (e: any) {
    console.error("Error storing targets:", e);
  }
}

function getStoredTargetNotifications(): TargetNotification[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(TARGET_NOTIF_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Error reading target notifications:", e);
    return [];
  }
}

function storeTargetNotifications(notifs: TargetNotification[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(TARGET_NOTIF_STORAGE_KEY, JSON.stringify(notifs));
  } catch (e) {
    console.error("Error storing target notifications:", e);
  }
}

function readSquareAuth(): SquareAuth | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SQUARE_AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error("Error reading Square auth:", e);
    return null;
  }
}

function storeSquareAuth(auth: SquareAuth) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SQUARE_AUTH_STORAGE_KEY, JSON.stringify(auth));
  } catch (e) {
    console.error("Error storing Square auth:", e);
  }
}

function storeSquareLastSync(ts: string) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SQUARE_SYNC_STORAGE_KEY, ts);
  } catch (e) {
    console.error("Error storing Square last sync:", e);
  }
}

function readSquareLastSync(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(SQUARE_SYNC_STORAGE_KEY);
  } catch (e) {
    console.error("Error reading Square last sync:", e);
    return null;
  }
}

// ------------------ Target evaluation ------------------
const METRIC_ALIASES: Record<string, string[]> = {
  total_sales: ["revenue", "sales", "total sales", "gross sales", "turnover"],
  covers: ["guests", "customers", "covers", "total covers", "heads"],
  aov: ["average order value", "average spend", "spend per head", "basket size"],
  gross_margin: ["margin", "gross margin", "profit margin", "gp percentage"],
  sales_pace: ["pace", "sales pace", "current pace", "pace %", "vs target"],
  social_reach: ["social reach", "impressions", "followers", "engagement", "reach"],
  food_cost_pct: ["food cost", "food cost %", "food cost pct", "food cost percentage", "cogs", "cogs %", "cost of goods"],
  labor_cost_pct: ["labor", "labour", "labor %", "labour %", "labor cost %", "labour cost %", "payroll %", "staff cost %"],
  waste_rate: ["waste", "wastage", "waste %", "waste rate", "spoilage", "shrink"],
  inventory_value: ["inventory value", "stock value", "inventory valuation", "stock valuation", "total stock value"],
};

function findMetricInFiles(metric: string, files: any[]): number | undefined {
  const raw = findRawMetricInFiles(metric, files);
  return typeof raw === "number" ? raw : undefined;
}

function findRawMetricInFiles(metric: string, files: any[]): any {
  const aliases = [metric.toLowerCase(), ...(METRIC_ALIASES[metric] || [])];
  const scanJsonContent = (content: string) => {
    try {
      const obj = JSON.parse(content);
      if (!obj || typeof obj !== "object") return undefined;
      for (const alias of aliases) {
        if ((obj as any)[alias] !== undefined) return (obj as any)[alias];
        for (const k of Object.keys(obj)) {
          if (k.toLowerCase().includes(alias)) return (obj as any)[k];
        }
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  // Prefer the canonical KPI store first so dashboards stay consistent even if other files also contain metrics.
  const manualDataFile = (files || []).find((f: any) => String(f?.fileName || "").toLowerCase() === "manual_data.json");
  if (manualDataFile) {
    const v = scanJsonContent(String(manualDataFile.content || ""));
    if (v !== undefined) return v;
  }

  const matchedFiles = [...(files || [])].sort((a: any, b: any) => (b.uploadedAt || 0) > (a.uploadedAt || 0) ? 1 : -1);

  for (const file of matchedFiles) {
    const name = String(file.fileName || "").toLowerCase();
    const content = String(file.content || "");

    // 1. JSON Search
    if (name.endsWith(".json") || content.trim().startsWith("{")) {
      const v = scanJsonContent(content);
      if (v !== undefined) return v;
    }

    // 2. CSV Search
    if (name.endsWith(".csv") || content.includes(",")) {
      const lines = content.split(/\r?\n/).filter(Boolean);
      if (lines.length >= 2) {
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
        for (const alias of aliases) {
          const metricIdx = headers.findIndex((h) => h === alias);
          if (metricIdx >= 0) {
            const lastRow = lines[lines.length - 1].split(",");
            const v = parseFloat((lastRow[metricIdx] || "").replace(/[^0-9.-]/g, ""));
            if (!isNaN(v)) return v;
          }
        }
      }
    }

    // 3. Pattern Search (TXT/other)
    for (const alias of aliases) {
      const escapedMetric = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`${escapedMetric}\\s*[:=\\-]?\\s*[£$]?\\s*([0-9,.]+)`, "i");
      const m = content.match(regex);
      if (m) {
        const num = parseFloat(m[1].replace(/,/g, ""));
        if (!isNaN(num)) return num;
      }
    }
  }
  return undefined;
}

async function evaluateTarget(target: Target, uploadedFiles: any[]): Promise<{ triggered: boolean; value?: number }> {
  const metric = target.metric;
  const candidates = uploadedFiles || getStoredFiles();
  const foundValue = findMetricInFiles(metric, candidates);

  // If not found in files, check if we should return a mock value for demo/dashboard consistency
  let finalValue = foundValue;
  if (finalValue === undefined) {
    const key = metric.toLowerCase();
    if (key.includes("sales") || key.includes("revenue")) finalValue = 4000 + (Math.random() * 1000);
    else if (key.includes("cost") || key.includes("waste")) finalValue = 10 + (Math.random() * 20);
    else if (key.includes("margin")) finalValue = 60 + (Math.random() * 10);
    else if (key.includes("covers")) finalValue = 100 + (Math.random() * 50);
    else finalValue = target.threshold * (0.8 + Math.random() * 0.4); // Random value near threshold
  }

  const tolerance = target.tolerance || 0;
  const threshold = target.threshold;
  let triggered = false;
  if (target.direction === "above") triggered = finalValue > threshold + tolerance;
  else triggered = finalValue < threshold - tolerance;

  return { triggered, value: finalValue };
}

// ------------------ Upload → Dataset Ingestion ------------------

type UploadScope = "inventory" | "menu" | "sales" | "marketing" | "invoices";

type IngestRequirementDef = {
  key: string;
  headers: string[];
};

const INGEST_REQUIREMENTS_BY_SCOPE: Record<UploadScope, IngestRequirementDef[]> = {
  inventory: [
    { key: "items", headers: ["item_name", "category", "unit", "current_qty", "cost_per_unit", "min_threshold"] },
    { key: "waste", headers: ["item_name", "quantity", "reason", "value", "date"] },
  ],
  menu: [
    { key: "menu_items", headers: ["item_name", "category", "price", "cost", "prep_time"] },
    { key: "menu_performance", headers: ["item_name", "orders_per_week", "trend"] },
  ],
  sales: [
    { key: "sales_summary", headers: ["date", "revenue_so_far", "expected_by_now", "full_day_target", "transactions"] },
    { key: "revenue_vs_cost", headers: ["week_label", "revenue", "cost"] },
    { key: "demand_forecast", headers: ["day", "predicted_revenue", "predicted_covers", "weather"] },
    { key: "staffing_rota", headers: ["staff_name", "role", "shift", "cost"] },
  ],
  marketing: [
    { key: "marketing_kpis", headers: ["reputation_score", "review_velocity", "total_reach", "engagement_rate"] },
    { key: "social_reach", headers: ["date", "reach"] },
    { key: "reviews", headers: ["platform", "user", "rating", "text", "date"] },
    { key: "top_content", headers: ["platform", "type", "reach", "engagement", "sentiment", "date"] },
  ],
  invoices: [
    { key: "invoice_header", headers: ["supplier_name", "invoice_number", "invoice_date", "total_amount", "currency"] },
    { key: "invoice_items", headers: ["item_name", "quantity", "unit_cost", "line_total"] },
    { key: "payment_terms", headers: ["invoice_number", "due_date", "payment_status"] },
  ],
};

const INGEST_DELIMITERS = ["\t", ",", ";", "|"] as const;

function normalizeHeaderToken(value: string) {
  return (value || "")
    .replace(/^\uFEFF/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function inferUploadScope(fileRecord: any): UploadScope | null {
  const direct = String(fileRecord?.scope || "").toLowerCase().trim();
  if (direct && ["inventory", "menu", "sales", "marketing", "invoices"].includes(direct)) return direct as UploadScope;
  const tags = Array.isArray(fileRecord?.tags) ? fileRecord.tags : [];
  const tagScope = tags
    .map((t: any) => String(t || ""))
    .find((t: string) => t.toLowerCase().startsWith("scope:"));
  if (!tagScope) return null;
  const s = tagScope.replace(/scope:/i, "").trim().toLowerCase();
  return (["inventory", "menu", "sales", "marketing", "invoices"].includes(s) ? (s as UploadScope) : null);
}

function splitDelimitedLine(line: string, delimiter: string) {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  const s = String(line || "");

  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') {
      if (inQuotes && s[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && ch === delimiter) {
      out.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function parseNumberLoose(value: any): number | null {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const cleaned = s.replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function formatCompactNumber(value: number) {
  if (!Number.isFinite(value)) return "";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${Math.round(value)}`;
}

function toIsoDateMaybe(value: any) {
  const s = String(value || "").trim();
  if (!s) return new Date().toISOString();
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

type ParsedTable = {
  delimiter: string;
  headerIndex: number;
  rawHeaders: string[];
  headers: string[]; // normalized
  rows: Record<string, string>[];
};

function parseTableFromLines(lines: string[], headerIndex: number, delimiter: string): ParsedTable {
  const rawHeaders = splitDelimitedLine(lines[headerIndex] || "", delimiter)
    .map((c) => c.replace(/^\uFEFF/, "").trim())
    .filter(Boolean);
  const headers = rawHeaders.map(normalizeHeaderToken);

  const rows: Record<string, string>[] = [];
  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = (lines[i] || "").trim();
    if (!line) continue;
    const cells = splitDelimitedLine(line, delimiter);
    const row: Record<string, string> = {};
    let nonEmpty = 0;
    for (let c = 0; c < headers.length; c++) {
      const key = headers[c] || `col${c}`;
      const v = (cells[c] ?? "").trim();
      row[key] = v;
      if (v) nonEmpty += 1;
    }
    if (nonEmpty === 0) continue;
    rows.push(row);
  }

  return { delimiter, headerIndex, rawHeaders, headers, rows };
}

function findBestRequirementTable(content: string, scope: UploadScope): { requirementKey: string; table: ParsedTable } | null {
  const requirements = INGEST_REQUIREMENTS_BY_SCOPE[scope] || [];
  const lines = String(content || "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) return null;

  let best: { requirementKey: string; table: ParsedTable; score: number; ratio: number } | null = null;

  const scanLimit = Math.min(lines.length - 1, 25);
  for (let headerIndex = 0; headerIndex <= scanLimit; headerIndex++) {
    const headerLine = lines[headerIndex];
    for (const delimiter of INGEST_DELIMITERS) {
      if (!headerLine.includes(delimiter)) continue;
      const rawHeaders = splitDelimitedLine(headerLine, delimiter).filter(Boolean);
      if (rawHeaders.length < 2) continue;
      const normalizedHeaders = rawHeaders.map(normalizeHeaderToken);

      const hasDataRow = (() => {
        const lookAhead = Math.min(lines.length - 1, headerIndex + 20);
        for (let i = headerIndex + 1; i <= lookAhead; i++) {
          const line = lines[i];
          if (!line) continue;
          const cells = splitDelimitedLine(line, delimiter).map((c) => c.trim());
          if (cells.filter(Boolean).length >= 2) return true;
        }
        return false;
      })();
      if (!hasDataRow) continue;

      for (const req of requirements) {
        const required = req.headers.map(normalizeHeaderToken);
        const matchCount = required.filter((h) => normalizedHeaders.includes(h)).length;
        const threshold = Math.min(3, required.length);
        if (matchCount < threshold) continue;

        const ratio = required.length > 0 ? matchCount / required.length : 0;
        const score = matchCount;
        if (!best || score > best.score || (score === best.score && ratio > best.ratio)) {
          best = {
            requirementKey: req.key,
            table: parseTableFromLines(lines, headerIndex, delimiter),
            score,
            ratio
          };
        }
      }
    }
  }

  return best ? { requirementKey: best.requirementKey, table: best.table } : null;
}

function mapInventoryCategory(value: any): InventoryItem["category"] {
  const raw = String(value || "").trim();
  const v = raw.toLowerCase();
  const canonical: Record<string, InventoryItem["category"]> = {
    "protein": "Protein",
    "produce": "Produce",
    "dairy": "Dairy",
    "drygoods": "Dry Goods",
    "alcohol": "Alcohol",
    "beverage": "Beverage"
  };
  const normalized = normalizeHeaderToken(raw);
  if (canonical[normalized]) return canonical[normalized];
  if (v.includes("protein") || v.includes("meat") || v.includes("poultry") || v.includes("fish") || v.includes("seafood")) return "Protein";
  if (v.includes("produce") || v.includes("veg") || v.includes("fruit")) return "Produce";
  if (v.includes("dairy") || v.includes("cheese") || v.includes("milk")) return "Dairy";
  if (v.includes("alcohol") || v.includes("wine") || v.includes("beer") || v.includes("spirit")) return "Alcohol";
  if (v.includes("beverage") || v.includes("drink")) return "Beverage";
  return "Dry Goods";
}

function computeInventoryStatus(currentQty: number, minThreshold: number): InventoryStatus {
  if (!Number.isFinite(currentQty) || !Number.isFinite(minThreshold)) return "OK";
  if (currentQty <= Math.max(0, minThreshold * 0.5)) return "Critical";
  if (currentQty < minThreshold) return "Low";
  if (minThreshold > 0 && currentQty > minThreshold * 4) return "Overstocked";
  return "OK";
}

function mapWasteReason(value: any): WasteRecord["reason"] {
  const v = String(value || "").toLowerCase();
  if (v.includes("damag")) return "Damaged";
  if (v.includes("prep")) return "Prep Error";
  if (v.includes("spill")) return "Spillage";
  if (v.includes("theft")) return "Theft";
  return "Expired";
}

function mapMenuCategory(value: any): MenuItemInput["category"] {
  const v = String(value || "").toLowerCase();
  if (v.includes("app")) return "Appetizer";
  if (v.includes("dess")) return "Dessert";
  if (v.includes("bev") || v.includes("drink")) return "Beverage";
  return "Main";
}

function mapPrepTime(value: any): MenuItemInput["prepTime"] {
  const v = String(value || "").toLowerCase();
  if (v.includes("low") || v.includes("quick") || v.includes("fast")) return "Low";
  if (v.includes("high") || v.includes("long") || v.includes("slow")) return "High";
  return "Medium";
}

function mapTrend(value: any): MenuItemInput["trend"] {
  const v = String(value || "").toLowerCase();
  if (v.includes("up") || v.includes("rise") || v.includes("increas")) return "up";
  if (v.includes("down") || v.includes("fall") || v.includes("decreas")) return "down";
  return "stable";
}

function mapWeather(value: any): DailyForecast["weather"]["condition"] {
  const v = String(value || "").toLowerCase();
  if (v.includes("sun")) return "Sunny";
  if (v.includes("rain") || v.includes("show")) return "Rain";
  if (v.includes("snow")) return "Snow";
  return "Cloudy";
}

function toDayAbbrev(value: any) {
  const v = String(value || "").trim().toLowerCase();
  const map: Record<string, string> = {
    monday: "Mon", mon: "Mon",
    tuesday: "Tue", tue: "Tue", tues: "Tue",
    wednesday: "Wed", wed: "Wed",
    thursday: "Thu", thu: "Thu", thur: "Thu", thurs: "Thu",
    friday: "Fri", fri: "Fri",
    saturday: "Sat", sat: "Sat",
    sunday: "Sun", sun: "Sun",
  };
  if (map[v]) return map[v];
  const short = v.slice(0, 3);
  return short ? `${short[0].toUpperCase()}${short.slice(1)}` : "Day";
}

function initialsForName(name: any) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "NA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function computeFoodCostPctFromMenu(items: MenuItemInput[]): number | null {
  const usable = (items || []).filter((i) => Number(i.price) > 0 && Number(i.cost) >= 0);
  if (usable.length === 0) return null;

  let weightedSum = 0;
  let weightTotal = 0;
  for (const i of usable) {
    const costPct = (Number(i.cost) / Math.max(Number(i.price), 1e-9)) * 100;
    const weight = Number(i.ordersPerWeek) > 0 ? Number(i.ordersPerWeek) : 1;
    weightedSum += costPct * weight;
    weightTotal += weight;
  }
  if (weightTotal <= 0) return null;
  const pct = weightedSum / weightTotal;
  return Math.max(0, Math.min(100, pct));
}

function computeInventoryValueFromItems(items: InventoryItem[]): number | null {
  if (!Array.isArray(items) || items.length === 0) return null;
  const total = items.reduce((acc, item) => acc + (Number(item.currentQty) || 0) * (Number(item.costPerUnit) || 0), 0);
  return Number.isFinite(total) ? total : null;
}

function computeWasteValue(waste: WasteRecord[]): number | null {
  if (!Array.isArray(waste) || waste.length === 0) return null;
  const total = waste.reduce((acc, w) => acc + (Number(w.value) || 0), 0);
  return Number.isFinite(total) ? total : null;
}

function computeKpiPatchFromManualData(datasets: { inventory?: any; menu?: any; sales?: any; marketing?: any }): Record<string, number> {
  const patch: Record<string, number> = {};

  const salesNormalized = datasets.sales ? normalizeManualSalesData(datasets.sales) : null;
  const revenueSoFar = salesNormalized ? Number(salesNormalized.sales?.today?.revenueSoFar ?? NaN) : NaN;
  const expectedByNow = salesNormalized ? Number(salesNormalized.sales?.today?.expectedByNow ?? NaN) : NaN;
  const transactions = salesNormalized ? Number(salesNormalized.sales?.health?.transactions ?? NaN) : NaN;
  const pacePercent = (Number.isFinite(revenueSoFar) && Number.isFinite(expectedByNow) && expectedByNow > 0)
    ? (revenueSoFar / expectedByNow) * 100
    : (salesNormalized ? Number(salesNormalized.sales?.health?.pacePercent ?? NaN) : NaN);

  if (Number.isFinite(revenueSoFar)) patch.total_sales = Math.round(revenueSoFar);
  if (Number.isFinite(transactions)) patch.covers = Math.round(transactions);
  if (Number.isFinite(revenueSoFar) && Number.isFinite(transactions) && transactions > 0) patch.aov = Number((revenueSoFar / transactions).toFixed(2));
  if (Number.isFinite(pacePercent)) patch.sales_pace = Math.round(pacePercent);

  const staffCostPercent = salesNormalized ? Number(salesNormalized.staffing?.costVsSales?.staffCostPercent ?? NaN) : NaN;
  if (Number.isFinite(staffCostPercent)) patch.labor_cost_pct = Number(staffCostPercent.toFixed(1));

  const inventoryItems: InventoryItem[] = Array.isArray(datasets.inventory?.items) ? datasets.inventory.items : [];
  const inventoryValue = computeInventoryValueFromItems(inventoryItems);
  if (inventoryValue !== null) patch.inventory_value = Math.round(inventoryValue);

  const wasteList: WasteRecord[] = Array.isArray(datasets.inventory?.waste) ? datasets.inventory.waste : [];
  const wasteValue = computeWasteValue(wasteList);
  if (wasteValue !== null && Number.isFinite(revenueSoFar) && revenueSoFar > 0) {
    patch.waste_rate = Number(((wasteValue / revenueSoFar) * 100).toFixed(1));
  }

  const menuItems: MenuItemInput[] = Array.isArray(datasets.menu?.items)
    ? datasets.menu.items.map(normalizeMenuItemInput).filter(Boolean) as MenuItemInput[]
    : [];
  const foodCostPct = computeFoodCostPctFromMenu(menuItems);
  if (foodCostPct !== null) patch.food_cost_pct = Number(foodCostPct.toFixed(1));

  const marketingNormalized = datasets.marketing ? normalizeManualMarketingData(datasets.marketing) : null;
  const socialReach = marketingNormalized ? Number(marketingNormalized.kpis?.totalReach ?? NaN) : NaN;
  if (Number.isFinite(socialReach)) patch.social_reach = Math.round(socialReach);

  return patch;
}

function applyIngestToInventory(existing: any, requirementKey: string, table: ParsedTable) {
  const base = existing && typeof existing === "object" ? existing : {};
  const itemsExisting: InventoryItem[] = Array.isArray(base.items) ? base.items : [];
  const wasteExisting: WasteRecord[] = Array.isArray(base.waste) ? base.waste : [];

  if (requirementKey === "items") {
    const nextItems: InventoryItem[] = table.rows
      .map((row, idx) => {
        const name = String(row["itemname"] || row["name"] || "").trim();
        if (!name) return null;
        const category = mapInventoryCategory(row["category"]);
        const unit = String(row["unit"] || "pcs").trim() || "pcs";
        const currentQty = parseNumberLoose(row["currentqty"]) ?? 0;
        const costPerUnit = parseNumberLoose(row["costperunit"]) ?? 0;
        const minThreshold = parseNumberLoose(row["minthreshold"]) ?? 0;
        const status = computeInventoryStatus(currentQty, minThreshold);
        return {
          id: `upl_inv_${Date.now()}_${idx}`,
          name,
          category,
          currentQty,
          unit,
          minThreshold,
          avgDailyUsage: 1,
          costPerUnit,
          supplierId: "upload",
          lastDelivered: new Date().toISOString(),
          status
        } as InventoryItem;
      })
      .filter(Boolean) as InventoryItem[];

    const existingByName = new Map(itemsExisting.map((i) => [i.name.toLowerCase(), i]));
    const merged = nextItems.map((it) => {
      const prev = existingByName.get(it.name.toLowerCase());
      // Preserve stable fields if present
      return prev ? { ...prev, ...it } : it;
    });

    return { ...base, items: merged, waste: wasteExisting };
  }

  if (requirementKey === "waste") {
    const itemsByName = new Map(itemsExisting.map((i) => [i.name.toLowerCase(), i]));
    const nextWaste: WasteRecord[] = table.rows
      .map((row, idx) => {
        const itemName = String(row["itemname"] || row["name"] || "").trim();
        if (!itemName) return null;
        const qty = parseNumberLoose(row["quantity"]) ?? 0;
        const value = parseNumberLoose(row["value"]) ?? 0;
        const reason = mapWasteReason(row["reason"]);
        const date = toIsoDateMaybe(row["date"]);
        const matchedItem = itemsByName.get(itemName.toLowerCase());
        return {
          id: `upl_waste_${Date.now()}_${idx}`,
          itemId: matchedItem?.id || "upload",
          itemName,
          quantity: qty,
          reason,
          value,
          date
        } as WasteRecord;
      })
      .filter(Boolean) as WasteRecord[];

    return { ...base, items: itemsExisting, waste: nextWaste };
  }

  return null;
}

function applyIngestToMenu(existing: any, requirementKey: string, table: ParsedTable) {
  const base = existing && typeof existing === "object" ? existing : {};
  const existingItems: MenuItemInput[] = Array.isArray(base.items)
    ? base.items.map(normalizeMenuItemInput).filter(Boolean) as MenuItemInput[]
    : [];
  const existingByName = new Map(existingItems.map((i) => [i.name.toLowerCase(), i]));

  if (requirementKey === "menu_items") {
    const nextItemsRaw: MenuItemInput[] = table.rows
      .map((row, idx) => {
        const name = String(row["itemname"] || row["name"] || "").trim();
        if (!name) return null;
        const category = mapMenuCategory(row["category"]);
        const price = parseNumberLoose(row["price"]) ?? 0;
        const cost = parseNumberLoose(row["cost"]) ?? 0;
        const prepTime = mapPrepTime(row["preptime"]);
        const prev = existingByName.get(name.toLowerCase());
        return {
          id: prev?.id || `upl_menu_${Date.now()}_${idx}`,
          name,
          category,
          price,
          cost,
          ordersPerWeek: prev?.ordersPerWeek ?? 0,
          prepTime,
          operationalSignals: prev?.operationalSignals ?? [],
          trend: prev?.trend ?? "stable"
        } as MenuItemInput;
      })
      .filter(Boolean) as MenuItemInput[];

    return { ...base, items: nextItemsRaw };
  }

  if (requirementKey === "menu_performance") {
    const next = [...existingItems];
    const indexByName = new Map(next.map((i, idx) => [i.name.toLowerCase(), idx]));
    table.rows.forEach((row, idx) => {
      const name = String(row["itemname"] || row["name"] || "").trim();
      if (!name) return;
      const orders = parseNumberLoose(row["ordersperweek"]) ?? 0;
      const trend = mapTrend(row["trend"]);
      const key = name.toLowerCase();
      const existingIdx = indexByName.get(key);
      if (existingIdx !== undefined) {
        next[existingIdx] = { ...next[existingIdx], ordersPerWeek: orders, trend };
      } else {
        next.push({
          id: `upl_menuperf_${Date.now()}_${idx}`,
          name,
          category: "Main",
          price: 0,
          cost: 0,
          ordersPerWeek: orders,
          prepTime: "Medium",
          operationalSignals: [],
          trend
        });
      }
    });
    return { ...base, items: next };
  }

  return null;
}

function applyIngestToSales(existing: any, requirementKey: string, table: ParsedTable) {
  const base = normalizeManualSalesData(existing);

  if (requirementKey === "sales_summary") {
    const parsed = table.rows
      .map((row) => {
        const dateRaw = String(row["date"] || "").trim();
        const date = dateRaw ? new Date(dateRaw) : new Date(0);
        const revenueSoFar = parseNumberLoose(row["revenuesofar"]);
        const expectedByNow = parseNumberLoose(row["expectedbynow"]);
        const fullDayTarget = parseNumberLoose(row["fulldaytarget"]);
        const transactions = parseNumberLoose(row["transactions"]);
        return {
          date,
          revenueSoFar: revenueSoFar ?? 0,
          expectedByNow: expectedByNow ?? 0,
          fullDayTarget: fullDayTarget ?? 0,
          transactions: transactions ?? 0,
        };
      })
      .filter((r) => Number.isFinite(r.date.getTime()));

    if (parsed.length === 0) return base;
    parsed.sort((a, b) => b.date.getTime() - a.date.getTime());
    const latest = parsed[0];

    const pacePercent = latest.expectedByNow > 0 ? (latest.revenueSoFar / latest.expectedByNow) * 100 : base.sales.health.pacePercent;
    const aov = latest.transactions > 0 ? latest.revenueSoFar / latest.transactions : base.sales.aov.value;

    const paceLabel = pacePercent >= 105 ? "Ahead of pace" : pacePercent <= 95 ? "Behind pace" : "On pace";

    return {
      ...base,
      sales: {
        ...base.sales,
        today: {
          ...base.sales.today,
          revenueSoFar: latest.revenueSoFar,
          expectedByNow: latest.expectedByNow,
          fullDayTarget: latest.fullDayTarget,
        },
        health: {
          ...base.sales.health,
          transactions: latest.transactions,
          pacePercent: Math.round(pacePercent),
          paceLabel
        },
        aov: {
          ...base.sales.aov,
          value: Number(aov.toFixed(2))
        }
      }
    };
  }

  if (requirementKey === "revenue_vs_cost") {
    const labels: string[] = [];
    const revenue: number[] = [];
    const cost: number[] = [];
    table.rows.forEach((row) => {
      const label = String(row["weeklabel"] || row["label"] || "").trim();
      const rev = parseNumberLoose(row["revenue"]);
      const c = parseNumberLoose(row["cost"]);
      if (!label || rev === null || c === null) return;
      labels.push(label);
      revenue.push(rev);
      cost.push(c);
    });
    if (labels.length === 0) return base;

    return {
      ...base,
      sales: {
        ...base.sales,
        revenueVsCost: { labels, revenue, cost }
      }
    };
  }

  if (requirementKey === "demand_forecast") {
    const nextWeekly = base.demand.weekly.map((d: any) => ({ ...d, weather: { ...d.weather } }));
    table.rows.forEach((row) => {
      const day = toDayAbbrev(row["day"]);
      const predictedRevenue = parseNumberLoose(row["predictedrevenue"]);
      const predictedCovers = parseNumberLoose(row["predictedcovers"]);
      const weather = mapWeather(row["weather"]);
      const idx = nextWeekly.findIndex((d: any) => String(d.dayName || "").toLowerCase().startsWith(day.toLowerCase()));
      if (idx < 0) return;
      if (predictedRevenue !== null) nextWeekly[idx].predictedRevenue = predictedRevenue;
      if (predictedCovers !== null) nextWeekly[idx].predictedCovers = predictedCovers;
      nextWeekly[idx].weather.condition = weather;
    });

    return {
      ...base,
      demand: {
        ...base.demand,
        weekly: nextWeekly
      }
    };
  }

  if (requirementKey === "staffing_rota") {
    const rota = table.rows
      .map((row) => {
        const name = String(row["staffname"] || row["name"] || "").trim();
        if (!name) return null;
        return {
          name,
          role: String(row["role"] || "").trim(),
          shift: String(row["shift"] || "").trim(),
          cost: parseNumberLoose(row["cost"]) ?? 0
        };
      })
      .filter(Boolean) as any[];
    if (rota.length === 0) return base;
    const totalStaffingCost = rota.reduce((acc, r) => acc + (Number(r.cost) || 0), 0);

    const revenueSoFar = Number(base.sales.today.revenueSoFar || 0);
    const staffCostPercent = revenueSoFar > 0 ? (totalStaffingCost / revenueSoFar) * 100 : base.staffing.costVsSales.staffCostPercent;

    return {
      ...base,
      staffing: {
        ...base.staffing,
        rota,
        totalStaffingCost,
        costVsSales: {
          ...base.staffing.costVsSales,
          staffingCost: totalStaffingCost,
          staffCostPercent: Number(staffCostPercent.toFixed(1))
        }
      }
    };
  }

  return base;
}

function applyIngestToMarketing(existing: any, requirementKey: string, table: ParsedTable) {
  const base = normalizeManualMarketingData(existing);

  if (requirementKey === "marketing_kpis") {
    const row = table.rows[0] || {};
    const reputationScore = parseNumberLoose(row["reputationscore"]);
    const reviewVelocity = parseNumberLoose(row["reviewvelocity"]);
    const totalReach = parseNumberLoose(row["totalreach"]);
    const engagementRate = parseNumberLoose(row["engagementrate"]);

    return {
      ...base,
      kpis: {
        ...base.kpis,
        ...(reputationScore !== null ? { reputationScore } : {}),
        ...(reviewVelocity !== null ? { reviewVelocity } : {}),
        ...(totalReach !== null ? { totalReach } : {}),
        ...(engagementRate !== null ? { engagementRate } : {}),
      }
    };
  }

  if (requirementKey === "social_reach") {
    const points = table.rows
      .map((row) => ({
        date: String(row["date"] || "").trim(),
        reach: parseNumberLoose(row["reach"]) ?? 0
      }))
      .filter((p) => p.date);

    points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const series = points.map((p) => p.reach);
    const labels = (() => {
      if (points.length === 0) return base.reach.labels;
      const first = points[0].date;
      const last = points[points.length - 1].date;
      const mid = points[Math.floor(points.length / 2)]?.date || last;
      return [first, mid, last];
    })();

    const totalReach = series.reduce((acc, v) => acc + (Number(v) || 0), 0);

    return {
      ...base,
      kpis: {
        ...base.kpis,
        totalReach: Number.isFinite(totalReach) && totalReach > 0 ? totalReach : base.kpis.totalReach
      },
      reach: { series, labels }
    };
  }

  if (requirementKey === "reviews") {
    const reviews = table.rows
      .map((row) => {
        const platform = String(row["platform"] || "").trim();
        const user = String(row["user"] || "").trim();
        const text = String(row["text"] || "").trim();
        const date = String(row["date"] || "").trim();
        if (!platform || !user || !text) return null;
        const rating = parseNumberLoose(row["rating"]) ?? 0;
        return { platform, user, rating, text, date, avatar: initialsForName(user) };
      })
      .filter(Boolean);
    if (!reviews || (reviews as any[]).length === 0) return base;
    return { ...base, reviews };
  }

  if (requirementKey === "top_content") {
    const topPosts = table.rows
      .map((row, idx) => {
        const platform = String(row["platform"] || "").trim();
        const type = String(row["type"] || "").trim();
        const date = String(row["date"] || "").trim();
        if (!platform || !type) return null;
        const reachN = parseNumberLoose(row["reach"]) ?? 0;
        const engagementN = parseNumberLoose(row["engagement"]) ?? 0;
        const sentiment = String(row["sentiment"] || "Neutral").trim() || "Neutral";
        const thumbnail = String(type || platform).trim().slice(0, 1).toUpperCase() || "P";
        return {
          id: idx + 1,
          platform,
          type,
          reach: formatCompactNumber(reachN) || String(reachN),
          engagement: formatCompactNumber(engagementN) || String(engagementN),
          sentiment,
          date,
          thumbnail
        };
      })
      .filter(Boolean);
    if (!topPosts || (topPosts as any[]).length === 0) return base;
    return { ...base, topPosts };
  }

  return base;
}

// OpenAI helper (Secure via Supabase Edge Functions). 
// OpenAI helper (Secure via Proxy or Supabase Edge Functions).
// The API key is NEVER passed from the client to prevent leaks.
async function openaiChat(messages: Array<{ role: string; content: string | any }>, model = "gpt-4o-mini", temperature = 0) {
  const isLocalhost = typeof window !== "undefined" && (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );

  let lastError = "";

  // 1. If on localhost, try the local Node.js proxy first (port 3000)
  if (isLocalhost) {
    try {
      const proxyRes = await fetch("http://localhost:3000/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages, model, temperature }),
      });
      if (proxyRes.ok) {
        const data = await proxyRes.json();
        if (data.choices?.[0]?.message?.content !== undefined) {
          return data.choices[0].message.content;
        }
      } else {
        const txt = await proxyRes.text();
        lastError += `[Local Proxy ${proxyRes.status}]: ${txt}. `;
      }
    } catch (e) {
      lastError += `[Local Proxy Failed]: ${String(e)}. `;
    }
  }

  // 2. If on Cloudflare Pages (production), try the relative function endpoint
  if (!isLocalhost || lastError) {
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages, model, temperature }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.choices?.[0]?.message?.content !== undefined) {
          return data.choices[0].message.content;
        }
      } else {
        const errText = await res.text();
        lastError += `[Cloudflare Proxy ${res.status}]: ${errText}. `;
      }
    } catch (e) {
      lastError += `[Cloudflare Proxy Exception]: ${String(e)}. `;
    }
  }

  // 3. If we have a supabase instance, use the secure Edge Function proxy
  if (supabase) {
    try {
      const { data, error } = await supabase.functions.invoke('chat-proxy', {
        body: { messages, model, temperature }
      });

      if (!error && data?.choices?.[0]?.message?.content) {
        return data.choices[0].message.content;
      }
      if (error) {
        lastError += `[Supabase Proxy Error]: ${error.message || JSON.stringify(error)}. `;
      }
    } catch (invokeError: any) {
      lastError += `[Supabase Exception]: ${String(invokeError)}. `;
    }
  }

  // 4. Fallback: Direct OpenAI call if VITE_OPENAI_API_KEY is provided
  const directApiKey = import.meta.env.VITE_OPENAI_API_KEY || import.meta.env.OPENAI_API_KEY;
  if (directApiKey) {
    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${directApiKey}`,
        },
        body: JSON.stringify({ messages, model: model || "gpt-4o-mini", temperature }),
      });
      if (res.ok) {
        const data = await res.json();
        return data.choices?.[0]?.message?.content;
      } else {
        const txt = await res.text();
        lastError += `[Direct OpenAI Error ${res.status}]: ${txt}. `;
      }
    } catch (directError) {
      lastError += `[Direct OpenAI Exception]: ${String(directError)}. `;
    }
  }

  // 5. Final Fallback: Simulation Mode (Development/Demo Safety Net)
  console.warn("AI CONNECTION FAILED. Falling back to simulation mode. Debug Info:", lastError);

  // If this is an image analysis request (check both array format and semantic string intent)
  const isImageRequest = messages.some(m => {
    if (Array.isArray(m.content)) return m.content.some((c: any) => c.type === 'image_url');
    if (typeof m.content === 'string') return m.content.toLowerCase().includes('invoice image') || m.content.toLowerCase().includes('analyze this image');
    return false;
  });

  console.log("[Findsy] AI Simulation Check - isImageRequest:", isImageRequest);

  if (isImageRequest) {
    console.log("Simulating AI Vision result for demo.");
    return JSON.stringify({
      vendor: "Sysco Food Services",
      totalAmount: 432.50,
      currency: "GBP",
      date: new Date().toISOString().split('T')[0],
      confidence: 0.98,
      items: [
        { name: "Premium Ground Beef", qty: 4, price: 85.00 },
        { name: "Organic Avocados", qty: 2, price: 42.50 },
        { name: "Sourdough Buns", qty: 10, price: 1.20 }
      ],
      summary: "Weekly inventory replenishment from primary supplier."
    });
  }

  // Simulate a realistic delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  const userContent = messages.find(m => m.role === "user")?.content || "";

  // KPI Explanation Simulation
  if (userContent.includes("Explain:") || userContent.includes("Action:")) {
    if (userContent.includes("revenue") || userContent.includes("margin") || userContent.includes("cost")) {
      return "Based on the financial data, your revenue is trending positively (+8% vs last week), but cost pressures are mounting in the supply chain. \n\n**Key Insight:**\n- Food costs have risen by 12% due to supplier price hikes on dairy and protein.\n- Your 'Pasta Pomodoro' maintains a strong 68% margin, helping offset these increases.\n\n**Recommendation:**\nConsider renegotiating with your primary supplier or temporarily increasing the price of high-cost menu items by £0.50 to maintain your gross margin targets.";
    }
    if (userContent.includes("traffic") || userContent.includes("covers")) {
      return "Traffic analysis indicates a strong peak on **Saturday at 6pm**, where you are consistently reaching capacity. \n\n**Observations:**\n- You are missing potential revenue due to table turn times averaging 95 minutes during peaks.\n- Monday and Tuesday lunches remain underutilized (occupancy < 30%).\n\n**Actionable Advice:**\nImplement a 'limit 90 mins' booking policy for Friday/Saturday peak slots. For weekdays, launch a 'Business Lunch Express' menu to attract local workers.";
    }
    if (userContent.includes("staff") || userContent.includes("labour")) {
      return "Your labour costs are currently **32% of revenue**, which is slightly above the 30% target. \n\n**Breakdown:**\n- Friday night shift had 2 extra staff members compared to the actual covers served.\n- Overtime payments accounted for £150 last week.\n\n**Suggestion:**\nReview the rota for next Friday. You can likely safely reduce FOH staff by 1 between 2pm-5pm without impacting service quality.";
    }
    if (userContent.includes("menu") || userContent.includes("item")) {
      return "Your menu performance review shows a clear winner: **Double Smash Burger**.\n\n- **Sales:** £2.4k (Top Item)\n- **Margin:** 65%\n\nHowever, the **Vegan Lasagna** is underperforming with only 4 orders. \n\n**Recommendation:**\nRun a 2-for-1 promotion on the Vegan Lasagna next Tuesday to test demand. If sales don't improve, consider replacing it with a seasonal risotto which typically has lower waste.";
    }
  }

  // General Chat Simulation
  return "I'm currently running in **Demo Mode** because I couldn't connect to the AI backend. \n\nHowever, I can tell you that your dashboard shows generally positive trends. Your revenue is up, but keep an eye on those rising food costs. \n\nIs there a specific part of your business you'd like me to analyze deeper once we're back online?";
}

function storeFiles(files: any[]) {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(FILE_STORAGE_KEY, JSON.stringify(files));
    } catch (error) {
      console.error("Error storing files:", error);
    }
  }
}

// File Upload Component
function FileUpload({ userId, venueId, onFileUploaded, onFileDeleted, mode = "document", context }: {
  userId: string;
  venueId?: string;
  onFileUploaded?: (file: any) => void;
  onFileDeleted?: (fileId: number) => void;
  mode?: "document" | "image";
  context?: string;
}) {

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [uploadedFilesList, setUploadedFilesList] = useState<any[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [pendingDeleteKey, setPendingDeleteKey] = useState<any>(null);
  const showInventoryChecklist = mode === "document" && context === "inventory";
  const showMenuChecklist = mode === "document" && context === "menu";
  const showSalesChecklist = mode === "document" && context === "sales";
  const showMarketingChecklist = mode === "document" && context === "marketing";
  const showInvoicesChecklist = mode === "document" && context === "invoices";
  const [inventoryChecklistOpen, setInventoryChecklistOpen] = useState(true);
  const [menuChecklistOpen, setMenuChecklistOpen] = useState(true);
  const [salesChecklistOpen, setSalesChecklistOpen] = useState(true);
  const [marketingChecklistOpen, setMarketingChecklistOpen] = useState(true);
  const [invoicesChecklistOpen, setInvoicesChecklistOpen] = useState(true);
  const normalizedScope = ["inventory", "menu", "sales", "marketing", "invoices"].includes(context || "") ? context : null;
  const fileMatchesScope = (file: any, scopeValue: string) => {
    if (!scopeValue) return true;
    if (file?.scope && String(file.scope).toLowerCase() === scopeValue) return true;
    if (Array.isArray(file?.tags)) {
      return file.tags.some((t: any) => typeof t === "string" && t.toLowerCase() === `scope:${scopeValue}`);
    }
    return false;
  };
  const scopedFiles = normalizedScope
    ? uploadedFilesList.filter((file) => fileMatchesScope(file, normalizedScope))
    : uploadedFilesList;

  const availableTags = [
    "Menu/Recipes",
    "Ingredient Costs",
    "Supplier Invoices",
    "Sales",
    "Staffing/Ops",
    "Compliance Notes",
    "Marketing/Reviews",
  ];

  useEffect(() => {
    const storedFiles = getStoredFiles();
    setUploadedFilesList(storedFiles);
  }, []);

  const inventoryRequirements = [
    {
      key: "items",
      label: "Inventory Items",
      description: "Stock on hand with cost and reorder thresholds",
      headers: ["item_name", "category", "unit", "current_qty", "cost_per_unit", "min_threshold"],
      example: "Basmati Rice,Dry Goods,kg,25,1.80,5"
    },
    {
      key: "waste",
      label: "Waste Log",
      description: "Loss, spoilage, and shrinkage tracking",
      headers: ["item_name", "quantity", "reason", "value", "date"],
      example: "Tomatoes,4,Expired,12.50,2025-02-10"
    }
  ];

  const menuRequirements = [
    {
      key: "menu_items",
      label: "Menu Items & Pricing",
      description: "Core menu list with price and cost",
      headers: ["item_name", "category", "price", "cost", "prep_time"],
      example: "Spicy Tacos,Main,12.50,4.20,Medium"
    },
    {
      key: "menu_performance",
      label: "Weekly Performance",
      description: "Volume and trend by menu item",
      headers: ["item_name", "orders_per_week", "trend"],
      example: "Spicy Tacos,180,up"
    }
  ];

  const salesRequirements = [
    {
      key: "sales_summary",
      label: "Sales Summary",
      description: "Daily revenue targets and transactions",
      headers: ["date", "revenue_so_far", "expected_by_now", "full_day_target", "transactions"],
      example: "2025-02-10,3240,2850,5700,175"
    },
    {
      key: "revenue_vs_cost",
      label: "Revenue vs Cost (Weekly)",
      description: "4-week revenue and cost rollup",
      headers: ["week_label", "revenue", "cost"],
      example: "Week 1,4200,3200"
    },
    {
      key: "demand_forecast",
      label: "Demand Forecast",
      description: "Predicted revenue and covers by day",
      headers: ["day", "predicted_revenue", "predicted_covers", "weather"],
      example: "Fri,9200,210,Sunny"
    },
    {
      key: "staffing_rota",
      label: "Staffing Rota",
      description: "Shift plan by staff member",
      headers: ["staff_name", "role", "shift", "cost"],
      example: "Sarah Johnson,Head Chef,11:00-22:00,120"
    }
  ];

  const marketingRequirements = [
    {
      key: "marketing_kpis",
      label: "Marketing KPIs",
      description: "Reputation, reach, engagement totals",
      headers: ["reputation_score", "review_velocity", "total_reach", "engagement_rate"],
      example: "4.7,42,120000,6.1"
    },
    {
      key: "social_reach",
      label: "Social Reach Series",
      description: "Daily or weekly reach points",
      headers: ["date", "reach"],
      example: "2025-02-10,12000"
    },
    {
      key: "reviews",
      label: "Recent Reviews",
      description: "Platform reviews & sentiment",
      headers: ["platform", "user", "rating", "text", "date"],
      example: "Google,Alex M.,5,Great service,2025-02-09"
    },
    {
      key: "top_content",
      label: "Top Content",
      description: "Best performing posts",
      headers: ["platform", "type", "reach", "engagement", "sentiment", "date"],
      example: "Instagram,Reel,24500,1200,Positive,2025-02-08"
    }
  ];

  const invoiceRequirements = [
    {
      key: "invoice_header",
      label: "Invoice Header",
      description: "Supplier, invoice number, totals",
      headers: ["supplier_name", "invoice_number", "invoice_date", "total_amount", "currency"],
      example: "DairyCo,INV-2391,2025-02-10,1250.40,GBP"
    },
    {
      key: "invoice_items",
      label: "Invoice Line Items",
      description: "Itemized purchases and costs",
      headers: ["item_name", "quantity", "unit_cost", "line_total"],
      example: "Whole Milk,40,1.05,42.00"
    },
    {
      key: "payment_terms",
      label: "Payment Terms",
      description: "Due dates and payment status",
      headers: ["invoice_number", "due_date", "payment_status"],
      example: "INV-2391,2025-02-24,Unpaid"
    }
  ];

  const normalizeToken = (value: string) => (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  const extractHeaderRow = (content: string) => {
    const lines = (content || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const first = lines[0];
    const separators = [",", "\t", ";", "|"];
    for (const sep of separators) {
      if (first.includes(sep)) {
        return first.split(sep).map((c) => c.trim()).filter(Boolean);
      }
    }
    return [first];
  };

  const contentHasKeywords = (content: string, keywords: string[], minMatches = 2) => {
    const lc = (content || "").toLowerCase();
    let hits = 0;
    keywords.forEach((k) => {
      if (lc.includes(k)) hits += 1;
    });
    return hits >= minMatches;
  };

  const isRequirementPresent = (req: any) => {
    return scopedFiles.some((file) => {
      const content = file?.content || "";
      if (!content) return false;
      const headerCells = extractHeaderRow(content).map(normalizeToken);
      const normalizedHeaders = req.headers.map((h: string) => normalizeToken(h));
      const headerMatches = normalizedHeaders.filter((h: string) => headerCells.includes(h)).length;
      const headerThreshold = Math.min(3, normalizedHeaders.length);
      if (headerMatches >= headerThreshold) {
        // Avoid "All covered" when the file is only headers with no data rows.
        const lines = String(content || "")
          .split(/\r?\n/)
          .map((l: string) => l.trim())
          .filter((l: string) => Boolean(l));
        if (lines.length < 2) return false;
        const first = lines[0] || "";
        const separators = [",", "\t", ";", "|"];
        const sep = separators.find((s) => first.includes(s));
        if (!sep) return lines.slice(1).some((l: string) => l.length > 0);
        const hasData = lines.slice(1).some((l: string) => {
          const cells = l.split(sep).map((c: string) => c.trim()).filter((c: string) => Boolean(c));
          return cells.length >= 2;
        });
        return hasData;
      }
      return contentHasKeywords(content, req.headers.map((h: string) => h.replace(/_/g, " ")).concat(req.headers), 3);
    });
  };

  const copyTemplate = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ message: "Template copied to clipboard", type: "success" });
      setTimeout(() => setToast(null), 2000);
    } catch (e) {
      setToast({ message: "Copy failed", type: "error" });
      setTimeout(() => setToast(null), 2000);
    }
  };

  const handleFileUpload = async (event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (mode === "image") {
      setAnalyzing(true);
      try {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = (reader.result as string).split(',')[1];
          const result = await analyzeInvoiceImage(base64);
          console.log("AI Image Analysis Result:", result);

          if (result && (result.vendor || result.totalAmount)) {
            setExtractedData(result);
            setToast({ message: "AI extraction successful!", type: 'success' });
          } else {
            console.error("AI Analysis failed to return usable data:", result);
            setToast({ message: "AI Analysis complete, but couldn't read invoice details. Try a clearer photo.", type: 'error' });
          }
          setAnalyzing(false);
        };
        reader.readAsDataURL(file);
        return;
      } catch (e) {
        setAnalyzing(false);
        setToast({ message: "Analysis failed", type: 'error' });
        return;
      }
    }



    try {
      console.log("Starting upload. Selected tags:", selectedTags);
      const content = await file.text();

      // Auto-categorize the file
      const categorization = await categorizeFile(file.name, content);
      console.log("Categorization result:", categorization);

      // Use selected tags or auto-suggested tags
      const finalTags = selectedTags.length > 0 ? selectedTags : categorization.suggestedTags;
      const scopeTag = normalizedScope ? `scope:${normalizedScope}` : null;
      const finalTagsWithScope = scopeTag
        ? Array.from(new Set([...(finalTags || []), scopeTag]))
        : finalTags;
      console.log("Final tags to save:", finalTags);

      // Create file record
      const fileRecord = {
        id: Date.now(),
        userId: userId,
        venueId: venueId,
        fileName: file.name,
        content: content,
        tags: finalTagsWithScope,
        scope: normalizedScope,
        uploadedAt: new Date().toISOString(),
        size: file.size,
        summary: categorization.summary,
      };

      // Save to Supabase
      const savedRow = await supaSaveFileMetadata(fileRecord);

      const finalRecord = savedRow ? {
        id: savedRow.id,
        userId: savedRow.user_id,
        venueId: savedRow.venue_id,
        fileName: savedRow.file_name,
        content: savedRow.content,
        tags: savedRow.tags,
        uploadedAt: savedRow.created_at,
        size: savedRow.size,
        summary: savedRow.summary,
      } : fileRecord;

      // Store the file
      const currentFiles = getStoredFiles();
      const updatedFiles = [...currentFiles, { ...finalRecord, scope: normalizedScope }];
      storeFiles(updatedFiles);
      setUploadedFilesList(updatedFiles);

      // Show success toast
      setToast({
        message: `"${file.name}" uploaded successfully with ${(finalTagsWithScope || []).length} tag(s)`,
        type: 'success'
      });
      setTimeout(() => setToast(null), 4000);
      setSelectedTags([]);

      if (onFileUploaded) onFileUploaded({ ...finalRecord, scope: normalizedScope });
    } catch (error: any) {
      console.error("Upload error:", error);
      setToast({
        message: "Upload failed: " + (error?.message || "unknown error"),
        type: 'error'
      });
      setTimeout(() => setToast(null), 5000);
    } finally {

      event.target.value = "";
    }
  };

  const removeFile = async (file: any) => {
    const fileId = file?.id;
    try {
      if (fileId === undefined || fileId === null) {
        const currentFiles = getStoredFiles();
        const updatedFiles = currentFiles.filter((f: any) => f.fileName !== file?.fileName);
        storeFiles(updatedFiles);
        setUploadedFilesList(updatedFiles);
        setToast({
          message: "Removed locally (missing server id).",
          type: 'error'
        });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const deleted = await supaDeleteFileRecord(fileId);
      if (!deleted) {
        setToast({
          message: "Failed to delete from server. Please try again.",
          type: 'error'
        });
        setTimeout(() => setToast(null), 4000);
        return;
      }

      const currentFiles = getStoredFiles();
      const updatedFiles = currentFiles.filter((f: any) => f.id !== fileId);
      storeFiles(updatedFiles);
      setUploadedFilesList(updatedFiles);

      if (onFileDeleted) onFileDeleted(fileId);
      setToast({ message: "Document deleted successfully.", type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (e: any) {
      console.error("Error removing file:", e);
      setToast({
        message: "Failed to delete the file.",
        type: 'error'
      });
      setTimeout(() => setToast(null), 4000);
    }
  };

  return React.createElement("div", { style: { position: 'relative' } }, [
    // Toast Notification
    toast && React.createElement(
      "div",
      {
        key: "toast",
        style: {
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 9999,
          minWidth: 320,
          maxWidth: 500,
          background: toast.type === 'success'
            ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
            : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          color: "#ffffff",
          padding: "16px 20px",
          borderRadius: 12,
          boxShadow: "0 10px 40px rgba(0,0,0,.3), 0 2px 8px rgba(0,0,0,.2)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontWeight: 600,
          fontSize: 14,
        }
      },
      [
        React.createElement("span", { key: "icon", style: { fontSize: 20 } }, toast.type === 'success' ? "✓" : "✕"),
        React.createElement("span", { key: "msg", style: { flex: 1 } }, toast.message),
        React.createElement(
          "button",
          {
            key: "close",
            onClick: () => setToast(null),
            style: {
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: 6,
              width: 24,
              height: 24,
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              color: "#ffffff",
              fontSize: 16,
              fontWeight: 700,
            }
          },
          "×"
        )
      ]
    ),
    React.createElement(
      Card,
      {
        key: "upload-card",
        style: { background: "rgba(255, 255, 255, 0.4)", border: "1px solid rgba(6, 133, 206, 0.1)" }
      }, // Matching the blue theme
      React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 16 } }, [

        // Custom Header matching Notifications style
        React.createElement("div", { key: "header", className: "flex justify-between items-center mb-1" }, [
          React.createElement("h3", { className: "font-semibold text-gray-900 flex items-center gap-2" },
            mode === "image" ? "Scan Invoice Image" : "Upload Documents"
          )
        ]),

        // Inventory Data Requirements (Inventory context only)
        showInventoryChecklist && React.createElement("div", { key: "inventory-checklist", className: "bg-white/80 border border-slate-200 rounded-2xl p-4 shadow-sm" }, [
          React.createElement("div", { className: "flex items-start justify-between gap-4 mb-3" }, [
            React.createElement("div", {}, [
              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Inventory Data Requirements"),
              React.createElement("p", { className: "text-sm font-semibold text-slate-700 mt-1" }, "Copy the headers into Excel/Google Sheets before uploading.")
            ]),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-slate-500 border border-slate-200 bg-white px-3 py-1.5 rounded-xl hover:bg-slate-50",
                onClick: () => setInventoryChecklistOpen((prev) => !prev)
              }, inventoryChecklistOpen ? "Collapse" : "Expand"),
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-indigo-600 border border-indigo-200 bg-indigo-50 px-3 py-1.5 rounded-xl hover:bg-indigo-100",
                onClick: () => copyTemplate(inventoryRequirements.map((r) => r.headers.join("\t")).join("\n"))
              }, "Copy All")
            ])
          ]),
          inventoryChecklistOpen && React.createElement("div", { className: "grid grid-cols-1 gap-3" },
            inventoryRequirements.map((req) => {
              const present = isRequirementPresent(req);
              return React.createElement("div", { key: req.key, className: "grid grid-cols-1 md:grid-cols-[160px_1fr_120px] gap-3 items-start bg-slate-50/70 border border-slate-200 rounded-xl p-3" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-xs font-black text-slate-900" }, req.label),
                  React.createElement("p", { className: "text-[10px] text-slate-500 mt-1" }, req.description)
                ]),
                React.createElement("div", {}, [
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, "Headers"),
                  React.createElement("code", { className: "text-[11px] text-slate-700 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.headers.join("\t")),
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-2 mb-1" }, "Example row"),
                  React.createElement("code", { className: "text-[11px] text-slate-500 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.example)
                ]),
                React.createElement("div", { className: "flex flex-col gap-2 items-start" }, [
                  React.createElement("span", {
                    className: `inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`
                  }, present ? "Detected" : "Missing"),
                  React.createElement("button", {
                    className: "text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-600",
                    onClick: () => copyTemplate(req.headers.join("\t"))
                  }, "Copy")
                ])
              ]);
            })
          ),
          React.createElement("div", { className: "mt-3" }, [
            scopedFiles.length > 0 && React.createElement("div", { className: "flex flex-wrap gap-2 items-center" }, [
              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Missing data"),
              (() => {
                const missing = scopedFiles.length === 0
                  ? inventoryRequirements
                  : inventoryRequirements.filter((r) => !isRequirementPresent(r));
                return scopedFiles.length > 0 && missing.length === 0
                  ? React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" }, "All covered")
                  : React.createElement("div", { className: "flex flex-wrap gap-2" },
                    missing.map((r) =>
                      React.createElement("span", { key: r.key, className: "text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-full" }, r.label)
                    )
                  );
              })()
            ]),
            React.createElement("p", { className: "text-[10px] text-slate-400 mt-2" }, "Status is inferred from uploaded file tags/headers. Missing data means you should upload another document.")
          ])
        ]),

        showMenuChecklist && React.createElement("div", { key: "menu-checklist", className: "bg-white/80 border border-slate-200 rounded-2xl p-4 shadow-sm" }, [
          React.createElement("div", { className: "flex items-start justify-between gap-4 mb-3" }, [
            React.createElement("div", {}, [
              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Menu Data Requirements"),
              React.createElement("p", { className: "text-sm font-semibold text-slate-700 mt-1" }, "Use these headers in Excel/Sheets before uploading.")
            ]),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-slate-500 border border-slate-200 bg-white px-3 py-1.5 rounded-xl hover:bg-slate-50",
                onClick: () => setMenuChecklistOpen((prev) => !prev)
              }, menuChecklistOpen ? "Collapse" : "Expand"),
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-indigo-600 border border-indigo-200 bg-indigo-50 px-3 py-1.5 rounded-xl hover:bg-indigo-100",
                onClick: () => copyTemplate(menuRequirements.map((r) => r.headers.join("\t")).join("\n"))
              }, "Copy All")
            ])
          ]),
          menuChecklistOpen && React.createElement("div", { className: "grid grid-cols-1 gap-3" },
            menuRequirements.map((req) => {
              const present = isRequirementPresent(req);
              return React.createElement("div", { key: req.key, className: "grid grid-cols-1 md:grid-cols-[160px_1fr_120px] gap-3 items-start bg-slate-50/70 border border-slate-200 rounded-xl p-3" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-xs font-black text-slate-900" }, req.label),
                  React.createElement("p", { className: "text-[10px] text-slate-500 mt-1" }, req.description)
                ]),
                React.createElement("div", {}, [
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, "Headers"),
                  React.createElement("code", { className: "text-[11px] text-slate-700 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.headers.join("\t")),
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-2 mb-1" }, "Example row"),
                  React.createElement("code", { className: "text-[11px] text-slate-500 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.example)
                ]),
                React.createElement("div", { className: "flex flex-col gap-2 items-start" }, [
                  React.createElement("span", {
                    className: `inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`
                  }, present ? "Detected" : "Missing"),
                  React.createElement("button", {
                    className: "text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-600",
                    onClick: () => copyTemplate(req.headers.join("\t"))
                  }, "Copy")
                ])
              ]);
            })
          ),
          React.createElement("div", { className: "mt-3" }, [
            scopedFiles.length > 0 && React.createElement("div", { className: "flex flex-wrap gap-2 items-center" }, [
              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Missing data"),
              (() => {
                const missing = scopedFiles.length === 0
                  ? menuRequirements
                  : menuRequirements.filter((r) => !isRequirementPresent(r));
                return scopedFiles.length > 0 && missing.length === 0
                  ? React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" }, "All covered")
                  : React.createElement("div", { className: "flex flex-wrap gap-2" },
                    missing.map((r) =>
                      React.createElement("span", { key: r.key, className: "text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-full" }, r.label)
                    )
                  );
              })()
            ]),
            React.createElement("p", { className: "text-[10px] text-slate-400 mt-2" }, "Status is inferred from uploaded file headers/content. Missing data means you should upload another document.")
          ])
        ]),

        showSalesChecklist && React.createElement("div", { key: "sales-checklist", className: "bg-white/80 border border-slate-200 rounded-2xl p-4 shadow-sm" }, [
          React.createElement("div", { className: "flex items-start justify-between gap-4 mb-3" }, [
            React.createElement("div", {}, [
              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Sales, Demand & Staffing Data Requirements"),
              React.createElement("p", { className: "text-sm font-semibold text-slate-700 mt-1" }, "Use these headers in Excel/Sheets before uploading.")
            ]),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-slate-500 border border-slate-200 bg-white px-3 py-1.5 rounded-xl hover:bg-slate-50",
                onClick: () => setSalesChecklistOpen((prev) => !prev)
              }, salesChecklistOpen ? "Collapse" : "Expand"),
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-indigo-600 border border-indigo-200 bg-indigo-50 px-3 py-1.5 rounded-xl hover:bg-indigo-100",
                onClick: () => copyTemplate(salesRequirements.map((r) => r.headers.join("\t")).join("\n"))
              }, "Copy All")
            ])
          ]),
          salesChecklistOpen && React.createElement("div", { className: "grid grid-cols-1 gap-3" },
            salesRequirements.map((req) => {
              const present = isRequirementPresent(req);
              return React.createElement("div", { key: req.key, className: "grid grid-cols-1 md:grid-cols-[160px_1fr_120px] gap-3 items-start bg-slate-50/70 border border-slate-200 rounded-xl p-3" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-xs font-black text-slate-900" }, req.label),
                  React.createElement("p", { className: "text-[10px] text-slate-500 mt-1" }, req.description)
                ]),
                React.createElement("div", {}, [
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, "Headers"),
                  React.createElement("code", { className: "text-[11px] text-slate-700 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.headers.join("\t")),
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-2 mb-1" }, "Example row"),
                  React.createElement("code", { className: "text-[11px] text-slate-500 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.example)
                ]),
                React.createElement("div", { className: "flex flex-col gap-2 items-start" }, [
                  React.createElement("span", {
                    className: `inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`
                  }, present ? "Detected" : "Missing"),
                  React.createElement("button", {
                    className: "text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-600",
                    onClick: () => copyTemplate(req.headers.join("\t"))
                  }, "Copy")
                ])
              ]);
            })
          ),
          React.createElement("div", { className: "mt-3" }, [
            scopedFiles.length > 0 && React.createElement("div", { className: "flex flex-wrap gap-2 items-center" }, [
              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Missing data"),
              (() => {
                const missing = scopedFiles.length === 0
                  ? salesRequirements
                  : salesRequirements.filter((r) => !isRequirementPresent(r));
                return scopedFiles.length > 0 && missing.length === 0
                  ? React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" }, "All covered")
                  : React.createElement("div", { className: "flex flex-wrap gap-2" },
                    missing.map((r) =>
                      React.createElement("span", { key: r.key, className: "text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-full" }, r.label)
                    )
                  );
              })()
            ]),
            React.createElement("p", { className: "text-[10px] text-slate-400 mt-2" }, "Status is inferred from uploaded file headers/content. Missing data means you should upload another document.")
          ])
        ]),

        showMarketingChecklist && React.createElement("div", { key: "marketing-checklist", className: "bg-white/80 border border-slate-200 rounded-2xl p-4 shadow-sm" }, [
          React.createElement("div", { className: "flex items-start justify-between gap-4 mb-3" }, [
            React.createElement("div", {}, [
              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Marketing Data Requirements"),
              React.createElement("p", { className: "text-sm font-semibold text-slate-700 mt-1" }, "Use these headers in Excel/Sheets before uploading.")
            ]),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-slate-500 border border-slate-200 bg-white px-3 py-1.5 rounded-xl hover:bg-slate-50",
                onClick: () => setMarketingChecklistOpen((prev) => !prev)
              }, marketingChecklistOpen ? "Collapse" : "Expand"),
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-indigo-600 border border-indigo-200 bg-indigo-50 px-3 py-1.5 rounded-xl hover:bg-indigo-100",
                onClick: () => copyTemplate(marketingRequirements.map((r) => r.headers.join("\t")).join("\n"))
              }, "Copy All")
            ])
          ]),
          marketingChecklistOpen && React.createElement("div", { className: "grid grid-cols-1 gap-3" },
            marketingRequirements.map((req) => {
              const present = isRequirementPresent(req);
              return React.createElement("div", { key: req.key, className: "grid grid-cols-1 md:grid-cols-[160px_1fr_120px] gap-3 items-start bg-slate-50/70 border border-slate-200 rounded-xl p-3" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-xs font-black text-slate-900" }, req.label),
                  React.createElement("p", { className: "text-[10px] text-slate-500 mt-1" }, req.description)
                ]),
                React.createElement("div", {}, [
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, "Headers"),
                  React.createElement("code", { className: "text-[11px] text-slate-700 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.headers.join("\t")),
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-2 mb-1" }, "Example row"),
                  React.createElement("code", { className: "text-[11px] text-slate-500 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.example)
                ]),
                React.createElement("div", { className: "flex flex-col gap-2 items-start" }, [
                  React.createElement("span", {
                    className: `inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`
                  }, present ? "Detected" : "Missing"),
                  React.createElement("button", {
                    className: "text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-600",
                    onClick: () => copyTemplate(req.headers.join("\t"))
                  }, "Copy")
                ])
              ]);
            })
          ),
          React.createElement("div", { className: "mt-3" }, [
            scopedFiles.length > 0 && React.createElement("div", { className: "flex flex-wrap gap-2 items-center" }, [
              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Missing data"),
              (() => {
                const missing = scopedFiles.length === 0
                  ? marketingRequirements
                  : marketingRequirements.filter((r) => !isRequirementPresent(r));
                return scopedFiles.length > 0 && missing.length === 0
                  ? React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" }, "All covered")
                  : React.createElement("div", { className: "flex flex-wrap gap-2" },
                    missing.map((r) =>
                      React.createElement("span", { key: r.key, className: "text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-full" }, r.label)
                    )
                  );
              })()
            ]),
            React.createElement("p", { className: "text-[10px] text-slate-400 mt-2" }, "Status is inferred from uploaded file headers/content. Missing data means you should upload another document.")
          ])
        ]),

        showInvoicesChecklist && React.createElement("div", { key: "invoices-checklist", className: "bg-white/80 border border-slate-200 rounded-2xl p-4 shadow-sm" }, [
          React.createElement("div", { className: "flex items-start justify-between gap-4 mb-3" }, [
            React.createElement("div", {}, [
              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Invoice Data Requirements"),
              React.createElement("p", { className: "text-sm font-semibold text-slate-700 mt-1" }, "Use these headers in Excel/Sheets before uploading.")
            ]),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-slate-500 border border-slate-200 bg-white px-3 py-1.5 rounded-xl hover:bg-slate-50",
                onClick: () => setInvoicesChecklistOpen((prev) => !prev)
              }, invoicesChecklistOpen ? "Collapse" : "Expand"),
              React.createElement("button", {
                className: "text-[10px] font-black uppercase tracking-widest text-indigo-600 border border-indigo-200 bg-indigo-50 px-3 py-1.5 rounded-xl hover:bg-indigo-100",
                onClick: () => copyTemplate(invoiceRequirements.map((r) => r.headers.join("\t")).join("\n"))
              }, "Copy All")
            ])
          ]),
          invoicesChecklistOpen && React.createElement("div", { className: "grid grid-cols-1 gap-3" },
            invoiceRequirements.map((req) => {
              const present = isRequirementPresent(req);
              return React.createElement("div", { key: req.key, className: "grid grid-cols-1 md:grid-cols-[160px_1fr_120px] gap-3 items-start bg-slate-50/70 border border-slate-200 rounded-xl p-3" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-xs font-black text-slate-900" }, req.label),
                  React.createElement("p", { className: "text-[10px] text-slate-500 mt-1" }, req.description)
                ]),
                React.createElement("div", {}, [
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, "Headers"),
                  React.createElement("code", { className: "text-[11px] text-slate-700 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.headers.join("\t")),
                  React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-2 mb-1" }, "Example row"),
                  React.createElement("code", { className: "text-[11px] text-slate-500 font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg block break-words" }, req.example)
                ]),
                React.createElement("div", { className: "flex flex-col gap-2 items-start" }, [
                  React.createElement("span", {
                    className: `inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`
                  }, present ? "Detected" : "Missing"),
                  React.createElement("button", {
                    className: "text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-600",
                    onClick: () => copyTemplate(req.headers.join("\t"))
                  }, "Copy")
                ])
              ]);
            })
          ),
          React.createElement("div", { className: "mt-3" }, [
            scopedFiles.length > 0 && React.createElement("div", { className: "flex flex-wrap gap-2 items-center" }, [
              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Missing data"),
              (() => {
                const missing = scopedFiles.length === 0
                  ? invoiceRequirements
                  : invoiceRequirements.filter((r) => !isRequirementPresent(r));
                return scopedFiles.length > 0 && missing.length === 0
                  ? React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" }, "All covered")
                  : React.createElement("div", { className: "flex flex-wrap gap-2" },
                    missing.map((r) =>
                      React.createElement("span", { key: r.key, className: "text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-full" }, r.label)
                    )
                  );
              })()
            ]),
            React.createElement("p", { className: "text-[10px] text-slate-400 mt-2" }, "Status is inferred from uploaded file headers/content. Missing data means you should upload another document.")
          ])
        ]),

        // Drag & Drop Dropzone
        React.createElement(
          "div",
          {
            key: "dropzone",
            style: {
              border: "2px dashed rgba(6,133,206,0.25)",
              borderRadius: 16,
              background: "linear-gradient(180deg, rgba(238,242,255,0.5) 0%, rgba(224,231,255,0.3) 100%)",
              padding: "32px 20px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            },
            onMouseEnter: (e: any) => {
              e.currentTarget.style.borderColor = "rgba(6,133,206,0.6)";
              e.currentTarget.style.background = "linear-gradient(180deg, rgba(238,242,255,0.8) 0%, rgba(224,231,255,0.5) 100%)";
              e.currentTarget.style.transform = "scale(1.01)";
            },
            onMouseLeave: (e: any) => {
              e.currentTarget.style.borderColor = "rgba(6,133,206,0.25)";
              e.currentTarget.style.background = "linear-gradient(180deg, rgba(238,242,255,0.5) 0%, rgba(224,231,255,0.3) 100%)";
              e.currentTarget.style.transform = "scale(1)";
            },
            onClick: () => document.getElementById("file-upload-input")?.click(),
          },
          [
            React.createElement("div", {
              key: "icon",
              style: {
                fontSize: 32,
                background: "linear-gradient(135deg, rgba(6,133,206,0.1) 0%, rgba(139,92,246,0.1) 100%)",
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                marginBottom: 4,
              }
            }, "📄"),
            React.createElement("div", { key: "text", className: "text-sm font-medium text-gray-700" },
              analyzing ? "AI is analyzing your image..." : (mode === "image" ? "Click to scan invoice image" : "Click to upload or drag and drop")
            ),
            React.createElement("div", { key: "subtext", className: "text-xs text-gray-500" },
              mode === "image" ? "JPG, PNG, WEBP" : "PDF, DOCX, XLSX (max 10MB)"
            ),
            React.createElement(
              "input",
              {
                key: "input",
                id: "file-upload-input",
                type: "file",
                multiple: true,
                style: { display: "none" },
                onChange: handleFileUpload
              }
            ),
            analyzing && React.createElement("div", { key: "loader", className: "mt-2 w-full bg-gray-200 h-1 rounded-full overflow-hidden" }, [
              React.createElement("div", { className: "bg-indigo-600 h-full animate-pulse", style: { width: '100%' } })
            ])
          ]
        ),

        // Extracted Data Preview
        extractedData && React.createElement("div", { key: "extracted", className: "bg-green-50 p-6 rounded-xl border border-green-100 animate-in slide-in-from-top-4" }, [
          React.createElement("h4", { className: "text-sm font-bold text-green-800 mb-4 flex items-center gap-2" }, ["✨ AI Extracted Data", React.createElement("span", { className: "text-[10px] bg-green-200 px-2 py-0.5 rounded text-green-700" }, `${(extractedData.confidence * 100).toFixed(0)}% Confidence`)]),
          React.createElement("div", { className: "grid grid-cols-2 gap-4 text-xs" }, [
            React.createElement("div", {}, [
              React.createElement("span", { className: "text-gray-500 block" }, "Vendor"),
              React.createElement("span", { className: "font-bold text-gray-800" }, extractedData.vendor)
            ]),
            React.createElement("div", {}, [
              React.createElement("span", { className: "text-gray-500 block" }, "Total Amount"),
              React.createElement("span", { className: "font-bold text-gray-800" }, `${extractedData.currency} ${extractedData.totalAmount}`)
            ]),
            React.createElement("div", {}, [
              React.createElement("span", { className: "text-gray-500 block" }, "Date"),
              React.createElement("span", { className: "font-bold text-gray-800" }, extractedData.date)
            ])
          ]),
          React.createElement("button", {
            className: "mt-4 w-full py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all",
            onClick: () => {
              // In a real app, this would save to DB. For now we just close and notify.
              setExtractedData(null);
              setToast({ message: "Invoice added to records!", type: 'success' });
            }
          }, "Confirm & Insert into Records")
        ]),

        // Tags Selection (Hide in image mode)
        mode === "document" && React.createElement("div", { key: "tags-section" }, [
          React.createElement("label", { key: "label", className: "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 ml-1" }, "Select Tags"),
          React.createElement("div", { key: "tags", className: "flex flex-wrap gap-2", style: { maxHeight: 85, overflowY: "auto", paddingRight: 4 } },
            availableTags.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              return React.createElement(
                "button",
                {
                  key: tag,
                  onClick: () => {
                    if (isSelected) setSelectedTags(selectedTags.filter(t => t !== tag));
                    else setSelectedTags([...selectedTags, tag]);
                  },
                  style: {
                    padding: "6px 14px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    border: isSelected ? "1px solid rgba(6,133,206,0.4)" : "1px solid rgba(0,0,0,0.06)",
                    background: isSelected
                      ? "linear-gradient(135deg, rgba(6,133,206,0.15) 0%, rgba(139,92,246,0.1) 100%)"
                      : "rgba(255,255,255,0.6)",
                    color: isSelected ? "rgb(79,70,229)" : "rgba(0,0,0,0.6)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: isSelected ? "0 2px 8px rgba(6,133,206,0.15)" : "none",
                  },
                  onMouseEnter: (e: any) => !isSelected && (e.currentTarget.style.background = "rgba(255,255,255,0.9)"),
                  onMouseLeave: (e: any) => !isSelected && (e.currentTarget.style.background = "rgba(255,255,255,0.6)")
                },
                tag
              );
            })
          )
        ]),

        // File List (Hide in image mode)
        scopedFiles.length > 0 && mode === "document" &&
        React.createElement("div", { key: "file-list", className: "space-y-2 mt-2", style: { maxHeight: 150, overflowY: "auto", paddingRight: 4 } }, [
          React.createElement("label", { key: "label", className: "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1" }, "Uploaded Files"),
          ...scopedFiles.map((file) =>
            React.createElement(
              "div",
              {
                key: file.id,
                style: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  background: "rgba(255,255,255,0.7)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  borderRadius: 12,
                  transition: "all 0.2s ease",
                }
              },
              [
                React.createElement("div", { key: "info", className: "flex items-center gap-3 overflow-hidden" }, [
                  React.createElement("span", { key: "icon", style: { fontSize: 16 } }, "📎"),
                  React.createElement("div", { key: "meta", className: "truncate" }, [
                    React.createElement("div", { key: "name", className: "text-sm font-semibold text-gray-900 truncate" }, file.fileName),
                    React.createElement("div", { key: "size", className: "text-xs text-gray-700 flex flex-wrap gap-1 items-center mt-1" }, [
                      `${Math.round(file.size / 1024)} KB`,
                      React.createElement("span", { key: "sep", style: { margin: "0 4px", opacity: 0.3 } }, "•"),
                      ...(file.tags || []).map((t: string) =>
                        React.createElement("span", { key: t, style: { background: "rgba(6,133,206,0.1)", color: "#0685ce", padding: "0 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 } }, t)
                      )
                    ]),
                  ])
                ]),
                (() => {
                  const fileKey = file.id ?? file.fileName;
                  if (pendingDeleteKey === fileKey) {
                    return React.createElement("div", { className: "flex items-center gap-2" }, [
                      React.createElement("button", {
                        key: "confirm-delete",
                        onClick: () => { setPendingDeleteKey(null); removeFile(file); },
                        style: {
                          padding: "6px 10px",
                          borderRadius: 8,
                          border: "none",
                          background: "rgb(239,68,68)",
                          color: "#ffffff",
                          cursor: "pointer",
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em"
                        }
                      }, "Delete"),
                      React.createElement("button", {
                        key: "cancel-delete",
                        onClick: () => setPendingDeleteKey(null),
                        style: {
                          padding: "6px 10px",
                          borderRadius: 8,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "rgba(255,255,255,0.6)",
                          color: "rgba(15,23,42,0.7)",
                          cursor: "pointer",
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em"
                        }
                      }, "Cancel")
                    ]);
                  }
                  return React.createElement(
                    "button",
                    {
                      key: "remove",
                      onClick: () => setPendingDeleteKey(fileKey),
                      style: {
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        display: "grid",
                        placeItems: "center",
                        border: "none",
                        background: "rgba(239,68,68,0.1)",
                        color: "rgb(239,68,68)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      },
                      onMouseEnter: (e: any) => e.currentTarget.style.background = "rgba(239,68,68,0.2)",
                      onMouseLeave: (e: any) => e.currentTarget.style.background = "rgba(239,68,68,0.1)",
                      "aria-label": "Remove file"
                    },
                    "✕"
                  );
                })()
              ]
            )
          )
        ])
      ])
    )]);
}

// Simple file categorization function
async function categorizeFile(fileName: string, content: string) {
  try {
    const system = `Analyze this file and categorize it. Return a JSON object with:\n{\n  "suggestedTags": ["tag1", "tag2"],\n  "confidence": "high/medium/low",\n  "summary": "brief description"\n}\n\nAvailable tags:\n- Menu/Recipes: recipes, menu items, cooking instructions\n- Ingredient Costs: ingredient prices, costs per unit, supplier pricing\n- Supplier Invoices: invoices, bills, purchase orders\n- Sales: sales data, revenue, customer orders\n- Staffing/Ops: staff schedules, operational procedures\n- Compliance Notes: compliance info, regulations, permits\n- Marketing/Reviews: marketing materials, reviews, social media`;

    const userContent = `File: ${fileName}\n\nContent preview:\n${content.substring(0, 1500)}`;

    const assistant = await openaiChat([
      { role: "system", content: system },
      { role: "user", content: userContent },
    ], "gpt-4o-mini", 0.3);

    return JSON.parse(assistant || "{}");
  } catch (error) {
    console.error("Categorization error:", error);
    return {
      suggestedTags: ["Menu/Recipes"],
      confidence: "low",
      summary: "Document analysis complete",
    };
  }
}
async function analyzeInvoiceImage(base64Image: string) {
  try {
    const prompt = `Analyze this invoice image and extract the following structured data as JSON:
{
  "vendor": "Name of the supplier",
  "totalAmount": 123.45,
  "currency": "GBP",
  "date": "YYYY-MM-DD",
  "items": [
    { "name": "Item Name", "qty": 2, "price": 10.50 }
  ],
  "confidence": 0.95,
  "summary": "Brief summary of the invoice"
}
Only return the JSON.`;

    const assistant = await openaiChat([
      {
        role: "user",
        content: [
          { type: "text", text: prompt },
          {
            type: "image_url",
            image_url: {
              url: `data:image/jpeg;base64,${base64Image}`
            }
          }
        ]
      }
    ], "gpt-4o", 0);

    const match = assistant?.match(/\{[\s\S]*\}/);
    const jsonStr = match ? match[0] : (assistant || "{}");
    return JSON.parse(jsonStr);
  } catch (error) {
    console.error("Image analysis error:", error);
    return null;
  }
}


// Collapsible Citation Component
function CitationView({ citations }: { citations: any[] }) {
  const [expanded, setExpanded] = useState(false);

  if (!citations || citations.length === 0) return null;

  return React.createElement(
    "div",
    { className: "mt-3 pt-3 border-t border-gray-200/50" },
    [
      React.createElement(
        "button",
        {
          key: "toggle",
          onClick: () => setExpanded(!expanded),
          className: "text-xs font-bold text-gray-600 opacity-80 hover:opacity-100 uppercase tracking-wider flex items-center gap-1.5 transition-opacity",
        },
        [
          "SOURCES",
          React.createElement("span", { className: "bg-gray-200/50 px-1.5 py-0.5 rounded text-[10px]" }, citations.length),
          React.createElement("span", {
            style: {
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s"
            }
          }, "▼")
        ]
      ),
      expanded &&
      React.createElement("div", { key: "list", className: "mt-2 space-y-1" },
        citations.map((citation: any, idx: number) =>
          React.createElement(
            "div",
            { key: idx, className: "text-xs bg-white/40 px-2 py-1.5 rounded-md border border-white/40 flex items-center gap-2" },
            [
              React.createElement("span", { className: "opacity-60" }, "📄"),
              React.createElement("span", { className: "font-medium opacity-80" }, citation.fileName),
              React.createElement("span", { className: "opacity-50" }, `• ${citation.snippet?.substring(0, 30)}...`)
            ]
          )
        )
      )
    ]
  );
}

// Action Panel Component
function ActionPanel({ actionMeta }: { actionMeta: any }) {
  if (!actionMeta) return null;

  return React.createElement(
    "div",
    {
      className: "mt-4 p-4 rounded-xl border border-amber-200/50 relative overflow-hidden group",
      style: {
        background: "linear-gradient(135deg, rgba(255, 251, 235, 0.8) 0%, rgba(254, 243, 199, 0.4) 100%)",
      }
    },
    [
      // Background decorative glow
      React.createElement("div", {
        className: "absolute -right-4 -top-4 w-24 h-24 bg-amber-400/10 rounded-full blur-2xl group-hover:bg-amber-400/20 transition-colors"
      }),

      React.createElement("div", { className: "relative z-10" }, [
        React.createElement("div", { className: "flex items-center gap-2 mb-2" }, [
          React.createElement("div", {
            className: "w-6 h-6 rounded-full bg-amber-100/80 flex items-center justify-center text-amber-600 border border-amber-200/50 shadow-sm"
          }, "⚡"),
          React.createElement(
            "div",
            { className: "text-xs font-bold text-amber-800 uppercase tracking-widest" },
            "Suggested Action"
          ),
        ]),

        React.createElement(
          "div",
          { className: "text-sm text-gray-700 font-medium mb-4 pl-1" },
          "Review and publish this reply to Google Reviews."
        ),

        React.createElement(
          "div",
          { className: "flex space-x-3" },
          [
            React.createElement(
              "button",
              {
                key: "post-google",
                onClick: () => alert("Demo: This would post to Google Reviews."),
                className: "flex-1 bg-gradient-to-r from-indigo-600 to-indigo-600 text-white text-xs px-4 py-2.5 rounded-lg hover:shadow-md hover:from-indigo-700 hover:to-indigo-700 font-semibold transition-all flex items-center justify-center gap-2",
              },
              [
                React.createElement("span", null, "🚀"),
                "Post Now"
              ]
            ),
            React.createElement(
              "button",
              {
                key: "edit-reply",
                onClick: () => alert("You can edit this reply before posting."),
                className: "flex-1 bg-white/60 border border-amber-200/60 text-amber-900 text-xs px-4 py-2.5 rounded-lg hover:bg-white hover:border-amber-300 font-semibold transition-all shadow-sm",
              },
              "Edit Draft"
            ),
          ]
        ),
      ])
    ]
  );
}


// Chat Interface Component
function ChatInterface({
  activeAgent,
  routerMode,
  onSaveDecision,
  externalAlert,
  onClearExternalAlert,
  prefillInput,
  onClearPrefill,
  onAction,
  isWidget = false,
}: {
  activeAgent: AgentId;
  routerMode: boolean;
  onSaveDecision: (context: string) => void;
  externalAlert?: { id?: string; message: string; agentType?: AgentId } | null;
  onClearExternalAlert?: () => void;
  prefillInput?: string | null;
  onClearPrefill?: () => void;
  onAction?: (meta: any) => void;
  isWidget?: boolean;
}) {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // Tone selector state for Marketing agent only (calm|warm|firm)
  const [tone, setTone] = useState<"calm" | "warm" | "firm">("calm");
  const [responseLength, setResponseLength] = useState<"short" | "normal">("normal");
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  // Scroll the inner messages container to the bottom without affecting page scroll
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    try {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } catch (err) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (overrideInput?: string) => {
    const textToSend = overrideInput || input;
    if (!textToSend.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: textToSend,
      timestamp: new Date(),
    };

    const currentInput = textToSend;
    setMessages((prev) => [...prev, userMessage]);
    if (!overrideInput) setInput("");
    setLoading(true);

    try {
      const result = await findsyWorkflow({
        input: currentInput,
        agentType: activeAgent, // manual selection respected unless routerMode is true
        conversationHistory: messages.slice(-10),
        routerMode: routerMode,
        env: {
          LANGBASE_API_KEY: import.meta.env.VITE_LANGBASE_API_KEY,
        },
        // Pass the selected tone when using the Marketing agent
        tone: activeAgent === "marketing" ? tone : undefined,
        responseLength: responseLength,
      });

      const assistantMessage = {
        role: "assistant",
        content: result.response,
        agent: result.agentName,
        citations: result.citations,
        filesUsed: result.filesUsed,
        // actionMeta is set when findsyWorkflow detected an ACTION block
        actionMeta: result.actionMeta || null,
        timestamp: new Date(),
      };

      if (result.actionMeta && onAction) {
        onAction(result.actionMeta);
      }

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${errorMsg}\n\n(See DevTools Console for full details)`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle external alerts (e.g., from target notifications)
  useEffect(() => {
    if (!externalAlert || !externalAlert.message) return;
    const fire = async () => {
      if (loading) return;
      setLoading(true);
      const userMessage = { role: "user", content: externalAlert.message, timestamp: new Date() };
      setMessages((prev) => [...prev, userMessage]);

      try {
        const result = await findsyWorkflow({
          input: externalAlert.message,
          agentType: externalAlert.agentType || activeAgent,
          conversationHistory: messages.slice(-10),
          routerMode: false, // force single-agent handling for alerts
          env: {
            LANGBASE_API_KEY: import.meta.env.VITE_LANGBASE_API_KEY,
          },
          responseLength: responseLength,
        });

        const assistantMessage = {
          role: "assistant",
          content: result.response,
          agent: result.agentName,
          citations: result.citations,
          filesUsed: result.filesUsed,
          actionMeta: result.actionMeta || null,
          timestamp: new Date(),
        };

        if (result.actionMeta && onAction) {
          onAction(result.actionMeta);
        }
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        console.error("External alert handling error:", err);
      } finally {
        setLoading(false);
        if (onClearExternalAlert) onClearExternalAlert();
      }
    };

    fire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAlert]);

  // Handle input prefilling from dashboard KPI cards
  useEffect(() => {
    if (!prefillInput) return;

    // Auto-send if it's a specific action or explanation
    if (prefillInput.startsWith("I want to manually update") || prefillInput.startsWith("Explain:")) {
      sendMessage(prefillInput);
    } else {
      setInput(prefillInput);
    }

    if (onClearPrefill) onClearPrefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillInput]);

  return React.createElement(
    "div",
    {
      style: isWidget ? {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      } : {
        background: "rgba(255, 255, 255, 0.6)",
        borderRadius: 24,
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(255, 255, 255, 0.3)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        height: 600,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }
    },
    [
      React.createElement(
        "div",
        { key: "messages", ref: messagesContainerRef, className: "flex-1 overflow-y-auto p-6 space-y-6" },
        [
          ...messages.map((message, index) =>
            React.createElement(
              "div",
              {
                key: index,
                className: `flex ${message.role === "user" ? "justify-end" : "justify-start"
                  }`,
              },
              React.createElement(
                "div",
                {
                  className: `max-w-[85%] rounded-2xl p-4 shadow-sm ${message.role === "user"
                    ? "bg-gradient-to-br from-indigo-600 to-indigo-600 text-white rounded-tr-sm"
                    : "bg-white/50 border border-white/60 text-gray-800 rounded-tl-sm backdrop-blur-sm"
                    }`,
                },
                [
                  message.agent &&
                  React.createElement(
                    "div",
                    {
                      key: "agent",
                      className: `text-xs font-bold mb-1 uppercase tracking-wider ${message.role === "user" ? "text-blue-100" : "text-indigo-600"}`,
                    },
                    message.agent === "multiple"
                      ? `Multiple agents (${message.agentIds?.join(", ")})`
                      : message.agent
                  ),
                  React.createElement(
                    "div",
                    { key: "content", className: "whitespace-pre-wrap text-sm leading-relaxed" },
                    message.agent === "multiple"
                      ? // Split multi-agent response by agent name pattern and render each with border
                      (() => {
                        const lines = (message.content || "").split("\n");
                        const agents: any[] = [];
                        let currentAgent: string | null = null;
                        let currentText: string[] = [];

                        lines.forEach((line: string) => {
                          const agentMatch = line.match(/^(.*?):$/);
                          if (agentMatch && agentMatch[1].includes("Agent")) {
                            if (currentAgent) {
                              agents.push({ name: currentAgent, text: currentText.join("\n").trim() });
                            }
                            currentAgent = agentMatch[1];
                            currentText = [];
                          } else {
                            currentText.push(line);
                          }
                        });
                        if (currentAgent) {
                          agents.push({ name: currentAgent, text: currentText.join("\n").trim() });
                        }

                        return agents.map((agent, idx) =>
                          React.createElement(
                            "div",
                            {
                              key: `agent-${idx}`,
                              className: "mb-3 pb-3 border-b border-gray-200/50 last:border-b-0",
                            },
                            [
                              React.createElement(
                                "div",
                                { key: "name", className: "font-bold text-xs mb-1 text-gray-900" },
                                agent.name
                              ),
                              React.createElement(
                                "div",
                                { key: "text", className: "text-sm" },
                                agent.text
                              ),
                            ]
                          )
                        );
                      })()
                      : message.content
                  ),

                  // Replaced inline citations with CitationView
                  message.citations &&
                  message.citations.length > 0 &&
                  React.createElement(CitationView, { key: "citations", citations: message.citations }),

                  message.role === "assistant" &&
                  React.createElement(
                    "div",
                    {
                      key: "save-decision",
                      className: "mt-3 flex justify-end",
                    },
                    React.createElement(
                      "button",
                      {
                        onClick: () => onSaveDecision(message.content),
                        className:
                          "text-xs bg-green-50 text-green-700 px-3 py-1.5 rounded-full hover:bg-green-100 border border-green-200/50 flex items-center gap-1.5 transition-colors font-medium",
                      },
                      [
                        React.createElement("span", { key: "icon" }, "💾"),
                        "Save Decision"
                      ]
                    )
                  ),
                  // Action Panel Component
                  message.actionMeta &&
                  React.createElement(ActionPanel, { key: "action-panel", actionMeta: message.actionMeta }),
                ]
              )
            )
          ),
          loading &&
          React.createElement(
            "div",
            { key: "loading", className: "flex justify-start" },
            React.createElement(
              "div",
              { className: "bg-white/50 border border-white/60 rounded-2xl rounded-tl-sm px-4 py-2 text-sm text-gray-500 italic" },
              "Thinking..."
            )
          ),
          React.createElement("div", { key: "scroll-anchor" }),
        ]
      ),

      React.createElement(
        "div",
        {
          key: "input-area",
          className: "p-4 border-t border-white/40 bg-white/30 backdrop-blur-md rounded-b-[24px]"
        },
        [
          // Context selectors for agents
          React.createElement(
            "div",
            { key: "context-selectors", className: "mb-3 flex flex-wrap items-center gap-4" },
            [
              // Tone selector shown only for the Marketing agent
              activeAgent === "marketing" &&
              React.createElement(
                "div",
                { key: "tone-select", className: "flex items-center space-x-2" },
                [
                  React.createElement(
                    "label",
                    { key: "tone-label", className: "text-xs font-bold text-gray-600 uppercase tracking-widest" },
                    "Tone"
                  ),
                  React.createElement(
                    "select",
                    {
                      key: "tone-dropdown",
                      value: tone,
                      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setTone(e.target.value as any),
                      className: "border border-gray-200/60 rounded-lg px-2 py-1 text-sm bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20",
                    },
                    [
                      React.createElement("option", { key: "calm", value: "calm" }, "Calm (Professional)"),
                      React.createElement("option", { key: "warm", value: "warm" }, "Warm (Friendly)"),
                      React.createElement("option", { key: "firm", value: "firm" }, "Firm (Confident)"),
                    ]
                  ),
                ]
              ),
              // Length selector shown for all agents
              React.createElement(
                "div",
                { key: "length-select", className: "flex items-center space-x-2" },
                [
                  React.createElement(
                    "label",
                    { key: "length-label", className: "text-xs font-bold text-gray-600 uppercase tracking-widest" },
                    "Length"
                  ),
                  React.createElement(
                    "select",
                    {
                      key: "length-dropdown",
                      value: responseLength,
                      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setResponseLength(e.target.value as any),
                      className: "border border-gray-200/60 rounded-lg px-2 py-1 text-sm bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20",
                    },
                    [
                      React.createElement("option", { key: "short", value: "short" }, "Short"),
                      React.createElement("option", { key: "normal", value: "normal" }, "Normal"),
                    ]
                  ),
                ]
              ),
            ]
          ),

          React.createElement("div", { key: "input-row", className: "flex space-x-3 items-center" }, [
            React.createElement("input", {
              key: "input",
              type: "text",
              value: input,
              onChange: (e: any) => setInput(e.target.value),
              onKeyDown: (e: any) => e.key === "Enter" && sendMessage(),
              placeholder: routerMode
                ? "Ask any question..."
                : `Ask the ${activeAgent} agent...`,
              className:
                "flex-1 bg-white/60 border border-white/60 rounded-xl px-4 py-3 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition-all shadow-inner",
              disabled: loading,
            }),
            React.createElement(
              "button",
              {
                key: "send-btn",
                onClick: () => sendMessage(),
                disabled: loading || !input.trim(),
                className:
                  "bg-gradient-to-r from-indigo-600 to-indigo-600 text-white w-12 h-12 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center transition-all transform hover:scale-105 active:scale-95",
              },
              React.createElement("span", { style: { fontSize: 20 } }, "➤")
            ),
          ]),
        ]
      ),
    ]
  );
}

// Agent Selector Component
// Agent Selector Component
function AgentSelector({
  activeAgent,
  onAgentChange,
  routerMode,
  onRouterModeChange,
}: {
  activeAgent: AgentId;
  onAgentChange: (id: AgentId) => void;
  routerMode: boolean;
  onRouterModeChange: (enabled: boolean) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const agents = [
    { id: "router", name: "Auto Router", icon: "🤖", description: "AI selects best agent(s)", color: "bg-emerald-100 text-emerald-800", gradient: "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)", border: "rgba(16,185,129,0.4)", isRouter: true },
    { id: "chef", name: "Chef / Inventory", icon: "👨‍🍳", description: "Recipes, costs & suppliers", color: "bg-orange-100 text-orange-800", gradient: "linear-gradient(135deg, rgba(255,165,0,0.1) 0%, rgba(255,69,0,0.1) 100%)", border: "rgba(255,69,0,0.3)", isRouter: false },
    { id: "manager", name: "General Manager", icon: "📋", description: "Operations & staffing", color: "bg-blue-100 text-blue-800", gradient: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(37,99,235,0.1) 100%)", border: "rgba(37,99,235,0.3)", isRouter: false },
    { id: "accountant", name: "Accountant", icon: "💰", description: "Finances & reporting", color: "bg-green-100 text-green-800", gradient: "linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.1) 100%)", border: "rgba(5,150,105,0.3)", isRouter: false },
    { id: "marketing", name: "Marketing", icon: "📢", description: "Campaigns & reviews", color: "bg-purple-100 text-purple-800", gradient: "linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(124,58,237,0.1) 100%)", border: "rgba(124,58,237,0.3)", isRouter: false },
  ] as const;

  const activeAgentDef = routerMode
    ? agents[0]
    : agents.find(a => a.id === activeAgent) || agents[1];

  return React.createElement("div", {
    ref: dropdownRef,
    className: "relative"
  }, [
    // Trigger Button
    React.createElement("button", {
      key: "trigger",
      onClick: () => setIsExpanded(!isExpanded),
      className: `flex items-center gap-3 px-4 py-2 rounded-xl transition-all border ${isExpanded ? 'bg-white border-indigo-200 shadow-lg scale-[1.02]' : 'bg-gray-50 border-gray-200 hover:bg-white hover:border-gray-300 shadow-sm'}`
    }, [
      React.createElement("div", { key: "meta", className: "flex items-center gap-2" }, [
        React.createElement("span", { key: "title", className: "text-[10px] font-black uppercase tracking-wider text-gray-400" }, "Agent:"),
        React.createElement("div", { key: "active", className: "flex items-center gap-2" }, [
          React.createElement("span", { key: "icon", className: "text-lg" }, activeAgentDef.icon),
          React.createElement("span", { key: "name", className: "text-sm font-bold text-gray-900" }, activeAgentDef.name),
        ])
      ]),
      React.createElement("span", { key: "chevron", className: `text-gray-400 text-xs transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}` }, "▼")
    ]),

    // Dropdown Menu
    isExpanded && React.createElement("div", {
      key: "dropdown",
      className: "absolute top-full right-0 mt-2 w-72 bg-white rounded-2xl shadow-2x border border-gray-100 z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
    }, [
      React.createElement("div", { key: "header", className: "px-4 py-3 bg-gray-50 border-b border-gray-100" }, [
        React.createElement("h4", { key: "t", className: "text-[10px] font-black uppercase tracking-widest text-gray-400" }, "Switch Intelligence Mode")
      ]),
      React.createElement("div", { key: "items", className: "p-2 space-y-1" },
        agents.map((agent) => {
          const isActive = agent.isRouter ? routerMode : (!routerMode && activeAgent === agent.id);
          return React.createElement("button", {
            key: agent.id,
            onClick: () => {
              if (agent.isRouter) onRouterModeChange(true);
              else {
                onRouterModeChange(false);
                onAgentChange(agent.id as AgentId);
              }
              setIsExpanded(false);
            },
            className: `w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${isActive ? 'bg-indigo-50 border-indigo-100' : 'hover:bg-gray-50 border-transparent'}`
          }, [
            React.createElement("span", { key: "icon", className: "text-2xl" }, agent.icon),
            React.createElement("div", { key: "info", className: "flex-1" }, [
              React.createElement("div", { key: "name", className: `text-sm font-bold ${isActive ? 'text-indigo-900' : 'text-gray-900'}` }, agent.name),
              React.createElement("div", { key: "desc", className: "text-xs text-gray-500" }, agent.description)
            ]),
            isActive && React.createElement("div", { key: "dot", className: "w-2 h-2 bg-indigo-500 rounded-full" })
          ]);
        })
      )
    ])
  ]);
}

// ---- Dashboard UI Components ----

function DecisionModal({
  isOpen,
  onClose,
  onSave,
  initialContext,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (decision: string, impact: string) => void;
  initialContext: string;
}) {
  const [decision, setDecision] = useState("");
  const [impact, setImpact] = useState("");

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setDecision("");
      setImpact("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return React.createElement(
    "div",
    {
      className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50",
      onClick: (e) => {
        if (e.target === e.currentTarget) onClose();
      },
    },
    React.createElement(
      "div",
      { className: "bg-white rounded-lg shadow-xl w-full max-w-lg p-6 m-4" },
      [
        React.createElement(
          "h3",
          { key: "title", className: "text-lg font-bold text-gray-900 mb-4" },
          "Save Decision"
        ),
        React.createElement(
          "div",
          { key: "context-area", className: "mb-4" },
          [
            React.createElement("label", { key: "l", className: "block text-sm font-medium text-gray-700 mb-1" }, "Context (from chat)"),
            React.createElement("div", { key: "v", className: "text-sm text-gray-600 bg-gray-50 p-2 rounded max-h-24 overflow-y-auto" }, initialContext),
          ]
        ),
        React.createElement(
          "div",
          { key: "decision-input", className: "mb-4" },
          [
            React.createElement("label", { key: "l", className: "block text-sm font-medium text-gray-700 mb-1" }, "Decision Made"),
            React.createElement("textarea", {
              key: "input",
              className: "w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none",
              rows: 3,
              placeholder: "What decision did you make based on this advice?",
              value: decision,
              onChange: (e: any) => setDecision(e.target.value),
            }),
          ]
        ),
        React.createElement(
          "div",
          { key: "impact-input", className: "mb-6" },
          [
            React.createElement("label", { key: "l", className: "block text-sm font-medium text-gray-700 mb-1" }, "Expected Impact"),
            React.createElement("input", {
              key: "input",
              type: "text",
              className: "w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none",
              placeholder: "e.g., Save £200/month, Improve service speed",
              value: impact,
              onChange: (e: any) => setImpact(e.target.value),
            }),
          ]
        ),
        React.createElement(
          "div",
          { key: "actions", className: "flex justify-end gap-3" },
          [
            React.createElement(
              "button",
              {
                key: "cancel",
                onClick: onClose,
                className: "px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg",
              },
              "Cancel"
            ),
            React.createElement(
              "button",
              {
                key: "save",
                onClick: () => {
                  if (!decision.trim()) return;
                  onSave(decision, impact);
                  onClose();
                },
                disabled: !decision.trim(),
                className: "px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed",
              },
              "Save Decision"
            ),
          ]
        ),
      ]
    )
  );
}

function DecisionsView({ decisions }: { decisions: any[] }) {
  return React.createElement(
    "div",
    { className: "h-full flex flex-col" },
    [
      React.createElement(
        "div",
        { key: "header", className: "mb-6 flex items-center justify-between" },
        [
          React.createElement("h2", { key: "title", className: "text-2xl font-bold text-gray-800" }, "Insight Driven"),
          React.createElement(
            "span",
            { key: "badge", className: "bg-white/50 border border-white/60 px-3 py-1 rounded-full text-sm font-medium text-gray-600 shadow-sm" },
            `${decisions.length} Insight${decisions.length === 1 ? "" : "s"}`
          )
        ]
      ),
      decisions.length === 0
        ? React.createElement(
          "div",
          { key: "empty", className: "flex-1 flex flex-col items-center justify-center text-gray-500 opacity-60" },
          [
            React.createElement("span", { key: "icon", style: { fontSize: 48, marginBottom: 16 } }, "📝"),
            "No decisions recorded yet"
          ]
        )
        : React.createElement(
          "div",
          { key: "grid", className: "grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-min overflow-y-auto overflow-x-hidden pb-6 pr-2" },
          decisions.map((decision, index) => {
            // Agent styling
            const agentEmoji = decision.agentType === 'chef' ? '👨‍🍳'
              : decision.agentType === 'manager' ? '👔'
                : decision.agentType === 'accountant' ? '💼'
                  : '🤖';

            const agentColor = decision.agentType === 'chef' ? 'bg-orange-100 text-orange-700'
              : decision.agentType === 'manager' ? 'bg-blue-100 text-indigo-700'
                : decision.agentType === 'accountant' ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700';

            // Time ago
            const timeAgo = (() => {
              const now = new Date();
              const created = new Date(decision.timestamp);
              const diffMs = now.getTime() - created.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              if (diffMins < 1) return 'just now';
              if (diffMins < 60) return `${diffMins}m ago`;
              const diffHours = Math.floor(diffMins / 60);
              if (diffHours < 24) return `${diffHours}h ago`;
              const diffDays = Math.floor(diffHours / 24);
              return `${diffDays}d ago`;
            })();

            return React.createElement(
              "div",
              {
                key: index,
                className: "bg-white/80 backdrop-blur-sm border border-white/60 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all hover:scale-[1.01] flex flex-col gap-3",
              },
              [
                React.createElement(
                  "div",
                  { key: "header", className: "flex justify-between items-start" },
                  [
                    React.createElement(
                      "div",
                      { key: "agent", className: `flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${agentColor} text-xs font-bold uppercase tracking-wider` },
                      [
                        React.createElement("span", { key: "emoji" }, agentEmoji),
                        decision.agentType
                      ]
                    ),
                    React.createElement(
                      "span",
                      { key: "time", className: "text-xs text-gray-400 font-bold" },
                      timeAgo
                    )
                  ]
                ),
                React.createElement(
                  "div",
                  {
                    key: "decision-text",
                    className: "font-semibold text-gray-900 text-base leading-snug",
                  },
                  decision.decision
                ),
                React.createElement(
                  "div",
                  {
                    key: "footer",
                    className: "flex items-center gap-2 pt-3 border-t border-gray-100/50 mt-auto"
                  },
                  [
                    React.createElement("span", { key: "label", className: "text-xs text-gray-500 font-bold uppercase tracking-wide" }, "Impact"),
                    React.createElement(
                      "span",
                      { key: "impact", className: "text-sm text-gray-700 bg-gray-50/80 px-2 py-1 rounded-md border border-gray-100 truncate flex-1 font-medium" },
                      decision.expectedImpact
                    )
                  ]
                ),
              ]
            );
          })
        )
    ]
  );
}

// Channels View - Community platform for business owners
function ChannelsView({ user, venueId }: { user: any; venueId: string }) {
  const [activeTab, setActiveTab] = React.useState<"news" | "groups" | "messages">("news");
  const [selectedGroup, setSelectedGroup] = React.useState<string | null>(null);
  const [messageInput, setMessageInput] = React.useState("");
  const [newsItems, setNewsItems] = React.useState<any[]>([]);
  const [groups, setGroups] = React.useState<any[]>([]);
  const [groupMessages, setGroupMessages] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Fetch News and Groups
  React.useEffect(() => {
    const fetchData = async () => {
      if (!supabase) return;
      setLoading(true);

      // Fetch News
      const { data: newsData } = await supabase
        .from('community_news')
        .select('*')
        .order('created_at', { ascending: false });

      const welcomeNews = {
        id: 'welcome-001',
        title: "Welcome to Findsy: Your Business Ecosystem",
        summary: "Findsy empowers business owners by turning complex data into actionable AI-driven strategies. Connect, collaborate, and grow with our community.",
        author_name: "Findsy Team",
        category: "Announcements",
        icon: "🚀",
        created_at: new Date().toISOString()
      };

      if (newsData) {
        setNewsItems([welcomeNews, ...newsData]);
      } else {
        setNewsItems([welcomeNews]);
      }

      // Fetch Groups
      const { data: channelData } = await supabase
        .from('community_channels')
        .select('*')
        .eq('type', 'group');
      if (channelData) {
        // In a real app, we'd fetch member counts and unread counts separately
        // For now, let's map the schema to the UI needs
        const formattedGroups = channelData.map((c: any) => ({
          ...c,
          members: Math.floor(Math.random() * 200) + 50, // Mock member count for now
          unread: 0,
          lastMessage: "No messages yet",
          lastTime: ""
        }));
        setGroups(formattedGroups);
      }
      setLoading(false);
    };

    fetchData();
  }, [venueId]);

  // Fetch Messages when a group is selected
  React.useEffect(() => {
    if (!selectedGroup || !supabase) return;

    const fetchMessages = async () => {
      const { data } = await supabase
        .from('community_messages')
        .select('*')
        .eq('channel_id', selectedGroup)
        .order('created_at', { ascending: true });
      if (data) {
        setGroupMessages(data.map((m: any) => ({
          id: m.id,
          author: m.user_id === user?.id ? "You" : (m.author_name || "Anonymous"),
          avatar: m.avatar_icon || "👤",
          message: m.content,
          timestamp: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          likes: m.likes || 0,
          user_id: m.user_id
        })));
      }
    };

    fetchMessages();

    // Set up Realtime Subscription
    const channel = supabase
      .channel(`room:${selectedGroup}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'community_messages',
        filter: `channel_id=eq.${selectedGroup}`
      }, (payload: { new: any }) => {
        const m: any = payload.new;
        const newMsg = {
          id: m.id,
          author: m.user_id === user?.id ? "You" : (m.author_name || "Anonymous"),
          avatar: m.avatar_icon || "👤",
          message: m.content,
          timestamp: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          likes: m.likes || 0,
          user_id: m.user_id
        };
        setGroupMessages(prev => [...prev, newMsg]);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedGroup]);



  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedGroup || !supabase || !user) return;

    const content = messageInput;
    setMessageInput("");

    await supabase.from('community_messages').insert({
      channel_id: selectedGroup,
      user_id: user.id,
      author_name: user.user_metadata?.full_name || "Business Owner",
      avatar_icon: "🙋",
      content: content
    });
  };


  // Keep directMessages as mock for now or fetch profiles
  const directMessages = [
    {
      id: 1,
      name: "Sarah Chen",
      role: "Restaurant Owner",
      avatar: "👩‍💼",
      lastMessage: "Thanks for the supplier recommendation!",
      unread: 2,
      online: true,
      lastTime: "5m ago"
    }
  ];

  // groupMessages state is used instead of static mock

  if (loading) {
    return React.createElement("div", { className: "p-8 text-center text-gray-500 font-medium" }, "Loading community...");
  }


  return React.createElement(
    "div",
    { className: "h-full flex flex-col" },
    [
      // Header
      React.createElement(
        "div",
        { key: "header", className: "mb-6 flex items-center justify-between" },
        [
          React.createElement("h2", { key: "title", className: "text-2xl font-bold text-gray-800" }, "Community Channels"),
          React.createElement(
            "button",
            {
              key: "invite",
              className: "bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-lg shadow-indigo-200 flex items-center gap-2"
            },
            [
              React.createElement("span", { key: "icon" }, "➕"),
              "Invite Members"
            ]
          )
        ]
      ),

      // Tabs
      React.createElement(
        "div",
        { key: "tabs", className: "flex gap-2 mb-6 border-b border-gray-200 pb-2" },
        [
          React.createElement(
            "button",
            {
              key: "news-tab",
              onClick: () => setActiveTab("news"),
              className: `px-6 py-3 rounded-t-xl font-bold text-sm transition-all ${activeTab === "news"
                ? "bg-white text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
                }`
            },
            [
              React.createElement("span", { key: "icon", className: "mr-2" }, "📰"),
              "News & Insights"
            ]
          ),
          React.createElement(
            "button",
            {
              key: "groups-tab",
              onClick: () => setActiveTab("groups"),
              className: `px-6 py-3 rounded-t-xl font-bold text-sm transition-all ${activeTab === "groups"
                ? "bg-white text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
                }`
            },
            [
              React.createElement("span", { key: "icon", className: "mr-2" }, "👥"),
              "Groups"
            ]
          ),
          React.createElement(
            "button",
            {
              key: "messages-tab",
              onClick: () => setActiveTab("messages"),
              className: `px-6 py-3 rounded-t-xl font-bold text-sm transition-all ${activeTab === "messages"
                ? "bg-white text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
                }`
            },
            [
              React.createElement("span", { key: "icon", className: "mr-2" }, "💬"),
              "Messages"
            ]
          ),

        ]
      ),

      // Content Area
      React.createElement(
        "div",
        { key: "content", className: "flex-1 overflow-y-auto overflow-x-hidden pr-2 space-y-4" },
        [
          activeTab === "news" && React.createElement(
            "div",
            { key: "news-content", className: "space-y-4" },
            newsItems.map(item =>
              React.createElement(
                "div",
                {
                  key: item.id,
                  className: "bg-white/80 backdrop-blur-sm border border-white/60 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all hover:scale-[1.01] cursor-pointer"
                },
                [
                  React.createElement(
                    "div",
                    { key: "header", className: "flex items-start justify-between mb-3" },
                    [
                      React.createElement(
                        "div",
                        { key: "category", className: "flex items-center gap-2" },
                        [
                          React.createElement("span", { key: "icon", className: "text-2xl" }, item.icon),
                          React.createElement(
                            "span",
                            { key: "label", className: "text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg" },
                            item.category
                          )
                        ]
                      ),
                      React.createElement(
                        "span",
                        { key: "time", className: "text-xs text-gray-400 font-bold" },
                        item.timestamp
                      )
                    ]
                  ),
                  React.createElement(
                    "h3",
                    { key: "title", className: "text-lg font-bold text-gray-900 mb-2" },
                    item.title
                  ),
                  React.createElement(
                    "p",
                    { key: "summary", className: "text-sm text-gray-600 mb-3" },
                    item.summary
                  ),
                  React.createElement(
                    "div",
                    { key: "footer", className: "flex items-center justify-between pt-3 border-t border-gray-100" },
                    [
                      React.createElement(
                        "span",
                        { key: "author", className: "text-xs text-gray-500 font-bold" },
                        `By ${item.author_name || item.author || "Team"}`
                      ),
                      React.createElement(
                        "button",
                        { key: "read", className: "text-xs font-bold text-indigo-600 hover:text-indigo-700" },
                        "Read More →"
                      )
                    ]
                  )
                ]
              )
            )
          ),

          activeTab === "groups" && React.createElement(
            "div",
            { key: "groups-content" },
            [
              selectedGroup === null
                ? React.createElement(
                  "div",
                  { key: "group-list", className: "space-y-3" },
                  groups.map(group =>
                    React.createElement(
                      "div",
                      {
                        key: group.id,
                        onClick: () => setSelectedGroup(group.id),
                        className: "bg-white/80 backdrop-blur-sm border border-white/60 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all hover:scale-[1.01] cursor-pointer flex items-center justify-between"
                      },
                      [
                        React.createElement(
                          "div",
                          { key: "info", className: "flex items-center gap-4" },
                          [
                            React.createElement(
                              "div",
                              { key: "icon", className: "text-3xl bg-indigo-50 w-14 h-14 rounded-xl flex items-center justify-center" },
                              group.icon
                            ),
                            React.createElement(
                              "div",
                              { key: "details" },
                              [
                                React.createElement(
                                  "h3",
                                  { key: "name", className: "font-bold text-gray-900 mb-1" },
                                  group.name
                                ),
                                React.createElement(
                                  "p",
                                  { key: "last", className: "text-sm text-gray-600 truncate max-w-md" },
                                  group.lastMessage
                                ),
                                React.createElement(
                                  "p",
                                  { key: "members", className: "text-xs text-gray-400 mt-1" },
                                  `${group.members} members`
                                )
                              ]
                            )
                          ]
                        ),
                        group.unread > 0 &&
                        React.createElement(
                          "span",
                          { key: "badge", className: "bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full" },
                          group.unread
                        )
                      ]
                    )
                  )
                )
                : React.createElement(
                  "div",
                  { key: "group-chat", className: "flex flex-col h-full" },
                  [
                    React.createElement(
                      "div",
                      { key: "chat-header", className: "bg-white/80 backdrop-blur-sm border border-white/60 p-4 rounded-2xl mb-4 flex items-center justify-between" },
                      [
                        React.createElement(
                          "div",
                          { key: "info", className: "flex items-center gap-3" },
                          [
                            React.createElement(
                              "button",
                              {
                                key: "back",
                                onClick: () => setSelectedGroup(null),
                                className: "text-gray-600 hover:text-gray-900 font-bold"
                              },
                              "← Back"
                            ),
                            React.createElement(
                              "h3",
                              { key: "name", className: "font-bold text-gray-900" },
                              groups.find(g => g.id === selectedGroup)?.name
                            )
                          ]
                        ),
                        React.createElement(
                          "span",
                          { key: "members", className: "text-sm text-gray-500" },
                          `${groups.find(g => g.id === selectedGroup)?.members} members`
                        )
                      ]
                    ),

                    // Messages
                    React.createElement(
                      "div",
                      { key: "messages", className: "flex-1 space-y-4 mb-4 overflow-y-auto" },
                      groupMessages.map(msg =>
                        React.createElement(
                          "div",
                          {
                            key: msg.id,
                            className: `bg-white/80 backdrop-blur-sm border border-white/60 p-4 rounded-2xl shadow-sm ${msg.author === "You" ? "ml-8" : ""}`
                          },
                          [
                            React.createElement(
                              "div",
                              { key: "header", className: "flex items-center gap-2 mb-2" },
                              [
                                React.createElement("span", { key: "avatar", className: "text-xl" }, msg.avatar),
                                React.createElement(
                                  "span",
                                  { key: "author", className: "font-bold text-sm text-gray-900" },
                                  msg.author
                                ),
                                React.createElement(
                                  "span",
                                  { key: "time", className: "text-xs text-gray-400 ml-auto" },
                                  msg.timestamp
                                )
                              ]
                            ),
                            React.createElement(
                              "p",
                              { key: "message", className: "text-sm text-gray-700 mb-2" },
                              msg.message
                            ),
                            React.createElement(
                              "div",
                              { key: "actions", className: "flex items-center gap-3 text-xs text-gray-500" },
                              [
                                React.createElement(
                                  "button",
                                  { key: "like", className: "hover:text-indigo-600 font-bold" },
                                  `👍 ${msg.likes}`
                                ),
                                React.createElement(
                                  "button",
                                  { key: "reply", className: "hover:text-indigo-600 font-bold" },
                                  "Reply"
                                )
                              ]
                            )
                          ]
                        )
                      )
                    ),

                    // Message Input
                    React.createElement(
                      "div",
                      { key: "input", className: "bg-white/80 backdrop-blur-sm border border-white/60 p-4 rounded-2xl flex gap-3" },
                      [
                        React.createElement("input", {
                          key: "field",
                          type: "text",
                          value: messageInput,
                          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setMessageInput(e.target.value),
                          placeholder: "Type your message...",
                          className: "flex-1 bg-gray-50 border-0 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500"
                        }),
                        React.createElement(
                          "button",
                          {
                            key: "send",
                            onClick: handleSendMessage,
                            className: "bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold transition-all"
                          },
                          "Send"
                        )
                      ]
                    )
                  ]
                )
            ]
          ),

          activeTab === "messages" && React.createElement(
            "div",
            { key: "messages-content", className: "space-y-3" },
            directMessages.map(dm =>
              React.createElement(
                "div",
                {
                  key: dm.id,
                  className: "bg-white/80 backdrop-blur-sm border border-white/60 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all hover:scale-[1.01] cursor-pointer flex items-center justify-between"
                },
                [
                  React.createElement(
                    "div",
                    { key: "info", className: "flex items-center gap-4" },
                    [
                      React.createElement(
                        "div",
                        { key: "avatar", className: "relative" },
                        [
                          React.createElement(
                            "div",
                            { key: "icon", className: "text-3xl bg-indigo-50 w-14 h-14 rounded-full flex items-center justify-center" },
                            dm.avatar
                          ),
                          dm.online &&
                          React.createElement(
                            "div",
                            { key: "status", className: "absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full" }
                          )
                        ]
                      ),
                      React.createElement(
                        "div",
                        { key: "details" },
                        [
                          React.createElement(
                            "h3",
                            { key: "name", className: "font-bold text-gray-900 mb-0.5" },
                            dm.name
                          ),
                          React.createElement(
                            "p",
                            { key: "role", className: "text-xs text-gray-400 mb-1" },
                            dm.role
                          ),
                          React.createElement(
                            "p",
                            { key: "last", className: "text-sm text-gray-600 truncate max-w-md" },
                            dm.lastMessage
                          )
                        ]
                      )
                    ]
                  ),
                  React.createElement(
                    "div",
                    { key: "meta", className: "text-right" },
                    [
                      React.createElement(
                        "p",
                        { key: "time", className: "text-xs text-gray-400 mb-2" },
                        dm.lastTime
                      ),
                      dm.unread > 0 &&
                      React.createElement(
                        "span",
                        { key: "badge", className: "bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full" },
                        dm.unread
                      )
                    ]
                  )
                ]
              )
            )
          ),

        ]
      )
    ]
  );
}
function ActionsInsightsView({
  targets,
  runCheckForTarget,
  createNewTarget,
  decisions
}: {
  targets: any[];
  runCheckForTarget: (t: any) => void;
  createNewTarget: () => void;
  decisions: any[];
}) {
  const [activeTab, setActiveTab] = useState<'targets' | 'decisions'>('targets');

  return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500 pb-12" }, [
    // Page Title & Header
    React.createElement("div", { key: "header-main", className: "mb-8 bg-gradient-to-br from-indigo-50 to-white p-8 rounded-[32px] border border-indigo-100/50 shadow-sm flex items-end justify-between" }, [
      React.createElement("div", {}, [
        React.createElement("h1", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Operations & Action Tracker"),
        React.createElement("p", { className: "text-gray-500 font-medium max-w-3xl leading-relaxed" },
          "Transform data into progress. Set performance targets to receive AI alerts when metrics stray, or review insight-driven decisions generated by your AI agents to streamline your business operations."
        )
      ]),
      activeTab === 'targets' && React.createElement("button", {
        key: "create-btn",
        onClick: createNewTarget,
        className: "group flex items-center gap-2 bg-indigo-600 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100/50 transition-all hover:scale-105 active:scale-95 mb-1"
      }, [
        React.createElement("span", { key: "icon", className: "text-base group-hover:rotate-90 transition-transform duration-300" }, "+"),
        "New Target"
      ]),
    ]),

    // Tab Headers (Standardized with Simulation View)
    React.createElement("div", { key: "header-combined", className: "flex items-center justify-between mb-8 border-b border-gray-200" }, [
      React.createElement("div", { key: "tabs", className: "flex gap-6" }, [
        React.createElement("button", {
          key: "targets-tab",
          onClick: () => setActiveTab('targets'),
          className: `pb-3 px-1 text-sm font-bold transition-all border-b-2 ${activeTab === 'targets' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`
        }, "My Actions"),
        React.createElement("button", {
          key: "decisions-tab",
          onClick: () => setActiveTab('decisions'),
          className: `pb-3 px-1 text-sm font-bold transition-all border-b-2 ${activeTab === 'decisions' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`
        }, "Insight Driven")
      ]),
      React.createElement("div", { key: "total-count", className: "flex items-center gap-2 pb-3" }, [
        React.createElement("span", { className: "w-2 h-2 rounded-full bg-indigo-500 animate-pulse" }),
        React.createElement("span", { className: "text-[10px] font-black uppercase text-gray-400 tracking-widest" }, `${targets.length + decisions.length} items queued`)
      ])
    ]),

    // Content area
    React.createElement("div", { key: "content-area", className: "flex-1 overflow-hidden" },
      activeTab === 'targets'
        ? React.createElement(TargetsView, { targets, runCheckForTarget })
        : React.createElement(DecisionsView, { decisions })
    )
  ]);
}


function TargetsView({
  targets,
  runCheckForTarget,
}: {
  targets: any[];
  runCheckForTarget: (t: any) => void;
}) {
  return React.createElement(
    "div",
    { className: "h-full flex flex-col" },
    [
      React.createElement(
        "div",
        { key: "header", className: "mb-6 flex items-center justify-between" },
        [
          React.createElement(
            "div",
            { className: "flex items-center gap-3" },
            [
              React.createElement("h2", { key: "title", className: "text-2xl font-bold text-gray-800" }, "My Actions"),
              React.createElement(
                "span",
                { key: "badge", className: "bg-white/50 border border-white/60 px-3 py-1 rounded-full text-sm font-medium text-gray-600 shadow-sm" },
                `${targets.length} Action${targets.length === 1 ? "" : "s"}`
              )
            ]
          )
        ]
      ),
      targets.length === 0
        ? React.createElement(
          "div",
          { key: "empty", className: "flex-1 flex flex-col items-center justify-center text-gray-500 opacity-60" },
          [
            React.createElement("span", { key: "icon", style: { fontSize: 48, marginBottom: 16 } }, "🎯"),
            "No targets set yet"
          ]
        )
        : React.createElement(
          "div",
          { key: "grid", className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-min overflow-y-auto pb-6 pr-2" },
          targets.map((t) => {
            // Time ago for last check
            const lastCheckedAgo = t.lastCheckedAt ? (() => {
              const now = new Date();
              const checked = new Date(t.lastCheckedAt);
              const diffMs = now.getTime() - checked.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              if (diffMins < 1) return 'just now';
              if (diffMins < 60) return `${diffMins}m ago`;
              const diffHours = Math.floor(diffMins / 60);
              if (diffHours < 24) return `${diffHours}h ago`;
              const diffDays = Math.floor(diffHours / 24);
              return `${diffDays}d ago`;
            })() : 'Never';

            // Status style
            const statusBadge = t.status === 'ok'
              ? { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '✓ OK', dot: 'bg-emerald-500' }
              : { bg: 'bg-red-100', text: 'text-red-700', label: '⚠ Alert', dot: 'bg-red-500' };

            return React.createElement(
              "div",
              {
                key: t.id,
                className: `bg-white/80 backdrop-blur-sm border ${t.status === 'alerting' ? 'border-red-500 ring-4 ring-red-500/10' : 'border-white/60'} p-6 rounded-2xl shadow-sm hover:shadow-md transition-all hover:scale-[1.01] flex flex-col gap-4 relative overflow-hidden`
              },
              [
                React.createElement(
                  "div",
                  { key: "header", className: "flex items-start justify-between" },
                  [
                    React.createElement(
                      "div",
                      { key: "info" },
                      [
                        React.createElement("h3", { key: "name", className: "font-bold text-gray-900 text-lg" }, t.name),
                        React.createElement("p", { key: "rule", className: "text-sm text-gray-500 mt-1" }, `${t.metric} ${t.direction === "below" ? "<" : ">"} ${t.threshold}`),
                      ]
                    ),
                    React.createElement(
                      "div",
                      {
                        key: "status",
                        className: `flex items-center gap-1.5 px-3 py-1 rounded-full ${statusBadge.bg} ${statusBadge.text} text-xs font-bold uppercase tracking-wider`
                      },
                      [
                        React.createElement("span", { key: "dot", className: `w-2 h-2 rounded-full ${statusBadge.dot}` }),
                        statusBadge.label
                      ]
                    ),
                  ]
                ),

                React.createElement(
                  "div",
                  {
                    key: "value-box",
                    className: `rounded-2xl p-4 border transition-all ${t.status === 'alerting' ? 'bg-red-50/50 border-red-100' : 'bg-gray-50/50 border-gray-100'}`
                  },
                  [
                    React.createElement(
                      "div",
                      { key: "header", className: "flex justify-between items-center mb-1" },
                      [
                        React.createElement("span", { key: "label", className: "text-[10px] text-gray-400 uppercase font-black tracking-widest" }, "Current Figure"),
                        t.lastValue !== undefined && React.createElement("span", {
                          key: "trend",
                          className: `text-[10px] font-bold px-2 py-0.5 rounded-full ${t.status === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`
                        }, t.status === 'ok' ? "HEALTHY" : "CRITICAL")
                      ]
                    ),
                    React.createElement(
                      "div",
                      { key: "val", className: "text-3xl font-black text-gray-900 tracking-tight" },
                      t.isChecking
                        ? React.createElement("span", { className: "text-indigo-500 animate-pulse" }, "Running...")
                        : t.lastValue !== undefined
                          ? (t.metric.includes("pct") || t.metric.includes("rate") || t.metric.includes("margin") ? `${t.lastValue}%` : `£${t.lastValue.toLocaleString()}`)
                          : React.createElement("span", { className: "text-gray-300 italic text-xl font-medium" }, "Pending check...")
                    ),
                  ]
                ),

                React.createElement(
                  "div",
                  { key: "footer", className: "mt-auto pt-4 border-t border-gray-100/50 flex items-center justify-between gap-4" },
                  [
                    React.createElement(
                      "div",
                      { key: "meta", className: "text-xs text-gray-400 font-medium" },
                      [
                        React.createElement("div", { key: "check" }, `Last checked: ${lastCheckedAgo}`)
                      ]
                    ),
                    React.createElement(
                      "button",
                      {
                        key: "run",
                        disabled: t.isChecking,
                        onClick: () => runCheckForTarget(t),
                        className: `text-xs border px-3 py-2 rounded-lg font-bold transition-all shadow-sm flex items-center gap-2 ${t.isChecking ? 'bg-gray-50 border-gray-200 text-gray-400' : 'bg-white border-gray-200 hover:border-blue-400 text-gray-600 hover:text-indigo-600'}`,
                      },
                      t.isChecking ? "Checking..." : "▶ Run Check"
                    ),
                  ]
                )
              ]
            );
          })
        )
    ]
  );
}

function NotificationsView({
  notifications,
  onMarkAllRead,
  onNotificationClick,
}: {
  notifications: any[];
  onMarkAllRead: () => void;
  onNotificationClick: (n: any) => void;
}) {
  return React.createElement(
    "div",
    { className: "h-full flex flex-col" },
    [
      React.createElement(
        "div",
        { key: "header", className: "mb-6 flex items-center justify-between" },
        [
          React.createElement(
            "div",
            { className: "flex items-center gap-3" },
            [
              React.createElement("h2", { key: "title", className: "text-2xl font-bold text-gray-800" }, "Notifications"),
              React.createElement(
                "span",
                { key: "badge", className: "bg-white/50 border border-white/60 px-3 py-1 rounded-full text-sm font-medium text-gray-600 shadow-sm" },
                `${notifications.filter(n => !n.read).length} Unread`
              )
            ]
          ),
          notifications.filter(n => !n.read).length > 0 &&
          React.createElement(
            "button",
            {
              key: "mark-all",
              onClick: onMarkAllRead,
              className: "text-indigo-600 hover:text-blue-800 font-medium bg-indigo-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors",
            },
            "Mark all as read"
          ),
        ]
      ),
      notifications.length === 0
        ? React.createElement(
          "div",
          { key: "empty", className: "flex-1 flex flex-col items-center justify-center text-gray-500 opacity-60" },
          [
            React.createElement(Bell, { key: "icon", size: 48, className: "text-gray-400 mb-4" }),
            "No notifications"
          ]
        )
        : React.createElement(
          "div",
          { key: "list", className: "space-y-4 auto-rows-min overflow-y-auto pb-6 pr-2" },
          notifications.map((n) => {
            // Determine severity color
            const severity = n.direction === 'below' && parseFloat(`${n.value}`) < parseFloat(`${n.threshold}`) * 0.8
              ? 'critical'
              : 'warning';
            const bgColor = n.read
              ? "bg-gray-50/50 border-gray-200/60"
              : severity === 'critical'
                ? "bg-red-50/80 border-red-200"
                : "bg-amber-50/80 border-amber-200";
            const borderColor = n.read ? "" : severity === 'critical' ? "border-l-4 border-l-red-500" : "border-l-4 border-l-amber-500";

            // Time ago calculation
            const timeAgo = (() => {
              const now = new Date();
              const created = new Date(n.createdAt);
              const diffMs = now.getTime() - created.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              if (diffMins < 1) return 'just now';
              if (diffMins < 60) return `${diffMins}m ago`;
              const diffHours = Math.floor(diffMins / 60);
              if (diffHours < 24) return `${diffHours}h ago`;
              const diffDays = Math.floor(diffHours / 24);
              return `${diffDays}d ago`;
            })();

            // Agent styling
            const agentEmoji = n.agentType === 'chef' ? '👨‍🍳'
              : n.agentType === 'manager' ? '👔'
                : n.agentType === 'accountant' ? '💼'
                  : '📢';

            return React.createElement(
              "div",
              {
                key: n.id,
                className: `border rounded-xl p-5 ${bgColor} ${borderColor} cursor-pointer hover:shadow-md transition-all duration-200 flex gap-4 items-start`,
                onClick: () => onNotificationClick(n)
              },
              [
                React.createElement("span", { key: "emoji", className: "text-2xl mt-1" }, agentEmoji),
                React.createElement(
                  "div",
                  { key: "content", className: "flex-1" },
                  [
                    React.createElement(
                      "div",
                      { key: "top", className: "flex justify-between items-start mb-1" },
                      [
                        React.createElement("h3", { key: "title", className: `font-bold text-gray-900 ${n.read ? "font-semibold" : "font-extrabold"}` }, n.targetName),
                        !n.read && React.createElement("span", { key: "dot", className: "w-2.5 h-2.5 bg-indigo-500 rounded-full flex-shrink-0 mt-1.5" })
                      ]
                    ),
                    React.createElement("p", { key: "msg", className: "text-gray-700 text-sm mb-3" }, n.message),
                    React.createElement(
                      "div",
                      { key: "meta", className: "flex items-center gap-4 text-xs text-gray-500" },
                      [
                        React.createElement("span", { key: "time" }, timeAgo),
                        React.createElement("span", { key: "action", className: "text-indigo-600 font-semibold flex items-center gap-1" }, ["View Issue", React.createElement("span", { key: "arrow" }, "→")])
                      ]
                    )
                  ]
                )
              ]
            );
          })
        )
    ]
  );
}

const QUICK_METRICS = [
  { name: "Daily Sales", key: "total_sales", icon: "💰", agent: "accountant" },
  { name: "Food Cost %", key: "food_cost_pct", icon: "🍱", agent: "chef" },
  { name: "Waste Rate", key: "waste_rate", icon: "🗑️", agent: "chef" },
  { name: "Staff Costs", key: "staff_costs", icon: "👥", agent: "manager" },
  { name: "Gross Margin", key: "gross_margin", icon: "📊", agent: "accountant" },
  { name: "Social Reach", key: "social_reach", icon: "📢", agent: "marketing" },
  { name: "Avg Order Value", key: "aov", icon: "💰", agent: "marketing" },
] as const;

function TargetModal({
  isOpen,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (target: {
    name: string;
    metric: string;
    threshold: number;
    direction: "above" | "below";
    agentType: AgentId;
  }) => void;
}) {
  const [name, setName] = useState("");
  const [metric, setMetric] = useState("");
  const [threshold, setThreshold] = useState("");
  const [direction, setDirection] = useState<"above" | "below">("below");
  // Default to accountant but allow user to pick
  const [agentType, setAgentType] = useState<AgentId>("accountant");

  useEffect(() => {
    if (isOpen) {
      setName("");
      setMetric("");
      setThreshold("");
      setDirection("below");
      setAgentType("accountant");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleQuickSelect = (qm: typeof QUICK_METRICS[number]) => {
    setName(qm.name);
    setMetric(qm.key);
    setAgentType(qm.agent as AgentId);
  };

  const summaryText = name && threshold
    ? `🔔 Alert when ${name} is ${direction === "above" ? "more than" : "less than"} ${metric.includes("pct") || metric.includes("rate") || metric.includes("margin") ? threshold + "%" : "£" + threshold}`
    : "Fill in the details to see your alert logic...";

  const agentIcons: Record<ValidAgentId, string> = {
    chef: "👨‍🍳",
    manager: "📋",
    accountant: "📊",
    marketing: "📣",
  };

  return React.createElement(
    "div",
    {
      className: "fixed inset-0 flex items-center justify-center z-[100] px-4",
      onClick: (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
      },
    },
    [
      // Backdrop with blur
      React.createElement("div", {
        key: "backdrop",
        className: "absolute inset-0 bg-gray-900/40 backdrop-blur-md transition-opacity animate-in fade-in duration-300",
      }),

      // Modal Container
      React.createElement(
        "div",
        {
          key: "modal",
          className: "relative bg-white/90 backdrop-blur-xl rounded-[28px] shadow-2xl w-full max-w-lg overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300",
          style: { boxShadow: "0 25px 70px -10px rgba(0,0,0,0.3)" }
        },
        [
          // Header with Gradient
          React.createElement("div", {
            key: "header",
            className: "bg-gradient-to-r from-indigo-600 to-indigo-700 px-8 py-6 text-white"
          }, [
            React.createElement("h3", { key: "h", className: "text-2xl font-black tracking-tight" }, "Add Monitoring Target"),
            React.createElement("p", { key: "p", className: "text-blue-100 text-sm mt-1 font-medium" }, "Findsy AI will watch these metrics and alert you instantly.")
          ]),

          React.createElement("div", { key: "body", className: "p-8 space-y-6" }, [
            // Quick Select Section
            React.createElement("div", { key: "quick" }, [
              React.createElement("label", { className: "block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3" }, "Quick Start Metrics"),
              React.createElement("div", { className: "flex flex-wrap gap-2" },
                QUICK_METRICS.map(qm => React.createElement("button", {
                  key: qm.key,
                  onClick: () => handleQuickSelect(qm),
                  className: `px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${metric === qm.key ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`
                }, [
                  React.createElement("span", { key: "i" }, qm.icon),
                  qm.name
                ]))
              )
            ]),

            // Form Grid
            React.createElement("div", { key: "form", className: "space-y-4" }, [
              // Target Name
              React.createElement("div", { key: "name-row" }, [
                React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Target Name"),
                React.createElement("input", {
                  type: "text",
                  className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 transition-all",
                  placeholder: "e.g. Daily Revenue Safety Net",
                  value: name,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value),
                }),
              ]),

              // Logic Row
              React.createElement("div", { key: "logic", className: "grid grid-cols-12 gap-3 items-end" }, [
                React.createElement("div", { className: "col-span-12 md:col-span-5" }, [
                  React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Metric Key"),
                  React.createElement("input", {
                    type: "text",
                    className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-indigo-500",
                    placeholder: "metric_id",
                    value: metric,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setMetric(e.target.value),
                  }),
                ]),
                React.createElement("div", { className: "col-span-12 md:col-span-4" }, [
                  React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Threshold"),
                  React.createElement("input", {
                    type: "number",
                    className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-indigo-500",
                    placeholder: "0.00",
                    value: threshold,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setThreshold(e.target.value),
                  }),
                ]),
                React.createElement("div", { className: "col-span-12 md:col-span-3" }, [
                  React.createElement("select", {
                    className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-indigo-500 cursor-pointer",
                    value: direction,
                    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setDirection(e.target.value as "above" | "below"),
                  }, [
                    React.createElement("option", { key: "above", value: "above" }, "Above (>)"),
                    React.createElement("option", { key: "below", value: "below" }, "Below (<)"),
                  ]),
                ]),
              ]),

              // Agent Picker
              React.createElement("div", { key: "agent-row" }, [
                React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Assigned Expert"),
                React.createElement("div", { className: "grid grid-cols-4 gap-2" },
                  (Object.keys(AGENT_PERSONAS) as ValidAgentId[]).map(id =>
                    React.createElement("button", {
                      key: id,
                      onClick: () => setAgentType(id),
                      className: `flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all ${agentType === id ? "border-indigo-600 bg-indigo-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`
                    }, [
                      React.createElement("span", { key: "i", className: "text-xl" }, agentIcons[id]),
                      React.createElement("span", { key: "n", className: "text-[10px] font-black uppercase tracking-tighter" }, id)
                    ])
                  )
                )
              ]),
            ]),

            // Live Summary Bubble
            React.createElement("div", {
              key: "summary",
              className: `p-4 rounded-2xl text-sm font-bold transition-all ${name && threshold ? "bg-indigo-600 text-white shadow-xl shadow-indigo-200" : "bg-gray-100 text-gray-400"}`
            }, summaryText),

            // Modal Footer
            React.createElement("div", { key: "actions", className: "flex gap-3 pt-2" }, [
              React.createElement("button", {
                key: "cancel",
                onClick: onClose,
                className: "flex-1 px-6 py-4 rounded-2xl text-sm font-black text-gray-500 hover:bg-gray-100 transition-colors uppercase tracking-widest",
              }, "Cancel"),
              React.createElement("button", {
                key: "create",
                onClick: () => {
                  if (!name || !metric || !threshold) return;
                  onSave({
                    name,
                    metric,
                    threshold: parseFloat(threshold),
                    direction,
                    agentType,
                  });
                  onClose();
                },
                className: "flex-[2] px-6 py-4 rounded-2xl text-sm font-black text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 transition-all uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed",
                disabled: !name || !metric || !threshold
              }, "Create Target")
            ]),
          ]),
        ]
      )
    ]
  );
}


type DateFilterType = "day" | "week" | "month" | "yesterday" | "last-week" | "same-day-last-week" | "last-quarter" | "6-months" | "custom";

interface DateFilter {
  type: DateFilterType;
  startDate?: string;
  endDate?: string;
}

type Kpi = {
  title: string;
  value: string;
  delta?: string;
  icon?: string;
  trend?: "up" | "down" | "flat";
  isMissing?: boolean;
  suggestion?: string;
  metricKey?: string;
  agentType?: AgentId;
  targetView?: any;
  visual?: React.ReactNode;
  timeLabel?: string;
  coreValue?: string;
  moneyValue?: string;
  varianceValue?: string;
  driver?: string;
};

function renderMiniTrend(values: number[], color: string) {
  const max = Math.max(...values);
  return React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "flex-end",
      gap: 4,
      height: 26,
      marginTop: 6
    }
  }, values.map((v, i) =>
    React.createElement("div", {
      key: i,
      style: {
        width: 6,
        height: `${Math.max(20, Math.round((v / max) * 100))}%`,
        borderRadius: 4,
        background: `linear-gradient(180deg, ${color} 0%, ${color}99 100%)`,
        opacity: 0.9
      }
    })
  ));
}

function getDashboardKpis(filter: DateFilter, files: any[]): Kpi[] {
  const { type } = filter;

  // Try to find values in files
  const revenueVal = findMetricInFiles("total_sales", files);
  const coversVal = findMetricInFiles("covers", files);
  const aovVal = findMetricInFiles("aov", files);
  const paceVal = findMetricInFiles("sales_pace", files);
  const foodCostVal = findMetricInFiles("food_cost_pct", files);
  const laborCostVal = findMetricInFiles("labor_cost_pct", files);
  const wasteVal = findMetricInFiles("waste_rate", files);
  const inventoryVal = findMetricInFiles("inventory_value", files);

  // Fallback demo modifiers if not in files
  let revenue = revenueVal !== undefined ? revenueVal : 4520;
  let revenueDelta = 12;
  let coversDelta = 8;
  let paceDelta = 4;
  let foodCostDelta = -1;
  let laborDelta = 2;
  let wasteDelta = -1;
  let inventoryDelta = 4;
  const demo = {
    revenue: Math.round(revenue),
    covers: 210,
    aov: 26.5,
    pace: 104,
    foodCost: 31,
    labor: 27,
    waste: 4.5,
    inventory: 13200,
  };

  // Modifiers based on type (only for demo values)
  if (revenueVal === undefined) {
    if (type === "day" || type === "yesterday") { revenue *= 0.35; revenueDelta = -4; }
    else if (type === "week" || type === "last-week") { revenue *= 1.15; revenueDelta = 15; }
    else if (type === "month") { revenue *= 4.2; revenueDelta = 22; }
  }

  const rangeTitle = type === "custom"
    ? `${filter.startDate || "?"} to ${filter.endDate || "?"}`
    : type === "day" ? "Today"
      : type === "week" ? "Week to date"
        : type === "month" ? "Month to date"
          : type.replace(/-/g, " ");
  const timeLabel = rangeTitle;

  const rangeMultiplier = revenueVal === undefined ? (revenue / demo.revenue) : 1;
  const totalRevenue = revenue;
  const covers = coversVal ?? Math.round(demo.covers * rangeMultiplier);
  const aov = aovVal ?? (covers > 0 ? (totalRevenue / covers) : demo.aov);
  const pace = paceVal ?? demo.pace;
  const foodCostPct = foodCostVal ?? demo.foodCost;
  const laborPct = laborCostVal ?? demo.labor;
  const wastePct = wasteVal ?? demo.waste;
  const inventoryValue = inventoryVal ?? demo.inventory;
  const targetRevenue = pace ? totalRevenue / (pace / 100) : totalRevenue * 0.96;
  const revenueVariance = totalRevenue - targetRevenue;
  const targetFoodPct = 28;
  const targetLaborPct = 25;
  const targetWastePct = 3.5;
  const foodSpend = totalRevenue * (foodCostPct / 100);
  const foodTargetSpend = totalRevenue * (targetFoodPct / 100);
  const foodVariance = foodSpend - foodTargetSpend;
  const laborSpend = totalRevenue * (laborPct / 100);
  const laborTargetSpend = totalRevenue * (targetLaborPct / 100);
  const laborVariance = laborSpend - laborTargetSpend;
  const targetPrimeCostPct = 60;
  const primeCostPct = Number((foodCostPct + laborPct).toFixed(1));
  const primeCostValue = totalRevenue * (primeCostPct / 100);
  const primeCostTargetValue = totalRevenue * (targetPrimeCostPct / 100);
  const primeCostVariance = primeCostValue - primeCostTargetValue;
  const wasteSpend = totalRevenue * (wastePct / 100);
  const wasteTargetSpend = totalRevenue * (targetWastePct / 100);
  const wasteVariance = wasteSpend - wasteTargetSpend;
  const targetInventory = 12000;
  const inventoryVariance = inventoryValue - targetInventory;
  const targetGrossMarginPct = 100 - targetFoodPct;
  const grossMarginPct = Math.max(0, Math.round(100 - foodCostPct));
  const grossMarginValue = totalRevenue - foodSpend;
  const grossMarginTargetValue = totalRevenue * (targetGrossMarginPct / 100);
  const grossMarginVariance = grossMarginValue - grossMarginTargetValue;
  const targetCovers = Math.max(0, Math.round(covers * 0.95));
  const coversVariance = (covers - targetCovers) * aov;

  return [
    {
      title: `Revenue`,
      value: `£${Math.round(totalRevenue).toLocaleString()}`,
      delta: revenueVal !== undefined ? undefined : formatPercent(revenueDelta),
      icon: "REV",
      isMissing: false,
      suggestion: "Upload a POS Sales Report or Daily Revenue CSV",
      metricKey: "total_sales",
      agentType: "accountant",
      targetView: "sales",
      timeLabel,
      coreValue: `£${Math.round(totalRevenue).toLocaleString()}`,
      moneyValue: `£${Math.round(totalRevenue).toLocaleString()} sales`,
      varianceValue: `${revenueVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(revenueVariance)).toLocaleString()} vs target`,
      driver: "Driver: peak service throughput",
      visual: renderMiniTrend([42, 48, 46, 55, 60, 58], "#0ea5e9")
    },
    {
      title: "Covers",
      value: `${Math.round(covers).toLocaleString()}`,
      delta: coversVal !== undefined ? undefined : formatPercent(coversDelta),
      icon: "COV",
      isMissing: false,
      suggestion: "Upload a covers report or reservations summary",
      metricKey: "covers",
      agentType: "manager",
      targetView: "sales",
      timeLabel,
      coreValue: `${Math.round(covers).toLocaleString()}`,
      moneyValue: `£${Math.round(covers * aov).toLocaleString()} sales`,
      varianceValue: `${coversVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(coversVariance)).toLocaleString()} vs target`,
      driver: "Driver: evening cover volume",
      visual: renderMiniTrend([80, 90, 95, 105, 100, 110], "#0ea5e9")
    },
    {
      title: "Gross Margin",
      value: `${grossMarginPct}%`,
      delta: undefined,
      icon: "GM",
      isMissing: false,
      suggestion: "Upload COGS and sales for margin tracking",
      metricKey: "gross_margin",
      agentType: "accountant",
      targetView: "sales",
      timeLabel,
      coreValue: `£${Math.round(grossMarginValue).toLocaleString()}`,
      moneyValue: `£${Math.round(grossMarginValue).toLocaleString()} margin`,
      varianceValue: `${grossMarginVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(grossMarginVariance)).toLocaleString()} vs target`,
      driver: "Driver: menu mix & pricing",
      visual: renderMiniTrend([68, 70, 69, 71, 72, 70], "#6366f1")
    },
    {
      title: "Prime Cost",
      value: `${primeCostPct}%`,
      delta: undefined,
      icon: "PC",
      isMissing: false,
      suggestion: "Upload food + labor to track prime cost",
      metricKey: "prime_cost",
      agentType: "accountant",
      targetView: "inventory",
      timeLabel,
      coreValue: `£${Math.round(primeCostValue).toLocaleString()}`,
      moneyValue: `£${Math.round(primeCostValue).toLocaleString()} prime cost`,
      varianceValue: `${primeCostVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(primeCostVariance)).toLocaleString()} vs target`,
      driver: foodVariance > laborVariance ? "Driver: food cost over target" : "Driver: labor over plan",
      visual: renderMiniTrend([58, 60, 61, 59, 62, 60], "#0f172a")
    },
    {
      title: "Sales Pace",
      value: `${Math.round(pace)}%`,
      delta: paceVal !== undefined ? undefined : formatPercent(paceDelta),
      icon: "PCE",
      isMissing: false,
      suggestion: "Upload sales vs target or daily pace report",
      metricKey: "sales_pace",
      agentType: "manager",
      targetView: "sales",
      timeLabel,
      coreValue: `£${Math.round(totalRevenue).toLocaleString()}`,
      moneyValue: `£${Math.round(totalRevenue).toLocaleString()} sales`,
      varianceValue: `${revenueVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(revenueVariance)).toLocaleString()} vs target`,
      driver: "Driver: lunch & dinner throughput",
      visual: renderMiniTrend([90, 95, 98, 102, 105, 110], "#0ea5e9")
    },
    {
      title: "Food Cost %",
      value: `${foodCostPct}%`,
      delta: foodCostVal !== undefined ? undefined : `${foodCostDelta > 0 ? "+" : ""}${foodCostDelta}pts`,
      icon: "FC",
      isMissing: false,
      suggestion: "Upload COGS or inventory usage report",
      metricKey: "food_cost_pct",
      agentType: "chef",
      targetView: "inventory",
      timeLabel,
      coreValue: `£${Math.round(foodSpend).toLocaleString()}`,
      moneyValue: `£${Math.round(foodSpend).toLocaleString()} spent`,
      varianceValue: `${foodVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(foodVariance)).toLocaleString()} vs target`,
      driver: "Driver: chicken + oil usage",
      visual: renderMiniTrend([32, 31, 30, 29, 31, 28], "#f97316")
    },
    {
      title: "Labor %",
      value: `${laborPct}%`,
      delta: laborCostVal !== undefined ? undefined : `${laborDelta > 0 ? "+" : ""}${laborDelta}pts`,
      icon: "LAB",
      isMissing: false,
      suggestion: "Upload payroll or rota cost summary",
      metricKey: "labor_cost_pct",
      agentType: "manager",
      targetView: "staffing",
      timeLabel,
      coreValue: `£${Math.round(laborSpend).toLocaleString()}`,
      moneyValue: `£${Math.round(laborSpend).toLocaleString()} spent`,
      varianceValue: `${laborVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(laborVariance)).toLocaleString()} vs target`,
      driver: "Driver: overstaffed Tue–Wed",
      visual: renderMiniTrend([28, 27, 29, 30, 28, 26], "#e11d48")
    },
    {
      title: "Waste %",
      value: `${wastePct}%`,
      delta: wasteVal !== undefined ? undefined : `${wasteDelta > 0 ? "+" : ""}${wasteDelta}pts`,
      icon: "WST",
      isMissing: false,
      suggestion: "Upload waste log or shrink report",
      metricKey: "waste_rate",
      agentType: "chef",
      targetView: "inventory",
      timeLabel,
      coreValue: `£${Math.round(wasteSpend).toLocaleString()}`,
      moneyValue: `£${Math.round(wasteSpend).toLocaleString()} lost`,
      varianceValue: `${wasteVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(wasteVariance)).toLocaleString()} vs target`,
      driver: "Driver: fresh produce waste",
      visual: renderMiniTrend([6, 5, 7, 4, 5, 4], "#f59e0b")
    },
    {
      title: "Inventory On Hand",
      value: `£${Math.round(inventoryValue).toLocaleString()}`,
      delta: inventoryVal !== undefined ? undefined : formatPercent(inventoryDelta),
      icon: "INV",
      isMissing: false,
      suggestion: "Upload inventory valuation or stock count",
      metricKey: "inventory_value",
      agentType: "accountant",
      targetView: "inventory",
      timeLabel,
      coreValue: `£${Math.round(inventoryValue).toLocaleString()}`,
      moneyValue: `£${Math.round(inventoryValue).toLocaleString()} on hand`,
      varianceValue: `${inventoryVariance >= 0 ? "+" : "−"}£${Math.abs(Math.round(inventoryVariance)).toLocaleString()} vs target`,
      driver: "Driver: slow-moving SKUs",
      visual: renderMiniTrend([12000, 12500, 12300, 13000, 12800, 13500], "#0f766e")
    },
  ];
}

// Widget Renderers to restore full interactivity and complexity
// Widget Renderers to restore full interactivity and complexity
function renderMenuIntelligence(onKpiExplain: any, menuItems: MenuItem[] = []) {
  // Logic for categorization
  // 1. Making Money: High total profit (margin * orders)
  const makingMoney = [...menuItems]
    .map(i => ({ ...i, totalProfit: i.margin * i.ordersPerWeek }))
    .sort((a, b) => b.totalProfit - a.totalProfit)
    .slice(0, 5);

  // 2. High Effort / Low Return: High prep/signals but low profit or orders
  const highEffortLowReturn = menuItems.filter(i =>
    (i.prepTime === "High" || i.operationalSignals.some(s => s.includes("labor") || s.includes("waste"))) &&
    (i.ordersPerWeek < 20 || (i.margin * i.ordersPerWeek) < 200)
  ).slice(0, 5);

  // 3. Easy Wins: Low prep, decent volume
  const easyWins = menuItems.filter(i =>
    (i.prepTime === "Low" || i.operationalSignals.some(s => s.includes("Quick"))) &&
    i.ordersPerWeek > 50
  ).sort((a, b) => b.ordersPerWeek - a.ordersPerWeek) // Sort by volume for "easy" wins
    .slice(0, 5);

  const SectionHeader = ({ title }: any) => React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 700,
      color: "#1e293b",
      marginBottom: 12,
      textTransform: "uppercase",
      letterSpacing: "0.05em"
    }
  }, title);

  const ItemRow = ({ item, val, sub, color }: any) => React.createElement("div", {
    onClick: () => onKpiExplain?.({ title: item.name, value: val, icon: "", agentType: "chef" }),
    style: {
      marginBottom: 10,
      cursor: "pointer",
      padding: "10px 12px",
      background: "linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(6, 133, 206, 0.04) 100%)",
      borderRadius: 12,
      border: "1px solid rgba(6, 133, 206, 0.12)",
      boxShadow: "0 2px 8px rgba(6, 133, 206, 0.06)",
      transition: "all 0.25s ease"
    },
    className: "hover:shadow-lg hover:shadow-indigo-600/10 hover:border-indigo-600/30 hover:-translate-y-0.5"
  }, [
    React.createElement("div", { style: { display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 800, color: "#1e293b" } }, [
      React.createElement("span", { style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '110px' } }, item.name),
      React.createElement("span", {
        style: {
          background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          fontWeight: 900
        }
      }, val)
    ]),
    React.createElement("div", { style: { fontSize: 10, color: "#64748b", marginTop: 3, fontWeight: 600, letterSpacing: "0.02em" } }, sub)
  ]);

  return React.createElement(Card, { title: "Margin & Volume Drivers", style: { height: 480 } }, [
    React.createElement("div", { key: "grid", style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, height: "100%", overflowY: "auto", paddingRight: 4 } }, [
      // Col 1: Making Money
      React.createElement("div", { key: "money" }, [
        React.createElement(SectionHeader, { title: "Top Profit Drivers" }),
        ...makingMoney.map((item, i) =>
          React.createElement(ItemRow, {
            key: i,
            item: item,
            val: `£${Math.round(item.totalProfit).toLocaleString()}`,
            sub: "Total Profit this week",
            color: "#059669"
          })
        )
      ]),

      // Col 2: High Effort / Low Return
      React.createElement("div", { key: "effort" }, [
        React.createElement(SectionHeader, { title: "Margin Risks" }),
        ...highEffortLowReturn.map((item, i) =>
          React.createElement(ItemRow, {
            key: i,
            item: item,
            val: item.name === "Duck Confit" ? "£112 Profit" : item.ordersPerWeek + " orders", // Custom formatting based on user example
            sub: item.operationalSignals[0] || "High Prep Time",
            color: "#DC2626"
          })
        )
      ]),

      // Col 3: Easy Wins
      React.createElement("div", { key: "wins" }, [
        React.createElement(SectionHeader, { title: "Quick Wins" }),
        ...easyWins.map((item, i) =>
          React.createElement(ItemRow, {
            key: i,
            item: item,
            val: `${item.ordersPerWeek} orders`,
            sub: "Quick Prep / High Vol",
            color: "#0685ce"
          })
        )
      ])
    ])]);
}

function renderCostPressure(revenueData: any, costData: any, costLabels: any, onKpiExplain: any) {
  const count = Math.max(revenueData?.length || 0, costData?.length || 0, costLabels?.length || 0, 2);
  const rev = Array.from({ length: count }, (_, i) => Number(revenueData?.[i] ?? revenueData?.[revenueData?.length - 1] ?? 0));
  const cost = Array.from({ length: count }, (_, i) => Number(costData?.[i] ?? costData?.[costData?.length - 1] ?? 0));
  const labels = Array.from({ length: count }, (_, i) => costLabels?.[i] ?? `Week ${i + 1}`);
  const startX = 60;
  const endX = 545;
  const stepX = count > 1 ? (endX - startX) / (count - 1) : 0;

  return React.createElement(Card, { title: "Revenue vs Cost (4 Weeks)", style: { height: 350, width: "100%" } },
    React.createElement("div", { style: { padding: "12px 0 6px 0", height: 230, width: "100%" } }, [
      React.createElement("svg", { key: "svg", width: "100%", height: "100%", viewBox: "0 0 560 240", preserveAspectRatio: "none", style: { overflow: "visible" } }, [
        React.createElement("defs", { key: "d" }, [
          React.createElement("linearGradient", { id: "rvG", x1: "0%", y1: "0%", x2: "0%", y2: "100%" }, [
            React.createElement("stop", { offset: "0%", stopColor: "rgb(16,185,129)", stopOpacity: 0.2 }),
            React.createElement("stop", { offset: "100%", stopColor: "rgb(16,185,129)", stopOpacity: 0 }),
          ]),
          React.createElement("linearGradient", { id: "csG", x1: "0%", y1: "0%", x2: "0%", y2: "100%" }, [
            React.createElement("stop", { offset: "0%", stopColor: "rgb(239,68,68)", stopOpacity: 0.2 }),
            React.createElement("stop", { offset: "100%", stopColor: "rgb(239,68,68)", stopOpacity: 0 }),
          ]),
        ]),
        ...Array.from({ length: 5 }).map((_, i) => {
          const y = 40 + (i * 40);
          const val = 4500 - (i * 500);
          return React.createElement("g", { key: `grid-${i}` }, [
            React.createElement("line", { key: "l", x1: 60, y1: y, x2: 545, y2: y, stroke: "rgba(0,0,0,.04)", strokeWidth: 1 }),
            React.createElement("text", { key: "t", x: 54, y: y + 4, textAnchor: "end", fontSize: 10, fill: "rgba(0,0,0,.4)", fontWeight: 700 }, `£${val}`)
          ]);
        }),
        // Revenue Area
        React.createElement("path", { key: "ra", d: `M ${startX},${200 - ((rev[0] - 3000) / 10)} ${rev.map((v: any, i: any) => `L ${startX + i * stepX},${200 - ((v - 3000) / 10)}`).join(" ")} L ${endX},200 L ${startX},200 Z`, fill: "url(#rvG)", style: { transition: 'all 0.3s' } }),
        // Cost Area
        React.createElement("path", { key: "ca", d: `M ${startX},${200 - ((cost[0] - 3000) / 10)} ${cost.map((v: any, i: any) => `L ${startX + i * stepX},${200 - ((v - 3000) / 10)}`).join(" ")} L ${endX},200 L ${startX},200 Z`, fill: "url(#csG)", style: { transition: 'all 0.3s' } }),
        // Polylines
        React.createElement("polyline", { key: "rl", points: rev.map((v: any, i: any) => `${startX + i * stepX},${200 - ((v - 3000) / 10)}`).join(" "), fill: "none", stroke: "rgb(16,185,129)", strokeWidth: 4, strokeLinecap: "round" }),
        React.createElement("polyline", { key: "cl", points: cost.map((v: any, i: any) => `${startX + i * stepX},${200 - ((v - 3000) / 10)}`).join(" "), fill: "none", stroke: "rgb(239,68,68)", strokeWidth: 4, strokeLinecap: "round" }),

        // Revenue Points
        ...rev.map((v: any, i: any) => React.createElement("circle", {
          key: `rp-${i}`,
          cx: startX + i * stepX,
          cy: 200 - ((v - 3000) / 10),
          r: 6,
          fill: "rgb(16,185,129)",
          stroke: "#fff",
          strokeWidth: 2,
          style: { cursor: "pointer", transition: "all 0.2s" },
          onClick: () => onKpiExplain?.({ title: `Profitability Deep-Dive: ${labels[i]} Forecast`, value: `£${v} Revenue`, icon: "", agentType: "accountant" }),
          onMouseEnter: (e: any) => { e.target.setAttribute("r", "9"); e.target.style.filter = "drop-shadow(0 0 8px rgba(16,185,129,0.5))"; },
          onMouseLeave: (e: any) => { e.target.setAttribute("r", "6"); e.target.style.filter = "none"; }
        })),
        // Cost Points
        ...cost.map((v: any, i: any) => React.createElement("circle", {
          key: `cp-${i}`,
          cx: startX + i * stepX,
          cy: 200 - ((v - 3000) / 10),
          r: 6,
          fill: "rgb(239,68,68)",
          stroke: "#fff",
          strokeWidth: 2,
          style: { cursor: "pointer", transition: "all 0.2s" },
          onClick: () => onKpiExplain?.({ title: `Margin Risk Analysis: ${labels[i]} Expenses`, value: `£${v} Costs`, icon: "", agentType: "accountant" }),
          onMouseEnter: (e: any) => { e.target.setAttribute("r", "9"); e.target.style.filter = "drop-shadow(0 0 8px rgba(239,68,68,0.5))"; },
          onMouseLeave: (e: any) => { e.target.setAttribute("r", "6"); e.target.style.filter = "none"; }
        })),
        // X-Axis Labels
        ...labels.map((l: any, i: any) => React.createElement("text", { key: i, x: startX + i * stepX, y: 225, textAnchor: "middle", fontSize: 10, fill: "rgba(0,0,0,.4)", fontWeight: 700 }, l))
      ]),
      React.createElement("div", { key: "l", style: { display: "flex", gap: 16, marginTop: 12, paddingLeft: 40 } }, [
        React.createElement("div", { key: "r", style: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "rgb(16,185,129)" } }, [
          React.createElement("div", { style: { width: 10, height: 10, borderRadius: 2, background: "rgb(16,185,129)" } }), "Forecast"
        ]),
        React.createElement("div", { key: "c", style: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "rgb(239,68,68)" } }, [
          React.createElement("div", { style: { width: 10, height: 10, borderRadius: 2, background: "rgb(239,68,68)" } }), "Actual"
        ])
      ])
    ])
  );
}

function renderTrafficHeatmap(heatmapDays: any, heatmapHours: any, heatmapData: any, onKpiExplain: any) {
  return React.createElement(Card, { title: "Demand by Day & Hour", style: { height: 350, padding: 14, width: "100%" } },
    React.createElement("div", { style: { padding: "4px 0", height: "100%", display: "flex", flexDirection: "column" } }, [
      React.createElement("div", { key: "legend", style: { display: "flex", gap: 8, marginBottom: 8, fontSize: 9, fontWeight: 800, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px", flexWrap: "wrap" } }, [
        "Intensity:",
        React.createElement("div", { style: { display: "flex", gap: 4, alignItems: "center" } }, [
          React.createElement("div", { style: { width: 12, height: 12, borderRadius: 3, background: "rgba(59,130,246,.15)" } }), "Low"
        ]),
        React.createElement("div", { style: { display: "flex", gap: 4, alignItems: "center" } }, [
          React.createElement("div", { style: { width: 12, height: 12, borderRadius: 3, background: "rgba(16,185,129,.3)" } }), "Mid"
        ]),
        React.createElement("div", { style: { display: "flex", gap: 4, alignItems: "center" } }, [
          React.createElement("div", { style: { width: 12, height: 12, borderRadius: 3, background: "rgba(245,158,11,.4)" } }), "High"
        ]),
        React.createElement("div", { style: { display: "flex", gap: 4, alignItems: "center" } }, [
          React.createElement("div", { style: { width: 12, height: 12, borderRadius: 3, background: "rgba(220, 38, 38, .7)" } }), "Peak"
        ]),
      ]),
      React.createElement("div", { key: "grid", style: { display: "grid", gap: 3, flex: 1 } }, [
        React.createElement("div", { key: "h", style: { display: "grid", gridTemplateColumns: "38px repeat(6, 1fr)", gap: 3 } }, [
          React.createElement("div", {}),
          ...heatmapHours.map((h: any, i: any) => React.createElement("div", { key: i, style: { fontSize: 9, fontWeight: 800, textAlign: "center", opacity: 0.5 } }, h))
        ]),
        ...heatmapDays.map((day: any, dayIdx: any) =>
          React.createElement("div", { key: day, style: { display: "grid", gridTemplateColumns: "38px repeat(6, 1fr)", gap: 3, alignItems: "center" } }, [
            React.createElement("div", { style: { fontSize: 10, fontWeight: 700, color: "rgba(0,0,0,0.6)" } }, day),
            ...heatmapData[dayIdx].map((v: any, hIdx: any) => {
              // Multi-tier vibrancy mapping
              let bg = "rgba(59,130,246,0.1)"; // Default Blue
              let color = "#1e40af";

              if (v > 80) {
                bg = "rgba(220, 38, 38, 0.8)"; color = "#fff"; // Peak Red
              } else if (v > 65) {
                bg = "rgba(239, 68, 68, 0.4)"; color = "#991b1b"; // High Coral
              } else if (v > 45) {
                bg = "rgba(245, 158, 11, 0.35)"; color = "#92400e"; // Medium Amber
              } else if (v > 25) {
                bg = "rgba(16, 185, 129, 0.25)"; color = "#065f46"; // Low-Mid Green
              }

              return React.createElement("div", {
                key: hIdx,
                onClick: () => onKpiExplain?.({ title: `${day} Traffic Analysis: ${heatmapHours[hIdx]}`, value: `${v} covers`, icon: "", agentType: "manager" }),
                style: {
                  background: bg,
                  color: color,
                  borderRadius: 8,
                  padding: "7px 0",
                  textAlign: "center",
                  fontSize: 10,
                  fontWeight: 800,
                  cursor: "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: v > 80 ? "none" : "1px solid rgba(0,0,0,0.02)"
                },
                onMouseEnter: (e: any) => {
                  e.target.style.transform = "scale(1.1) translateY(-2px)";
                  e.target.style.boxShadow = "0 10px 15px -3px rgba(0,0,0,0.1)";
                  e.target.style.zIndex = "10";
                },
                onMouseLeave: (e: any) => {
                  e.target.style.transform = "scale(1) translateY(0)";
                  e.target.style.boxShadow = "none";
                  e.target.style.zIndex = "1";
                }
              }, v.toString());
            })
          ])
        )
      ]),
      React.createElement("div", { style: { marginTop: 12, fontSize: 10, color: "rgba(0,0,0,.4)", fontStyle: "italic" } }, "Click any slot for optimization tips")
    ])
  );
}

function formatPercent(n: number) {
  return `${n > 0 ? "+" : ""}${n}%`;
}

function Card({
  title,
  right,
  children,
  style = {} as React.CSSProperties,
  dragHandleProps,
  onClick,
}: {
  title?: string;
  right?: React.ReactNode;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  dragHandleProps?: any;
  onClick?: () => void;
}) {
  const safeStyle = style || {};
  return React.createElement(
    "div",
    {
      style: {
        borderRadius: 24,
        border: "1px solid rgba(6, 133, 206, 0.2)",
        background: "linear-gradient(145deg, #ffffff 0%, rgba(6, 133, 206, 0.06) 100%)",
        boxShadow: "0 20px 60px rgba(6, 133, 206, 0.1), 0 8px 24px rgba(6, 133, 206, 0.06), inset 0 1px 0 rgba(255,255,255,0.95)",
        backdropFilter: "blur(20px)",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: onClick ? "pointer" : "default",
        ...safeStyle,
        padding: safeStyle.padding !== undefined ? safeStyle.padding : 20,
      },
      className: "ring-1 ring-indigo-600/10 hover:ring-indigo-600/30 hover:shadow-2xl hover:shadow-indigo-600/15 hover:-translate-y-1",
      onClick,
    },
    [
      // Drag Handle
      dragHandleProps && React.createElement("div", {
        ...dragHandleProps,
        onClick: (e: any) => {
          e.stopPropagation();
          if (dragHandleProps?.onClick) dragHandleProps.onClick(e);
        },
        style: {
          position: "absolute",
          top: 12,
          right: 12,
          padding: 8,
          cursor: "grab",
          color: "rgba(0,0,0,0.1)",
          zIndex: 10
        },
        className: "hover:text-indigo-600 transition-colors"
      }, React.createElement(GripVertical, { size: 18 })),

      (title || right) &&
      React.createElement(
        "div",
        { key: "header", style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 } },
        [
          title
            ? React.createElement("div", {
              key: "title",
              style: {
                fontSize: 14,
                fontWeight: 700,
                color: "#64748b",
                lineHeight: 1.3,
                letterSpacing: "0.02em",
                textTransform: "uppercase"
              }
            }, title)
            : React.createElement("div", { key: "empty" }),
          right && React.createElement("div", { key: "right" }, right),
        ]
      ),
      React.createElement(
        "div",
        {
          key: "content",
          style: {
            marginTop: title || right ? 12 : 0,
            height: "100%",
            display: safeStyle.display || "block",
            flexDirection: safeStyle.flexDirection || "initial",
            gap: safeStyle.gap || "initial",
            flex: 1,
          }
        },
        children
      ),
    ]
  );
}

function KpiCard({ kpi, onExplain, onFillManual, dragHandleProps: _dragHandleProps, onNavigate, varianceOverride }: { kpi: Kpi; onExplain?: () => void; onFillManual?: () => void; dragHandleProps?: any; onNavigate?: (view: any) => void; varianceOverride?: string }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const coreValue = kpi.coreValue ?? kpi.value;
  const moneyValue = kpi.moneyValue ?? kpi.value;
  const cleanedMoneyValue = typeof moneyValue === "string"
    ? moneyValue.replace(/\s*(on hand|margin|prime cost)\b/i, "").trim()
    : moneyValue;
  const varianceLabel = varianceOverride ?? kpi.varianceValue ?? kpi.delta;
  const trimmedVariance = (varianceLabel || "").trim();
  const varianceSign = trimmedVariance.startsWith("+") ? 1 : (trimmedVariance.startsWith("−") || trimmedVariance.startsWith("-")) ? -1 : 0;
  const varianceClass = !trimmedVariance ? "text-slate-400"
    : varianceSign > 0 ? "text-emerald-700"
      : varianceSign < 0 ? "text-rose-700"
        : "text-slate-400";
  const agentTag = kpi.agentType === "chef" ? "Inventory"
    : kpi.agentType === "manager" ? "Operations"
      : kpi.agentType === "accountant" ? "Finance"
        : kpi.agentType === "marketing" ? "Marketing"
          : null;
  const handleTileClick = onNavigate && kpi.targetView ? () => onNavigate(kpi.targetView) : undefined;
  const rawPercentCandidate = (kpi.value && /%|pts/i.test(kpi.value)) ? kpi.value : "";
  const rawDeltaCandidate = (!rawPercentCandidate && kpi.delta && /%|pts/i.test(kpi.delta)) ? kpi.delta : "";
  const percentLabel = rawPercentCandidate || rawDeltaCandidate;
  const showTimeLabel = false;
  const kpiHelpText: Record<string, string> = {
    total_sales: "Total sales vs target for the selected period.",
    gross_margin: "Gross margin % after food cost.",
    prime_cost: "Food + Labor",
    food_cost_pct: "Food cost as % of revenue.",
    labor_cost_pct: "Labor cost as % of revenue.",
    waste_rate: "Waste as % of revenue.",
    inventory_value: "Value of stock on hand vs target.",
    covers: "Total guests served (covers).",
  };
  const infoText = (kpi.metricKey && kpiHelpText[kpi.metricKey]) || "KPI definition and context.";

  return React.createElement(
    Card,
    {
      onClick: handleTileClick,
      style: {
        padding: 22,
        display: "flex",
        flexDirection: "column",
        minHeight: 180,
        height: "auto",
        position: "relative",
        overflow: "visible",
        background: kpi.isMissing
          ? "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)",
        border: kpi.isMissing ? "2px dashed rgba(6,133,206,.25)" : "none",
      },
    },
    [
      React.createElement("div", { key: "top", style: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 } }, [
        React.createElement("div", { key: "meta", style: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 } }, [
          React.createElement("div", {
            key: "icon",
            style: {
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "rgba(6,133,206,.08)",
              display: "grid",
              placeItems: "center",
              fontSize: 10,
              fontWeight: 900,
              color: "#0f172a",
              letterSpacing: "0.12em",
              textTransform: "uppercase"
            }
          }, kpi.icon || "•"),
          React.createElement("div", { key: "title", style: { minWidth: 0 } }, [
            React.createElement("div", { style: { fontSize: 10, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.12em" } }, showTimeLabel ? (kpi.timeLabel || "") : ""),
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, minWidth: 0 } }, [
              React.createElement("span", { style: { fontSize: 13, fontWeight: 900, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 } }, kpi.title),
              React.createElement("span", { className: "relative inline-flex items-center" }, [
                React.createElement("span", {
                  "aria-label": "Info",
                  onMouseEnter: () => setInfoOpen(true),
                  onMouseLeave: () => setInfoOpen(false),
                  onFocus: () => setInfoOpen(true),
                  onBlur: () => setInfoOpen(false),
                  onClick: (e: any) => e.stopPropagation(),
                  onPointerDown: (e: any) => e.stopPropagation(),
                  onKeyDown: (e: any) => { if (e.key === "Escape") setInfoOpen(false); },
                  tabIndex: 0,
                  style: {
                    width: 16,
                    height: 16,
                    borderRadius: 999,
                    border: "1px solid rgba(15,23,42,0.15)",
                    color: "#64748b",
                    fontSize: 10,
                    fontWeight: 900,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "help",
                    flexShrink: 0,
                    userSelect: "none"
                  }
                }, "i"),
                infoOpen && React.createElement("div", {
                  className: "absolute z-50 -top-2 left-1/2 -translate-x-1/2 -translate-y-full px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] font-semibold shadow-2xl border border-white/10 whitespace-normal leading-snug",
                  style: { pointerEvents: "none", width: "max-content", maxWidth: 600, textAlign: "center" }
                }, infoText)
              ])
            ])
          ])
        ]),
        agentTag && React.createElement("div", {
          key: "agent",
          style: {
            fontSize: 9,
            fontWeight: 900,
            color: "#0f172a",
            background: "rgba(15,23,42,0.06)",
            padding: "4px 8px",
            borderRadius: 999,
            textTransform: "uppercase",
            letterSpacing: "0.08em"
          }
        }, agentTag)
      ]),

      React.createElement("div", { key: "value", style: { marginTop: 14, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", textAlign: "center" } }, [
        React.createElement("div", {
          style: {
            fontSize: kpi.isMissing ? 20 : 28,
            fontWeight: 900,
            letterSpacing: "-0.02em",
            color: kpi.isMissing ? "#cbd5e1" : "#0f172a",
            lineHeight: 1.1
          }
        }, cleanedMoneyValue || coreValue || "—"),
        (!kpi.isMissing && (trimmedVariance || percentLabel)) && React.createElement("div", { className: "text-xs font-semibold text-slate-600 flex flex-wrap items-center justify-center gap-2" }, [
          trimmedVariance && React.createElement("span", { className: `${varianceClass} font-black` }, trimmedVariance.replace(/\s*vs target/gi, "")),
          trimmedVariance && percentLabel && React.createElement("span", { className: "text-slate-300" }, "|"),
          percentLabel && React.createElement("span", { className: "text-slate-500 font-bold" }, percentLabel)
        ]),
        kpi.driver && React.createElement("div", {
          key: "driver",
          className: "text-[11px] font-semibold text-slate-500"
        }, kpi.driver.startsWith("Driver:") ? kpi.driver : `Driver: ${kpi.driver}`)
      ]),

      // Custom Visual (removed for cleaner KPI layout)

      // Suggestion for missing
      kpi.isMissing && kpi.suggestion && React.createElement(
        "div",
        { key: "suggestion-box", style: { display: "flex", flexDirection: "column", gap: 8, marginTop: 10 } },
        [
          React.createElement(
            "div",
            { key: "msg", style: { fontSize: 10, color: "#0685ce", fontWeight: 600, padding: "8px 12px", background: "rgba(6,133,206,.08)", borderRadius: 8, lineHeight: 1.3 } },
            kpi.suggestion
          ),
          onFillManual && React.createElement(
            "button",
            {
              key: "fill-btn",
              onClick: (e: any) => { e.stopPropagation(); onFillManual?.(); },
              onPointerDown: (e: any) => { e.stopPropagation(); },
              style: {
                fontSize: 10,
                fontWeight: 800,
                color: "#ffffff",
                background: "#0685ce",
                border: "none",
                padding: "6px 14px",
                borderRadius: 6,
                cursor: "pointer",
                width: "fit-content"
              },
            },
            "Fix Now"
          )
        ]
      ),

      // The circular '?' button at bottom right
      !kpi.isMissing && onExplain &&
      React.createElement(
        "button",
        {
          key: "explain-bubble",
          onClick: (e: any) => { e.stopPropagation(); onExplain?.(); },
          onPointerDown: (e: any) => { e.stopPropagation(); },
          style: {
            position: "absolute",
            bottom: 16,
            right: 16,
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "white",
            border: "1px solid rgba(6,133,206,.2)",
            color: "#0685ce",
            fontSize: 16,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(6,133,206,.1)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          },
          onMouseEnter: (e: any) => {
            e.currentTarget.style.background = "#0685ce";
            e.currentTarget.style.color = "white";
            e.currentTarget.style.transform = "scale(1.1) translateY(-1px)";
          },
          onMouseLeave: (e: any) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.color = "#0685ce";
            e.currentTarget.style.transform = "scale(1) translateY(0)";
          },
          title: "Explain this metric"
        },
        "?"
      ),
    ]
  );
}

function SortableKpiCard({ id, kpi, onExplain, onFillManual, onNavigate, varianceOverride }: { id: string, kpi: Kpi; onExplain?: () => void; onFillManual?: () => void; onNavigate?: (view: any) => void; varianceOverride?: string }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : "auto",
    position: 'relative' as any,
  };

  return React.createElement("div", {
    ref: setNodeRef,
    style: { ...style, cursor: isDragging ? "grabbing" : "grab", touchAction: "none" },
    className: isDragging ? "opacity-30" : "opacity-100",
    ...attributes,
    ...listeners
  }, [
    React.createElement(KpiCard, {
      kpi,
      onExplain,
      onFillManual,
      onNavigate,
      varianceOverride
    })
  ]);
}

function DashboardView({ onKpiExplain, onFillManual, uploadedFiles, onNavigate, venueName, kpiLayout, onReorderKpis }: { onKpiExplain?: (kpi: Kpi) => void; onFillManual?: (kpi: Kpi) => void; uploadedFiles: any[]; onNavigate: (view: any) => void; venueName?: string; kpiLayout: string[]; onReorderKpis: (newOrder: string[]) => void }) {
  const [homeRange, setHomeRange] = useState<"day" | "week" | "month">("week");
  const dateFilter = useMemo<DateFilter>(() => ({ type: homeRange }), [homeRange]);
  const [goalsByRange, setGoalsByRange] = useState<Record<string, { id: string; label: string; targetPct?: string; note?: string } | null>>({});
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [goalDraft, setGoalDraft] = useState<{ id: string; targetPct: string; note: string }>({
    id: "increase_revenue",
    targetPct: "",
    note: ""
  });
  const [goalProgressExpanded, setGoalProgressExpanded] = useState(true);
  const [revenueHoverIndex, setRevenueHoverIndex] = useState<number | null>(null);
  const [laborHoverIndex, setLaborHoverIndex] = useState<number | null>(null);
  const [revenueExpanded, setRevenueExpanded] = useState(false);
  const [laborExpanded, setLaborExpanded] = useState(false);
  const [performanceInfoOpen, setPerformanceInfoOpen] = useState(false);
  const [pulseInfoOpen, setPulseInfoOpen] = useState(false);
  const [homeModal, setHomeModal] = useState<null | "risks" | "actions" | "profit" | "performance">(null);
  const [goalCardExpanded, setGoalCardExpanded] = useState(false);
  const [profitInfoOpen, setProfitInfoOpen] = useState(false);
  const [riskInfoOpen, setRiskInfoOpen] = useState(false);
  const [actionsInfoOpen, setActionsInfoOpen] = useState(false);
  const kpis = getDashboardKpis(dateFilter, uploadedFiles);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEndKpis = (event: any) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = kpiLayout.indexOf(active.id);
      const newIndex = kpiLayout.indexOf(over.id);
      const newOrder = arrayMove(kpiLayout, oldIndex, newIndex);
      onReorderKpis(newOrder);
    }
  };

  const goalOptions = [
    { id: "increase_revenue", label: "Increase revenue", helper: "Focus on revenue pace, covers, and margin" },
    { id: "increase_covers", label: "Increase covers", helper: "Drive traffic and throughput" },
    { id: "reduce_food_cost", label: "Reduce food cost", helper: "Track food spend, waste, and prime cost" },
    { id: "improve_labor", label: "Improve labor efficiency", helper: "Balance labor spend vs demand" },
    { id: "reduce_waste", label: "Reduce waste", helper: "Shrink, spoilage, and inventory control" },
  ];

  const goalMetricMap: Record<string, string[]> = {
    increase_revenue: ["total_sales", "gross_margin", "prime_cost", "covers"],
    increase_covers: ["covers", "total_sales", "gross_margin", "prime_cost"],
    reduce_food_cost: ["food_cost_pct", "waste_rate", "prime_cost", "gross_margin"],
    improve_labor: ["labor_cost_pct", "prime_cost", "total_sales", "gross_margin"],
    reduce_waste: ["waste_rate", "food_cost_pct", "inventory_value", "prime_cost"],
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem("findsy_goals_by_range");
      if (saved) {
        setGoalsByRange(JSON.parse(saved));
      }
    } catch (e) { }
  }, []);

  const applyGoal = () => {
    const selected = goalOptions.find(g => g.id === goalDraft.id);
    if (!selected) return;
    const pct = goalDraft.targetPct?.trim();
    const label = pct ? `${selected.label} by ${pct}%` : selected.label;
    const nextGoal = { id: selected.id, label, targetPct: pct, note: goalDraft.note.trim() };
    setGoalsByRange((prev) => {
      const updated = { ...prev, [homeRange]: nextGoal };
      if (typeof window !== "undefined") {
        localStorage.setItem("findsy_goals_by_range", JSON.stringify(updated));
      }
      return updated;
    });
    setGoalModalOpen(false);
  };

  const openGoalModal = () => {
    const activeGoal = goalsByRange[homeRange];
    if (activeGoal) {
      setGoalDraft({
        id: activeGoal.id,
        targetPct: activeGoal.targetPct || "",
        note: activeGoal.note || ""
      });
    }
    setGoalModalOpen(true);
  };

  const clearGoal = () => {
    setGoalsByRange((prev) => {
      const updated = { ...prev, [homeRange]: null };
      if (typeof window !== "undefined") {
        localStorage.setItem("findsy_goals_by_range", JSON.stringify(updated));
      }
      return updated;
    });
    setGoalCardExpanded(false);
  };

  const sortedKpis = [...kpis].sort((a, b) => {
    const idxA = kpiLayout.indexOf(a.metricKey || "");
    const idxB = kpiLayout.indexOf(b.metricKey || "");
    return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
  });
  const activeGoal = goalsByRange[homeRange];
  const visibleKeys = activeGoal && goalMetricMap[activeGoal.id] ? goalMetricMap[activeGoal.id] : kpiLayout;
  const filteredKpis = sortedKpis.filter(k => visibleKeys.includes(k.metricKey || ""));
  const displayedKpis = filteredKpis.length > 0 ? filteredKpis : sortedKpis;

  const parseDeltaValue = (d?: string) => {
    if (!d) return null;
    const m = d.match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  };
  const parseSignedMoney = (val?: string) => {
    if (!val) return null;
    const m = String(val).match(/([+\-−])?\s*£?\s*([\d,.]+)/);
    if (!m) return null;
    const sign = m[1] === "-" || m[1] === "−" ? -1 : 1;
    return sign * parseFloat(m[2].replace(/,/g, ""));
  };
  const parseMoney = (val?: string) => {
    if (!val) return null;
    const m = String(val).match(/£?\s*([\d,.]+)/);
    return m ? parseFloat(m[1].replace(/,/g, "")) : null;
  };

  const goalPrimaryKeyMap: Record<string, string> = {
    increase_revenue: "total_sales",
    increase_covers: "covers",
    reduce_food_cost: "food_cost_pct",
    improve_labor: "labor_cost_pct",
    reduce_waste: "waste_rate",
  };
  const goalPrimaryKey = activeGoal ? goalPrimaryKeyMap[activeGoal.id] : null;
  const goalKpi = goalPrimaryKey ? kpis.find(k => k.metricKey === goalPrimaryKey) : null;
  const goalDeltaValue = parseSignedMoney(goalKpi?.varianceValue) ?? parseSignedMoney(goalKpi?.delta) ?? parseDeltaValue(goalKpi?.delta);
  const targetPct = activeGoal?.targetPct ? parseFloat(activeGoal.targetPct) : null;
  const isReductionGoal = activeGoal ? ["reduce_food_cost", "reduce_waste", "improve_labor"].includes(activeGoal.id) : false;
  const totalRevenueForGoal = parseMoney((kpis.find(k => k.metricKey === "total_sales")?.moneyValue) || "");
  const targetValue = (targetPct && totalRevenueForGoal)
    ? (totalRevenueForGoal * (targetPct / 100))
    : null;
  const goalTargetAmount = (targetPct !== null && goalKpi)
    ? (() => {
      const base = parseMoney(goalKpi.moneyValue || goalKpi.coreValue || goalKpi.value || "");
      if (base === null) return null;
      return base * (targetPct / 100);
    })()
    : null;
  const goalVarianceOverride = goalTargetAmount !== null
    ? `${isReductionGoal ? "−" : "+"}£${Math.abs(Math.round(goalTargetAmount)).toLocaleString()} vs target`
    : null;
  const currentProgress = goalDeltaValue !== null
    ? (isReductionGoal ? -goalDeltaValue : goalDeltaValue)
    : null;
  const progressRatio = (targetValue && currentProgress !== null)
    ? Math.max(0, Math.min(Math.abs(currentProgress) / targetValue, 1))
    : null;
  const goalHeadline = activeGoal ? activeGoal.label : "";
  const showGoalDetails = !!activeGoal && goalCardExpanded;
  const progressTone = progressRatio === null ? "neutral"
    : progressRatio >= 1 ? "good"
      : progressRatio >= 0.7 ? "ok"
        : "warn";
  const progressBadgeClass = progressTone === "good"
    ? "text-emerald-700 bg-emerald-50 border-emerald-100"
    : progressTone === "ok"
      ? "text-indigo-600 bg-indigo-50 border-indigo-100"
      : progressTone === "warn"
        ? "text-amber-700 bg-amber-50 border-amber-100"
        : "text-gray-400 bg-gray-50 border-gray-100";
  const progressBarClass = progressTone === "good"
    ? "from-emerald-500 to-emerald-700"
    : progressTone === "ok"
      ? "from-indigo-500 to-indigo-700"
      : progressTone === "warn"
        ? "from-amber-500 to-amber-700"
        : "from-gray-300 to-gray-400";

  const revenueKpi = kpis.find(k => k.metricKey === "total_sales");
  const grossMarginKpi = kpis.find(k => k.metricKey === "gross_margin");
  const primeCostKpi = kpis.find(k => k.metricKey === "prime_cost");
  const foodCostKpi = kpis.find(k => k.metricKey === "food_cost_pct");
  const laborKpi = kpis.find(k => k.metricKey === "labor_cost_pct");
  const wasteKpi = kpis.find(k => k.metricKey === "waste_rate");
  const dataFreshnessDays = (() => {
    const ts = uploadedFiles && uploadedFiles.length > 0
      ? Math.max(...uploadedFiles.map((f: any) => new Date(f.uploadedAt || 0).getTime()))
      : null;
    if (!ts) return null;
    const diffMs = Date.now() - ts;
    return Math.max(0, Math.floor(diffMs / 86400000));
  })();
  const lastUpdatedAt = (() => {
    const ts = uploadedFiles && uploadedFiles.length > 0
      ? Math.max(...uploadedFiles.map((f: any) => new Date(f.uploadedAt || 0).getTime()))
      : null;
    return ts ? new Date(ts) : new Date();
  })();
  const lastUpdatedLabel = lastUpdatedAt.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
  const homeRangeLabel = homeRange === "day" ? "Today"
    : homeRange === "week" ? "Week to date"
      : "Month to date";
  const freshnessLabel = dataFreshnessDays === null ? "No data" : dataFreshnessDays === 0 ? "Updated today" : `${dataFreshnessDays}d old`;

  const fmtPounds = (n: number | null) => n === null ? "—" : `£${Math.round(n).toLocaleString()}`;
  const kpiPounds = (k?: Kpi | null) => parseMoney(k?.coreValue || k?.moneyValue || k?.value || "") ?? null;
  const kpiDelta = (k?: Kpi | null) => parseSignedMoney(k?.varianceValue) ?? null;
  const trimDriver = (d?: string) => (d || "").replace(/^Driver:\s*/i, "").trim();

  const revenuePounds = kpiPounds(revenueKpi);
  const grossProfitPounds = kpiPounds(grossMarginKpi);
  const primeCostPounds = kpiPounds(primeCostKpi);
  const foodSpendPounds = kpiPounds(foodCostKpi);
  const laborSpendPounds = kpiPounds(laborKpi);
  const wasteSpendPounds = kpiPounds(wasteKpi);

  const revenueVar = kpiDelta(revenueKpi);
  const grossProfitVar = kpiDelta(grossMarginKpi);
  const primeCostVar = kpiDelta(primeCostKpi);
  const foodSpendVar = kpiDelta(foodCostKpi);
  const laborSpendVar = kpiDelta(laborKpi);
  const wasteSpendVar = kpiDelta(wasteKpi);

  const deltaMeta = (v: number | null, kind: "profit" | "cost") => {
    if (v === null) return { label: "—", className: "text-slate-400" };
    const abs = Math.abs(Math.round(v));
    if (abs === 0) return { label: "On target", className: "text-slate-500" };
    if (kind === "cost") {
      return v > 0
        ? { label: `Overspend £${abs.toLocaleString()}`, className: "text-rose-700" }
        : { label: `Saving £${abs.toLocaleString()}`, className: "text-emerald-700" };
    }
    return v > 0
      ? { label: `Ahead £${abs.toLocaleString()}`, className: "text-emerald-700" }
      : { label: `Behind £${abs.toLocaleString()}`, className: "text-rose-700" };
  };

  const revenueDelta = deltaMeta(revenueVar, "profit");
  const grossProfitDelta = deltaMeta(grossProfitVar, "profit");
  const primeCostDelta = deltaMeta(primeCostVar, "cost");
  const foodDelta = deltaMeta(foodSpendVar, "cost");
  const laborDelta = deltaMeta(laborSpendVar, "cost");
  const wasteDelta = deltaMeta(wasteSpendVar, "cost");

  const contributionPounds = (typeof revenuePounds === "number" && typeof foodSpendPounds === "number" && typeof laborSpendPounds === "number" && typeof wasteSpendPounds === "number")
    ? (revenuePounds - foodSpendPounds - laborSpendPounds - wasteSpendPounds)
    : null;
  const contributionVar = (typeof revenueVar === "number" && typeof foodSpendVar === "number" && typeof laborSpendVar === "number" && typeof wasteSpendVar === "number")
    ? (revenueVar - foodSpendVar - laborSpendVar - wasteSpendVar)
    : null;
  const contributionDelta = deltaMeta(contributionVar, "profit");

  const profitTone = contributionVar === null
    ? (grossProfitVar === null ? "neutral" : grossProfitVar >= 0 ? "ahead" : "behind")
    : contributionVar >= 0 ? "ahead" : "behind";
  const profitToneLabel = profitTone === "ahead" ? "Ahead" : profitTone === "behind" ? "Behind" : "—";
  const profitToneClass = profitTone === "ahead"
    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
    : profitTone === "behind"
      ? "bg-rose-50 text-rose-700 border-rose-100"
      : "bg-slate-50 text-slate-600 border-slate-200";

  const varianceDrivers = [
    { label: "Food", value: foodSpendVar, driver: trimDriver(foodCostKpi?.driver) },
    { label: "Labor", value: laborSpendVar, driver: trimDriver(laborKpi?.driver) },
    { label: "Waste", value: wasteSpendVar, driver: trimDriver(wasteKpi?.driver) },
  ].filter((d) => typeof d.value === "number" && d.driver);
  const topVarianceDriver = varianceDrivers.sort((a, b) => Math.abs((b.value as number)) - Math.abs((a.value as number)))[0];
  const topVarianceText = topVarianceDriver ? `${topVarianceDriver.label}: ${topVarianceDriver.driver}` : null;

  const pulseKpis = (kpiLayout || [])
    .map((key) => kpis.find((k) => k.metricKey === key))
    .filter(Boolean) as Kpi[];
  const pulseStatuses = pulseKpis.map((k) => {
    const delta = parseSignedMoney(k.varianceValue ?? k.delta) ?? null;
    const positiveGood = ["total_sales", "gross_margin", "covers"].includes(k.metricKey || "");
    const isGood = delta === null ? null : (positiveGood ? delta >= 0 : delta <= 0);
    const ratio = (typeof revenuePounds === "number" && delta !== null) ? (Math.abs(delta) / Math.max(1, revenuePounds)) : null;
    const withinTolerance = ratio !== null && ratio <= 0.01;
    const status = delta === null
      ? "No data"
      : isGood
        ? "On target"
        : withinTolerance
          ? "Watch"
          : "At risk";
    return {
      metricKey: k.metricKey || k.title,
      title: k.title,
      status,
      delta,
      driver: trimDriver(k.driver),
      targetView: k.targetView
    };
  });
  const pulseOnTarget = pulseStatuses.filter(p => p.status === "On target").length;
  const pulseWatch = pulseStatuses.filter(p => p.status === "Watch").length;
  const pulseRisk = pulseStatuses.filter(p => p.status === "At risk").length;
  const pulseMissing = pulseStatuses.filter(p => p.status === "No data").length;
  const pulseTotal = Math.max(1, pulseStatuses.length);
  const pulseTone = pulseMissing === pulseStatuses.length
    ? "No data"
    : pulseRisk > 0
      ? "At risk"
      : pulseWatch > 0
        ? "Watch"
        : "On track";
  const pulseToneClass = pulseTone === "On track"
    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
    : pulseTone === "Watch"
      ? "bg-amber-50 text-amber-700 border-amber-100"
      : pulseTone === "At risk"
        ? "bg-rose-50 text-rose-700 border-rose-100"
        : "bg-slate-50 text-slate-600 border-slate-200";

  const revenueSeries = [8200, 8600, 9050, 9480, 9900, 11200, 10150];
  const revenueTargetSeries = [9000, 9000, 9300, 9600, 10000, 11000, 10500];
  const paceLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const revenueSum = revenueSeries.reduce((acc, v) => acc + v, 0);
  const rolling7Avg = Math.round(revenueSum / revenueSeries.length);
  const weekToDateRevenue = revenueSum;
  const monthToDateRevenue = Math.round(revenueSum * 4.2);
  const todayRevenue = revenueSeries[revenueSeries.length - 1] || 0;
  const lastCompletedRevenue = revenueSeries[revenueSeries.length - 2] ?? todayRevenue;
  const lastCompletedDate = new Date(lastUpdatedAt);
  lastCompletedDate.setDate(lastCompletedDate.getDate() - 1);
  const lastCompletedLabel = lastCompletedDate.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
  const revenueMin = Math.min(...revenueSeries, ...revenueTargetSeries) - 800;
  const revenueMax = Math.max(...revenueSeries, ...revenueTargetSeries) + 800;
  const salesPerLaborSeries = [82, 88, 90, 95, 98, 102, 99];
  const laborMin = 70;
  const laborMax = 110;
  const chartWidth = 360;
  const chartHeight = 120;
  const chartYBase = 110;
  const chartYRange = 80;

  const getHoverIndex = (event: any, length: number) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    if (typeof clientX !== "number") return 0;
    const x = Math.min(Math.max(0, clientX - rect.left), rect.width);
    const ratio = rect.width ? x / rect.width : 0;
    return Math.round(ratio * (length - 1));
  };

  const revenueLinePoints = revenueSeries.map((v, i) => {
    const x = (i / (revenueSeries.length - 1)) * chartWidth;
    const y = chartYBase - ((v - revenueMin) / (revenueMax - revenueMin)) * chartYRange;
    return `${x},${y}`;
  }).join(" ");
  const revenueAreaPoints = `${revenueLinePoints} ${chartWidth},${chartYBase} 0,${chartYBase}`;

  const revenueTargetPoints = revenueTargetSeries.map((v, i) => {
    const x = (i / (revenueTargetSeries.length - 1)) * chartWidth;
    const y = chartYBase - ((v - revenueMin) / (revenueMax - revenueMin)) * chartYRange;
    return `${x},${y}`;
  }).join(" ");

  const laborLinePoints = salesPerLaborSeries.map((v, i) => {
    const x = (i / (salesPerLaborSeries.length - 1)) * chartWidth;
    const y = chartYBase - ((v - laborMin) / (laborMax - laborMin)) * chartYRange;
    return `${x},${y}`;
  }).join(" ");
  const laborAreaPoints = `${laborLinePoints} ${chartWidth},${chartYBase} 0,${chartYBase}`;

  const activeRevenueIndex = revenueHoverIndex !== null ? revenueHoverIndex : revenueSeries.length - 1;
  const activeLaborIndex = laborHoverIndex !== null ? laborHoverIndex : salesPerLaborSeries.length - 1;
  const activeRevenueValue = revenueSeries[activeRevenueIndex];
  const activeRevenueTarget = revenueTargetSeries[activeRevenueIndex];
  const revenueVariance = activeRevenueTarget ? ((activeRevenueValue - activeRevenueTarget) / activeRevenueTarget) * 100 : 0;
  const activeLaborValue = salesPerLaborSeries[activeLaborIndex];
  const activeRevenueX = (activeRevenueIndex / (revenueSeries.length - 1)) * chartWidth;
  const activeLaborX = (activeLaborIndex / (salesPerLaborSeries.length - 1)) * chartWidth;
  const activeRevenueY = chartYBase - ((activeRevenueValue - revenueMin) / (revenueMax - revenueMin)) * chartYRange;
  const activeLaborY = chartYBase - ((activeLaborValue - laborMin) / (laborMax - laborMin)) * chartYRange;
  const revenueTooltipWidth = 72;
  const revenueTooltipHeight = 28;
  const laborTooltipWidth = 72;
  const laborTooltipHeight = 28;
  const revenueTooltipX = Math.min(Math.max(activeRevenueX - 32, 6), chartWidth - (revenueTooltipWidth + 6));
  const revenueTooltipY = Math.max(activeRevenueY - (revenueTooltipHeight + 10), 6);
  const laborTooltipX = Math.min(Math.max(activeLaborX - 32, 6), chartWidth - (laborTooltipWidth + 6));
  const laborTooltipY = Math.max(activeLaborY - (laborTooltipHeight + 10), 6);
  const revenueDisplayValue = revenueHoverIndex !== null
    ? `£${activeRevenueValue.toLocaleString()}`
    : `£${revenueSeries[revenueSeries.length - 1].toLocaleString()}`;
  const revenueDeltaLabel = revenueVariance >= 0 ? `+${revenueVariance.toFixed(1)}%` : `${revenueVariance.toFixed(1)}%`;
  const formatCurrency = (value: number) => `£${Math.round(value).toLocaleString()}`;
  const formatCurrencyShort = (value: number) => {
    if (value >= 10000) return `£${(value / 1000).toFixed(1)}k`;
    if (value >= 1000) return `£${(value / 1000).toFixed(1)}k`;
    return `£${Math.round(value)}`;
  };
  const monthLabel = new Date().toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  const weekTargetRevenue = revenueTargetSeries.reduce((acc, v) => acc + v, 0);
  const avgTargetRevenue = weekTargetRevenue / revenueTargetSeries.length;
  const monthTargetRevenue = weekTargetRevenue * 4.2;
  const foodPctNum = parseDeltaValue(foodCostKpi?.value);
  const laborPctNum = parseDeltaValue(laborKpi?.value);
  const grossMarginPctNum = parseDeltaValue(grossMarginKpi?.value) ?? (foodPctNum !== null ? 100 - foodPctNum : null);
  const primeCostPctNum = parseDeltaValue(primeCostKpi?.value) ?? (foodPctNum !== null && laborPctNum !== null ? foodPctNum + laborPctNum : null);
  const makeRevenueSummary = (label: string, actual: number, target: number, sub: string) => {
    const delta = actual - target;
    const abs = Math.abs(Math.round(delta));
    const tone = abs === 0 ? "neutral" : delta >= 0 ? "ahead" : "behind";
    const status = tone === "ahead" ? "Ahead" : tone === "behind" ? "Behind" : "On target";
    const deltaPct = target > 0 ? (delta / target) * 100 : 0;
    const deltaPctLabel = target > 0 ? `${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)}%` : "—";
    const accentClass = tone === "ahead" ? "bg-emerald-500" : tone === "behind" ? "bg-rose-500" : "bg-slate-200";
    const pillClass = tone === "ahead"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : tone === "behind"
        ? "bg-rose-50 text-rose-700 border-rose-100"
        : "bg-slate-50 text-slate-600 border-slate-200";
    const deltaClass = tone === "ahead" ? "text-emerald-700" : tone === "behind" ? "text-rose-700" : "text-slate-500";
    const deltaMoneyLabel = abs === 0 ? "£0" : `${delta >= 0 ? "+" : "−"}£${abs.toLocaleString()}`;
    const grossProfit = typeof grossMarginPctNum === "number" ? (actual * (grossMarginPctNum / 100)) : null;
    const primeCost = typeof primeCostPctNum === "number" ? (actual * (primeCostPctNum / 100)) : null;
    return {
      label,
      value: formatCurrency(actual),
      sub,
      status,
      grossProfitValue: grossProfit !== null ? formatCurrency(grossProfit) : "—",
      primeCostValue: primeCost !== null ? formatCurrency(primeCost) : "—",
      deltaMoneyLabel,
      deltaPctLabel,
      accentClass,
      pillClass,
      deltaClass
    };
  };
  const revenueSummaryItems = [
    makeRevenueSummary("Today's revenue", todayRevenue, revenueTargetSeries[revenueTargetSeries.length - 1] || todayRevenue, lastUpdatedLabel),
    makeRevenueSummary("Last completed day", lastCompletedRevenue, revenueTargetSeries[revenueTargetSeries.length - 2] ?? lastCompletedRevenue, lastCompletedLabel),
    makeRevenueSummary("Rolling 7-day avg", rolling7Avg, avgTargetRevenue, "Last 7 days"),
    makeRevenueSummary("Week to date", weekToDateRevenue, weekTargetRevenue, "This week"),
    makeRevenueSummary("Month to date", monthToDateRevenue, monthTargetRevenue, monthLabel)
  ];
  const performanceItems = [revenueSummaryItems[0], revenueSummaryItems[3]].filter(Boolean);
  const laborTarget = 95;
  const laborVariance = activeLaborValue - laborTarget;
  const laborDeltaLabel = laborVariance >= 0 ? `+£${laborVariance.toFixed(0)}` : `-£${Math.abs(laborVariance).toFixed(0)}`;

  // Warnings data
  const warnings = [
    {
      id: 1,
      severity: "red",
      title: "Sales pace off target",
      reason: "Run-rate below plan; prioritize peak throughput and high-margin upsells.",
      priority: "Immediate attention required",
      due: "Today",
      consequence: "£1.1k revenue at risk",
      targetView: "sales"
    },
    {
      id: 2,
      severity: "amber",
      title: "Food cost rising",
      reason: "COGS % above target; tighten prep yields and supplier mix.",
      priority: "High priority",
      due: "This week",
      consequence: "£280 margin loss",
      targetView: "inventory"
    },
    {
      id: 3,
      severity: "amber",
      title: "Labor % high",
      reason: "Labor cost % elevated in low-traffic hours.",
      priority: "High priority",
      due: "This week",
      consequence: "£200 overspend",
      targetView: "staffing"
    },
    {
      id: 4,
      severity: "amber",
      title: "Waste trending up",
      reason: "Fresh produce waste accounts for the majority of losses.",
      priority: "Planned",
      due: "This month",
      consequence: "£180 avoidable waste",
      targetView: "inventory"
    },
    {
      id: 5,
      severity: "amber",
      title: "Inventory over target",
      reason: "Stock value above target; tighten ordering on slow-moving items.",
      priority: "Planned",
      due: "This month",
      consequence: "£1.2k cash tied up",
      targetView: "inventory"
    },
  ];
  const riskTone = warnings[0]?.severity === "red" ? "high" : "medium";
  const riskPillClass = riskTone === "high"
    ? "bg-rose-50 text-rose-700 border-rose-100"
    : "bg-amber-50 text-amber-700 border-amber-100";
  const riskBarClass = riskTone === "high"
    ? "from-rose-600 via-rose-400 to-amber-300"
    : "from-amber-500 via-amber-300 to-orange-300";
  const visibleRisks = warnings.slice(0, 1);

  // Recommended actions
  const actions = [
    {
      id: 1,
      title: "Recover sales pace",
      rationale: "Push high-margin items and speed service during peak windows.",
      priority: "Immediate attention required",
      due: "Today",
      consequence: "£1.1k revenue at risk"
    },
    {
      id: 2,
      title: "Reduce food cost 2–3 pts",
      rationale: "Audit top 5 cost items and tighten prep yields.",
      priority: "High priority",
      due: "This week",
      consequence: "£280 margin loss"
    },
    {
      id: 3,
      title: "Align labor to demand",
      rationale: "Shift coverage from low-traffic hours into peak periods.",
      priority: "High priority",
      due: "This week",
      consequence: "£200 overspend"
    },
  ];
  const visibleActions = actions.slice(0, 1);
  const costDriverRows = [
    {
      key: "food",
      label: `Food cost${foodCostKpi?.value ? ` (${foodCostKpi.value})` : ""}`,
      spend: foodSpendPounds,
      delta: foodDelta,
      driver: trimDriver(foodCostKpi?.driver)
    },
    {
      key: "labor",
      label: `Labor${laborKpi?.value ? ` (${laborKpi.value})` : ""}`,
      spend: laborSpendPounds,
      delta: laborDelta,
      driver: trimDriver(laborKpi?.driver)
    },
    {
      key: "waste",
      label: `Waste${wasteKpi?.value ? ` (${wasteKpi.value})` : ""}`,
      spend: wasteSpendPounds,
      delta: wasteDelta,
      driver: trimDriver(wasteKpi?.driver)
    }
  ];

  const homeModalTitle = homeModal === "risks"
    ? "Top Risks"
    : homeModal === "actions"
      ? "Recommended Actions"
      : homeModal === "profit"
        ? "Profit & Loss Snapshot"
        : homeModal === "performance"
          ? "Performance Snapshot"
          : "";
  const homeModalSubtitle = homeModal === "risks"
    ? "Ranked by urgency and potential impact."
    : homeModal === "actions"
      ? "Prioritized actions to protect margin."
      : homeModal === "profit"
        ? "Full breakdown of revenue, profit, and expense drivers."
        : homeModal === "performance"
          ? "Revenue performance across key timeframes."
          : "";
  const homeModalBody = homeModal === "risks"
    ? React.createElement("div", { className: "space-y-3" },
      warnings.map((risk) =>
        React.createElement("div", { key: risk.id, className: "rounded-xl border border-gray-100 bg-gray-50/60 p-4" }, [
          React.createElement("div", { className: "flex items-center justify-between" }, [
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("span", { className: `text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${risk.severity === "red" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}` }, risk.priority),
              React.createElement("span", { className: "text-xs font-black text-gray-900" }, risk.title)
            ]),
            React.createElement("span", { className: "text-[10px] font-black text-gray-400 uppercase tracking-widest" }, risk.due)
          ]),
          React.createElement("p", { className: "text-[11px] text-gray-600 mt-2" }, risk.reason),
          React.createElement("div", { className: "flex items-center justify-between mt-3 text-[10px] font-semibold text-gray-500" }, [
            React.createElement("span", {}, risk.consequence),
            React.createElement("button", {
              onClick: () => onNavigate(risk.targetView),
              className: "text-[10px] font-black uppercase tracking-widest text-indigo-600"
            }, "Review →")
          ])
        ])
      )
    )
    : homeModal === "actions"
      ? React.createElement("div", { className: "space-y-3" },
        actions.map((action) =>
          React.createElement("div", { key: action.id, className: "rounded-xl border border-indigo-100/80 bg-indigo-50/50 p-4" }, [
            React.createElement("div", { className: "flex items-center justify-between" }, [
              React.createElement("div", { className: "flex items-center gap-2" }, [
                React.createElement("span", { className: `text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${action.priority.includes("Immediate") ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-indigo-100 text-indigo-700 border-indigo-200"}` }, action.priority),
                React.createElement("span", { className: "text-xs font-black text-gray-900" }, action.title)
              ]),
              React.createElement("span", { className: "text-[10px] font-black text-indigo-600 uppercase tracking-widest" }, action.due)
            ]),
            React.createElement("p", { className: "text-[11px] text-gray-600 mt-2" }, action.rationale),
            React.createElement("div", { className: "flex items-center justify-between mt-3 text-[10px] font-semibold text-indigo-700" }, [
              React.createElement("span", {}, action.consequence),
              React.createElement("button", {
                onClick: () => onKpiExplain?.({ title: `Action: ${action.title}. Explain why, expected impact, and how to implement. Include confidence`, value: "", icon: "" }),
                className: "text-[10px] font-black uppercase tracking-widest text-indigo-600"
              }, "Open plan →")
            ])
          ])
        )
      )
      : homeModal === "profit"
        ? React.createElement("div", { className: "space-y-4" }, [
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
            React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3" }, [
              React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Revenue"),
                  React.createElement("p", { className: "text-2xl font-black text-slate-900 mt-1" }, fmtPounds(revenuePounds))
                ]),
                React.createElement("div", { className: "text-right" }, [
                  React.createElement("p", { className: `text-[11px] font-bold ${revenueDelta.className}` }, revenueDelta.label),
                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1" }, homeRangeLabel)
                ])
              ])
            ]),
            React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white px-4 py-3" }, [
              React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Gross Profit"),
                  React.createElement("p", { className: "text-xl font-black text-slate-900 mt-1" }, fmtPounds(grossProfitPounds))
                ]),
                React.createElement("div", { className: "text-right" }, [
                  React.createElement("p", { className: "text-sm font-black text-slate-900" }, grossMarginKpi?.value || "—"),
                  React.createElement("p", { className: `text-[11px] font-bold ${grossProfitDelta.className}` }, grossProfitDelta.label)
                ])
              ])
            ]),
            React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white px-4 py-3" }, [
              React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Prime Cost"),
                  React.createElement("p", { className: "text-xl font-black text-slate-900 mt-1" }, fmtPounds(primeCostPounds))
                ]),
                React.createElement("div", { className: "text-right" }, [
                  React.createElement("p", { className: "text-sm font-black text-slate-900" }, primeCostKpi?.value || "—"),
                  React.createElement("p", { className: `text-[11px] font-bold ${primeCostDelta.className}` }, primeCostDelta.label)
                ])
              ])
            ]),
            React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3" }, [
              React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
                React.createElement("div", {}, [
                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Contribution (Est.)"),
                  React.createElement("p", { className: "text-xl font-black text-slate-900 mt-1" }, fmtPounds(contributionPounds))
                ]),
                React.createElement("div", { className: "text-right" }, [
                  React.createElement("p", { className: `text-[11px] font-bold ${contributionDelta.className}` }, contributionDelta.label),
                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1" }, "After food, labor, waste")
                ])
              ])
            ])
          ]),
          topVarianceText && React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3" }, [
            React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Primary Driver"),
            React.createElement("p", { className: "text-xs font-semibold text-slate-700 mt-1 leading-snug" }, topVarianceText)
          ]),
          React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white p-4" }, [
            React.createElement("div", { className: "flex items-center justify-between mb-3" }, [
              React.createElement("span", { className: "text-[10px] uppercase tracking-widest text-slate-400 font-black" }, "Expense Drivers"),
              React.createElement("span", { className: "text-[10px] font-black text-slate-500 uppercase tracking-widest" }, freshnessLabel)
            ]),
            React.createElement("div", { className: "space-y-2" },
              costDriverRows.map((row) =>
                React.createElement("div", { key: row.key, className: "flex items-start justify-between gap-3 rounded-lg bg-slate-50/60 border border-slate-200 px-3 py-2" }, [
                  React.createElement("div", { className: "min-w-0" }, [
                    React.createElement("p", { className: "text-[10px] uppercase tracking-widest text-slate-400 font-black" }, row.label),
                    React.createElement("p", { className: "text-xs font-semibold text-slate-700 truncate" }, row.driver || "—")
                  ]),
                  React.createElement("div", { className: "text-right flex-shrink-0" }, [
                    React.createElement("p", { className: "text-sm font-black text-slate-900" }, fmtPounds(row.spend)),
                    React.createElement("p", { className: `text-[11px] font-bold ${row.delta.className}` }, row.delta.label)
                  ])
                ])
              )
            )
          ])
        ])
        : homeModal === "performance"
          ? React.createElement("div", { className: "rounded-2xl border border-slate-200 bg-slate-50/40 overflow-hidden" },
            revenueSummaryItems.map((item, idx) =>
              React.createElement("button", {
                key: item.label,
                onClick: () => onNavigate("sales"),
                className: `w-full text-left relative px-5 py-4 bg-transparent hover:bg-white transition-colors flex items-start justify-between gap-5 ${idx !== 0 ? "border-t border-slate-200/70" : ""}`,
                style: { WebkitTapHighlightColor: "transparent" }
              }, [
                React.createElement("div", { className: `absolute left-0 top-0 h-full w-0.5 ${item.accentClass}` }),
                React.createElement("div", { className: "min-w-0" }, [
                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-500" }, item.label),
                  React.createElement("p", { className: "text-[11px] text-slate-500 font-semibold mt-1 truncate" }, item.sub)
                ]),
                React.createElement("div", { className: "flex flex-col items-end flex-shrink-0" }, [
                  React.createElement("p", { className: "text-2xl font-black text-slate-900 leading-none" }, item.value),
                  React.createElement("div", { className: "mt-2 flex items-center justify-end gap-2" }, [
                    React.createElement("span", { className: `text-[10px] font-black px-2.5 py-1 rounded-full border ${item.pillClass}` }, item.status),
                    React.createElement("span", { className: `text-[10px] font-black px-2.5 py-1 rounded-full border bg-white/70 border-slate-200 ${item.deltaClass}` }, `${item.deltaMoneyLabel} (${item.deltaPctLabel})`)
                  ]),
                  React.createElement("div", { className: "mt-3 flex items-center justify-end gap-2 text-[11px] font-semibold text-slate-600" }, [
                    React.createElement("span", { className: "px-2 py-1 rounded-full bg-white/70 border border-slate-200" }, `GP ${item.grossProfitValue}`),
                    React.createElement("span", { className: "px-2 py-1 rounded-full bg-white/70 border border-slate-200" }, `PC ${item.primeCostValue}`)
                  ])
                ])
              ])
            )
          )
          : null;

  return React.createElement("div", { style: { display: "grid", gap: 20 } }, [
    // Combined Header + Goal
    React.createElement("div", {
      key: "dashboard-hero",
      className: "relative overflow-visible mb-6 rounded-[32px] border border-indigo-100/60 bg-white p-8 shadow-sm",
      style: {
        background: "linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(255,255,255,0.98) 40%, rgba(14,165,233,0.08) 100%)"
      }
    }, [
      React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
      React.createElement("div", { className: "flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6 text-xs text-slate-500" }, [
        React.createElement("div", { className: "flex items-center gap-2" }, [
          React.createElement("span", { className: "font-black uppercase tracking-widest text-slate-400" }, "Last updated"),
          React.createElement("span", { className: "font-semibold text-slate-600" }, lastUpdatedLabel)
        ]),
        React.createElement("div", { className: "flex items-center gap-4" }, [
          React.createElement("div", { key: "goal-inline", className: "flex items-center gap-3" }, [
            activeGoal && React.createElement("span", { className: "text-xs font-black text-slate-700 uppercase tracking-widest" }, goalHeadline || activeGoal.label),
            activeGoal
              ? React.createElement("div", { key: "goal-actions", className: "flex items-center gap-2" }, [
                React.createElement("button", {
                  key: "goal-open",
                  onClick: openGoalModal,
                  className: "inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] shadow-md shadow-indigo-100"
                }, "Current Goal"),
                React.createElement("button", {
                  key: "goal-toggle",
                  onClick: () => setGoalCardExpanded(!goalCardExpanded),
                  className: "h-7 w-7 rounded-full border border-indigo-200 text-indigo-600 text-xs font-black flex items-center justify-center bg-white/90 shadow-sm"
                }, goalCardExpanded ? "−" : "+")
              ])
              : React.createElement("button", {
                onClick: openGoalModal,
                className: "inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] shadow-md shadow-indigo-100"
              }, "Set Goal")
          ]),
          React.createElement("div", { className: "flex items-center gap-2 bg-white/80 rounded-full p-1 shadow-sm", style: { border: "none" } }, [
            React.createElement("button", {
              onClick: () => setHomeRange("day"),
              className: `px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border-0 ${homeRange === "day" ? "bg-indigo-600 text-white" : "text-slate-500"}`
            }, "Day"),
            React.createElement("button", {
              onClick: () => setHomeRange("week"),
              className: `px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border-0 ${homeRange === "week" ? "bg-indigo-600 text-white" : "text-slate-500"}`
            }, "Week"),
            React.createElement("button", {
              onClick: () => setHomeRange("month"),
              className: `px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border-0 ${homeRange === "month" ? "bg-indigo-600 text-white" : "text-slate-500"}`
            }, "Month")
          ])
        ])
      ]),
      React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-[1.1fr_1.9fr] gap-4 items-end" }, [
	        React.createElement("div", { key: "page-header" }, [
	          React.createElement("h2", { key: "title", className: "text-3xl font-black text-gray-900 mb-2" }, "Welcome back!"),
	          React.createElement("p", { key: "subtitle", className: "text-gray-500 font-medium" }, [
	            "Here’s what’s happening across ",
	            React.createElement("span", { key: "venue", className: "font-black text-gray-900" }, venueName || 'your venue'),
	            " today"
	          ]),
          React.createElement("div", { key: "pulse", className: "mt-5 rounded-2xl border border-white/60 bg-white/70 p-4 shadow-sm backdrop-blur-sm relative overflow-visible" }, [
	            React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
	              React.createElement("div", { className: "min-w-0" }, [
	                React.createElement("div", { className: "flex items-center gap-2" }, [
	                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Business Health"),
	                  React.createElement("span", { className: "relative inline-flex items-center" }, [
	                    React.createElement("span", {
	                      "aria-label": "Info",
	                      onMouseEnter: () => setPulseInfoOpen(true),
	                      onMouseLeave: () => setPulseInfoOpen(false),
	                      onFocus: () => setPulseInfoOpen(true),
	                      onBlur: () => setPulseInfoOpen(false),
	                      onClick: (e: any) => e.stopPropagation(),
	                      onPointerDown: (e: any) => e.stopPropagation(),
	                      tabIndex: 0,
	                      style: {
	                        width: 16,
	                        height: 16,
	                        borderRadius: 999,
	                        border: "1px solid rgba(15,23,42,0.15)",
	                        color: "#64748b",
	                        fontSize: 10,
	                        fontWeight: 900,
	                        display: "inline-flex",
	                        alignItems: "center",
	                        justifyContent: "center",
	                        cursor: "help",
	                        flexShrink: 0,
	                        userSelect: "none"
	                      }
	                    }, "i"),
                    pulseInfoOpen && React.createElement("div", {
                      className: "absolute z-50 -top-2 left-1/2 -translate-y-full ml-5 px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] font-semibold shadow-2xl border border-white/10 whitespace-normal leading-snug",
                      style: { pointerEvents: "none", width: "max-content", maxWidth: 600, textAlign: "center", transform: "translate(-50%, -100%)" }
                    }, [
                      "A simple pulse check based on the KPI tiles below.",
                      React.createElement("br"),
                      "Green = on target, amber = watch, red = attention.",
                      React.createElement("br"),
                      "Helps you spot margin pressure fast."
                    ])
                  ])
	                ]),
	                React.createElement("p", { className: "text-[11px] text-slate-600 font-semibold mt-2" }, `${pulseOnTarget}/${pulseStatuses.length || 0} KPIs on target`)
	              ]),
	              React.createElement("span", { className: `text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${pulseToneClass}` }, pulseTone)
	            ]),
	            React.createElement("div", { className: "mt-3 h-2 w-full rounded-full bg-slate-200 overflow-hidden flex" }, [
	              pulseOnTarget > 0 && React.createElement("div", { style: { width: `${(pulseOnTarget / pulseTotal) * 100}%` }, className: "h-full bg-emerald-500" }),
	              pulseWatch > 0 && React.createElement("div", { style: { width: `${(pulseWatch / pulseTotal) * 100}%` }, className: "h-full bg-amber-500" }),
	              pulseRisk > 0 && React.createElement("div", { style: { width: `${(pulseRisk / pulseTotal) * 100}%` }, className: "h-full bg-rose-500" }),
	              pulseMissing > 0 && React.createElement("div", { style: { width: `${(pulseMissing / pulseTotal) * 100}%` }, className: "h-full bg-slate-300" }),
	            ]),
	            React.createElement("div", { className: "mt-3 grid grid-cols-2 gap-2 text-[11px] font-semibold text-slate-600" }, [
	              React.createElement("div", { className: "flex items-center gap-2" }, [
	                React.createElement("span", { className: "h-2 w-2 rounded-full bg-emerald-500" }),
	                React.createElement("span", {}, `On target (${pulseOnTarget})`)
	              ]),
	              React.createElement("div", { className: "flex items-center gap-2" }, [
	                React.createElement("span", { className: "h-2 w-2 rounded-full bg-amber-500" }),
	                React.createElement("span", {}, `Watch (${pulseWatch})`)
	              ]),
	              React.createElement("div", { className: "flex items-center gap-2" }, [
	                React.createElement("span", { className: "h-2 w-2 rounded-full bg-rose-500" }),
	                React.createElement("span", {}, `Attention (${pulseRisk})`)
	              ]),
	              pulseMissing > 0 && React.createElement("div", { className: "flex items-center gap-2" }, [
	                React.createElement("span", { className: "h-2 w-2 rounded-full bg-slate-300" }),
	                React.createElement("span", {}, `No data (${pulseMissing})`)
	              ])
	            ]),
            topVarianceText && React.createElement("p", { className: "mt-3 text-[11px] text-slate-600" }, [
              React.createElement("span", { className: "font-black text-slate-900" }, "Biggest driver: "),
              topVarianceText
            ]),
            onKpiExplain && React.createElement("button", {
              onClick: (e: any) => {
                e.stopPropagation();
                onKpiExplain({
                  title: "Business Health Pulse",
                  value: "",
                  icon: ""
                });
              },
              onPointerDown: (e: any) => { e.stopPropagation(); },
              className: "absolute bottom-3 right-3 w-8 h-8 rounded-full border border-indigo-200 bg-white text-indigo-600 text-sm font-black flex items-center justify-center shadow-sm hover:bg-indigo-50"
            }, "?")
          ])
	        ]),
        React.createElement("div", { key: "risk-actions-inline", className: "grid grid-cols-1 lg:grid-cols-2 gap-4" }, [
          React.createElement("div", { className: "relative overflow-visible bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col h-full" }, [
            React.createElement("div", { className: `absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${riskBarClass}` }),
            React.createElement("div", { className: "flex items-start justify-between mb-3" }, [
              React.createElement("div", {}, [
                React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-gray-400" }, "Top Risks"),
                React.createElement("div", { className: "flex items-center gap-2 mt-2" }, [
                  React.createElement("h3", { className: "text-lg font-black text-gray-900" }, `${warnings.length} active risks`),
                  React.createElement("span", { className: "relative inline-flex items-center" }, [
                    React.createElement("span", {
                      "aria-label": "Info",
                      onMouseEnter: () => setRiskInfoOpen(true),
                      onMouseLeave: () => setRiskInfoOpen(false),
                      onFocus: () => setRiskInfoOpen(true),
                      onBlur: () => setRiskInfoOpen(false),
                      onClick: (e: any) => e.stopPropagation(),
                      onPointerDown: (e: any) => e.stopPropagation(),
                      tabIndex: 0,
                      style: {
                        width: 16,
                        height: 16,
                        borderRadius: 999,
                        border: "1px solid rgba(15,23,42,0.15)",
                        color: "#64748b",
                        fontSize: 10,
                        fontWeight: 900,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "help",
                        flexShrink: 0,
                        userSelect: "none"
                      }
                    }, "i"),
                    riskInfoOpen && React.createElement("div", {
                      className: "absolute z-50 -top-2 left-1/2 -translate-x-1/2 -translate-y-full px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] font-semibold shadow-2xl border border-white/10 whitespace-normal leading-snug",
                      style: { pointerEvents: "none", width: "max-content", maxWidth: 600, textAlign: "center" }
                    }, "Top operational risks ranked by urgency and potential impact.")
                  ])
                ])
              ]),
              React.createElement("span", { className: `text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${riskPillClass}` }, riskTone === "high" ? "High" : "Moderate")
            ]),
            React.createElement("div", { className: "space-y-3" },
              visibleRisks.map((risk) =>
                React.createElement("div", { key: risk.id, className: "rounded-xl border border-gray-100 bg-gray-50/60 p-3" }, [
                  React.createElement("div", { className: "flex items-center justify-between" }, [
                    React.createElement("div", { className: "flex items-center gap-2" }, [
                      React.createElement("span", { className: `text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${risk.severity === "red" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}` }, risk.priority),
                      React.createElement("span", { className: "text-xs font-black text-gray-900" }, risk.title)
                    ]),
                    React.createElement("span", { className: "text-[10px] font-black text-gray-400 uppercase tracking-widest" }, risk.due)
                  ]),
                  React.createElement("p", { className: "text-[11px] text-gray-600 mt-2" }, risk.reason),
                  React.createElement("div", { className: "flex items-center justify-between mt-3 text-[10px] font-semibold text-gray-500" }, [
                    React.createElement("span", {}, risk.consequence),
                    React.createElement("button", {
                      onClick: () => onNavigate(risk.targetView),
                      className: "text-[10px] font-black uppercase tracking-widest text-indigo-600"
                    }, "Review →")
                  ])
                ])
              )
            ),
            warnings.length > 1 && React.createElement("button", {
              onClick: () => setHomeModal("risks"),
              className: "mt-auto text-[10px] font-black uppercase tracking-widest text-indigo-600"
            }, "View all risks")
          ]),
          React.createElement("div", { className: "relative overflow-visible bg-white border border-indigo-100 rounded-2xl p-5 shadow-sm flex flex-col h-full" }, [
            React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
            React.createElement("div", { className: "flex items-start justify-between mb-3" }, [
              React.createElement("div", {}, [
                React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-indigo-400" }, "Recommended Actions"),
                React.createElement("div", { className: "flex items-center gap-2 mt-2" }, [
                  React.createElement("h3", { className: "text-lg font-black text-gray-900" }, "Priority action list"),
                  React.createElement("span", { className: "relative inline-flex items-center" }, [
                    React.createElement("span", {
                      "aria-label": "Info",
                      onMouseEnter: () => setActionsInfoOpen(true),
                      onMouseLeave: () => setActionsInfoOpen(false),
                      onFocus: () => setActionsInfoOpen(true),
                      onBlur: () => setActionsInfoOpen(false),
                      onClick: (e: any) => e.stopPropagation(),
                      onPointerDown: (e: any) => e.stopPropagation(),
                      tabIndex: 0,
                      style: {
                        width: 16,
                        height: 16,
                        borderRadius: 999,
                        border: "1px solid rgba(15,23,42,0.15)",
                        color: "#64748b",
                        fontSize: 10,
                        fontWeight: 900,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "help",
                        flexShrink: 0,
                        userSelect: "none"
                      }
                    }, "i"),
                    actionsInfoOpen && React.createElement("div", {
                      className: "absolute z-50 -top-2 left-1/2 -translate-x-1/2 -translate-y-full px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] font-semibold shadow-2xl border border-white/10 whitespace-normal leading-snug",
                      style: { pointerEvents: "none", width: "max-content", maxWidth: 600, textAlign: "center" }
                    }, "Prioritized actions to resolve risks and protect margin.")
                  ])
                ])
              ]),
              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-100" }, homeRangeLabel)
            ]),
            React.createElement("div", { className: "space-y-3" },
              visibleActions.map((action) =>
                React.createElement("div", { key: action.id, className: "rounded-xl border border-indigo-100/80 bg-indigo-50/50 p-3" }, [
                  React.createElement("div", { className: "flex items-center justify-between" }, [
                    React.createElement("div", { className: "flex items-center gap-2" }, [
                      React.createElement("span", { className: `text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${action.priority.includes("Immediate") ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-indigo-100 text-indigo-700 border-indigo-200"}` }, action.priority),
                      React.createElement("span", { className: "text-xs font-black text-gray-900" }, action.title)
                    ]),
                    React.createElement("span", { className: "text-[10px] font-black text-indigo-600 uppercase tracking-widest" }, action.due)
                  ]),
                  React.createElement("p", { className: "text-[11px] text-gray-600 mt-2" }, action.rationale),
                  React.createElement("div", { className: "flex items-center justify-between mt-3 text-[10px] font-semibold text-indigo-700" }, [
                    React.createElement("span", {}, action.consequence),
                    React.createElement("button", {
                      onClick: () => onKpiExplain?.({ title: `Action: ${action.title}. Explain why, expected impact, and how to implement. Include confidence`, value: "", icon: "" }),
                      className: "text-[10px] font-black uppercase tracking-widest text-indigo-600"
                    }, "Open plan →")
                  ])
                ])
              )
            ),
            actions.length > 1 && React.createElement("button", {
              onClick: () => setHomeModal("actions"),
              className: "mt-auto text-[10px] font-black uppercase tracking-widest text-indigo-600"
            }, "View all actions")
          ])
        ])
      ]),
      showGoalDetails && React.createElement("div", { className: "mt-4 flex justify-end" }, [
        React.createElement("div", { className: "w-full lg:w-[620px] grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center" }, [
          React.createElement("div", { className: "flex flex-col items-end text-right" }, [
            activeGoal && activeGoal.note && React.createElement("p", { className: "text-xs text-gray-500" }, activeGoal.note),
            activeGoal && React.createElement("p", { className: "text-[11px] text-gray-400 mt-1" }, "KPI tiles adapt to this focus.")
          ]),
          React.createElement("div", {
            className: "rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm"
          }, [
            React.createElement("div", { className: "flex items-start justify-between mb-2" }, [
              React.createElement("div", { className: "flex flex-col items-center text-center w-full" }, [
                React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-gray-400" }, "Progress"),
                React.createElement("div", { className: "text-2xl font-black text-gray-900 mt-2" }, progressRatio !== null ? `${Math.round(progressRatio * 100)}%` : "—"),
                progressRatio !== null && React.createElement("span", { className: `inline-flex mt-2 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${progressBadgeClass}` },
                  progressRatio >= 1 ? "Achieved"
                    : progressRatio >= 0.7 ? "On track"
                      : "At risk"
                )
              ]),
              React.createElement("button", {
                onClick: (e: any) => { e.stopPropagation(); setGoalProgressExpanded(!goalProgressExpanded); },
                className: "h-6 w-6 rounded-full border border-gray-200 text-gray-400 hover:text-gray-600 text-xs font-black flex items-center justify-center"
              }, goalProgressExpanded ? "−" : "+")
            ]),
            goalProgressExpanded
              ? React.createElement(React.Fragment, null, [
                React.createElement("p", { className: "text-xs text-gray-500 mb-3" },
                  progressRatio === null
                    ? "Set a target to track progress."
                    : progressRatio >= 1
                      ? "Target achieved. Lock in the gains."
                      : progressRatio >= 0.7
                        ? "On track. Maintain current pace."
                        : "At risk. Focus on the goal drivers."
                ),
                React.createElement("div", { className: "h-2 w-full rounded-full bg-gray-100 overflow-hidden" }, [
                  React.createElement("div", {
                    className: `h-full bg-gradient-to-r ${progressBarClass}`,
                    style: { width: `${Math.round((progressRatio ?? 0) * 100)}%` }
                  })
                ])
              ])
              : React.createElement("div", { className: "text-xs text-gray-500" }, progressRatio !== null ? `${Math.round(progressRatio * 100)}%` : "Awaiting data")
          ])
        ])
      ])
    ]),

    goalModalOpen && React.createElement("div", { key: "goal-modal", className: "fixed inset-0 z-[9999] flex items-center justify-center p-4", style: { background: "rgba(0,0,0,0.6)" } }, [
        React.createElement("div", { className: "bg-white w-full max-w-xl rounded-2xl p-6 shadow-2xl" }, [
          React.createElement("div", { className: "flex items-center justify-between mb-6" }, [
            React.createElement("h3", { className: "text-xl font-black text-gray-900" }, "Set Current Goal"),
            React.createElement("button", { onClick: () => setGoalModalOpen(false), className: "text-gray-400 hover:text-gray-600 text-xl" }, "×")
          ]),
          React.createElement("div", { className: "space-y-3 mb-5" },
            goalOptions.map(opt =>
              React.createElement("button", {
                key: opt.id,
                onClick: () => setGoalDraft(d => ({ ...d, id: opt.id })),
                className: `w-full text-left p-4 rounded-xl border transition-all ${goalDraft.id === opt.id ? "border-indigo-600 bg-indigo-50/60" : "border-gray-200 hover:border-gray-300"}`
              }, [
                React.createElement("div", { className: "text-sm font-black text-gray-900" }, opt.label),
                React.createElement("div", { className: "text-xs text-gray-500 mt-1" }, opt.helper)
              ])
            )
          ),
          React.createElement("div", { className: "grid grid-cols-2 gap-4 mb-6" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: "block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2" }, "Target % (optional)"),
              React.createElement("input", {
                type: "number",
                value: goalDraft.targetPct,
                onChange: (e: any) => setGoalDraft(d => ({ ...d, targetPct: e.target.value })),
                placeholder: "20",
                className: "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
              })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: "block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2" }, "Notes (optional)"),
              React.createElement("input", {
                value: goalDraft.note,
                onChange: (e: any) => setGoalDraft(d => ({ ...d, note: e.target.value })),
                placeholder: "e.g., focus on lunch trade",
                className: "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
              })
            ])
          ]),
          React.createElement("div", { className: "flex justify-between items-center gap-3" }, [
            activeGoal && React.createElement("button", {
              onClick: () => { clearGoal(); setGoalModalOpen(false); },
              className: "px-4 py-2.5 text-sm font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl"
            }, "Clear Goal"),
            React.createElement("div", { className: "flex justify-end gap-3 ml-auto" }, [
              React.createElement("button", {
                onClick: () => setGoalModalOpen(false),
                className: "px-5 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl"
              }, "Cancel"),
              React.createElement("button", {
                onClick: applyGoal,
                className: "px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg shadow-indigo-200"
              }, "Save Goal")
            ])
          ])
        ])
      ]),

    homeModal && React.createElement("div", {
      key: "home-modal",
      className: "fixed inset-0 z-[9999] flex items-center justify-center p-4",
      style: { background: "rgba(15,23,42,0.6)" },
      onClick: () => setHomeModal(null)
    }, [
      React.createElement("div", {
        className: "bg-white w-full max-w-3xl rounded-2xl p-6 shadow-2xl border border-slate-200",
        onClick: (e: any) => e.stopPropagation()
      }, [
        React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
          React.createElement("div", {}, [
            React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, homeModalSubtitle),
            React.createElement("h3", { className: "text-xl font-black text-gray-900 mt-2" }, homeModalTitle)
          ]),
          React.createElement("button", {
            onClick: () => setHomeModal(null),
            className: "h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:text-slate-700 text-lg font-black flex items-center justify-center"
          }, "×")
        ]),
        React.createElement("div", { className: "mt-5 max-h-[65vh] overflow-y-auto pr-1" }, homeModalBody),
        React.createElement("div", { className: "mt-6 flex justify-end" }, [
          React.createElement("button", {
            onClick: () => setHomeModal(null),
            className: "px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-600 bg-slate-100 rounded-xl"
          }, "Close")
        ])
      ])
    ]),


	      React.createElement("div", { key: "health-performance", className: "grid grid-cols-1 lg:grid-cols-2 gap-4" }, [
	        React.createElement("div", { className: "relative overflow-visible bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col h-full" }, [
	          React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-indigo-500 to-sky-400" }),
	          React.createElement("div", { className: "flex items-start justify-between mb-4" }, [
	            React.createElement("div", {}, [
	              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-gray-400" }, "Flash Report"),
	              React.createElement("div", { className: "flex items-center gap-2 mt-2" }, [
	                React.createElement("h3", { className: "text-lg font-black text-gray-900" }, "Profit & Loss Snapshot"),
	                React.createElement("span", { className: "relative inline-flex items-center" }, [
	                  React.createElement("span", {
	                    "aria-label": "Info",
	                    onMouseEnter: () => setProfitInfoOpen(true),
	                    onMouseLeave: () => setProfitInfoOpen(false),
                    onFocus: () => setProfitInfoOpen(true),
                    onBlur: () => setProfitInfoOpen(false),
                    onClick: (e: any) => e.stopPropagation(),
                    onPointerDown: (e: any) => e.stopPropagation(),
                    tabIndex: 0,
                    style: {
                      width: 16,
                      height: 16,
                      borderRadius: 999,
                      border: "1px solid rgba(15,23,42,0.15)",
                      color: "#64748b",
                      fontSize: 10,
                      fontWeight: 900,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "help",
                      flexShrink: 0,
	                      userSelect: "none"
	                    }
	                  }, "i"),
	                  profitInfoOpen && React.createElement("div", {
	                    className: "absolute z-50 -top-2 left-1/2 -translate-x-1/2 -translate-y-full px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] font-semibold shadow-2xl border border-white/10 whitespace-normal leading-snug",
	                    style: { pointerEvents: "none", width: "max-content", maxWidth: 600, textAlign: "center" }
	                  }, "Mini P&L view: revenue, gross profit, prime cost, and estimated contribution vs target, plus the biggest drivers impacting profit.")
	                ])
	              ])
	            ]),
	            React.createElement("span", { className: `text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${profitToneClass}` }, profitToneLabel)
	          ]),
	          React.createElement("div", { className: "space-y-3" }, [
	            React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-3" }, [
	              React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3" }, [
	                React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
	                  React.createElement("div", {}, [
	                    React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Revenue"),
	                    React.createElement("p", { className: "text-2xl font-black text-slate-900 mt-1" }, fmtPounds(revenuePounds))
	                  ]),
	                  React.createElement("div", { className: "text-right" }, [
	                    React.createElement("p", { className: `text-[11px] font-bold ${revenueDelta.className}` }, revenueDelta.label),
	                    React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1" }, homeRangeLabel)
	                  ])
	                ])
	              ]),
	              React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white px-4 py-3" }, [
	                React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
	                  React.createElement("div", {}, [
	                    React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Gross Profit"),
	                    React.createElement("p", { className: "text-xl font-black text-slate-900 mt-1" }, fmtPounds(grossProfitPounds))
	                  ]),
	                  React.createElement("div", { className: "text-right" }, [
	                    React.createElement("p", { className: "text-sm font-black text-slate-900" }, grossMarginKpi?.value || "—"),
	                    React.createElement("p", { className: `text-[11px] font-bold ${grossProfitDelta.className}` }, grossProfitDelta.label)
	                  ])
	                ])
	              ]),
	              React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white px-4 py-3" }, [
	                React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
	                  React.createElement("div", {}, [
	                    React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Prime Cost"),
	                    React.createElement("p", { className: "text-xl font-black text-slate-900 mt-1" }, fmtPounds(primeCostPounds))
	                  ]),
	                  React.createElement("div", { className: "text-right" }, [
	                    React.createElement("p", { className: "text-sm font-black text-slate-900" }, primeCostKpi?.value || "—"),
	                    React.createElement("p", { className: `text-[11px] font-bold ${primeCostDelta.className}` }, primeCostDelta.label)
	                  ])
	                ])
	              ]),
	              React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3" }, [
	                React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
	                  React.createElement("div", {}, [
	                    React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Contribution (Est.)"),
	                    React.createElement("p", { className: "text-xl font-black text-slate-900 mt-1" }, fmtPounds(contributionPounds))
	                  ]),
	                  React.createElement("div", { className: "text-right" }, [
	                    React.createElement("p", { className: `text-[11px] font-bold ${contributionDelta.className}` }, contributionDelta.label),
	                    React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1" }, "After food, labor, waste")
	                  ])
	                ])
	              ]),
	            ]),
	            topVarianceText && React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3" }, [
	              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Primary Driver"),
	              React.createElement("p", { className: "text-xs font-semibold text-slate-700 mt-1 leading-snug" }, topVarianceText)
	            ]),
          ]),
          React.createElement("button", {
            onClick: () => setHomeModal("profit"),
            className: "mt-auto pt-4 text-[10px] font-black uppercase tracking-widest text-indigo-600"
          }, "View breakdown")
        ]),
	        React.createElement("div", { className: "relative overflow-visible bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" }, [
	          React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-indigo-500 to-sky-400" }),
		          React.createElement("div", { className: "flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5" }, [
		            React.createElement("div", {}, [
              React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Revenue"),
		              React.createElement("div", { className: "flex items-center gap-2 mt-2" }, [
		                React.createElement("h3", { className: "text-lg font-black text-gray-900" }, "Performance Snapshot"),
		                React.createElement("span", { className: "relative inline-flex items-center" }, [
		                  React.createElement("span", {
		                    "aria-label": "Info",
		                    onMouseEnter: () => setPerformanceInfoOpen(true),
		                    onMouseLeave: () => setPerformanceInfoOpen(false),
		                    onFocus: () => setPerformanceInfoOpen(true),
		                    onBlur: () => setPerformanceInfoOpen(false),
		                    onClick: (e: any) => e.stopPropagation(),
		                    onPointerDown: (e: any) => e.stopPropagation(),
		                    tabIndex: 0,
		                    style: {
		                      width: 16,
		                      height: 16,
		                      borderRadius: 999,
		                      border: "1px solid rgba(15,23,42,0.15)",
		                      color: "#64748b",
		                      fontSize: 10,
		                      fontWeight: 900,
		                      display: "inline-flex",
		                      alignItems: "center",
		                      justifyContent: "center",
		                      cursor: "help",
		                      flexShrink: 0,
		                      userSelect: "none"
		                    }
		                  }, "i"),
		                  performanceInfoOpen && React.createElement("div", {
		                    className: "absolute z-50 -top-2 left-1/2 -translate-x-1/2 -translate-y-full px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] font-semibold shadow-2xl border border-white/10 whitespace-normal leading-snug",
		                    style: { pointerEvents: "none", width: "max-content", maxWidth: 600, textAlign: "center" }
		                  }, "Snapshot of revenue vs target across key timeframes with profit context (gross profit and prime cost).")
		                ])
		              ]),
		              React.createElement("p", { className: "text-[11px] text-slate-500 font-semibold mt-2" }, " ")
		            ]),
		            React.createElement("div", { className: "flex flex-wrap items-center justify-start sm:justify-end gap-2" }, [
		              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200" }, freshnessLabel),
		              React.createElement("span", { className: "text-[10px] font-bold px-3 py-1 rounded-full bg-white text-slate-600 border border-slate-200" }, `As of ${lastUpdatedLabel}`)
		            ])
		          ]),
		          React.createElement("div", { className: "rounded-2xl border border-slate-200 bg-slate-50/40 overflow-hidden" },
		            performanceItems.map((item, idx) =>
		              React.createElement("button", {
		                key: item.label,
		                onClick: () => onNavigate("sales"),
		                className: `w-full text-left relative px-5 py-4 bg-transparent hover:bg-white transition-colors flex items-start justify-between gap-5 ${idx !== 0 ? "border-t border-slate-200/70" : ""}`,
	                style: { WebkitTapHighlightColor: "transparent" }
	              }, [
	                React.createElement("div", { className: `absolute left-0 top-0 h-full w-0.5 ${item.accentClass}` }),
	                React.createElement("div", { className: "min-w-0" }, [
	                  React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-500" }, item.label),
	                  React.createElement("p", { className: "text-[11px] text-slate-500 font-semibold mt-1 truncate" }, item.sub)
	                ]),
	                React.createElement("div", { className: "flex flex-col items-end flex-shrink-0" }, [
	                  React.createElement("p", { className: "text-2xl font-black text-slate-900 leading-none" }, item.value),
	                  React.createElement("div", { className: "mt-2 flex items-center justify-end gap-2" }, [
	                    React.createElement("span", { className: `text-[10px] font-black px-2.5 py-1 rounded-full border ${item.pillClass}` }, item.status),
	                    React.createElement("span", { className: `text-[10px] font-black px-2.5 py-1 rounded-full border bg-white/70 border-slate-200 ${item.deltaClass}` }, `${item.deltaMoneyLabel} (${item.deltaPctLabel})`)
	                  ]),
	                  React.createElement("div", { className: "mt-3 flex items-center justify-end gap-2 text-[11px] font-semibold text-slate-600" }, [
	                    React.createElement("span", { className: "px-2 py-1 rounded-full bg-white/70 border border-slate-200" }, `GP ${item.grossProfitValue}`),
	                    React.createElement("span", { className: "px-2 py-1 rounded-full bg-white/70 border border-slate-200" }, `PC ${item.primeCostValue}`)
	                  ])
	                ])
		              ])
		            )
		          ),
		          React.createElement("div", { className: "mt-4 flex justify-center" },
		            React.createElement("button", {
		              onClick: (e: any) => { e.stopPropagation(); setHomeModal("performance"); },
		              className: "text-[10px] font-black uppercase tracking-widest text-indigo-600"
		            }, "View breakdown")
		          )
		        ])
		      ]),

      // Row 1: KPI cards (reorderable)
      React.createElement(DndContext, {
        key: "dnd-kpis",
        sensors: sensors,
        collisionDetection: closestCenter,
        onDragEnd: handleDragEndKpis,
        modifiers: [restrictToWindowEdges]
      }, [
        React.createElement(SortableContext, {
          key: "sortable-ctx",
          items: displayedKpis.map((k) => k.metricKey || k.title).filter(Boolean) as string[],
          strategy: rectSortingStrategy,
          children: React.createElement("div", {
            key: "kpi-grid",
            style: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16, alignItems: "stretch" }
          }, [
            ...displayedKpis.map((k) => {
              const kpiId = k.metricKey || k.title;
              const varianceOverride = goalPrimaryKey && goalVarianceOverride && k.metricKey === goalPrimaryKey
                ? goalVarianceOverride
                : undefined;
              return React.createElement(SortableKpiCard, {
                key: kpiId,
                id: kpiId,
                kpi: k,
                onExplain: onKpiExplain ? () => onKpiExplain(k) : undefined,
                onFillManual: onFillManual ? () => onFillManual(k) : undefined,
                onNavigate: onNavigate,
                varianceOverride
              });
            })
          ])
        })
      ]),


      // Owner Charts (compact)
      React.createElement("div", { key: "owner-charts", className: "grid grid-cols-1 lg:grid-cols-2 gap-4" }, [
        React.createElement("div", { className: "relative overflow-hidden bg-white border border-gray-200/70 rounded-2xl p-5 shadow-[0_12px_30px_rgba(15,23,42,0.08)] flex flex-col" }, [
          React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-400" }),
          React.createElement("div", { className: "flex items-center justify-between mb-2" }, [
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("h3", { className: "text-xs font-black uppercase tracking-widest text-gray-500" }, "Revenue"),
              React.createElement("span", { className: "text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full" }, homeRangeLabel)
            ]),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("span", { className: "text-xs font-black text-gray-900" }, revenueDisplayValue),
              React.createElement("span", {
                className: `text-[10px] font-bold px-2 py-0.5 rounded-full border ${revenueVariance >= 0 ? "text-emerald-700 bg-emerald-50 border-emerald-100" : "text-amber-700 bg-amber-50 border-amber-100"}`
              }, revenueDeltaLabel),
              React.createElement("button", {
                onClick: (e: any) => { e.stopPropagation(); setRevenueExpanded(!revenueExpanded); },
                className: "text-[10px] font-bold uppercase tracking-widest text-indigo-600"
              }, revenueExpanded ? "Collapse" : "Expand")
            ])
          ]),
          revenueExpanded ? React.createElement(React.Fragment, null, [
            React.createElement("div", { className: "flex items-center justify-between text-[10px] text-gray-400 mb-2" }, [
              React.createElement("span", {}, formatCurrency(revenueMin)),
              React.createElement("span", {}, `Target ${formatCurrency(activeRevenueTarget)}`),
              React.createElement("span", {}, formatCurrency(revenueMax))
            ]),
            React.createElement("svg", {
              width: "100%",
              height: "120",
              viewBox: `0 0 ${chartWidth} ${chartHeight}`,
              preserveAspectRatio: "none",
              className: "cursor-crosshair",
              onMouseMove: (event: any) => setRevenueHoverIndex(getHoverIndex(event, revenueSeries.length)),
              onMouseLeave: () => setRevenueHoverIndex(null),
              onTouchMove: (event: any) => setRevenueHoverIndex(getHoverIndex(event, revenueSeries.length)),
              onTouchEnd: () => setRevenueHoverIndex(null)
            }, [
              React.createElement("defs", {}, [
                React.createElement("linearGradient", { id: "revenueGrad", x1: "0", y1: "0", x2: "0", y2: "1" }, [
                  React.createElement("stop", { offset: "0%", stopColor: "#4338ca", stopOpacity: 0.25 }),
                  React.createElement("stop", { offset: "100%", stopColor: "#4338ca", stopOpacity: 0 })
                ])
              ]),
              React.createElement("polyline", {
                fill: "none",
                stroke: "rgba(148,163,184,0.7)",
                strokeWidth: 2,
                strokeDasharray: "5 5",
                points: revenueTargetPoints
              }),
              React.createElement("polygon", { points: revenueAreaPoints, fill: "url(#revenueGrad)" }),
              React.createElement("polyline", {
                fill: "none",
                stroke: "#4338ca",
                strokeWidth: 3,
                points: revenueLinePoints
              }),
              ...revenueSeries.map((v, i) => {
                const x = (i / (revenueSeries.length - 1)) * chartWidth;
                const y = chartYBase - ((v - revenueMin) / (revenueMax - revenueMin)) * chartYRange;
                return React.createElement("circle", { key: i, cx: x, cy: y, r: 2.6, fill: "#4338ca", opacity: 0.55 });
              }),
              revenueHoverIndex !== null ? React.createElement("line", { x1: activeRevenueX, y1: 16, x2: activeRevenueX, y2: chartYBase, stroke: "rgba(67,56,202,0.25)", strokeDasharray: "3 3" }) : null,
              React.createElement("circle", { cx: activeRevenueX, cy: activeRevenueY, r: 6, fill: "#ffffff", stroke: "#4338ca", strokeWidth: 2 }),
              revenueHoverIndex !== null ? React.createElement("g", { pointerEvents: "none" }, [
                React.createElement("rect", { x: revenueTooltipX, y: revenueTooltipY, width: revenueTooltipWidth, height: revenueTooltipHeight, rx: 6, fill: "#111827" }),
                React.createElement("text", { x: revenueTooltipX + 6, y: revenueTooltipY + 12, fill: "#ffffff", fontSize: "9", fontWeight: 600 }, `${paceLabels[activeRevenueIndex]} ${formatCurrencyShort(activeRevenueValue)}`),
                React.createElement("text", { x: revenueTooltipX + 6, y: revenueTooltipY + 23, fill: "rgba(255,255,255,0.7)", fontSize: "8", fontWeight: 500 }, `Tgt ${formatCurrencyShort(activeRevenueTarget)}`)
              ]) : null
            ]),
            React.createElement("div", { className: "mt-3 flex items-center justify-between text-[10px] text-gray-400" },
              paceLabels.map((l, idx) => React.createElement("span", {
                key: l,
                className: idx === 0 ? "text-left" : idx === paceLabels.length - 1 ? "text-right" : "text-center",
                style: { minWidth: 0 }
              }, l))
            ),
            React.createElement("p", { className: "text-[11px] text-gray-500 mt-3" }, "Solid line shows actual revenue; dotted line shows daily target. Hover to inspect.")
          ]) : React.createElement("div", { className: "mt-2 grid gap-2 text-[12px] text-gray-600" }, [
            React.createElement("div", { className: "flex items-center justify-between" }, [
              React.createElement("span", { className: "text-[10px] uppercase tracking-widest text-gray-400" }, "Revenue"),
              React.createElement("span", { className: "font-black text-gray-900" }, revenueDisplayValue)
            ]),
            React.createElement("div", { className: "flex items-center justify-between" }, [
              React.createElement("span", { className: "text-[10px] uppercase tracking-widest text-gray-400" }, "Target"),
              React.createElement("span", { className: "font-semibold text-gray-900" }, formatCurrency(activeRevenueTarget))
            ]),
            React.createElement("div", { className: "flex items-center justify-between" }, [
              React.createElement("span", { className: "text-[10px] uppercase tracking-widest text-gray-400" }, "Variance"),
              React.createElement("span", {
                className: revenueVariance >= 0 ? "font-semibold text-emerald-700" : "font-semibold text-amber-700"
              }, revenueDeltaLabel)
            ]),
            React.createElement("div", { className: "flex items-center justify-between text-[11px] text-gray-500" }, [
              React.createElement("span", {}, "Range"),
              React.createElement("span", {}, `${formatCurrency(revenueMin)}–${formatCurrency(revenueMax)}`)
            ])
          ])
        ]),
        React.createElement("div", { className: "relative overflow-hidden bg-white border border-gray-200/70 rounded-2xl p-5 shadow-[0_12px_30px_rgba(15,23,42,0.08)] flex flex-col" }, [
          React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-rose-600 via-orange-400 to-amber-300" }),
          React.createElement("div", { className: "flex items-center justify-between mb-2" }, [
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("h3", { className: "text-xs font-black uppercase tracking-widest text-gray-500" }, "Sales per Labor Hour"),
              React.createElement("span", { className: "text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full" }, "Efficiency")
            ]),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("span", { className: "text-xs font-black text-gray-900" }, `£${activeLaborValue}/hr`),
              React.createElement("span", {
                className: `text-[10px] font-bold px-2 py-0.5 rounded-full border ${laborVariance >= 0 ? "text-emerald-700 bg-emerald-50 border-emerald-100" : "text-amber-700 bg-amber-50 border-amber-100"}`
              }, laborDeltaLabel),
              React.createElement("button", {
                onClick: (e: any) => { e.stopPropagation(); setLaborExpanded(!laborExpanded); },
                className: "text-[10px] font-bold uppercase tracking-widest text-rose-600"
              }, laborExpanded ? "Collapse" : "Expand")
            ])
          ]),
          laborExpanded ? React.createElement(React.Fragment, null, [
            React.createElement("div", { className: "flex items-center justify-between text-[10px] text-gray-400 mb-2" }, [
              React.createElement("span", {}, `Target £${laborTarget}/hr`),
              React.createElement("span", {}, "Higher is better"),
              React.createElement("span", {}, `Range £${laborMin}–£${laborMax}`)
            ]),
            React.createElement("svg", {
              width: "100%",
              height: "120",
              viewBox: `0 0 ${chartWidth} ${chartHeight}`,
              preserveAspectRatio: "none",
              className: "cursor-crosshair",
              onMouseMove: (event: any) => setLaborHoverIndex(getHoverIndex(event, salesPerLaborSeries.length)),
              onMouseLeave: () => setLaborHoverIndex(null),
              onTouchMove: (event: any) => setLaborHoverIndex(getHoverIndex(event, salesPerLaborSeries.length)),
              onTouchEnd: () => setLaborHoverIndex(null)
            }, [
              React.createElement("defs", {}, [
                React.createElement("linearGradient", { id: "laborGrad", x1: "0", y1: "0", x2: "0", y2: "1" }, [
                  React.createElement("stop", { offset: "0%", stopColor: "#dc2626", stopOpacity: 0.22 }),
                  React.createElement("stop", { offset: "100%", stopColor: "#dc2626", stopOpacity: 0 })
                ])
              ]),
              React.createElement("line", { x1: 0, y1: 90, x2: chartWidth, y2: 90, stroke: "rgba(0,0,0,0.08)" }),
              React.createElement("polygon", { points: laborAreaPoints, fill: "url(#laborGrad)" }),
              React.createElement("polyline", {
                fill: "none",
                stroke: "#dc2626",
                strokeWidth: 3,
                points: laborLinePoints
              }),
              ...salesPerLaborSeries.map((v, i) => {
                const x = (i / (salesPerLaborSeries.length - 1)) * chartWidth;
                const y = chartYBase - ((v - laborMin) / (laborMax - laborMin)) * chartYRange;
                return React.createElement("circle", { key: i, cx: x, cy: y, r: 2.6, fill: "#dc2626", opacity: 0.6 });
              }),
              laborHoverIndex !== null ? React.createElement("line", { x1: activeLaborX, y1: 18, x2: activeLaborX, y2: chartYBase, stroke: "rgba(220,38,38,0.25)", strokeDasharray: "3 3" }) : null,
              React.createElement("circle", { cx: activeLaborX, cy: activeLaborY, r: 6, fill: "#ffffff", stroke: "#dc2626", strokeWidth: 2 }),
              laborHoverIndex !== null ? React.createElement("g", { pointerEvents: "none" }, [
                React.createElement("rect", { x: laborTooltipX, y: laborTooltipY, width: laborTooltipWidth, height: laborTooltipHeight, rx: 6, fill: "#111827" }),
                React.createElement("text", { x: laborTooltipX + 6, y: laborTooltipY + 12, fill: "#ffffff", fontSize: "9", fontWeight: 600 }, `${paceLabels[activeLaborIndex]} £${activeLaborValue}`),
                React.createElement("text", { x: laborTooltipX + 6, y: laborTooltipY + 23, fill: "rgba(255,255,255,0.7)", fontSize: "8", fontWeight: 500 }, `Tgt £${laborTarget}/hr`)
              ]) : null
            ]),
            React.createElement("div", { className: "mt-3 flex items-center justify-between text-[10px] text-gray-400" },
              paceLabels.map((l, idx) => React.createElement("span", {
                key: `s-${l}`,
                className: idx === 0 ? "text-left" : idx === paceLabels.length - 1 ? "text-right" : "text-center",
                style: { minWidth: 0 }
              }, l))
            ),
            React.createElement("p", { className: "text-[11px] text-gray-500 mt-3" }, "Higher values indicate better labor efficiency. Hover to inspect.")
          ]) : React.createElement("div", { className: "mt-2 grid gap-2 text-[12px] text-gray-600" }, [
            React.createElement("div", { className: "flex items-center justify-between" }, [
              React.createElement("span", { className: "text-[10px] uppercase tracking-widest text-gray-400" }, "Efficiency"),
              React.createElement("span", { className: "font-black text-gray-900" }, `£${activeLaborValue}/hr`)
            ]),
            React.createElement("div", { className: "flex items-center justify-between" }, [
              React.createElement("span", { className: "text-[10px] uppercase tracking-widest text-gray-400" }, "Target"),
              React.createElement("span", { className: "font-semibold text-gray-900" }, `£${laborTarget}/hr`)
            ]),
            React.createElement("div", { className: "flex items-center justify-between" }, [
              React.createElement("span", { className: "text-[10px] uppercase tracking-widest text-gray-400" }, "Delta"),
              React.createElement("span", {
                className: laborVariance >= 0 ? "font-semibold text-emerald-700" : "font-semibold text-amber-700"
              }, laborDeltaLabel)
            ]),
            React.createElement("div", { className: "flex items-center justify-between text-[11px] text-gray-500" }, [
              React.createElement("span", {}, "Range"),
              React.createElement("span", {}, `£${laborMin}–£${laborMax}`)
            ])
          ])
        ])
      ])
    ]);
}

function AuthView({ onAuthSuccess }: { onAuthSuccess: (user: any) => void }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [venueName, setVenueName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Clear form fields when switching modes to prevent data transfer between Login/Register
  useEffect(() => {
    setEmail("");
    setPassword("");
    setFullName("");
    setVenueName("");
    setError(null);
    setResetSuccess(false);
  }, [isLogin, forgotMode]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (forgotMode) {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
        setResetSuccess(true);
      } else if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        onAuthSuccess(data.user);
      } else {
        // Sign up
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              venue_name: venueName,
            }
          }
        });
        if (error) throw error;

        if (data.user) {
          onAuthSuccess(data.user);
        }
      }
    } catch (err: any) {
      setError(err.message || "An error occurred during authentication");
    } finally {
      setLoading(false);
    }
  };

  return React.createElement("div", {
    className: "min-h-screen flex items-center justify-center p-4",
    style: {
      background: "radial-gradient(1400px 700px at 30% 10%, rgba(6,133,206,.15) 0%, rgba(255,255,255,0) 60%), radial-gradient(1000px 600px at 75% 40%, rgba(139,92,246,.1) 0%, rgba(255,255,255,0) 55%), #f9fafb",
    }
  }, [
    React.createElement("div", {
      key: "auth-card",
      className: "w-full max-w-md bg-white/70 backdrop-blur-xl border border-white/40 shadow-2xl rounded-[32px] overflow-hidden p-8",
    }, [
      // Logo & Header
      React.createElement("div", { key: "header", className: "text-center mb-8" }, [
        React.createElement("div", {
          key: "logo-container",
          className: "mb-4 flex items-center justify-center",
        }, React.createElement("img", {
          key: "logo",
          src: "/logo.png",
          alt: "Findsy",
          className: "h-12 w-auto",
        })),
        React.createElement("h1", { key: "title", className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, forgotMode ? "Reset Password" : "Findsy"),
        React.createElement("p", { key: "sub", className: "text-gray-500 font-medium px-4" },
          forgotMode
            ? "Enter your email to receive a reset link"
            : "Turn data into insights. Understand your business."
        ),
      ]),

      // Tabs
      !forgotMode ? React.createElement("div", {
        key: "tabs",
        className: "flex bg-gray-100/50 p-1 rounded-xl mb-8 border border-gray-200/20",
      }, [
        React.createElement("button", {
          key: "login-tab",
          onClick: () => setIsLogin(true),
          className: `flex-1 py-2 rounded-lg text-sm font-bold transition-all ${isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`
        }, "Login"),
        React.createElement("button", {
          key: "signup-tab",
          onClick: () => setIsLogin(false),
          className: `flex-1 py-2 rounded-lg text-sm font-bold transition-all ${!isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`
        }, "Register Venue"),
      ]) : null,

      // Form
      React.createElement("form", { key: "form", onSubmit: handleAuth, className: "space-y-4" }, [
        !isLogin && React.createElement("div", { key: "name-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Full Name"),
          React.createElement("input", {
            required: true,
            value: fullName,
            onChange: (e: any) => setFullName(e.target.value),
            placeholder: "Gordon Ramsay",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        !isLogin && React.createElement("div", { key: "venue-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Venue Name"),
          React.createElement("input", {
            required: true,
            value: venueName,
            onChange: (e: any) => setVenueName(e.target.value),
            placeholder: "The Lucky Fig Bakery",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        React.createElement("div", { key: "email-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Work Email"),
          React.createElement("input", {
            type: "email",
            required: true,
            value: email,
            onChange: (e: any) => setEmail(e.target.value),
            placeholder: "chef@venue.com",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        !forgotMode && React.createElement("div", { key: "password-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Password"),
          React.createElement("input", {
            type: "password",
            required: true,
            value: password,
            onChange: (e: any) => setPassword(e.target.value),
            placeholder: "••••••••",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        isLogin && !forgotMode && React.createElement("div", { key: "forgot-link", className: "text-right" }, [
          React.createElement("button", {
            key: "forgot-btn",
            type: "button",
            onClick: () => setForgotMode(true),
            className: "text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:text-indigo-700 transition-colors"
          }, "Forgot Password?")
        ]),

        forgotMode && resetSuccess && React.createElement("div", {
          key: "reset-success",
          className: "bg-green-50 text-green-600 text-xs font-bold p-4 rounded-xl border border-green-100 mb-2"
        }, "Password reset link sent! Please check your inbox (including spam)."),

        error && React.createElement("div", {
          key: "error",
          className: "bg-red-50 text-red-600 text-xs font-bold p-3 rounded-xl border border-red-100 animate-shake"
        }, error),

        React.createElement("button", {
          key: "submit",
          disabled: loading,
          className: "w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-indigo-200 transition-all active:scale-[0.98] mt-2 flex items-center justify-center gap-2",
        }, loading ? "Connecting..." : [
          forgotMode ? "Send Reset Link" : isLogin ? "Sign In" : "Create My Account",
          React.createElement("span", { key: "arrow" }, "→")
        ]),

        React.createElement("p", {
          key: "switch",
          className: "text-center text-xs text-gray-400 font-medium mt-6"
        }, [
          forgotMode
            ? "Remembered your password? "
            : isLogin ? "Don't have an account? " : "Already using Findsy? ",
          React.createElement("button", {
            key: "toggle",
            type: "button",
            onClick: () => {
              if (forgotMode) {
                setForgotMode(false);
                setResetSuccess(false);
              } else {
                setIsLogin(!isLogin);
              }
            },
            className: "text-indigo-600 font-bold hover:underline"
          }, forgotMode ? "Back to Login" : isLogin ? "Register your venue" : "Sign in here")
        ])
      ])
    ])
  ]);
}

function SettingsView({ user, venueName, onUpdate, navLayout, onNavLayoutChange }: { user: any; venueName: string; onUpdate: () => void; navLayout: "top" | "left"; onNavLayoutChange: (layout: "top" | "left") => void }) {
  const [fullName, setFullName] = useState("");
  const [vName, setVName] = useState(venueName);
  const [businessType, setBusinessType] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [venueId, setVenueId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    const load = async () => {
      const data = await supaFetchVenueProfile(user.id) as any;
      if (data) {
        setFullName(data.full_name || "");
        setVName(data.venues?.name || "");
        setBusinessType(data.venues?.business_type || "");
        setVenueId(data.venues?.id || "");
      }
      setLoading(false);
    };
    load();
  }, [user.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await supaUpdateVenueProfile(user.id, venueId, {
        fullName,
        venueName: vName,
        businessType
      });
      alert("Settings saved successfully!");
      onUpdate();
    } catch (e: any) {
      alert("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword) {
      alert("Please enter a new password.");
      return;
    }
    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      alert("Password updated successfully!");
      setNewPassword("");
    } catch (e: any) {
      alert("Failed to update password: " + e.message);
    } finally {
      setUpdatingPassword(false);
    }
  };

  if (loading) return React.createElement("div", { className: "p-8 text-center text-gray-500 font-medium" }, "Loading settings...");

  return React.createElement("div", { className: "max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" }, [
    React.createElement("div", { key: "header", className: "mb-8 text-center" }, [
      React.createElement("h2", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Venue Settings"),
      React.createElement("p", { className: "text-gray-500 font-medium" }, "Manage your restaurant's profile and preferences"),
    ]),

    React.createElement("div", { key: "form-container", className: "bg-white/70 backdrop-blur-xl border border-white/40 shadow-xl rounded-[32px] overflow-hidden p-8 space-y-6" }, [
      // Individual Fields
      React.createElement("div", { key: "name-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Owner/Manager Name"),
        React.createElement("input", {
          value: fullName,
          onChange: (e: any) => setFullName(e.target.value),
          className: "w-full bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium",
          placeholder: "e.g. Gordon Ramsay"
        })
      ]),

      React.createElement("div", { key: "venue-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Restaurant Name"),
        React.createElement("input", {
          value: vName,
          onChange: (e: any) => setVName(e.target.value),
          className: "w-full bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium",
          placeholder: "e.g. The Lucky Fig"
        })
      ]),

      React.createElement("div", { key: "type-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Business Type"),
        React.createElement("select", {
          value: businessType,
          onChange: (e: any) => setBusinessType(e.target.value),
          className: "w-full bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium appearance-none",
        }, [
          React.createElement("option", { key: "opt1", value: "" }, "Select business type..."),
          React.createElement("option", { key: "opt2", value: "Fine Dining" }, "Fine Dining"),
          React.createElement("option", { key: "opt3", value: "Casual Dining" }, "Casual Dining"),
          React.createElement("option", { key: "opt4", value: "Quick Service" }, "Quick Service / Fast Food"),
          React.createElement("option", { key: "opt5", value: "Bakery / Cafe" }, "Bakery / Cafe"),
          React.createElement("option", { key: "opt6", value: "Bar / Pub" }, "Bar / Pub"),
          React.createElement("option", { key: "opt7", value: "Dark Kitchen" }, "Dark Kitchen"),
        ])
      ]),

      React.createElement("div", { key: "nav-layout" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Navigation Layout"),
        React.createElement("div", { className: "flex items-center gap-2 bg-gray-100/70 p-2 rounded-2xl" }, [
          React.createElement("button", {
            onClick: () => onNavLayoutChange("top"),
            className: `flex-1 px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${navLayout === "top" ? "bg-indigo-600 text-white shadow-md shadow-indigo-100" : "bg-white text-gray-500 hover:text-gray-700"}`
          }, "Top Bar"),
          React.createElement("button", {
            onClick: () => onNavLayoutChange("left"),
            className: `flex-1 px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${navLayout === "left" ? "bg-indigo-600 text-white shadow-md shadow-indigo-100" : "bg-white text-gray-500 hover:text-gray-700"}`
          }, "Left Sidebar")
        ])
      ]),

      React.createElement("div", { key: "email-field", className: "opacity-50 pointer-events-none" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Account Email"),
        React.createElement("input", {
          value: user.email,
          readOnly: true,
          className: "w-full bg-gray-100/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm font-medium",
        })
      ]),

      React.createElement("button", {
        key: "save-btn",
        onClick: handleSave,
        disabled: saving,
        className: "w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-black py-5 rounded-2xl shadow-xl shadow-indigo-100 transition-all active:scale-[0.98] mt-4 flex items-center justify-center gap-3 text-sm uppercase tracking-widest",
      }, saving ? "Saving Details..." : [
        React.createElement("span", { key: "icon" }, "💾"),
        "Save All Changes"
      ])
    ]),

    React.createElement("div", { key: "password-card", className: "bg-white/70 backdrop-blur-xl border border-white/40 shadow-xl rounded-[32px] p-8 space-y-6" }, [
      React.createElement("div", { key: "header" }, [
        React.createElement("h4", { className: "font-black text-gray-900 mb-1" }, "Security"),
        React.createElement("p", { className: "text-gray-500 text-sm font-medium" }, "Update your account password"),
      ]),
      React.createElement("div", { key: "pass-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "New Password"),
        React.createElement("div", { className: "flex gap-3" }, [
          React.createElement("input", {
            key: "input",
            type: "password",
            value: newPassword,
            onChange: (e: any) => setNewPassword(e.target.value),
            className: "flex-1 bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium",
            placeholder: "Minimum 6 characters"
          }),
          React.createElement("button", {
            key: "btn",
            onClick: handleUpdatePassword,
            disabled: updatingPassword || !newPassword,
            className: "bg-gray-900 hover:bg-black disabled:bg-gray-300 text-white font-bold px-6 rounded-2xl transition-all shadow-lg active:scale-[0.98] text-sm",
          }, updatingPassword ? "Updating..." : "Update")
        ])
      ])
    ]),

    React.createElement("div", { key: "billing-card", className: "bg-indigo-50 border border-indigo-100 rounded-[32px] p-8 flex items-center justify-between" }, [
      React.createElement("div", { key: "text" }, [
        React.createElement("h4", { className: "font-black text-indigo-900 mb-1" }, "SaaS Subscription"),
        React.createElement("p", { className: "text-indigo-700/70 text-sm font-medium" }, "You are currently on the Professional Plan"),
      ]),
      React.createElement("button", {
        key: "billing-btn",
        className: "bg-white text-indigo-600 font-bold px-6 py-3 rounded-xl shadow-sm hover:shadow-md transition-all text-sm",
        onClick: () => alert("Stripe Billing Integration coming soon!")
      }, "Manage Billing")
    ])
  ]);
}
// ------------------ Menu Intelligence View ------------------

function MenuIntelligenceView({ menuItems, onNavigate, onKpiExplain, onOpenUpload }: { menuItems: MenuItem[]; onNavigate: (v: any) => void; onKpiExplain?: any; onOpenUpload?: () => void }) {
  const [selectedRole, setSelectedRole] = useState<MenuRole | 'all'>('all');
  const [showUploadMenu, setShowUploadMenu] = useState(false);

  const safeItems = (menuItems && menuItems.length > 0) ? menuItems : generateMockMenu();
  const filteredItems = selectedRole === 'all'
    ? safeItems
    : safeItems.filter(i => i.menuRole === selectedRole);

  const topSeller = [...safeItems].sort((a, b) => b.ordersPerWeek - a.ordersPerWeek)[0];
  const highestMargin = [...safeItems].sort((a, b) => b.marginPercent - a.marginPercent)[0];
  const totalAvgMargin = safeItems.length > 0
    ? (safeItems.reduce((acc, i) => acc + i.marginPercent, 0) / safeItems.length).toFixed(1)
    : "0.0";

  return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500" }, [
    // Header
    React.createElement("div", {
      key: "header-main",
      className: "relative overflow-visible mb-6 rounded-[32px] border border-indigo-100/60 bg-white p-8 shadow-sm",
      style: {
        background: "linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(255,255,255,0.98) 40%, rgba(14,165,233,0.08) 100%)"
      }
    }, [
      React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
      React.createElement("div", { className: "flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6" }, [
        React.createElement("div", {}, [
          React.createElement("h2", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Menu Intelligence"),
          React.createElement("p", { className: "text-gray-500 font-medium max-w-2xl leading-relaxed" },
            "Profit engineering and menu performance analysis."
          )
        ]),
        React.createElement("div", { className: "relative" }, [
          React.createElement("button", {
            key: "upload-btn",
            onClick: () => setShowUploadMenu(!showUploadMenu),
            className: "group flex items-center gap-2 bg-indigo-600 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100/50 transition-all hover:scale-105 active:scale-95"
          }, [
            React.createElement("span", { key: "icon", className: "text-base group-hover:rotate-90 transition-transform duration-300" }, "+"),
            "Upload"
          ]),
          showUploadMenu && React.createElement("div", {
            className: "absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2"
          }, [
            React.createElement("button", {
              className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2",
              onClick: () => {
                if (onOpenUpload) onOpenUpload();
                else onNavigate("upload");
                setShowUploadMenu(false);
              }
            }, "Upload Document"),
            React.createElement("button", {
              className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2 border-t border-gray-50",
              onClick: () => { onNavigate("manual-menu"); setShowUploadMenu(false); }
            }, "Data Form")
          ])
        ])
      ])
    ]),

    // AI Insight Grid
    React.createElement("div", { key: "ai-insights", className: "grid grid-cols-1 lg:grid-cols-3 gap-6" }, [
      React.createElement("div", { className: "relative bg-white border border-slate-200/80 p-6 rounded-2xl shadow-sm" }, [
        React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-400 to-emerald-300" }),
        React.createElement("div", { className: "flex justify-between items-start mb-4" }, [
          React.createElement("div", {}, [
            React.createElement("h4", { className: "text-slate-400 font-black uppercase text-[10px] tracking-widest mb-1" }, "Top Profit Driver"),
            React.createElement("p", { className: "text-lg font-black text-slate-900" }, highestMargin.name)
          ]),
          React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full" }, "High Margin")
        ]),
        React.createElement("div", { className: "space-y-2" }, [
          React.createElement("div", { className: "flex justify-between text-xs" }, [
            React.createElement("span", { className: "text-slate-500" }, "Margin:"),
            React.createElement("span", { className: "font-bold text-slate-900" }, `${highestMargin.marginPercent.toFixed(0)}%`)
          ]),
          React.createElement("div", { className: "flex justify-between text-xs" }, [
            React.createElement("span", { className: "text-slate-500" }, "Weekly Orders:"),
            React.createElement("span", { className: "font-bold text-indigo-600" }, `${highestMargin.ordersPerWeek}`)
          ])
        ])
      ]),
      React.createElement("div", { className: "relative bg-white border border-slate-200/80 p-6 rounded-2xl shadow-sm" }, [
        React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 to-sky-400" }),
        React.createElement("div", { className: "flex justify-between items-start mb-4" }, [
          React.createElement("div", {}, [
            React.createElement("h4", { className: "text-slate-400 font-black uppercase text-[10px] tracking-widest mb-1" }, "Highest Contributor"),
            React.createElement("p", { className: "text-lg font-black text-slate-900" }, topSeller.name)
          ]),
          React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full" }, topSeller.category)
        ]),
        React.createElement("div", { className: "space-y-2" }, [
          React.createElement("div", { className: "flex justify-between text-xs" }, [
            React.createElement("span", { className: "text-slate-500" }, "Rev. Contribution:"),
            React.createElement("span", { className: "font-bold text-indigo-600" }, `${topSeller.revenueContribution.toFixed(1)}%`)
          ]),
          React.createElement("div", { className: "flex justify-between text-xs" }, [
            React.createElement("span", { className: "text-slate-500" }, "Margin:"),
            React.createElement("span", { className: "font-bold text-slate-900" }, `${topSeller.marginPercent.toFixed(0)}%`)
          ])
        ])
      ]),
      React.createElement("div", { className: "relative bg-white border border-slate-200/80 p-6 rounded-2xl shadow-sm" }, [
        React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-sky-400" }),
        React.createElement("div", { className: "flex justify-between items-start mb-4" }, [
          React.createElement("div", {}, [
            React.createElement("h4", { className: "text-slate-400 font-black uppercase text-[10px] tracking-widest mb-1" }, "Menu Avg Margin"),
            React.createElement("p", { className: "text-lg font-black text-slate-900" }, `${totalAvgMargin}%`)
          ]),
          React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full" }, "Healthy")
        ]),
        React.createElement("div", { className: "h-2 w-full bg-slate-100 rounded-full overflow-hidden mt-4" },
          React.createElement("div", { className: "h-full bg-emerald-500", style: { width: `${totalAvgMargin}%` } })
        ),
        React.createElement("p", { className: "text-[10px] text-emerald-600 font-bold mt-2" }, "↑ 2.4% vs last month")
      ])
    ]),

    // Performance Drivers (restored deep-dive - only in All tab)
    selectedRole === 'all' && React.createElement("div", { key: "menu-drivers" }, [
      renderMenuIntelligence(onKpiExplain, safeItems)
    ]),

    // Filters Row (Below insight tiles, above table)
    React.createElement("div", { key: "filters", className: "inline-flex flex-wrap gap-2 p-1.5 bg-slate-100/70 border border-slate-200 rounded-2xl" },
      ['all', 'Core Seller', 'Opportunity', 'Underperforming'].map(role =>
        React.createElement("button", {
          key: role,
          onClick: () => setSelectedRole(role as any),
          className: `px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${selectedRole === role ? 'bg-white text-slate-900 border border-slate-200 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`
        }, role)
      )
    ),

    // Item Table
    React.createElement("div", { key: "table", className: "bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-sm" }, [
      React.createElement("table", { className: "w-full text-left" }, [
        React.createElement("thead", { className: "bg-slate-50 border-b border-slate-200" },
          React.createElement("tr", {},
            ['Item', 'Role', 'Price', 'Margin', 'Weekly Vol.', 'AI Recommendation'].map(h =>
              React.createElement("th", { key: h, className: "px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest" }, h)
            )
          )
        ),
        React.createElement("tbody", { className: "divide-y divide-slate-100" },
          filteredItems.map(item =>
            React.createElement("tr", { key: item.id, className: "hover:bg-slate-50/60 transition-colors" }, [
              React.createElement("td", { className: "px-6 py-4" }, [
                React.createElement("p", { className: "font-bold text-gray-900 text-sm" }, item.name),
                React.createElement("p", { className: "text-[10px] text-slate-400" }, item.category)
              ]),
              React.createElement("td", { className: "px-6 py-4" },
                React.createElement("span", {
                  className: `px-2 py-1 rounded text-[9px] font-black uppercase tracking-tighter ${item.menuRole === 'Core Seller' ? 'bg-green-100 text-green-700' :
                    item.menuRole === 'Opportunity' ? 'bg-blue-100 text-indigo-700' :
                      item.menuRole === 'Underperforming' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                    }`
                }, item.menuRole)
              ),
              React.createElement("td", { className: "px-6 py-4 text-sm font-medium text-gray-600" }, `£${item.price.toFixed(2)}`),
              React.createElement("td", { className: "px-6 py-4" }, [
                React.createElement("p", { className: "text-sm font-black text-gray-800" }, `${item.marginPercent.toFixed(0)}%`),
                React.createElement("p", { className: "text-[10px] text-gray-400" }, `£${item.margin.toFixed(2)}/unit`)
              ]),
              React.createElement("td", { className: "px-6 py-4 text-sm font-bold" }, [
                React.createElement("span", { className: "text-gray-900" }, item.ordersPerWeek),
                item.trend === 'up' && React.createElement("span", { className: "text-green-500 ml-1" }, "↑"),
                item.trend === 'down' && React.createElement("span", { className: "text-red-500 ml-1" }, "↓")
              ]),
              React.createElement("td", { className: "px-6 py-4" }, [
                React.createElement("p", { className: "text-xs font-bold text-indigo-600" }, item.recommendation),
                React.createElement("p", { className: "text-[10px] text-gray-400" }, item.estimatedImpact)
              ])
            ])
          )
        )
      ])
    ])
  ]);
}

// ------------------ Sales Demand View ------------------

function SalesDemandView({ onNavigate, onKpiExplain, manualData, onOpenUpload }: { onNavigate: (v: any) => void; onKpiExplain: any; manualData?: any; onOpenUpload?: () => void }) {
  const [activeTab, setActiveTab] = useState<'sales' | 'demand' | 'staffing'>('sales');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredPointId, setHoveredPointId] = useState<number | null>(null);
  const [hoveredCoverageHour, setHoveredCoverageHour] = useState<string | null>(null);
  const [showUploadMenu, setShowUploadMenu] = useState(false);

  const merged = useMemo(() => normalizeManualSalesData(manualData), [manualData]);
  const data = useMemo(() => ({ weekly: merged.demand.weekly }), [merged]);

  useEffect(() => {
    if (selectedIndex >= data.weekly.length) setSelectedIndex(0);
  }, [selectedIndex, data.weekly.length]);

  const selectedDay = data.weekly[selectedIndex] || data.weekly[0];
  const salesToday = merged.sales.today;
  const salesHealth = merged.sales.health;
  const salesAov = merged.sales.aov;
  const salesMix = merged.sales.salesMix;
  const revenueData = merged.sales.revenueVsCost.revenue;
  const costData = merged.sales.revenueVsCost.cost;
  const costLabels = merged.sales.revenueVsCost.labels;
  const heatmapDays = merged.demand.heatmap.days;
  const heatmapHours = merged.demand.heatmap.hours;
  const heatmapData = merged.demand.heatmap.data;
  type RevenueProgressPoint = { id: number; time: string; actual?: number; expected?: number };
  const progressPoints: RevenueProgressPoint[] = (merged.sales.revenueProgress.points || []).map((p: any, idx: number): RevenueProgressPoint => ({
    id: idx,
    time: typeof p?.time === "string" && p.time ? p.time : `T${idx + 1}`,
    actual: p?.actual !== undefined && p?.actual !== null ? Number(p.actual) : undefined,
    expected: p?.expected !== undefined && p?.expected !== null ? Number(p.expected) : undefined
  }));
  const maxProgressValue = Math.max(...progressPoints.map((p) => Math.max(p.actual || 0, p.expected || 0)), 1);
  const roundedMaxProgress = Math.ceil(maxProgressValue / 1000) * 1000;
  const chartTop = 20;
  const chartBottom = 170;
  const chartHeight = chartBottom - chartTop;
  const chartLeft = 70;
  const chartRight = 680;
  const chartWidth = chartRight - chartLeft;
  const pointSpacing = progressPoints.length > 1 ? (chartWidth / (progressPoints.length - 1)) : 0;
  const yForValue = (value: number) => chartBottom - ((value / roundedMaxProgress) * chartHeight);
  const paceDiff = salesToday.revenueSoFar - salesToday.expectedByNow;
  const paceNote = salesToday.note || (paceDiff >= 0
    ? `You're ahead of pace by £${Math.abs(Math.round(paceDiff)).toLocaleString()}. If this continues, you'll finish £${Math.round(paceDiff * 2).toLocaleString()} above target.`
    : `You're behind pace by £${Math.abs(Math.round(paceDiff)).toLocaleString()}. Focus on boosting throughput to recover the gap.`);
  const transactionsDeltaLabel = `${salesHealth.transactionsDelta >= 0 ? "+" : ""}${salesHealth.transactionsDelta}% vs avg`;
  const paceLabel = salesHealth.paceLabel || (salesHealth.pacePercent >= 110 ? "Strong" : salesHealth.pacePercent >= 100 ? "On Track" : "Behind");
  const aovStatus = salesAov.status || (salesAov.value > salesAov.rangeHigh ? "High" : salesAov.value < salesAov.rangeLow ? "Low" : "Normal");
  const aovRangeSpan = Math.max(1, salesAov.rangeHigh - salesAov.rangeLow);
  const aovPosition = Math.min(100, Math.max(0, ((salesAov.value - salesAov.rangeLow) / aovRangeSpan) * 100));
  const staffingCoverage = merged.staffing.coverage;
  const maxStaff = Math.max(...staffingCoverage.map((s: any) => Number(s.staff || 0)), 6);
  const staffingRota = merged.staffing.rota;
  const staffingCost = merged.staffing.costVsSales.staffingCost;
  const staffingExpectedRevenue = merged.staffing.costVsSales.expectedRevenue;
  const staffingPercent = merged.staffing.costVsSales.staffCostPercent || Number(((staffingCost / Math.max(1, staffingExpectedRevenue)) * 100).toFixed(1));
  const staffingRangeLow = merged.staffing.costVsSales.rangeLow;
  const staffingRangeHigh = merged.staffing.costVsSales.rangeHigh;
  const staffingNote = merged.staffing.costVsSales.statusNote || (staffingPercent >= staffingRangeHigh ? "Staffing costs are above the preferred range. Consider optimizing peak coverage." : "Your staffing costs are within the healthy range. You're efficiently staffed for today's expected demand.");
  const totalStaffCost = merged.staffing.totalStaffingCost || staffingRota.reduce((acc: number, cur: any) => acc + Number(cur.cost || 0), 0);
  const peakHours = merged.demand.peakHours.map((item: any) => {
    const intensity = Number(item.intensity ?? 0);
    const level = item.level || (intensity >= 80 ? "Peak" : intensity >= 60 ? "High" : intensity >= 45 ? "Med" : "Low");
    const color = item.color || (level === "Peak" ? "bg-red-500" : level === "High" ? "bg-orange-500" : level === "Med" ? "bg-yellow-500" : "bg-green-500");
    return {
      ...item,
      intensity,
      level,
      color
    };
  });
  return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500" }, [
    // Header
    React.createElement("div", {
      key: "header",
      className: "relative overflow-visible mb-6 rounded-[32px] border border-indigo-100/60 bg-white p-8 shadow-sm",
      style: {
        background: "linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(255,255,255,0.98) 40%, rgba(14,165,233,0.08) 100%)"
      }
    }, [
      React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
      React.createElement("div", { className: "flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6" }, [
        React.createElement("div", {}, [
          React.createElement("h2", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Sales, Demand & Staffing"),
          React.createElement("p", { className: "text-gray-500 font-medium max-w-2xl leading-relaxed" },
            "AI-driven deep dive into your business metrics."
          )
        ]),
        React.createElement("div", { className: "relative" }, [
          React.createElement("button", {
            key: "upload-btn",
            onClick: () => setShowUploadMenu(!showUploadMenu),
            className: "group flex items-center gap-2 bg-indigo-600 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100/50 transition-all hover:scale-105 active:scale-95"
          }, [
            React.createElement("span", { key: "icon", className: "text-base group-hover:rotate-90 transition-transform duration-300" }, "+"),
            "Upload"
          ]),
          showUploadMenu && React.createElement("div", {
            className: "absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2"
          }, [
            React.createElement("button", {
              className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2",
              onClick: () => {
                if (onOpenUpload) onOpenUpload();
                else onNavigate("upload");
                setShowUploadMenu(false);
              }
            }, "Upload Document"),
            React.createElement("button", {
              className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2 border-t border-gray-50",
              onClick: () => { onNavigate("manual-sales"); setShowUploadMenu(false); }
            }, "Data Form")
          ])
        ])
      ])
    ]),

    // Sub-Tab Navigation
    React.createElement("div", {
      key: "sub-tabs",
      className: "inline-flex flex-wrap gap-2 p-1.5 bg-slate-100/70 border border-slate-200 rounded-2xl"
    }, [
      (["sales", "demand", "staffing"] as const).map(tab =>
        React.createElement("button", {
          key: tab,
          onClick: () => setActiveTab(tab),
          className: `px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-white text-slate-900 border border-slate-200 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`
        }, tab)
      )
    ]),

    // SALES TAB
    activeTab === "sales" && React.createElement("div", { key: "sales-tab", className: "space-y-5 animate-in slide-in-from-bottom-4" }, [
      // Top Row: Today at a Glance + Sales Health
      React.createElement("div", { key: "top-row", className: "grid grid-cols-1 lg:grid-cols-2 gap-5" }, [
        // Today at a Glance Tile
        React.createElement("div", { key: "today-glance", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm flex flex-col h-full" }, [
          React.createElement("h3", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Sales"),
          React.createElement("div", { className: "flex-1 flex items-center" }, [
            React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-3 gap-4 w-full" }, [
              // Actual Revenue
              React.createElement("div", { key: "actual" }, [
                React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1.5" }, "Revenue so far"),
                React.createElement("p", { className: "text-2xl font-bold text-gray-900" }, `£${salesToday.revenueSoFar.toLocaleString()}`)
              ]),
              // Expected Revenue
              React.createElement("div", { key: "expected" }, [
                React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1.5" }, "Expected by now"),
                React.createElement("p", { className: "text-2xl font-bold text-gray-600" }, `£${salesToday.expectedByNow.toLocaleString()}`)
              ]),
              // Target Revenue
              React.createElement("div", { key: "target" }, [
                React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1.5" }, "Full-day target"),
                React.createElement("p", { className: "text-2xl font-bold text-gray-500" }, `£${salesToday.fullDayTarget.toLocaleString()}`)
              ])
            ])
          ]),
          // Status Text
          React.createElement("div", { className: "bg-green-50 border border-green-100 rounded-lg p-3 mt-4" }, [
            React.createElement("p", { className: "text-xs text-green-800 font-medium leading-relaxed" }, paceNote)
          ])
        ]),

        // Sales Health Tile
        React.createElement("div", { key: "sales-health", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
          React.createElement("h3", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Sales Health"),
          React.createElement("div", { className: "space-y-4" }, [
            // Transactions
            React.createElement("div", { key: "transactions", className: "flex items-center justify-between" }, [
              React.createElement("div", {}, [
                React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1" }, "Transactions today"),
                React.createElement("p", { className: "text-xl font-bold text-gray-900" }, salesHealth.transactions)
              ]),
              React.createElement("div", { className: "bg-green-50 border border-green-100 rounded-lg px-3 py-1.5" }, [
                React.createElement("p", { className: "text-xs text-green-700 font-semibold" }, transactionsDeltaLabel)
              ])
            ]),
            // Pace
            React.createElement("div", { key: "pace", className: "flex items-center justify-between" }, [
              React.createElement("div", {}, [
                React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1" }, "Current pace"),
                React.createElement("p", { className: "text-xl font-bold text-gray-900" }, `${salesHealth.pacePercent}%`)
              ]),
              React.createElement("div", { className: "bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5" }, [
                React.createElement("p", { className: "text-xs text-blue-700 font-semibold" }, paceLabel)
              ])
            ]),
            // Peak Hour
            React.createElement("div", { key: "peak", className: "flex items-center justify-between pt-3 border-t border-gray-100" }, [
              React.createElement("div", {}, [
                React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1" }, "Busiest hour"),
                React.createElement("p", { className: "text-sm font-bold text-gray-700" }, salesHealth.busiestHour)
              ]),
              React.createElement("div", {}, [
                React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1 text-right" }, "Revenue"),
                React.createElement("p", { className: "text-sm font-bold text-gray-700" }, `£${salesHealth.busiestHourRevenue.toLocaleString()}`)
              ])
            ])
          ])
        ])
      ]),

      // AOV + Companion KPI row
      React.createElement("div", { key: "aov-row", className: "grid grid-cols-1 lg:grid-cols-2 gap-5" }, [
        React.createElement("div", { key: "aov-tile", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
          React.createElement("h3", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Average Order Value"),
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-6" }, [
            // Left side - AOV value
            React.createElement("div", { key: "aov-value" }, [
              React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-2" }, "Today's average"),
              React.createElement("p", { className: "text-3xl font-bold text-gray-900 mb-3" }, `£${salesAov.value.toFixed(2)}`),
              React.createElement("div", { className: "bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 inline-block" }, [
                React.createElement("p", { className: "text-xs text-blue-800 font-semibold" }, aovStatus)
              ])
            ]),
            // Middle - Range
            React.createElement("div", { key: "aov-range", className: "flex flex-col justify-center" }, [
              React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-2" }, "Typical range"),
              React.createElement("p", { className: "text-xl font-bold text-gray-700 mb-2" }, `£${salesAov.rangeLow} - £${salesAov.rangeHigh}`),
              React.createElement("div", { className: "w-full bg-gray-100 rounded-full h-2 mt-1" }, [
                React.createElement("div", { className: "bg-blue-500 h-2 rounded-full", style: { width: `${aovPosition}%` } })
              ])
            ]),
            // Right side - Explanation
            React.createElement("div", { key: "aov-explanation", className: "flex flex-col justify-center" }, [
              React.createElement("p", { className: "text-xs text-gray-600 leading-relaxed" }, salesAov.note || "Your average order value is within the normal range for your venue type. Orders below £15 are considered low, above £23 are high.")
            ])
          ])
        ]),
        React.createElement("div", { key: "covers-tile", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
          React.createElement("h3", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Sales Mix"),
          React.createElement("div", { className: "space-y-4" }, [
            React.createElement("div", {}, [
              React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-2" }, "Net sales today"),
              React.createElement("p", { className: "text-3xl font-bold text-gray-900" }, `£${salesMix.netSales.toLocaleString()}`)
            ]),
            React.createElement("div", { className: "flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2" }, [
              React.createElement("span", { className: "text-xs font-semibold text-emerald-700" }, "Promo sales"),
              React.createElement("span", { className: "text-sm font-black text-emerald-700" }, `£${salesMix.promoSales.toLocaleString()}`)
            ]),
            React.createElement("div", { className: "flex items-center justify-between text-xs text-gray-600" }, [
              React.createElement("span", {}, "Refunds & comps"),
              React.createElement("span", { className: "font-bold text-gray-900" }, `£${salesMix.refundsComps.toLocaleString()}`)
            ])
          ])
        ])
      ]),

      React.createElement("div", { key: "revenue-cost-row", className: "grid grid-cols-1 lg:grid-cols-2 gap-5" }, [
        // Revenue Comparison Chart
        React.createElement("div", { key: "revenue-chart", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm max-h-[350px] flex flex-col" }, [
          React.createElement("div", { className: "flex items-center justify-between mb-5" }, [
            React.createElement("h3", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider" }, "Revenue Progress Today"),
            React.createElement("div", { className: "flex gap-4 text-xs" }, [
              React.createElement("div", { key: "actual-value", className: "flex items-center gap-2" }, [
                React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-green-500" }),
                React.createElement("span", { className: "text-gray-500 font-medium" }, "Actual:"),
                React.createElement("span", { className: "text-gray-900 font-bold" }, `£${salesToday.revenueSoFar.toLocaleString()}`)
              ]),
              React.createElement("div", { key: "expected-value", className: "flex items-center gap-2" }, [
                React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-gray-400" }),
                React.createElement("span", { className: "text-gray-500 font-medium" }, "Expected:"),
                React.createElement("span", { className: "text-gray-900 font-bold" }, `£${salesToday.expectedByNow.toLocaleString()}`)
              ])
            ])
          ]),
          React.createElement("div", { className: "relative flex-1 min-h-[220px]" }, [
            React.createElement("svg", {
              width: "100%",
              height: "100%",
              viewBox: "0 0 700 200",
              className: "overflow-visible",
              onMouseLeave: () => setHoveredPointId(null)
            }, [
              // Grid lines
              ...Array.from({ length: 6 }).map((_, i) => {
                const y = chartTop + (i * (chartHeight / 5));
                const value = roundedMaxProgress - (i * (roundedMaxProgress / 5));
                return React.createElement("g", { key: `grid-${i}` }, [
                  React.createElement("line", {
                    key: "l",
                    x1: chartLeft, y1: y, x2: chartRight, y2: y,
                    stroke: "#f3f4f6", strokeWidth: 1
                  }),
                  React.createElement("text", {
                    key: "t",
                    x: chartLeft - 5, y: y + 4, textAnchor: "end",
                    fontSize: 10, fill: "#9ca3af", fontWeight: 500
                  }, `£${(value / 1000).toFixed(1)}k`)
                ]);
              }),

              // Data Points for Logic
              (() => {
                const points = progressPoints.map((p, idx) => {
                  const x = chartLeft + (idx * pointSpacing);
                  const expectedVal = p.expected ?? p.actual ?? 0;
                  const actualVal = p.actual;
                  return {
                    ...p,
                    x,
                    exp: expectedVal,
                    act: actualVal,
                    expY: yForValue(expectedVal),
                    actY: actualVal !== undefined ? yForValue(actualVal) : undefined
                  };
                });

                const hovered = points.find((p) => p.id === hoveredPointId);

                return [
                  // Expected line
                  React.createElement("path", {
                    key: "expected-line",
                    d: `M ${points.map((p) => `${p.x},${p.expY}`).join(" L ")}`,
                    fill: "none", stroke: "#d1d5db", strokeWidth: 2.5, strokeDasharray: "5,3"
                  }),

                  // Actual line
                  React.createElement("path", {
                    key: "actual-line",
                    d: `M ${points.filter((p) => p.actY !== undefined).map((p) => `${p.x},${p.actY}`).join(" L ")}`,
                    fill: "none", stroke: "#10b981", strokeWidth: 3, strokeLinecap: "round"
                  }),

                  // Vertical Guide Line on Hover
                  hovered && React.createElement("line", {
                    key: "guide-y",
                    x1: hovered.x, y1: chartTop, x2: hovered.x, y2: chartBottom,
                    stroke: "#10b98133", strokeWidth: 1, strokeDasharray: "3,3"
                  }),

                  // Actual points
                  ...points.filter((p) => p.actY !== undefined).map((p) =>
                    React.createElement("circle", {
                      key: `actual-point-${p.id}`,
                      cx: p.x, cy: p.actY,
                      r: hoveredPointId === p.id ? 7 : 5,
                      fill: "#10b981", stroke: "#fff", strokeWidth: 2,
                      className: "transition-all duration-200"
                    })
                  ),

                  // Hover Interaction Areas (Vertical strips)
                  ...points.map((p) =>
                    React.createElement("rect", {
                      key: `interaction-${p.id}`,
                      x: p.x - 40, y: 0, width: 80, height: 180,
                      fill: "transparent",
                      onMouseEnter: () => setHoveredPointId(p.id),
                      className: "cursor-pointer"
                    })
                  ),

                  // Tooltip
                  hovered && React.createElement("g", { key: "tooltip", className: "pointer-events-none" }, [
                    React.createElement("rect", {
                      x: hovered.x + (hovered.x > 500 ? -130 : 15),
                      y: ((hovered.actY !== undefined ? hovered.actY : hovered.expY) || chartTop) - 30,
                      width: 110, height: 60,
                      rx: 8, fill: "#1f2937", opacity: 0.95
                    }),
                    React.createElement("text", {
                      x: hovered.x + (hovered.x > 500 ? -120 : 25),
                      y: ((hovered.actY !== undefined ? hovered.actY : hovered.expY) || chartTop) - 12,
                      fontSize: 10, fontWeight: "bold", fill: "#9ca3af"
                    }, hovered.time),
                    hovered.act !== undefined && React.createElement("text", {
                      x: hovered.x + (hovered.x > 500 ? -120 : 25),
                      y: ((hovered.actY !== undefined ? hovered.actY : hovered.expY) || chartTop) + 6,
                      fontSize: 11, fontWeight: "bold", fill: "#10b981"
                    }, `Actual: £${Number(hovered.act).toLocaleString()}`),
                    React.createElement("text", {
                      x: hovered.x + (hovered.x > 500 ? -120 : 25),
                      y: ((hovered.actY !== undefined ? hovered.actY : hovered.expY) || chartTop) + 21,
                      fontSize: 11, fontWeight: "bold", fill: "#d1d5db"
                    }, `Exp: £${Number(hovered.exp).toLocaleString()}`)
                  ]),

                  // Time labels
                  ...points.map(p =>
                    React.createElement("text", {
                      key: `time-${p.id}`,
                      x: p.x, y: 195,
                      textAnchor: "middle", fontSize: 10,
                      fill: hoveredPointId === p.id ? "#10b981" : "#6b7280",
                      fontWeight: hoveredPointId === p.id ? 800 : 500,
                      className: "transition-all duration-200"
                    }, p.time)
                  )
                ];
              })()
            ])
          ]),
          // Legend
          React.createElement("div", { className: "flex gap-5 mt-4 justify-center" }, [
            React.createElement("div", { key: "actual-legend", className: "flex items-center gap-2" }, [
              React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-green-500" }),
              React.createElement("span", { className: "text-xs text-gray-600 font-medium" }, "Actual cumulative")
            ]),
            React.createElement("div", { key: "expected-legend", className: "flex items-center gap-2" }, [
              React.createElement("div", { className: "w-6 h-0.5 bg-gray-400", style: { borderTop: "2px dashed #d1d5db" } }),
              React.createElement("span", { className: "text-xs text-gray-600 font-medium" }, "Expected cumulative")
            ])
          ])
        ]),

        // Revenue vs Cost (restored deep-dive)
        React.createElement("div", { key: "cost-pressure", className: "min-h-[480px] flex w-full" }, [
          renderCostPressure(revenueData, costData, costLabels, onKpiExplain)
        ])
      ]),

    ]),

    // DEMAND TAB
    activeTab === "demand" && React.createElement("div", { key: "demand-tab", className: "space-y-8 animate-in slide-in-from-bottom-4" }, [
      // 7-Day Forecast Strip
      React.createElement("div", { key: "strip", className: "flex gap-3 overflow-x-auto pb-4 no-scrollbar" },
        data.weekly.map((day: any, idx: number) =>
          React.createElement("button", {
            key: day.date,
            onClick: () => setSelectedIndex(idx),
            className: `flex-1 min-w-[140px] p-4 rounded-2xl border transition-all text-left ${selectedIndex === idx ? 'bg-indigo-600 border-indigo-600 shadow-xl shadow-indigo-100 -translate-y-1' : 'bg-white border-slate-200/80 hover:border-indigo-200 hover:shadow-sm'}`
          }, [
            React.createElement("div", { className: "flex justify-between items-start mb-3" }, [
              React.createElement("span", { className: `font-black text-sm ${selectedIndex === idx ? 'text-white' : 'text-indigo-600'}` }, day.dayName),
              React.createElement("span", { className: "text-xl" },
                day.weather.condition === 'Sunny' ? '☀️' : day.weather.condition === 'Rain' ? '🌧️' : '☁️'
              )
            ]),
            React.createElement("p", { className: `text-lg font-black ${selectedIndex === idx ? 'text-white' : 'text-gray-900'}` }, `£${(day.predictedRevenue / 1000).toFixed(1)}k`),
            React.createElement("p", { className: `text-[10px] font-bold ${selectedIndex === idx ? 'text-indigo-200' : 'text-gray-400'}` }, `${day.predictedCovers} covers`)
          ])
        )
      ),
      // Demand Charts Row (Hourly + Heatmap)
      React.createElement("div", { key: "demand-charts-row", className: "grid grid-cols-1 lg:grid-cols-2 gap-5" }, [
        (() => {
          const maxRevenue = Math.max(...selectedDay.hourlyTrend.map((h: any) => h.projectedRevenue));
          const roundedMax = Math.max(100, Math.ceil(maxRevenue / 100) * 100); // Round up to nearest 100 for clean axis

          return React.createElement("div", { key: "hourly", className: "relative overflow-hidden bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm h-[350px] max-h-[350px] flex flex-col" }, [
            React.createElement("div", { className: "flex items-center justify-between mb-8" }, [
              React.createElement("h4", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider" }, `Hourly Revenue Projection: ${selectedDay.dayName}`),
              React.createElement("div", { className: "flex gap-3 text-[10px]" }, [
                React.createElement("div", { key: "low", className: "flex items-center gap-1.5" }, [
                  React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-green-500" }),
                  React.createElement("span", { className: "text-gray-600" }, "Low")
                ]),
                React.createElement("div", { key: "medium", className: "flex items-center gap-1.5" }, [
                  React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-yellow-500" }),
                  React.createElement("span", { className: "text-gray-600" }, "Medium")
                ]),
                React.createElement("div", { key: "high", className: "flex items-center gap-1.5" }, [
                  React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-orange-500" }),
                  React.createElement("span", { className: "text-gray-600" }, "High")
                ]),
                React.createElement("div", { key: "peak", className: "flex items-center gap-1.5" }, [
                  React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-red-500" }),
                  React.createElement("span", { className: "text-gray-600" }, "Peak")
                ])
              ])
            ]),
            React.createElement("div", { className: "relative pl-12 flex-1 flex flex-col" }, [
              // Y-axis Labels (Dynamic)
              React.createElement("div", { className: "absolute left-0 top-0 bottom-0 w-10 flex flex-col justify-between text-[10px] font-bold text-gray-400 pb-0" }, [
                React.createElement("span", { key: "label-100" }, `£${roundedMax}`),
                React.createElement("span", { key: "label-75" }, `£${(roundedMax * 0.75).toFixed(0)}`),
                React.createElement("span", { key: "label-50" }, `£${(roundedMax * 0.5).toFixed(0)}`),
                React.createElement("span", { key: "label-25" }, `£${(roundedMax * 0.25).toFixed(0)}`),
                React.createElement("span", { key: "label-0" }, "£0")
              ]),

              // Grid lines
              React.createElement("div", { className: "absolute inset-0 pointer-events-none" },
                [100, 75, 50, 25, 0].map(level =>
                  React.createElement("div", {
                    key: `grid-${level}`,
                    className: "absolute w-full border-t border-gray-100",
                    style: { bottom: `${level}%` }
                  })
                )
              ),

              // Peak period labels above bars
              React.createElement("div", { className: "absolute -top-6 left-0 right-0 flex justify-between px-2" },
                selectedDay.hourlyTrend.map((h: any, i: number) => {
                  const isPeak = h.projectedRevenue >= maxRevenue * 0.8;
                  return isPeak ? React.createElement("div", {
                    key: `peak-${i}`,
                    className: "flex-1 flex justify-center"
                  }, [
                    React.createElement("span", {
                      className: "bg-red-100 text-red-700 text-[8px] font-bold px-2 py-0.5 rounded-full border border-red-200 shadow-sm"
                    }, "PEAK")
                  ]) : React.createElement("div", { key: `peak-${i}`, className: "flex-1" });
                })
              ),
              // Bar chart
              React.createElement("div", { className: "flex-1 min-h-[140px] flex items-end justify-between gap-2 px-2 relative z-0" },
                selectedDay.hourlyTrend.map((h: any, i: number) => {
                  const barHeight = (h.projectedRevenue / roundedMax) * 100;

                  // Determine color based on revenue relative to max
                  const getColorClass = (rev: number) => {
                    if (rev >= maxRevenue * 0.8) return "from-red-600 to-red-400 shadow-red-100";
                    if (rev >= maxRevenue * 0.6) return "from-orange-600 to-orange-400 shadow-orange-100";
                    if (rev >= maxRevenue * 0.4) return "from-yellow-600 to-yellow-400 shadow-yellow-100";
                    return "from-green-600 to-green-400 shadow-green-100";
                  };

                  const getDemandLabel = (rev: number) => {
                    if (rev >= maxRevenue * 0.8) return "Peak";
                    if (rev >= maxRevenue * 0.6) return "High";
                    if (rev >= maxRevenue * 0.4) return "Medium";
                    return "Low";
                  };

                  return React.createElement("div", {
                    key: i,
                    className: `flex-1 bg-gradient-to-t ${getColorClass(h.projectedRevenue)} rounded-t-lg opacity-80 hover:opacity-100 transition-all cursor-pointer relative group shadow-sm hover:shadow-md`,
                    style: { height: `${barHeight}%` }
                  }, [
                    React.createElement("div", { className: "absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 shadow-xl" }, [
                      React.createElement("div", { className: "font-bold text-gray-300 mb-0.5" }, h.hour),
                      React.createElement("div", { className: "text-lg font-black text-white" }, `£${h.projectedRevenue.toFixed(0)}`),
                      React.createElement("div", { className: "text-[9px] text-gray-400 font-medium uppercase tracking-wider" }, `${getDemandLabel(h.projectedRevenue)} demand`)
                    ])
                  ]);
                })
              )
            ]),
            React.createElement("div", { className: "flex justify-between mt-3 px-2 pl-14" },
              selectedDay.hourlyTrend.map((h: any) => React.createElement("span", { key: h.hour, className: "text-[9px] font-black text-gray-400 uppercase tracking-tighter" }, h.hour))
            )
          ]);
        })(),
        React.createElement("div", { key: "traffic-heatmap", className: "h-[350px] max-h-[350px] flex w-full" }, [
          renderTrafficHeatmap(heatmapDays, heatmapHours, heatmapData, onKpiExplain)
        ])
      ]),

      // DEMAND FORECAST SECTION (No title, compact 2-column layout)
      React.createElement("div", { key: "demand-forecast-section", className: "grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6" }, [
        // 1. Rolling 7-Day Forecast
        React.createElement("div", { key: "7day-forecast", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm h-[350px] max-h-[350px] flex flex-col" }, [
          React.createElement("h4", { className: "text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3" }, "Next 7 Days"),
          React.createElement("div", { className: "overflow-x-auto flex-1" }, [
            React.createElement("table", { className: "w-full" }, [
              // Header
              React.createElement("thead", { className: "border-b border-gray-200" },
                React.createElement("tr", {}, [
                  React.createElement("th", { className: "text-left py-2 px-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider" }, "Day"),
                  React.createElement("th", { className: "text-right py-2 px-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider" }, "Revenue"),
                  React.createElement("th", { className: "text-right py-2 px-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider" }, "Covers"),
                  React.createElement("th", { className: "text-center py-2 px-2 text-[9px] font-bold text-gray-400 uppercase tracking-wider" }, "Level")
                ])
              ),
              // Body
              React.createElement("tbody", { className: "divide-y divide-gray-100" },
                data.weekly.map((day: any, idx: number) =>
                  React.createElement("tr", {
                    key: day.date,
                    className: "hover:bg-gray-50 transition-colors cursor-pointer",
                    onClick: () => setSelectedIndex(idx)
                  }, [
                    React.createElement("td", { className: "py-2 px-2" }, [
                      React.createElement("div", { className: "flex items-center gap-2" }, [
                        React.createElement("span", { className: "text-[11px] font-bold text-gray-900" }, day.dayName),
                        React.createElement("span", { className: "text-[11px]" },
                          day.weather.condition === 'Sunny' ? '☀️' : day.weather.condition === 'Rain' ? '🌧️' : '☁️'
                        )
                      ])
                    ]),
                    React.createElement("td", { className: "py-2 px-2 text-right text-[11px] font-bold text-gray-900" }, `£${(day.predictedRevenue / 1000).toFixed(1)}k`),
                    React.createElement("td", { className: "py-2 px-2 text-right text-[11px] font-bold text-gray-700" }, day.predictedCovers),
                    React.createElement("td", { className: "py-2 px-2 text-center" }, [
                      React.createElement("span", {
                        className: `inline-block px-2 py-0.5 rounded-full text-[9px] font-bold ${day.predictedCovers > 200 ? 'bg-red-100 text-red-700' :
                          day.predictedCovers > 150 ? 'bg-orange-100 text-orange-700' :
                            day.predictedCovers > 100 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                          }`
                      },
                        day.predictedCovers > 200 ? 'Peak' :
                          day.predictedCovers > 150 ? 'High' :
                            day.predictedCovers > 100 ? 'Med' :
                              'Low'
                      )
                    ])
                  ])
                )
              )
            ])
          ])
        ]),

        // 2. Peak Hours Forecast
        React.createElement("div", { key: "peak-hours", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm h-[350px] max-h-[350px] flex flex-col" }, [
          React.createElement("h4", { className: "text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3" }, "Peak Hours This Week"),
          React.createElement("div", { className: "flex-1 flex flex-col" }, [
            // Intensity Bar Chart
            React.createElement("div", { className: "space-y-2 flex-1 overflow-hidden flex flex-col justify-center pt-1 pb-2" },
              peakHours.map((item: any) =>
                React.createElement("div", { key: item.day, className: "flex items-center gap-2" }, [
                  React.createElement("div", { className: "w-9 text-[9px] font-bold text-gray-700" }, item.day),
                  React.createElement("div", { className: "flex-1" }, [
                    React.createElement("div", { className: "w-full bg-gray-100 rounded-full h-4 relative overflow-hidden" }, [
                      React.createElement("div", {
                        className: `h-full ${item.color} transition-all duration-500 flex items-center justify-end pr-2`,
                        style: { width: `${item.intensity}%` }
                      }, [
                        React.createElement("span", { className: "text-[8px] font-bold text-white" }, item.level)
                      ])
                    ])
                  ]),
                  React.createElement("div", { className: "w-20 text-[9px] text-gray-600 font-medium" }, item.hours)
                ])
              )
            ),

            // Legend
            React.createElement("div", { className: "flex gap-3 pt-2 border-t border-gray-100 justify-center flex-wrap mt-auto" }, [
              React.createElement("div", { key: "low", className: "flex items-center gap-1.5" }, [
                React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-green-500" }),
                React.createElement("span", { className: "text-[9px] text-gray-600" }, "Low <100")
              ]),
              React.createElement("div", { key: "medium", className: "flex items-center gap-1.5" }, [
                React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-yellow-500" }),
                React.createElement("span", { className: "text-[9px] text-gray-600" }, "Med 100-150")
              ]),
              React.createElement("div", { key: "high", className: "flex items-center gap-1.5" }, [
                React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-orange-500" }),
                React.createElement("span", { className: "text-[9px] text-gray-600" }, "High 150-200")
              ]),
              React.createElement("div", { key: "peak", className: "flex items-center gap-1.5" }, [
                React.createElement("div", { className: "w-2.5 h-2.5 rounded-full bg-red-500" }),
                React.createElement("span", { className: "text-[9px] text-gray-600" }, "Peak 200+")
              ])
            ])
          ])
        ])
      ]),

      // Demand Drivers Panel (Operational Impact)
      React.createElement("div", { key: "demand-drivers", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm mt-5" }, [
        React.createElement("h4", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Demand Drivers & Operational Impact"),
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-5" }, [
          // Weather Impact
          React.createElement("div", { key: "weather-driver", className: "border-l-4 border-blue-500 pl-4 py-2" }, [
            React.createElement("div", { className: "flex items-start gap-2 mb-2" }, [
              React.createElement("span", { className: "text-2xl" }, "🌤️"),
              React.createElement("div", { className: "flex-1" }, [
                React.createElement("h5", { className: "text-xs font-bold text-gray-900 mb-1" }, "Weather Patterns"),
                React.createElement("p", { className: "text-[10px] text-gray-600 leading-relaxed mb-2" },
                  "Sunny weather expected Fri-Sun. Historically increases foot traffic by 25-30%."
                ),
                React.createElement("div", { className: "bg-blue-50 border border-blue-100 rounded-lg p-2 mt-2" }, [
                  React.createElement("p", { className: "text-[10px] text-blue-900 font-semibold" }, "→ Action:"),
                  React.createElement("p", { className: "text-[10px] text-blue-800 mt-0.5" }, "Open outdoor seating. +40% cold beverages. +1-2 FOH staff.")
                ])
              ])
            ])
          ]),

          // Events Impact
          React.createElement("div", { key: "events-driver", className: "border-l-4 border-purple-500 pl-4 py-2" }, [
            React.createElement("div", { className: "flex items-start gap-2 mb-2" }, [
              React.createElement("span", { className: "text-2xl" }, "🎪"),
              React.createElement("div", { className: "flex-1" }, [
                React.createElement("h5", { className: "text-xs font-bold text-gray-900 mb-1" }, "Local Events"),
                React.createElement("p", { className: "text-[10px] text-gray-600 leading-relaxed mb-2" },
                  "Saturday: Music festival nearby (5,000 attendees). Expected spillover traffic."
                ),
                React.createElement("div", { className: "bg-purple-50 border border-purple-100 rounded-lg p-2 mt-2" }, [
                  React.createElement("p", { className: "text-[10px] text-purple-900 font-semibold" }, "→ Action:"),
                  React.createElement("p", { className: "text-[10px] text-purple-800 mt-0.5" }, "Prepare for +35% covers. Pre-prep quick items. Full staffing 11am-10pm.")
                ])
              ])
            ])
          ]),

          // Promotions Impact
          React.createElement("div", { key: "promo-driver", className: "border-l-4 border-green-500 pl-4 py-2" }, [
            React.createElement("div", { className: "flex items-start gap-2 mb-2" }, [
              React.createElement("span", { className: "text-2xl" }, "🎯"),
              React.createElement("div", { className: "flex-1" }, [
                React.createElement("h5", { className: "text-xs font-bold text-gray-900 mb-1" }, "Active Promotions"),
                React.createElement("p", { className: "text-[10px] text-gray-600 leading-relaxed mb-2" },
                  "Mon-Wed: 20% off lunch special. Historically drives +15-20% weekday lunch covers."
                ),
                React.createElement("div", { className: "bg-green-50 border border-green-100 rounded-lg p-2 mt-2" }, [
                  React.createElement("p", { className: "text-[10px] text-green-900 font-semibold" }, "→ Action:"),
                  React.createElement("p", { className: "text-[10px] text-green-800 mt-0.5" }, "Stock lunch ingredients. Extra kitchen prep 10am-2pm. Monitor margins.")
                ])
              ])
            ])
          ])
        ])
      ])
      ,
    ]),

    // STAFFING TAB
    activeTab === "staffing" && React.createElement("div", { key: "staffing-tab", className: "space-y-5 animate-in slide-in-from-bottom-4" }, [
      // Staffing Recommendation Banner
      React.createElement("div", { key: "recommendation", className: "bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6 rounded-xl shadow-lg border border-indigo-500" }, [
        React.createElement("div", { className: "flex items-start gap-4" }, [
          React.createElement("div", { className: "text-3xl" }, "💡"),
          React.createElement("div", { className: "flex-1" }, [
            React.createElement("h4", { className: "text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2" }, "Today's Staffing Recommendation"),
            React.createElement("p", { className: "text-base font-semibold text-white leading-relaxed" },
              merged.staffing.recommendation
            )
          ])
        ])
      ]),

      // Top Row: Staff Cost vs Sales + Demand Coverage Chart
      React.createElement("div", { key: "top-row", className: "grid grid-cols-1 lg:grid-cols-2 gap-5" }, [
        // 1. Staff Cost vs Expected Sales
        React.createElement("div", { key: "cost-vs-sales", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
          React.createElement("h4", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Staff Cost vs Expected Sales"),
          React.createElement("div", { className: "space-y-4" }, [
            // Total Staffing Cost
            React.createElement("div", { key: "staff-cost" }, [
              React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1.5" }, "Total staffing cost today"),
              React.createElement("p", { className: "text-3xl font-bold text-gray-900" }, `£${staffingCost.toLocaleString()}`)
            ]),
            // Expected Revenue
            React.createElement("div", { key: "expected-rev" }, [
              React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-1.5" }, "Expected revenue today"),
              React.createElement("p", { className: "text-3xl font-bold text-gray-700" }, `£${staffingExpectedRevenue.toLocaleString()}`)
            ]),
            // Divider
            React.createElement("div", { className: "border-t border-gray-200 my-4" }),
            // Staff Cost %
            React.createElement("div", { key: "cost-percent" }, [
              React.createElement("p", { className: "text-[10px] text-gray-400 font-medium mb-2" }, "Staff cost as % of sales"),
              React.createElement("div", { className: "flex items-baseline gap-3 mb-3" }, [
                React.createElement("p", { className: "text-4xl font-bold text-indigo-600" }, `${staffingPercent.toFixed(1)}%`),
                React.createElement("span", { className: "text-xs text-gray-500" }, "of revenue")
              ]),
              // Normal Range
              React.createElement("div", { className: "bg-gray-50 rounded-lg p-3 mb-3" }, [
                React.createElement("p", { className: "text-[10px] text-gray-500 font-medium mb-1" }, "Normal range for your venue type"),
                React.createElement("p", { className: "text-sm font-bold text-gray-700" }, `${staffingRangeLow}% - ${staffingRangeHigh}%`)
              ]),
              // Status
              React.createElement("div", { className: "bg-green-50 border border-green-100 rounded-lg p-3" }, [
                React.createElement("p", { className: "text-xs text-green-800 font-semibold leading-relaxed" },
                  staffingNote
                )
              ])
            ])
          ])
        ]),

        // 2. Demand vs Staffing Coverage Chart
        React.createElement("div", { key: "coverage-chart", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
          React.createElement("h4", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Demand vs Staffing Coverage"),
          React.createElement("div", { className: "space-y-3" },
            staffingCoverage.map((slot: any) => {
              const demand = Number(slot.demand ?? 0);
              const staff = Number(slot.staff ?? 0);
              const status = slot.status || (demand > ((staff / maxStaff) * 100) + 15 ? "under" : "ok");
              const detail = slot.detail || "Coverage details not provided.";

              return React.createElement("div", {
                key: slot.hour,
                className: `flex items-center gap-3 p-1.5 rounded-lg transition-all duration-200 cursor-default ${hoveredCoverageHour === slot.hour ? 'bg-gray-50 shadow-sm scale-[1.01]' : ''}`,
                onMouseEnter: () => setHoveredCoverageHour(slot.hour),
                onMouseLeave: () => setHoveredCoverageHour(null)
              }, [
                // Hour label
                React.createElement("div", { className: "w-20 text-[10px] font-bold text-gray-700" }, slot.hour),
                // Demand bar
                React.createElement("div", { className: "flex-1 relative" }, [
                  React.createElement("div", { className: "w-full bg-gray-100 rounded-full h-6 relative overflow-hidden" }, [
                    // Demand level (background)
                    React.createElement("div", {
                      className: `absolute inset-y-0 left-0 ${demand >= 80 ? 'bg-red-200' : demand >= 60 ? 'bg-orange-200' : 'bg-yellow-200'} rounded-full transition-all duration-500`,
                      style: { width: `${demand}%` }
                    }),
                    // Staff coverage (foreground)
                    React.createElement("div", {
                      className: `absolute inset-y-0 left-0 ${status === 'under' ? 'bg-red-500' : 'bg-green-500'} rounded-full opacity-70 transition-all duration-500`,
                      style: { width: `${(staff / maxStaff) * 100}%` }
                    }),
                    // Label
                    React.createElement("div", { className: "absolute inset-0 flex items-center justify-center pointer-events-none" }, [
                      React.createElement("span", { className: "text-[9px] font-bold text-gray-700" }, `${staff} staff`)
                    ])
                  ])
                ]),
                // Status indicator
                React.createElement("div", { className: "w-20 text-right" }, [
                  status === 'under'
                    ? React.createElement("span", { className: "text-[9px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full border border-red-100" }, "Under")
                    : React.createElement("span", { className: "text-[9px] font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-100" }, "Good")
                ]),

                // Detailed Tooltip
                hoveredCoverageHour === slot.hour && React.createElement("div", {
                  key: `tooltip-${slot.hour}`,
                  className: "absolute left-1/2 -top-12 -translate-x-1/2 bg-gray-900 text-white text-[10px] p-3 rounded-xl shadow-2xl z-50 w-64 pointer-events-none animate-in fade-in zoom-in duration-200 border border-gray-800"
                }, [
                  React.createElement("div", { className: "font-bold text-indigo-400 mb-1" }, `${slot.hour} Capacity Analysis`),
                  React.createElement("p", { className: "text-gray-200 font-medium leading-relaxed" }, detail)
                ])
              ]);
            })
          ),
          // Legend
          React.createElement("div", { className: "flex gap-4 pt-4 border-t border-gray-100 mt-4 text-[10px]" }, [
            React.createElement("div", { key: "demand-legend", className: "flex items-center gap-1.5" }, [
              React.createElement("div", { className: "w-4 h-3 bg-orange-200 rounded" }),
              React.createElement("span", { className: "text-gray-600" }, "Demand level")
            ]),
            React.createElement("div", { key: "staff-legend", className: "flex items-center gap-1.5" }, [
              React.createElement("div", { className: "w-4 h-3 bg-green-500 opacity-70 rounded" }),
              React.createElement("span", { className: "text-gray-600" }, "Staff coverage")
            ])
          ])
        ])
      ]),

      // Today's Rota
      React.createElement("div", { key: "rota", className: "relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
        React.createElement("h4", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider mb-5" }, "Today's Rota"),
        React.createElement("div", { className: "overflow-x-auto" }, [
          React.createElement("table", { className: "w-full" }, [
            // Header
            React.createElement("thead", { className: "border-b border-gray-200" },
              React.createElement("tr", {}, [
                React.createElement("th", { className: "text-left py-2.5 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider" }, "Staff Name"),
                React.createElement("th", { className: "text-left py-2.5 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider" }, "Role"),
                React.createElement("th", { className: "text-left py-2.5 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider" }, "Shift Time"),
                React.createElement("th", { className: "text-right py-2.5 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider" }, "Cost")
              ])
            ),
            // Body
            React.createElement("tbody", { className: "divide-y divide-gray-100" },
              staffingRota.map((staff: any) =>
                React.createElement("tr", {
                  key: staff.name,
                  className: "hover:bg-gray-50 transition-colors"
                }, [
                  React.createElement("td", { className: "py-2.5 px-3 text-sm font-bold text-gray-900" }, staff.name),
                  React.createElement("td", { className: "py-2.5 px-3 text-xs text-gray-600 font-medium" }, staff.role),
                  React.createElement("td", { className: "py-2.5 px-3 text-xs text-gray-700 font-medium" }, staff.shift),
                  React.createElement("td", { className: "py-2.5 px-3 text-right text-sm font-bold text-gray-900" }, `£${Number(staff.cost || 0).toLocaleString()}`)
                ])
              )
            )
          ])
        ]),
        // Total
        React.createElement("div", { className: "flex justify-between items-center pt-4 mt-4 border-t border-gray-200" }, [
          React.createElement("span", { className: "text-xs font-bold text-gray-500 uppercase tracking-wider" }, "Total Staffing Cost"),
          React.createElement("span", { className: "text-2xl font-bold text-indigo-600" }, `£${totalStaffCost.toLocaleString()}`)
        ])
      ])
    ])
  ]);
}


function ManualInventoryView({ onSave, onCancel, initialData }: { onSave: (data: any) => void; onCancel: () => void; initialData?: any }) {
  // State for Inventory Items
  const [items, setItems] = useState<InventoryItem[]>(initialData?.items || []);
  const [newItem, setNewItem] = useState<Partial<InventoryItem>>({
    name: "", category: "Dry Goods", currentQty: 0, unit: "pcs", costPerUnit: 0, minThreshold: 5
  });

  // State for Waste
  const [waste, setWaste] = useState<WasteRecord[]>(initialData?.waste || []);
  const [newWaste, setNewWaste] = useState<{ name: string, qty: number, reason: string, value: number }>({
    name: "", qty: 0, reason: "Expired", value: 0
  });

  // State for Insight Override
  const [insight, setInsight] = useState(initialData?.insight || "");

  const addItem = () => {
    if (!newItem.name) return;
    const item: InventoryItem = {
      id: Math.random().toString(),
      name: newItem.name!,
      category: newItem.category as any,
      currentQty: Number(newItem.currentQty),
      unit: newItem.unit || 'pcs',
      minThreshold: Number(newItem.minThreshold),
      avgDailyUsage: 1, // Default
      costPerUnit: Number(newItem.costPerUnit),
      supplierId: "manual",
      lastDelivered: new Date().toISOString(),
      status: Number(newItem.currentQty) < Number(newItem.minThreshold) ? "Low" : "OK" as any
    };
    setItems([...items, item]);
    setNewItem({ name: "", category: "Dry Goods", currentQty: 0, unit: "pcs", costPerUnit: 0, minThreshold: 5 });
  };

  const addWaste = () => {
    if (!newWaste.name) return;
    const w: WasteRecord = {
      id: Math.random().toString(),
      itemId: "manual",
      itemName: newWaste.name,
      quantity: Number(newWaste.qty),
      reason: newWaste.reason as any,
      value: Number(newWaste.value),
      date: new Date().toISOString()
    };
    setWaste([...waste, w]);
    setNewWaste({ name: "", qty: 0, reason: "Expired", value: 0 });
  };

  return React.createElement("div", { className: "relative overflow-hidden max-w-4xl mx-auto p-6 md:p-8 bg-white rounded-3xl shadow-[0_24px_80px_-50px_rgba(15,23,42,0.55)] border border-slate-200/80 animate-in slide-in-from-bottom-4" }, [
    React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
    // Header
    React.createElement("div", { className: "flex justify-between items-center mb-8" }, [
      React.createElement("div", {}, [
        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2" }, "Inventory Intelligence"),
        React.createElement("h2", { className: "text-2xl font-black text-gray-900" }, "Manual Inventory Update")
      ]),
      React.createElement("button", { onClick: onCancel, className: "text-slate-600 font-black text-[10px] uppercase tracking-widest bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-xl transition-colors" }, "Cancel")
    ]),

    // AI Insight Override
    React.createElement("div", { className: "mb-8 bg-slate-50/70 p-6 rounded-2xl border border-slate-200/80 shadow-[0_12px_30px_-24px_rgba(15,23,42,0.6)] relative overflow-hidden" }, [
      React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-indigo-400 to-sky-300" }),
      React.createElement("label", { className: "block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3" }, "Dashboard Insight (Optional)"),
      React.createElement("textarea", {
        value: insight,
        onChange: (e: any) => setInsight(e.target.value),
        placeholder: "e.g. Stock levels are healthy, but waste is high in prep.",
        className: "w-full p-3.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 outline-none h-28 resize-none shadow-sm"
      })
    ]),

    // Add Items Section
    React.createElement("div", { className: "mb-8" }, [
      React.createElement("div", { className: "flex items-end justify-between mb-4" }, [
        React.createElement("h3", { className: "text-sm font-black uppercase tracking-widest text-slate-600" }, "Inventory Items"),
        React.createElement("span", { className: "text-[11px] text-slate-400 font-semibold" }, "Add current stock and reorder thresholds")
      ]),
      // Entry Form
      React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-4 items-end bg-slate-50/60 p-5 rounded-2xl border border-slate-200/80 shadow-[0_10px_24px_-22px_rgba(15,23,42,0.6)]" }, [
        React.createElement("div", { className: "lg:col-span-2" }, [
          React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Name"),
          React.createElement("input", { className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.name, onChange: (e: any) => setNewItem({ ...newItem, name: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Qty"),
          React.createElement("input", { type: "number", className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.currentQty, onChange: (e: any) => setNewItem({ ...newItem, currentQty: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Cost"),
          React.createElement("input", { type: "number", className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.costPerUnit, onChange: (e: any) => setNewItem({ ...newItem, costPerUnit: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Min"),
          React.createElement("input", { type: "number", className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.minThreshold, onChange: (e: any) => setNewItem({ ...newItem, minThreshold: e.target.value }) })
        ]),
        React.createElement("button", { onClick: addItem, className: "sm:col-span-2 lg:col-span-1 bg-indigo-600 text-white font-black text-[10px] uppercase tracking-widest h-[44px] rounded-xl hover:bg-indigo-700 transition-colors shadow-sm" }, "Add")
      ]),
      // List
      items.length > 0 && React.createElement("div", { className: "border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm" }, [
        React.createElement("div", { className: "flex justify-between items-center px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100" }, [
          React.createElement("span", {}, "Item"),
          React.createElement("span", {}, "Stock / Cost")
        ]),
        items.map((i, idx) =>
          React.createElement("div", { key: idx, className: "flex justify-between items-center p-3 border-b border-slate-100 last:border-0 text-sm hover:bg-slate-50 transition-colors" }, [
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("span", { className: "font-bold text-slate-700" }, i.name),
              React.createElement("span", { className: "text-[10px] text-slate-400 font-mono" }, i.category)
            ]),
            React.createElement("span", { className: "text-slate-600" }, `${i.currentQty} ${i.unit} @ £${i.costPerUnit}`)
          ])
        )
      ])
    ]),

    // Add Waste Section
    React.createElement("div", { className: "mb-8" }, [
      React.createElement("div", { className: "flex items-end justify-between mb-4" }, [
        React.createElement("h3", { className: "text-sm font-black uppercase tracking-widest text-slate-600" }, "Waste Log (Optional)"),
        React.createElement("span", { className: "text-[11px] text-slate-400 font-semibold" }, "Track spoilage, trim loss, and expired items")
      ]),
      // Entry Form
      React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4 items-end bg-rose-50/60 p-5 rounded-2xl border border-rose-200/70 shadow-[0_10px_24px_-22px_rgba(15,23,42,0.6)]" }, [
        React.createElement("div", { className: "lg:col-span-2" }, [
          React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-rose-500 mb-2 block" }, "Item Name"),
          React.createElement("input", { className: "w-full p-2.5 rounded-xl border border-rose-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-rose-500/15 focus:border-rose-300 shadow-sm", value: newWaste.name, onChange: (e: any) => setNewWaste({ ...newWaste, name: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-rose-500 mb-2 block" }, "Qty"),
          React.createElement("input", { type: "number", className: "w-full p-2.5 rounded-xl border border-rose-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-rose-500/15 focus:border-rose-300 shadow-sm", value: newWaste.qty, onChange: (e: any) => setNewWaste({ ...newWaste, qty: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-rose-500 mb-2 block" }, "Value (£)"),
          React.createElement("input", { type: "number", className: "w-full p-2.5 rounded-xl border border-rose-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-rose-500/15 focus:border-rose-300 shadow-sm", value: newWaste.value, onChange: (e: any) => setNewWaste({ ...newWaste, value: e.target.value }) })
        ]),
        React.createElement("button", { onClick: addWaste, className: "sm:col-span-2 lg:col-span-1 bg-rose-600 text-white font-black text-[10px] uppercase tracking-widest h-[44px] rounded-xl hover:bg-rose-700 transition-colors shadow-sm" }, "Add")
      ]),
      // List
      waste.length > 0 && React.createElement("div", { className: "border border-rose-200/70 rounded-2xl overflow-hidden bg-white shadow-sm" }, [
        React.createElement("div", { className: "flex justify-between items-center px-3 py-2 text-[10px] font-black uppercase tracking-widest text-rose-400 bg-rose-50 border-b border-rose-100" }, [
          React.createElement("span", {}, "Item"),
          React.createElement("span", {}, "Reason / Value")
        ]),
        waste.map((w, idx) =>
          React.createElement("div", { key: idx, className: "flex justify-between items-center p-3 border-b border-rose-100 last:border-0 text-sm hover:bg-rose-50 transition-colors" }, [
            React.createElement("span", { className: "font-bold text-rose-700" }, w.itemName),
            React.createElement("div", { className: "flex items-center gap-2" }, [
              React.createElement("span", { className: "text-[10px] font-black bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full uppercase tracking-widest" }, w.reason),
              React.createElement("span", { className: "font-mono text-slate-700" }, `£${w.value}`)
            ])
          ])
        )
      ])
    ]),

    // Footer
    React.createElement("div", { className: "flex justify-end pt-6 border-t border-slate-200" }, [
      React.createElement("button", {
        onClick: () => onSave({ items, waste, insight }),
        className: "bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black shadow-[0_18px_35px_-20px_rgba(37,99,235,0.65)] hover:bg-indigo-700 hover:scale-[1.02] transition-all text-sm uppercase tracking-widest flex items-center gap-2"
      }, ["Confirm & Update Dashboard"])
    ])
  ]);
}

function ManualMenuView({ onSave, onCancel, initialData }: { onSave: (data: any) => void; onCancel: () => void; initialData?: any }) {
  const [items, setItems] = useState<MenuItemInput[]>(
    (initialData?.items || []).map(normalizeMenuItemInput).filter(Boolean) as MenuItemInput[]
  );
  const [newItem, setNewItem] = useState<Partial<MenuItemInput> & { signals?: string }>({
    name: "",
    category: "Main",
    price: 0,
    cost: 0,
    ordersPerWeek: 0,
    prepTime: "Medium",
    trend: "stable",
    signals: ""
  });

  const addItem = () => {
    if (!newItem.name) return;
    const signals = (newItem.signals || "")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);
    const item: MenuItemInput = {
      id: Math.random().toString(),
      name: newItem.name!,
      category: (newItem.category || "Main") as MenuItemInput["category"],
      price: Number(newItem.price) || 0,
      cost: Number(newItem.cost) || 0,
      ordersPerWeek: Number(newItem.ordersPerWeek) || 0,
      prepTime: (newItem.prepTime || "Medium") as MenuItemInput["prepTime"],
      trend: (newItem.trend || "stable") as MenuItemInput["trend"],
      operationalSignals: signals
    };
    setItems([...items, item]);
    setNewItem({
      name: "",
      category: "Main",
      price: 0,
      cost: 0,
      ordersPerWeek: 0,
      prepTime: "Medium",
      trend: "stable",
      signals: ""
    });
  };

  const previewItems = enrichMenuItems(items);

  return React.createElement("div", { className: "relative overflow-hidden max-w-5xl mx-auto p-6 md:p-8 bg-white rounded-3xl shadow-[0_24px_80px_-50px_rgba(15,23,42,0.55)] border border-slate-200/80 animate-in slide-in-from-bottom-4" }, [
    React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
    // Header
    React.createElement("div", { className: "flex justify-between items-center mb-8" }, [
      React.createElement("div", {}, [
        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2" }, "Menu Intelligence"),
        React.createElement("h2", { className: "text-2xl font-black text-gray-900" }, "Manual Menu Update")
      ]),
      React.createElement("button", { onClick: onCancel, className: "text-slate-600 font-black text-[10px] uppercase tracking-widest bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-xl transition-colors" }, "Cancel")
    ]),

    // Add Items Section
    React.createElement("div", { className: "mb-8" }, [
      React.createElement("div", { className: "flex items-end justify-between mb-4" }, [
        React.createElement("h3", { className: "text-sm font-black uppercase tracking-widest text-slate-600" }, "Menu Items"),
        React.createElement("span", { className: "text-[11px] text-slate-400 font-semibold" }, "Add pricing, cost, and weekly volume")
      ]),
      React.createElement("div", { className: "bg-slate-50/60 p-5 rounded-2xl border border-slate-200/80 shadow-[0_10px_24px_-22px_rgba(15,23,42,0.6)]" }, [
        React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-4" }, [
          React.createElement("div", { className: "lg:col-span-2" }, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Item Name"),
            React.createElement("input", { className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.name, onChange: (e: any) => setNewItem({ ...newItem, name: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Category"),
            React.createElement("select", { className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.category, onChange: (e: any) => setNewItem({ ...newItem, category: e.target.value }) }, [
              React.createElement("option", { value: "Appetizer" }, "Appetizer"),
              React.createElement("option", { value: "Main" }, "Main"),
              React.createElement("option", { value: "Dessert" }, "Dessert"),
              React.createElement("option", { value: "Beverage" }, "Beverage")
            ])
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Price"),
            React.createElement("input", { type: "number", step: "0.01", className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.price, onChange: (e: any) => setNewItem({ ...newItem, price: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Cost"),
            React.createElement("input", { type: "number", step: "0.01", className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.cost, onChange: (e: any) => setNewItem({ ...newItem, cost: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Orders / Week"),
            React.createElement("input", { type: "number", className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.ordersPerWeek, onChange: (e: any) => setNewItem({ ...newItem, ordersPerWeek: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Prep"),
            React.createElement("select", { className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.prepTime, onChange: (e: any) => setNewItem({ ...newItem, prepTime: e.target.value }) }, [
              React.createElement("option", { value: "Low" }, "Low"),
              React.createElement("option", { value: "Medium" }, "Medium"),
              React.createElement("option", { value: "High" }, "High")
            ])
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Trend"),
            React.createElement("select", { className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.trend, onChange: (e: any) => setNewItem({ ...newItem, trend: e.target.value }) }, [
              React.createElement("option", { value: "up" }, "Up"),
              React.createElement("option", { value: "stable" }, "Stable"),
              React.createElement("option", { value: "down" }, "Down")
            ])
          ])
        ]),
        React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end mt-4" }, [
          React.createElement("div", { className: "lg:col-span-4" }, [
            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Operational Signals (optional)"),
            React.createElement("input", { className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm", value: newItem.signals, onChange: (e: any) => setNewItem({ ...newItem, signals: e.target.value }), placeholder: "e.g. High labor, Quick prep, Waste risk" })
          ]),
          React.createElement("button", { onClick: addItem, className: "sm:col-span-2 lg:col-span-2 bg-indigo-600 text-white font-black text-[10px] uppercase tracking-widest h-[44px] rounded-xl hover:bg-indigo-700 transition-colors shadow-sm" }, "Add Item")
        ])
      ]),
      previewItems.length > 0 && React.createElement("div", { className: "border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm mt-6" }, [
        React.createElement("div", { className: "grid grid-cols-6 gap-4 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100" }, [
          React.createElement("span", { className: "col-span-2" }, "Item"),
          React.createElement("span", {}, "Price"),
          React.createElement("span", {}, "Cost"),
          React.createElement("span", {}, "Orders"),
          React.createElement("span", {}, "Role")
        ]),
        previewItems.map((i, idx) =>
          React.createElement("div", { key: idx, className: "grid grid-cols-6 gap-4 px-4 py-3 text-sm border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" }, [
            React.createElement("div", { className: "col-span-2" }, [
              React.createElement("p", { className: "font-bold text-slate-800" }, i.name),
              React.createElement("p", { className: "text-[10px] text-slate-400" }, i.category)
            ]),
            React.createElement("span", { className: "text-slate-700 font-semibold" }, `£${i.price.toFixed(2)}`),
            React.createElement("span", { className: "text-slate-500" }, `£${i.cost.toFixed(2)}`),
            React.createElement("span", { className: "text-slate-700 font-semibold" }, i.ordersPerWeek),
            React.createElement("span", {
              className: `text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full w-fit ${i.menuRole === "Core Seller" ? "bg-emerald-50 text-emerald-700" :
                i.menuRole === "Opportunity" ? "bg-indigo-50 text-indigo-700" :
                  i.menuRole === "Underperforming" ? "bg-rose-50 text-rose-700" :
                    "bg-slate-100 text-slate-600"}`
            }, i.menuRole)
          ])
        )
      ])
    ]),

    // Footer
    React.createElement("div", { className: "flex justify-end pt-6 border-t border-slate-200" }, [
      React.createElement("button", {
        onClick: () => onSave({ items }),
        className: "bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black shadow-[0_18px_35px_-20px_rgba(37,99,235,0.65)] hover:bg-indigo-700 hover:scale-[1.02] transition-all text-sm uppercase tracking-widest flex items-center gap-2"
      }, ["Confirm & Update Dashboard"])
    ])
  ]);
}

function RotaEditor({ rows, onDraftChange }: { rows: any[]; onDraftChange: (rows: any[]) => void }) {
  const [draft, setDraft] = useState<any[]>(rows || []);
  const inputClass = "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm";
  const labelClass = "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block";

  useEffect(() => {
    setDraft(rows || []);
  }, [rows]);

  useEffect(() => {
    if (onDraftChange) onDraftChange(draft);
  }, [draft, onDraftChange]);

  const updateRow = (idx: number, patch: any) => {
    setDraft((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const addRow = () => setDraft((prev) => ([...prev, { name: "", role: "", shift: "", cost: 0 }]));

  return React.createElement("div", { className: "space-y-3" }, [
    ...draft.map((row, idx) =>
      React.createElement("div", { key: idx, className: "grid grid-cols-1 md:grid-cols-4 gap-3 items-end" }, [
        React.createElement("div", {}, [
          React.createElement("label", { className: labelClass }, "Name"),
          React.createElement("input", { className: inputClass, value: row.name || "", onChange: (e: any) => updateRow(idx, { name: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: labelClass }, "Role"),
          React.createElement("input", { className: inputClass, value: row.role || "", onChange: (e: any) => updateRow(idx, { role: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: labelClass }, "Shift"),
          React.createElement("input", { className: inputClass, value: row.shift || "", onChange: (e: any) => updateRow(idx, { shift: e.target.value }) })
        ]),
        React.createElement("div", {}, [
          React.createElement("label", { className: labelClass }, "Cost"),
          React.createElement("input", { type: "number", className: inputClass, value: row.cost ?? 0, onChange: (e: any) => updateRow(idx, { cost: Number(e.target.value) || 0 }) })
        ])
      ])
    ),
    React.createElement("button", { className: "mt-4 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50", onClick: addRow }, "Add Staff")
  ]);
}

function ManualSalesDemandView({ onSave, onCancel, initialData }: { onSave: (data: any) => void; onCancel: () => void; initialData?: any }) {
  const [form, setForm] = useState(() => normalizeManualSalesData(initialData));

  const inputClass = "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm";
  const labelClass = "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block";

  const toNumber = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const rotaDraftRef = useRef(form.staffing.rota);

  useEffect(() => {
    rotaDraftRef.current = form.staffing.rota;
  }, [form.staffing.rota]);

  const SectionCard = ({ title, subtitle, children }: any) => React.createElement("div", { className: "bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
    React.createElement("div", { className: "mb-4" }, [
      React.createElement("h3", { className: "text-[11px] font-black uppercase tracking-widest text-slate-500" }, title),
      subtitle && React.createElement("p", { className: "text-[11px] text-slate-400 mt-1" }, subtitle)
    ]),
    children
  ]);

  const updateSalesToday = (patch: any) => setForm((prev: any) => ({ ...prev, sales: { ...prev.sales, today: { ...prev.sales.today, ...patch } } }));
  const updateSalesHealth = (patch: any) => setForm((prev: any) => ({ ...prev, sales: { ...prev.sales, health: { ...prev.sales.health, ...patch } } }));
  const updateSalesAov = (patch: any) => setForm((prev: any) => ({ ...prev, sales: { ...prev.sales, aov: { ...prev.sales.aov, ...patch } } }));
  const updateSalesMix = (patch: any) => setForm((prev: any) => ({ ...prev, sales: { ...prev.sales, salesMix: { ...prev.sales.salesMix, ...patch } } }));
  const updateRevenueProgressPoint = (idx: number, patch: any) => setForm((prev: any) => {
    const points = [...prev.sales.revenueProgress.points];
    points[idx] = { ...points[idx], ...patch };
    return { ...prev, sales: { ...prev.sales, revenueProgress: { ...prev.sales.revenueProgress, points } } };
  });
  const addRevenueProgressPoint = () => setForm((prev: any) => ({
    ...prev,
    sales: {
      ...prev.sales,
      revenueProgress: {
        ...prev.sales.revenueProgress,
        points: [...prev.sales.revenueProgress.points, { time: "", actual: 0, expected: 0 }]
      }
    }
  }));
  const removeRevenueProgressPoint = (idx: number) => setForm((prev: any) => ({
    ...prev,
    sales: {
      ...prev.sales,
      revenueProgress: {
        ...prev.sales.revenueProgress,
        points: prev.sales.revenueProgress.points.filter((_: any, i: number) => i !== idx)
      }
    }
  }));
  const updateRevenueVsCostRow = (idx: number, patch: any) => setForm((prev: any) => {
    const labels = [...prev.sales.revenueVsCost.labels];
    const revenue = [...prev.sales.revenueVsCost.revenue];
    const cost = [...prev.sales.revenueVsCost.cost];
    if (patch.label !== undefined) labels[idx] = patch.label;
    if (patch.revenue !== undefined) revenue[idx] = patch.revenue;
    if (patch.cost !== undefined) cost[idx] = patch.cost;
    return { ...prev, sales: { ...prev.sales, revenueVsCost: { labels, revenue, cost } } };
  });
  const addRevenueVsCostRow = () => setForm((prev: any) => ({
    ...prev,
    sales: {
      ...prev.sales,
      revenueVsCost: {
        labels: [...prev.sales.revenueVsCost.labels, `Week ${prev.sales.revenueVsCost.labels.length + 1}`],
        revenue: [...prev.sales.revenueVsCost.revenue, 0],
        cost: [...prev.sales.revenueVsCost.cost, 0]
      }
    }
  }));

  const updateWeeklyDay = (idx: number, patch: any) => setForm((prev: any) => {
    const weekly = [...prev.demand.weekly];
    const current = weekly[idx];
    weekly[idx] = {
      ...current,
      ...patch,
      weather: {
        ...(current.weather || {}),
        ...(patch.weather || {})
      }
    };
    return { ...prev, demand: { ...prev.demand, weekly } };
  });
  const updateHourlyTemplate = (idx: number, patch: any) => setForm((prev: any) => {
    const hourlyTemplate = [...prev.demand.hourlyTemplate];
    hourlyTemplate[idx] = { ...hourlyTemplate[idx], ...patch };
    return { ...prev, demand: { ...prev.demand, hourlyTemplate } };
  });
  const addHourlyTemplateRow = () => setForm((prev: any) => ({
    ...prev,
    demand: {
      ...prev.demand,
      hourlyTemplate: [...prev.demand.hourlyTemplate, { hour: "", projectedRevenue: 0, demandLevel: 0 }]
    }
  }));
  const updatePeakHourRow = (idx: number, patch: any) => setForm((prev: any) => {
    const peakHours = [...prev.demand.peakHours];
    peakHours[idx] = { ...peakHours[idx], ...patch };
    return { ...prev, demand: { ...prev.demand, peakHours } };
  });
  const addPeakHourRow = () => setForm((prev: any) => ({
    ...prev,
    demand: {
      ...prev.demand,
      peakHours: [...prev.demand.peakHours, { day: "", hours: "", intensity: 50 }]
    }
  }));

  const updateStaffingCost = (patch: any) => setForm((prev: any) => ({ ...prev, staffing: { ...prev.staffing, costVsSales: { ...prev.staffing.costVsSales, ...patch } } }));
  const updateStaffingRecommendation = (value: string) => setForm((prev: any) => ({ ...prev, staffing: { ...prev.staffing, recommendation: value } }));
  const updateCoverageRow = (idx: number, patch: any) => setForm((prev: any) => {
    const coverage = [...prev.staffing.coverage];
    coverage[idx] = { ...coverage[idx], ...patch };
    return { ...prev, staffing: { ...prev.staffing, coverage } };
  });
  const addCoverageRow = () => setForm((prev: any) => ({
    ...prev,
    staffing: { ...prev.staffing, coverage: [...prev.staffing.coverage, { hour: "", demand: 0, staff: 0, status: "ok", detail: "" }] }
  }));

  return React.createElement("div", { className: "relative overflow-hidden max-w-6xl mx-auto p-6 md:p-8 bg-white rounded-3xl shadow-[0_24px_80px_-50px_rgba(15,23,42,0.55)] border border-slate-200/80 animate-in slide-in-from-bottom-4" }, [
    React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
    React.createElement("div", { className: "flex justify-between items-center mb-8" }, [
      React.createElement("div", {}, [
        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2" }, "Sales, Demand & Staffing"),
        React.createElement("h2", { className: "text-2xl font-black text-gray-900" }, "Manual Data Update")
      ]),
      React.createElement("button", { onClick: onCancel, className: "text-slate-600 font-black text-[10px] uppercase tracking-widest bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-xl transition-colors" }, "Cancel")
    ]),

    React.createElement("div", { className: "space-y-8" }, [
      React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6" }, [
        React.createElement(SectionCard, { title: "Sales Snapshot", subtitle: "Today at a glance" }, [
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Revenue so far"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.today.revenueSoFar, onChange: (e: any) => updateSalesToday({ revenueSoFar: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Expected by now"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.today.expectedByNow, onChange: (e: any) => updateSalesToday({ expectedByNow: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Full-day target"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.today.fullDayTarget, onChange: (e: any) => updateSalesToday({ fullDayTarget: toNumber(e.target.value) }) })
            ])
          ]),
          React.createElement("div", { className: "mt-4" }, [
            React.createElement("label", { className: labelClass }, "Status note (optional)"),
            React.createElement("textarea", { className: "w-full p-3 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm h-20 resize-none", value: form.sales.today.note, onChange: (e: any) => updateSalesToday({ note: e.target.value }) })
          ])
        ]),
        React.createElement(SectionCard, { title: "Sales Health", subtitle: "Pace and volume" }, [
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Transactions today"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.health.transactions, onChange: (e: any) => updateSalesHealth({ transactions: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Transactions delta (%)"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.health.transactionsDelta, onChange: (e: any) => updateSalesHealth({ transactionsDelta: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Current pace (%)"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.health.pacePercent, onChange: (e: any) => updateSalesHealth({ pacePercent: toNumber(e.target.value) }) })
            ])
          ]),
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Pace label"),
              React.createElement("input", { className: inputClass, value: form.sales.health.paceLabel, onChange: (e: any) => updateSalesHealth({ paceLabel: e.target.value }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Busiest hour"),
              React.createElement("input", { className: inputClass, value: form.sales.health.busiestHour, onChange: (e: any) => updateSalesHealth({ busiestHour: e.target.value }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Hour revenue"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.health.busiestHourRevenue, onChange: (e: any) => updateSalesHealth({ busiestHourRevenue: toNumber(e.target.value) }) })
            ])
          ])
        ])
      ]),

      React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6" }, [
        React.createElement(SectionCard, { title: "Average Order Value", subtitle: "AOV settings" }, [
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "AOV value"),
              React.createElement("input", { type: "number", step: "0.01", className: inputClass, value: form.sales.aov.value, onChange: (e: any) => updateSalesAov({ value: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Status label"),
              React.createElement("input", { className: inputClass, value: form.sales.aov.status, onChange: (e: any) => updateSalesAov({ status: e.target.value }) })
            ])
          ]),
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Range low"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.aov.rangeLow, onChange: (e: any) => updateSalesAov({ rangeLow: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Range high"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.aov.rangeHigh, onChange: (e: any) => updateSalesAov({ rangeHigh: toNumber(e.target.value) }) })
            ])
          ]),
          React.createElement("div", { className: "mt-4" }, [
            React.createElement("label", { className: labelClass }, "AOV note"),
            React.createElement("textarea", { className: "w-full p-3 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm h-20 resize-none", value: form.sales.aov.note, onChange: (e: any) => updateSalesAov({ note: e.target.value }) })
          ])
        ]),
        React.createElement(SectionCard, { title: "Sales Mix", subtitle: "Net sales and adjustments" }, [
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Net sales"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.salesMix.netSales, onChange: (e: any) => updateSalesMix({ netSales: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Promo sales"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.salesMix.promoSales, onChange: (e: any) => updateSalesMix({ promoSales: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Refunds & comps"),
              React.createElement("input", { type: "number", className: inputClass, value: form.sales.salesMix.refundsComps, onChange: (e: any) => updateSalesMix({ refundsComps: toNumber(e.target.value) }) })
            ])
          ])
        ])
      ]),

      React.createElement(SectionCard, { title: "Revenue Progress Today", subtitle: "Chart points" }, [
        React.createElement("div", { className: "space-y-3" },
          form.sales.revenueProgress.points.map((point: any, idx: number) =>
            React.createElement("div", { key: idx, className: "grid grid-cols-1 md:grid-cols-4 gap-3 items-end" }, [
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Time label"),
                React.createElement("input", { className: inputClass, value: point.time, onChange: (e: any) => updateRevenueProgressPoint(idx, { time: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Actual"),
                React.createElement("input", { type: "number", className: inputClass, value: point.actual ?? "", onChange: (e: any) => updateRevenueProgressPoint(idx, { actual: e.target.value === "" ? undefined : toNumber(e.target.value) }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Expected"),
                React.createElement("input", { type: "number", className: inputClass, value: point.expected ?? "", onChange: (e: any) => updateRevenueProgressPoint(idx, { expected: e.target.value === "" ? undefined : toNumber(e.target.value) }) })
              ]),
              React.createElement("button", { className: "h-[42px] bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50", onClick: () => removeRevenueProgressPoint(idx) }, "Remove")
            ])
          )
        ),
        React.createElement("button", { className: "mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm hover:bg-indigo-700", onClick: addRevenueProgressPoint }, "Add Point")
      ]),

      React.createElement(SectionCard, { title: "Revenue vs Cost (4 Weeks)", subtitle: "Weekly inputs" }, [
        React.createElement("div", { className: "space-y-3" },
          form.sales.revenueVsCost.labels.map((label: string, idx: number) =>
            React.createElement("div", { key: idx, className: "grid grid-cols-1 md:grid-cols-4 gap-3 items-end" }, [
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Label"),
                React.createElement("input", { className: inputClass, value: label, onChange: (e: any) => updateRevenueVsCostRow(idx, { label: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Revenue"),
                React.createElement("input", { type: "number", className: inputClass, value: form.sales.revenueVsCost.revenue[idx] ?? 0, onChange: (e: any) => updateRevenueVsCostRow(idx, { revenue: toNumber(e.target.value) }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Cost"),
                React.createElement("input", { type: "number", className: inputClass, value: form.sales.revenueVsCost.cost[idx] ?? 0, onChange: (e: any) => updateRevenueVsCostRow(idx, { cost: toNumber(e.target.value) }) })
              ]),
              React.createElement("div", { className: "text-[10px] text-slate-400 font-semibold pb-2" }, idx === 0 ? "Weekly rollup" : "")
            ])
          )
        ),
        React.createElement("button", { className: "mt-4 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50", onClick: addRevenueVsCostRow }, "Add Week")
      ]),

      React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6" }, [
        React.createElement(SectionCard, { title: "Demand Forecast (7 Days)", subtitle: "Revenue, covers, weather" }, [
          React.createElement("div", { className: "space-y-3" },
            form.demand.weekly.map((day: any, idx: number) =>
              React.createElement("div", { key: idx, className: "grid grid-cols-1 md:grid-cols-4 gap-3 items-end" }, [
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Day"),
                  React.createElement("input", { className: inputClass, value: day.dayName, onChange: (e: any) => updateWeeklyDay(idx, { dayName: e.target.value }) })
                ]),
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Revenue"),
                  React.createElement("input", { type: "number", className: inputClass, value: day.predictedRevenue, onChange: (e: any) => updateWeeklyDay(idx, { predictedRevenue: toNumber(e.target.value) }) })
                ]),
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Covers"),
                  React.createElement("input", { type: "number", className: inputClass, value: day.predictedCovers, onChange: (e: any) => updateWeeklyDay(idx, { predictedCovers: toNumber(e.target.value) }) })
                ]),
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Weather"),
                  React.createElement("select", { className: inputClass, value: day.weather?.condition || "Cloudy", onChange: (e: any) => updateWeeklyDay(idx, { weather: { condition: e.target.value } }) }, [
                    React.createElement("option", { value: "Sunny" }, "Sunny"),
                    React.createElement("option", { value: "Cloudy" }, "Cloudy"),
                    React.createElement("option", { value: "Rain" }, "Rain"),
                    React.createElement("option", { value: "Snow" }, "Snow")
                  ])
                ])
              ])
            )
          )
        ]),
        React.createElement(SectionCard, { title: "Hourly Projection Template", subtitle: "Applies to all days" }, [
          React.createElement("div", { className: "space-y-3" },
            form.demand.hourlyTemplate.map((h: any, idx: number) =>
              React.createElement("div", { key: idx, className: "grid grid-cols-1 md:grid-cols-3 gap-3 items-end" }, [
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Hour"),
                  React.createElement("input", { className: inputClass, value: h.hour, onChange: (e: any) => updateHourlyTemplate(idx, { hour: e.target.value }) })
                ]),
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Projected revenue"),
                  React.createElement("input", { type: "number", className: inputClass, value: h.projectedRevenue, onChange: (e: any) => updateHourlyTemplate(idx, { projectedRevenue: toNumber(e.target.value) }) })
                ]),
                React.createElement("div", { className: "text-[10px] text-slate-400 font-semibold pb-2" }, "Template")
              ])
            )
          ),
          React.createElement("button", { className: "mt-4 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50", onClick: addHourlyTemplateRow }, "Add Hour")
        ])
      ]),

      React.createElement(SectionCard, { title: "Peak Hours This Week", subtitle: "Demand intensity" }, [
        React.createElement("div", { className: "space-y-3" },
          form.demand.peakHours.map((item: any, idx: number) =>
            React.createElement("div", { key: idx, className: "grid grid-cols-1 md:grid-cols-4 gap-3 items-end" }, [
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Day"),
                React.createElement("input", { className: inputClass, value: item.day, onChange: (e: any) => updatePeakHourRow(idx, { day: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Hours"),
                React.createElement("input", { className: inputClass, value: item.hours, onChange: (e: any) => updatePeakHourRow(idx, { hours: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Intensity"),
                React.createElement("input", { type: "number", className: inputClass, value: item.intensity, onChange: (e: any) => updatePeakHourRow(idx, { intensity: toNumber(e.target.value) }) })
              ]),
              React.createElement("div", { className: "text-[10px] text-slate-400 font-semibold pb-2" }, "0-100")
            ])
          )
        ),
        React.createElement("button", { className: "mt-4 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50", onClick: addPeakHourRow }, "Add Peak Slot")
      ]),

      React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6" }, [
        React.createElement(SectionCard, { title: "Staffing Summary", subtitle: "Costs and guidance" }, [
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Staffing cost"),
              React.createElement("input", { type: "number", className: inputClass, value: form.staffing.costVsSales.staffingCost, onChange: (e: any) => updateStaffingCost({ staffingCost: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Expected revenue"),
              React.createElement("input", { type: "number", className: inputClass, value: form.staffing.costVsSales.expectedRevenue, onChange: (e: any) => updateStaffingCost({ expectedRevenue: toNumber(e.target.value) }) })
            ])
          ]),
          React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" }, [
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Staff cost %"),
              React.createElement("input", { type: "number", className: inputClass, value: form.staffing.costVsSales.staffCostPercent, onChange: (e: any) => updateStaffingCost({ staffCostPercent: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Range low"),
              React.createElement("input", { type: "number", className: inputClass, value: form.staffing.costVsSales.rangeLow, onChange: (e: any) => updateStaffingCost({ rangeLow: toNumber(e.target.value) }) })
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: labelClass }, "Range high"),
              React.createElement("input", { type: "number", className: inputClass, value: form.staffing.costVsSales.rangeHigh, onChange: (e: any) => updateStaffingCost({ rangeHigh: toNumber(e.target.value) }) })
            ])
          ]),
          React.createElement("div", { className: "mt-4" }, [
            React.createElement("label", { className: labelClass }, "Status note"),
            React.createElement("textarea", { className: "w-full p-3 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm h-20 resize-none", value: form.staffing.costVsSales.statusNote, onChange: (e: any) => updateStaffingCost({ statusNote: e.target.value }) })
          ]),
          React.createElement("div", { className: "mt-4" }, [
            React.createElement("label", { className: labelClass }, "Recommendation"),
            React.createElement("textarea", { className: "w-full p-3 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm h-24 resize-none", value: form.staffing.recommendation, onChange: (e: any) => updateStaffingRecommendation(e.target.value) })
          ])
        ]),
        React.createElement(SectionCard, { title: "Coverage Slots", subtitle: "Demand vs staffing" }, [
          React.createElement("div", { className: "space-y-3" },
            form.staffing.coverage.map((slot: any, idx: number) =>
              React.createElement("div", { key: idx, className: "grid grid-cols-1 md:grid-cols-4 gap-3 items-end" }, [
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Hour"),
                  React.createElement("input", { className: inputClass, value: slot.hour, onChange: (e: any) => updateCoverageRow(idx, { hour: e.target.value }) })
                ]),
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Demand"),
                  React.createElement("input", { type: "number", className: inputClass, value: slot.demand, onChange: (e: any) => updateCoverageRow(idx, { demand: toNumber(e.target.value) }) })
                ]),
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Staff count"),
                  React.createElement("input", { type: "number", className: inputClass, value: slot.staff, onChange: (e: any) => updateCoverageRow(idx, { staff: toNumber(e.target.value) }) })
                ]),
                React.createElement("div", {}, [
                  React.createElement("label", { className: labelClass }, "Detail"),
                  React.createElement("input", { className: inputClass, value: slot.detail || "", onChange: (e: any) => updateCoverageRow(idx, { detail: e.target.value }) })
                ])
              ])
            )
          ),
          React.createElement("button", { className: "mt-4 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50", onClick: addCoverageRow }, "Add Slot")
        ])
      ]),

      React.createElement(SectionCard, { title: "Today's Rota", subtitle: "Shift roster" }, [
        React.createElement(RotaEditor, {
          rows: form.staffing.rota,
          onDraftChange: (rows: any[]) => { rotaDraftRef.current = rows; }
        })
      ])
    ]),

    React.createElement("div", { className: "flex justify-end pt-6 border-t border-slate-200 mt-8" }, [
      React.createElement("button", {
        onClick: () => onSave({
          ...form,
          staffing: {
            ...form.staffing,
            rota: rotaDraftRef.current
          },
          demand: {
            ...form.demand,
            weekly: form.demand.weekly.map((day: any) => ({
              ...day,
              hourlyTrend: form.demand.hourlyTemplate
            }))
          }
        }),
        className: "bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black shadow-[0_18px_35px_-20px_rgba(37,99,235,0.65)] hover:bg-indigo-700 hover:scale-[1.02] transition-all text-sm uppercase tracking-widest flex items-center gap-2"
      }, ["Confirm & Update Dashboard"])
    ])
  ]);
}

function MarketingListEditor({
  rows,
  columns,
  onDraftChange,
  addLabel,
  createRow,
  gridClassName,
  inputClass,
  labelClass,
  textAreaClass,
  hideRemove
}: any) {
  const [draft, setDraft] = useState<any[]>(rows || []);

  useEffect(() => {
    setDraft(rows || []);
  }, [rows]);

  useEffect(() => {
    if (onDraftChange) onDraftChange(draft);
  }, [draft, onDraftChange]);

  const updateRow = (idx: number, key: string, value: any) => {
    setDraft((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  const addRow = () => setDraft((prev) => ([...prev, createRow()]));
  const removeRow = (idx: number) => setDraft((prev) => prev.filter((_: any, i: number) => i !== idx));

  return React.createElement("div", { className: "space-y-3" }, [
    ...draft.map((row, idx) =>
      React.createElement("div", { key: idx, className: gridClassName || "grid grid-cols-1 md:grid-cols-4 gap-3 items-end" }, [
        ...columns.map((col: any) =>
          React.createElement("div", { key: col.key, className: col.className || "" }, [
            React.createElement("label", { className: labelClass }, col.label),
            col.type === "textarea"
              ? React.createElement("textarea", {
                className: `${textAreaClass} ${col.inputClass || ""}`,
                value: row[col.key] ?? "",
                rows: col.rows || 3,
                onChange: (e: any) => updateRow(idx, col.key, e.target.value)
              })
              : React.createElement("input", {
                className: `${inputClass} ${col.inputClass || ""}`,
                value: row[col.key] ?? "",
                inputMode: col.inputMode,
                onChange: (e: any) => updateRow(idx, col.key, e.target.value)
              })
          ])
        ),
        !hideRemove && React.createElement("button", {
          className: "h-[42px] bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50",
          onClick: () => removeRow(idx)
        }, "Remove")
      ])
    ),
    React.createElement("button", {
      className: "mt-4 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50",
      onClick: addRow
    }, addLabel)
  ]);
}

function ManualMarketingView({ onSave, onCancel, initialData }: { onSave: (data: any) => void; onCancel: () => void; initialData?: any }) {
  const [form, setForm] = useState(() => normalizeManualMarketingData(initialData));
  const [reachSeriesText, setReachSeriesText] = useState<string>(() => String((form.reach?.series || []).join(", ")));
  const [reachLabels, setReachLabels] = useState<string[]>(() => form.reach?.labels || []);

  const alertsDraftRef = useRef(form.alerts || []);
  const platformsDraftRef = useRef(form.platforms || []);
  const postsDraftRef = useRef(form.topPosts || []);
  const reviewsDraftRef = useRef(form.reviews || []);

  const inputClass = "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm";
  const labelClass = "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block";
  const textAreaClass = "w-full p-3 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm";

  const SectionCard = ({ title, subtitle, children }: any) => React.createElement("div", { className: "bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm" }, [
    React.createElement("div", { className: "mb-4" }, [
      React.createElement("h3", { className: "text-[11px] font-black uppercase tracking-widest text-slate-500" }, title),
      subtitle && React.createElement("p", { className: "text-[11px] text-slate-400 mt-1" }, subtitle)
    ]),
    children
  ]);

  const updateKpis = (patch: any) => setForm((prev: any) => ({ ...prev, kpis: { ...prev.kpis, ...patch } }));
  const updateAlertSummary = (patch: any) => setForm((prev: any) => ({ ...prev, alertSummary: { ...prev.alertSummary, ...patch } }));
  const updateContentFocus = (key: string, patch: any) => setForm((prev: any) => ({
    ...prev,
    contentFocus: {
      ...prev.contentFocus,
      copy: {
        ...prev.contentFocus.copy,
        [key]: {
          ...prev.contentFocus.copy[key],
          ...patch
        }
      }
    }
  }));

  const updateReachLabel = (idx: number, value: string) => {
    setReachLabels((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const contentFocusKeys = ["Awareness", "Reputation", "Offers"] as const;

  return React.createElement("div", { className: "relative overflow-hidden max-w-6xl mx-auto p-6 md:p-8 bg-white rounded-3xl shadow-[0_24px_80px_-50px_rgba(15,23,42,0.55)] border border-slate-200/80 animate-in slide-in-from-bottom-4" }, [
    React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
    React.createElement("div", { className: "flex justify-between items-center mb-8" }, [
      React.createElement("div", {}, [
        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2" }, "Marketing & Reputation"),
        React.createElement("h2", { className: "text-2xl font-black text-gray-900" }, "Manual Data Update")
      ]),
      React.createElement("button", { onClick: onCancel, className: "text-slate-600 font-black text-[10px] uppercase tracking-widest bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-xl transition-colors" }, "Cancel")
    ]),

    React.createElement("div", { className: "space-y-8" }, [
      React.createElement(SectionCard, { title: "KPI Strip", subtitle: "Executive snapshot values" }, [
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4" }, [
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Reputation score"),
            React.createElement("input", { className: inputClass, value: form.kpis.reputationScore, onChange: (e: any) => updateKpis({ reputationScore: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Review velocity (30d)"),
            React.createElement("input", { className: inputClass, value: form.kpis.reviewVelocity, onChange: (e: any) => updateKpis({ reviewVelocity: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Total reach (30d)"),
            React.createElement("input", { className: inputClass, value: form.kpis.totalReach, onChange: (e: any) => updateKpis({ totalReach: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Engagement rate (%)"),
            React.createElement("input", { className: inputClass, value: form.kpis.engagementRate, onChange: (e: any) => updateKpis({ engagementRate: e.target.value }) })
          ])
        ]),
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4 mt-4" }, [
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Reputation max"),
            React.createElement("input", { className: inputClass, value: form.kpis.reputationMax, onChange: (e: any) => updateKpis({ reputationMax: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Review velocity delta (%)"),
            React.createElement("input", { className: inputClass, value: form.kpis.reviewVelocityDelta, onChange: (e: any) => updateKpis({ reviewVelocityDelta: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Reach delta (%)"),
            React.createElement("input", { className: inputClass, value: form.kpis.totalReachDelta, onChange: (e: any) => updateKpis({ totalReachDelta: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Engagement delta (pts)"),
            React.createElement("input", { className: inputClass, value: form.kpis.engagementDelta, onChange: (e: any) => updateKpis({ engagementDelta: e.target.value }) })
          ])
        ])
      ]),

      React.createElement(SectionCard, { title: "Reputation Alert Tile", subtitle: "Summary copy and alert signals" }, [
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" }, [
          React.createElement("div", { className: "md:col-span-2" }, [
            React.createElement("label", { className: labelClass }, "Title"),
            React.createElement("input", { className: inputClass, value: form.alertSummary.title, onChange: (e: any) => updateAlertSummary({ title: e.target.value }) })
          ]),
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Priority label"),
            React.createElement("input", { className: inputClass, value: form.alertSummary.priority, onChange: (e: any) => updateAlertSummary({ priority: e.target.value }) })
          ])
        ]),
        React.createElement("div", { className: "mt-4" }, [
          React.createElement("label", { className: labelClass }, "Subtitle"),
          React.createElement("input", { className: inputClass, value: form.alertSummary.subtitle, onChange: (e: any) => updateAlertSummary({ subtitle: e.target.value }) })
        ]),
        React.createElement("div", { className: "mt-4" }, [
          React.createElement("label", { className: labelClass }, "Insight copy"),
          React.createElement("textarea", { className: `${textAreaClass} h-24`, value: form.alertSummary.insight, onChange: (e: any) => updateAlertSummary({ insight: e.target.value }) })
        ]),
        React.createElement("div", { className: "mt-6" }, [
          React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3" }, "Alert items"),
          React.createElement(MarketingListEditor, {
            rows: form.alerts,
            onDraftChange: (rows: any[]) => { alertsDraftRef.current = rows; },
            addLabel: "Add Alert",
            createRow: () => ({ platform: "", user: "", rating: "", text: "", date: "", avatar: "", impact: "" }),
            gridClassName: "grid grid-cols-1 md:grid-cols-6 gap-3 items-end",
            inputClass,
            labelClass,
            textAreaClass,
            columns: [
              { key: "platform", label: "Platform" },
              { key: "user", label: "User" },
              { key: "rating", label: "Rating", inputMode: "decimal" },
              { key: "impact", label: "Impact" },
              { key: "date", label: "Date" },
              { key: "text", label: "Text", type: "textarea", className: "md:col-span-2", rows: 2 }
            ]
          })
        ])
      ]),

      React.createElement(SectionCard, { title: "Content Focus Playbooks", subtitle: "Update recommended actions per focus" }, [
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" }, [
          React.createElement("div", {}, [
            React.createElement("label", { className: labelClass }, "Default focus"),
            React.createElement("select", {
              className: inputClass,
              value: form.contentFocus.defaultFocus,
              onChange: (e: any) => setForm((prev: any) => ({ ...prev, contentFocus: { ...prev.contentFocus, defaultFocus: e.target.value } }))
            }, [
              React.createElement("option", { value: "Awareness" }, "Awareness"),
              React.createElement("option", { value: "Reputation" }, "Reputation"),
              React.createElement("option", { value: "Offers" }, "Offers")
            ])
          ])
        ]),
        React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-4" }, contentFocusKeys.map((focus) =>
          React.createElement("div", { key: focus, className: "border border-slate-200 rounded-2xl p-4 bg-slate-50/70" }, [
            React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3" }, focus),
            React.createElement("div", { className: "space-y-3" }, [
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Headline"),
                React.createElement("input", { className: inputClass, value: form.contentFocus.copy[focus].headline, onChange: (e: any) => updateContentFocus(focus, { headline: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Subtext"),
                React.createElement("textarea", { className: `${textAreaClass} h-20`, value: form.contentFocus.copy[focus].sub, onChange: (e: any) => updateContentFocus(focus, { sub: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Best channel"),
                React.createElement("input", { className: inputClass, value: form.contentFocus.copy[focus].channel, onChange: (e: any) => updateContentFocus(focus, { channel: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Best window"),
                React.createElement("input", { className: inputClass, value: form.contentFocus.copy[focus].window, onChange: (e: any) => updateContentFocus(focus, { window: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Metric label"),
                React.createElement("input", { className: inputClass, value: form.contentFocus.copy[focus].metricLabel, onChange: (e: any) => updateContentFocus(focus, { metricLabel: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "Metric value"),
                React.createElement("input", { className: inputClass, value: form.contentFocus.copy[focus].metricValue, onChange: (e: any) => updateContentFocus(focus, { metricValue: e.target.value }) })
              ]),
              React.createElement("div", {}, [
                React.createElement("label", { className: labelClass }, "CTA"),
                React.createElement("input", { className: inputClass, value: form.contentFocus.copy[focus].cta, onChange: (e: any) => updateContentFocus(focus, { cta: e.target.value }) })
              ])
            ])
          ])
        ))
      ]),

      React.createElement(SectionCard, { title: "Platform Connections", subtitle: "Cards in the platform strip" }, [
        React.createElement(MarketingListEditor, {
          rows: form.platforms,
          onDraftChange: (rows: any[]) => { platformsDraftRef.current = rows; },
          addLabel: "Add Platform",
          createRow: () => ({ name: "", icon: "", status: "", followers: "", engagement: "", rating: "", reviews: "" }),
          gridClassName: "grid grid-cols-1 md:grid-cols-7 gap-3 items-end",
          inputClass,
          labelClass,
          textAreaClass,
          columns: [
            { key: "name", label: "Name", className: "md:col-span-2" },
            { key: "icon", label: "Icon" },
            { key: "status", label: "Status" },
            { key: "followers", label: "Followers" },
            { key: "engagement", label: "Engagement" },
            { key: "rating", label: "Rating" },
            { key: "reviews", label: "Reviews" }
          ]
        })
      ]),

      React.createElement(SectionCard, { title: "Reach Trend", subtitle: "Chart series values" }, [
        React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-4" }, [
          React.createElement("div", { className: "lg:col-span-2" }, [
            React.createElement("label", { className: labelClass }, "Series values (comma separated)"),
            React.createElement("textarea", { className: `${textAreaClass} h-24`, value: reachSeriesText, onChange: (e: any) => setReachSeriesText(e.target.value) })
          ]),
          React.createElement("div", { className: "space-y-3" }, [
            React.createElement("label", { className: labelClass }, "Axis labels"),
            [0, 1, 2].map((idx) => React.createElement("input", {
              key: idx,
              className: inputClass,
              value: reachLabels[idx] || "",
              placeholder: `Label ${idx + 1}`,
              onChange: (e: any) => updateReachLabel(idx, e.target.value)
            }))
          ])
        ])
      ]),

      React.createElement(SectionCard, { title: "Top Content This Month", subtitle: "Cards inside the top content tile" }, [
        React.createElement(MarketingListEditor, {
          rows: form.topPosts,
          onDraftChange: (rows: any[]) => { postsDraftRef.current = rows; },
          addLabel: "Add Post",
          createRow: () => ({ id: Date.now(), platform: "", type: "", reach: "", engagement: "", sentiment: "", date: "", thumbnail: "" }),
          gridClassName: "grid grid-cols-1 md:grid-cols-7 gap-3 items-end",
          inputClass,
          labelClass,
          textAreaClass,
          columns: [
            { key: "platform", label: "Platform" },
            { key: "type", label: "Type" },
            { key: "reach", label: "Reach" },
            { key: "engagement", label: "Engagement" },
            { key: "sentiment", label: "Sentiment" },
            { key: "date", label: "Date" },
            { key: "thumbnail", label: "Thumb" }
          ]
        })
      ]),

      React.createElement(SectionCard, { title: "Recent Reviews", subtitle: "Cards inside the reviews tile" }, [
        React.createElement(MarketingListEditor, {
          rows: form.reviews,
          onDraftChange: (rows: any[]) => { reviewsDraftRef.current = rows; },
          addLabel: "Add Review",
          createRow: () => ({ platform: "", user: "", rating: "", text: "", date: "", avatar: "" }),
          gridClassName: "grid grid-cols-1 md:grid-cols-6 gap-3 items-end",
          inputClass,
          labelClass,
          textAreaClass,
          columns: [
            { key: "platform", label: "Platform" },
            { key: "user", label: "User" },
            { key: "rating", label: "Rating", inputMode: "decimal" },
            { key: "date", label: "Date" },
            { key: "avatar", label: "Avatar" },
            { key: "text", label: "Review Text", type: "textarea", className: "md:col-span-2", rows: 2 }
          ]
        })
      ])
    ]),

    React.createElement("div", { className: "flex justify-end pt-6 border-t border-slate-200 mt-8" }, [
      React.createElement("button", {
        onClick: () => {
          const parsedSeries = reachSeriesText.split(",").map((v: string) => Number(v.trim())).filter((v: number) => Number.isFinite(v));
          onSave({
            ...form,
            kpis: {
              ...form.kpis,
              reputationScore: Number(form.kpis.reputationScore),
              reputationMax: Number(form.kpis.reputationMax),
              reviewVelocity: Number(form.kpis.reviewVelocity),
              reviewVelocityDelta: Number(form.kpis.reviewVelocityDelta),
              totalReach: Number(form.kpis.totalReach),
              totalReachDelta: Number(form.kpis.totalReachDelta),
              engagementRate: Number(form.kpis.engagementRate),
              engagementDelta: Number(form.kpis.engagementDelta)
            },
            alertSummary: { ...form.alertSummary },
            alerts: alertsDraftRef.current,
            platforms: platformsDraftRef.current,
            topPosts: postsDraftRef.current,
            reviews: reviewsDraftRef.current,
            reach: {
              series: parsedSeries.length > 0 ? parsedSeries : form.reach.series,
              labels: reachLabels.filter(Boolean).length > 0 ? reachLabels : form.reach.labels
            }
          });
        },
        className: "bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black shadow-[0_18px_35px_-20px_rgba(37,99,235,0.65)] hover:bg-indigo-700 hover:scale-[1.02] transition-all text-sm uppercase tracking-widest flex items-center gap-2"
      }, ["Confirm & Update Dashboard"])
    ])
  ]);
}

function InventoryView({ onNavigate, uploadedFiles, manualData, setShowUploadModal }: {
  onNavigate: (view: any) => void;
  uploadedFiles: any[];
  manualData?: any;
  setShowUploadModal: (show: boolean) => void;
}) {
  const [data, setData] = useState(() => {
    const liveItemsCount = findMetricInFiles("inventory_items", uploadedFiles);
    const topItems = findRawMetricInFiles("top_items", uploadedFiles);
    let mock = generateMockInventory();

    if (manualData) {
      if (manualData.items && Array.isArray(manualData.items)) mock.items = manualData.items;
      if (manualData.waste && Array.isArray(manualData.waste)) mock.waste = manualData.waste;
    } else if (liveItemsCount && topItems && Array.isArray(topItems)) {
      const realItems: InventoryItem[] = topItems.map((ti, idx) => ({
        id: `live_${idx}`,
        name: ti.name,
        category: "Dry Goods",
        currentQty: ti.qty,
        unit: "pcs",
        minThreshold: Math.floor(ti.qty * 0.1),
        avgDailyUsage: 1,
        costPerUnit: 0,
        supplierId: "square",
        lastDelivered: new Date().toISOString(),
        status: (ti.qty < 5 ? "Critical" : (ti.qty < 10 ? "Low" : "OK")) as InventoryStatus
      }));
      mock.items = [...realItems, ...mock.items.slice(realItems.length)];
    }
    return mock;
  });

  const [loading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'critical' | 'waste' | 'overstocked' | 'reorder'>('all');
  const [activeTab, setActiveTab] = useState<'stock' | 'waste' | 'reordering'>('stock');
  const [showUploadMenu, setShowUploadMenu] = useState(false);

  useEffect(() => {
    // Keep data in sync if props change
    const liveItemsCount = findMetricInFiles("inventory_items", uploadedFiles);
    const topItems = findRawMetricInFiles("top_items", uploadedFiles);
    let mock = generateMockInventory();

    if (manualData) {
      if (manualData.items && Array.isArray(manualData.items)) mock.items = manualData.items;
      if (manualData.waste && Array.isArray(manualData.waste)) mock.waste = manualData.waste;
    } else if (liveItemsCount && topItems && Array.isArray(topItems)) {
      const realItems: InventoryItem[] = topItems.map((ti, idx) => ({
        id: `live_${idx}`,
        name: ti.name,
        category: "Dry Goods",
        currentQty: ti.qty,
        unit: "pcs",
        minThreshold: Math.floor(ti.qty * 0.1),
        avgDailyUsage: 1,
        costPerUnit: 0,
        supplierId: "square",
        lastDelivered: new Date().toISOString(),
        status: (ti.qty < 5 ? "Critical" : (ti.qty < 10 ? "Low" : "OK")) as InventoryStatus
      }));
      mock.items = [...realItems, ...mock.items.slice(realItems.length)];
    }
    setData(mock);
  }, [uploadedFiles, manualData]);

  if (loading || !data) {
    return React.createElement("div", { className: "p-8 text-center text-gray-500" }, [
      React.createElement("div", { key: "spin", className: "animate-spin text-3xl mb-2" }, "↻"),
      "Loading Inventory Intelligence..."
    ]);
  }

  const { items, waste, suppliers } = data;

  // -- KPIs --
  const totalValue = items.reduce((acc, item) => acc + (item.currentQty * item.costPerUnit), 0);
  const lowStockCount = items.filter(i => i.status === "Low" || i.status === "Critical").length;
  const overStockCount = items.filter(i => i.status === "Overstocked").length;
  const avgStockDays = items.length > 0
    ? items.reduce((acc, item) => acc + (item.currentQty / (item.avgDailyUsage || 1)), 0) / items.length
    : 0;

  // -- Components --

  const InventoryCard = ({ title, subtitle, right, tone = "neutral", children }: any) => {
    const toneStyles: Record<string, { bg: string; border: string; bar: string; title: string }> = {
      neutral: { bg: "bg-white", border: "border-slate-200", bar: "from-slate-300 via-slate-200 to-slate-100", title: "text-slate-900" },
      indigo: { bg: "bg-indigo-50/40", border: "border-indigo-100", bar: "from-indigo-600 via-indigo-400 to-sky-300", title: "text-indigo-900" },
      amber: { bg: "bg-amber-50/40", border: "border-amber-100", bar: "from-amber-500 via-amber-300 to-orange-200", title: "text-amber-900" },
      emerald: { bg: "bg-emerald-50/40", border: "border-emerald-100", bar: "from-emerald-500 via-emerald-300 to-teal-200", title: "text-emerald-900" },
    };
    const styles = toneStyles[tone] || toneStyles.neutral;

    return React.createElement("div", { className: `relative overflow-hidden rounded-2xl border ${styles.border} ${styles.bg} p-6 shadow-sm` }, [
      React.createElement("div", { className: `absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${styles.bar}` }),
      (title || right) && React.createElement("div", { className: "flex items-start justify-between mb-4" }, [
        React.createElement("div", {}, [
          title && React.createElement("h3", { className: `text-sm font-black uppercase tracking-widest ${styles.title}` }, title),
          subtitle && React.createElement("p", { className: "text-xs text-slate-500 mt-1" }, subtitle)
        ]),
        right && React.createElement("div", {}, right)
      ]),
      children
    ]);
  };

  const KpiWidget = ({ label, value, sub, accent }: any) => (
    React.createElement("div", { className: "relative overflow-hidden bg-white p-4 rounded-2xl border border-slate-200 shadow-sm" }, [
      React.createElement("div", { className: `absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent || "from-indigo-600 via-sky-400 to-emerald-300"}` }),
      React.createElement("p", { key: "l", className: "text-[10px] font-black text-slate-400 uppercase tracking-widest" }, label),
      React.createElement("p", { key: "v", className: "text-2xl font-black mt-2 text-slate-900" }, value),
      sub && React.createElement("p", { key: "s", className: "text-[11px] text-slate-500 mt-1" }, sub)
    ])
  );

  return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500" }, [

    // 1. Header & Overview
    React.createElement("div", { key: "overview" }, [
      React.createElement("div", {
        className: "relative overflow-visible mb-6 rounded-[32px] border border-indigo-100/60 bg-white p-8 shadow-sm",
        style: {
          background: "linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(255,255,255,0.98) 40%, rgba(14,165,233,0.08) 100%)"
        }
      }, [
        React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
        React.createElement("div", { className: "flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6" }, [
          React.createElement("div", {}, [
            React.createElement("h2", { className: "text-3xl font-black text-gray-900 mb-2" }, "Inventory Intelligence"),
            React.createElement("p", { className: "text-gray-500 font-medium" }, "AI-managed stock levels, waste tracking, and supplier optimization."),
          ]),
          React.createElement("div", { className: "relative pb-1" }, [
            React.createElement("button", {
              key: "upload-btn",
              onClick: () => setShowUploadMenu(!showUploadMenu),
              className: "group flex items-center gap-2 bg-indigo-600 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100/50 transition-all hover:scale-105 active:scale-95"
            }, [
              React.createElement("span", { key: "icon", className: "text-base group-hover:rotate-90 transition-transform duration-300" }, "+"),
              "Upload"
            ]),
            showUploadMenu && React.createElement("div", {
              className: "absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2"
            }, [
              React.createElement("button", {
                className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2",
                onClick: () => { setShowUploadModal(true); setShowUploadMenu(false); }
              }, ["Upload Document"]),
              React.createElement("button", {
                className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2 border-t border-gray-50",
                onClick: () => onNavigate("manual-inventory")
              }, ["Data Form"])
            ])
          ])
        ])
      ]),

    ]),

    // KPI Strip
    React.createElement("div", { key: "inventory-kpis", className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" }, [
      React.createElement(KpiWidget, { label: "Total Stock Value", value: `£${totalValue.toFixed(2)}`, sub: "vs Last Week: +2.4%", accent: "from-indigo-600 via-sky-400 to-emerald-300" }),
      React.createElement(KpiWidget, { label: "Avg Days Stock", value: `${avgStockDays.toFixed(1)} Days`, sub: "Optimal range: 5-7 days", accent: "from-sky-500 via-indigo-400 to-violet-400" }),
      React.createElement(KpiWidget, { label: "Low/Critical Items", value: lowStockCount.toString(), sub: "Action needed today", accent: "from-rose-600 via-orange-400 to-amber-300" }),
      React.createElement(KpiWidget, { label: "Overstocked", value: overStockCount.toString(), sub: "Potential waste risk", accent: "from-amber-500 via-yellow-300 to-orange-200" }),
    ]),

    // Tab Navigation
    React.createElement("div", {
      key: "tabs",
      className: "inline-flex gap-1 bg-slate-100 rounded-full p-1 shadow-sm border border-slate-200 mb-6"
    }, [
      { id: 'stock', label: 'Stock Levels' },
      { id: 'waste', label: 'Waste & Shrinkage Control' },
      { id: 'reordering', label: 'Reordering & Suppliers' }
    ].map(tab =>
      React.createElement("button", {
        key: tab.id,
        onClick: () => setActiveTab(tab.id as any),
        className: `px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`
      }, tab.label)
    )),

    // TAB 1: STOCK LEVELS (Includes AI Insight, Priorities, Risk, KPIs, and Table)
    activeTab === 'stock' && React.createElement("div", { key: "tab-stock", className: "space-y-8 animate-in fade-in slide-in-from-bottom-4" }, [
      // Top Row: AI Insight & Priorities
      React.createElement("div", { key: "top-row", className: "grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" }, [
        // Inventory Insight
        React.createElement(InventoryCard, { key: "ai-insight", title: "Stock Coverage Insight", subtitle: "Coverage summary", tone: "indigo" }, [
          React.createElement("p", { className: "text-sm text-indigo-800 leading-relaxed font-medium" },
            manualData?.insight || `You have approximately ${Math.round(avgStockDays)} days of stock remaining overall. £${Math.round(totalValue * 0.15)} is tied up in slow-moving items like Basmati Rice.`
          )
        ]),

        // Today's Inventory Priorities
        React.createElement(InventoryCard, { key: "priorities", title: "Today's Priorities", subtitle: "Focus for today", tone: "neutral" }, [
          React.createElement("div", { key: "list", className: "space-y-3" }, [
            ...items.filter(i => i.status === "Critical").map(i =>
              React.createElement("div", { key: i.id, className: "flex items-center justify-between text-sm" }, [
                React.createElement("span", { className: "font-medium text-slate-700" }, `Reorder ${i.name} today`),
                React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-rose-600 bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-full" }, "Critical")
              ])
            ),
            ...items.filter(i => i.status === "Overstocked" && i.category === "Produce").map(i =>
              React.createElement("div", { key: i.id, className: "flex items-center justify-between text-sm" }, [
                React.createElement("span", { className: "font-medium text-slate-700" }, `Reduce ${i.name} order by 30%`),
                React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full" }, "Overstock")
              ])
            ),
            items.every(i => i.category !== "Dry Goods" || i.status === "OK") &&
            React.createElement("div", { key: "dry-goods", className: "flex items-center justify-between text-sm" }, [
              React.createElement("span", { className: "font-medium text-slate-700" }, "No action needed on dry goods"),
              React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full" }, "Stable")
            ])
          ])
        ])
      ]),

      // Risk Outlook & Cash Summary Row
      React.createElement("div", { key: "risk-cash-row", className: "grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" }, [
        // Next 7 Days Risk Outlook
        React.createElement(InventoryCard, {
          key: "risk-outlook",
          title: "Next 7 Days Risk",
          subtitle: "Operational exposure",
          tone: "amber",
          right: React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-100" }, "Elevated")
        }, [
          React.createElement("div", { className: "space-y-3" }, [
            React.createElement("div", { key: "stockouts", className: "flex justify-between items-center" }, [
              React.createElement("span", { className: "text-sm text-slate-600" }, "Likely stockouts"),
              React.createElement("span", { className: "text-sm font-black text-rose-600" }, items.filter(i => i.currentQty / (i.avgDailyUsage || 1) < 7).length)
            ]),
            React.createElement("div", { key: "waste-risk", className: "flex justify-between items-center" }, [
              React.createElement("span", { className: "text-sm text-slate-600" }, "Expected waste risk"),
              React.createElement("span", { className: "text-sm font-black text-amber-600" }, `£${(totalValue * 0.08).toFixed(0)}`)
            ]),
            React.createElement("div", { key: "cash-tied", className: "flex justify-between items-center" }, [
              React.createElement("span", { className: "text-sm text-slate-600" }, "Cash tied (no action)"),
              React.createElement("span", { className: "text-sm font-black text-slate-900" }, `£${(totalValue * 0.25).toFixed(0)}`)
            ]),
            React.createElement("div", { key: "progress", className: "pt-2" }, [
              React.createElement("div", { className: "text-[10px] text-slate-400 uppercase font-black mb-1" }, "Risk level"),
              React.createElement("div", { className: "h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" },
                React.createElement("div", { className: "h-full bg-gradient-to-r from-amber-500 to-orange-400", style: { width: "65%" } })
              )
            ])
          ])
        ]),

        // Cash & Margin Impact Summary
        React.createElement(InventoryCard, {
          key: "cash-impact",
          title: "Cash & Margin Impact",
          subtitle: "Working capital",
          tone: "emerald",
          right: React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-100" }, "This Week")
        }, [
          React.createElement("div", { className: "space-y-3" }, [
            React.createElement("div", { key: "slow-movers", className: "flex justify-between items-center" }, [
              React.createElement("span", { className: "text-sm text-slate-600" }, "Cash in slow movers"),
              React.createElement("span", { className: "text-sm font-black text-slate-900" }, `£${items.filter(i => i.status === "Overstocked").reduce((acc, cur) => acc + (cur.currentQty * cur.costPerUnit), 0).toFixed(0)}`)
            ]),
            React.createElement("div", { key: "margin-loss", className: "flex justify-between items-center" }, [
              React.createElement("span", { className: "text-sm text-slate-600" }, "Est. margin loss (week)"),
              React.createElement("span", { className: "text-sm font-black text-rose-600" }, `£${waste.reduce((acc, cur) => acc + cur.value, 0).toFixed(0)}`)
            ]),
            React.createElement("div", { key: "waste-rev", className: "flex justify-between items-center" }, [
              React.createElement("span", { className: "text-sm text-slate-600" }, "Waste as % of revenue"),
              React.createElement("span", { className: "text-sm font-black text-amber-600" }, "2.4%")
            ]),
            React.createElement("div", { key: "note", className: "mt-3 p-3 bg-emerald-50/70 rounded-lg text-xs text-emerald-800 leading-relaxed border border-emerald-100" },
              "Addressing overstocked produce could free up ~£450 in working capital this month."
            )
          ])
        ])
      ]),

      // Stock Levels Table Section
      React.createElement(InventoryCard, { key: "stock-levels-section", title: "Stock Levels", subtitle: "Prioritized by urgency", tone: "neutral" }, [
        // Quick Toggles
        React.createElement("div", { className: "flex flex-wrap gap-2 mb-4" }, [
          { id: 'all', label: 'All Items' },
          { id: 'critical', label: 'Critical Only' },
          { id: 'waste', label: 'Waste Risk' },
          { id: 'overstocked', label: 'Overstocked' },
          { id: 'reorder', label: 'Reorder Today' }
        ].map(t =>
          React.createElement("button", {
            key: t.id,
            onClick: () => setFilter(t.id as any),
            className: `px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border ${filter === t.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`
          }, t.label)
        )),

        React.createElement("div", { className: "bg-white border border-slate-200 rounded-xl overflow-hidden" }, [
          React.createElement("table", { className: "w-full text-left text-sm" }, [
            React.createElement("thead", { className: "bg-slate-50 border-b border-slate-200" }, [
              React.createElement("tr", {}, [
                ["Item", "Category", "Qty", "Min", "Usage", "Status", "Action"].map(h =>
                  React.createElement("th", { key: h, className: "px-6 py-4 font-black text-slate-400 text-[10px] uppercase tracking-widest" }, h)
                )
              ])
            ]),
            React.createElement("tbody", { className: "divide-y divide-slate-100" },
              items
                .filter(item => {
                  if (filter === 'critical') return item.status === 'Critical';
                  if (filter === 'waste') return item.category === 'Produce' && item.status === 'Overstocked';
                  if (filter === 'overstocked') return item.status === 'Overstocked';
                  if (filter === 'reorder') return item.status === 'Critical' || item.status === 'Low';
                  return true;
                })
                .sort((a, _b) => (filter === 'all' && a.status === 'Critical' ? -1 : 0))
                .map(item =>
                  React.createElement("tr", { key: item.id, className: "hover:bg-slate-50/60 transition-colors" }, [
                    React.createElement("td", { key: "name", className: "px-6 py-4 font-bold text-slate-900" }, item.name),
                    React.createElement("td", { key: "cat", className: "px-6 py-4 text-slate-500" }, item.category),
                    React.createElement("td", { key: "qty", className: "px-6 py-4 font-medium" }, `${item.currentQty} ${item.unit}`),
                    React.createElement("td", { key: "min", className: "px-6 py-4 text-slate-400" }, item.minThreshold),
                    React.createElement("td", { key: "usage", className: "px-6 py-4 text-slate-500" }, `${item.avgDailyUsage}/day`),
                    React.createElement("td", { key: "stats", className: "px-6 py-4" },
                      React.createElement("span", {
                        className: `inline-flex px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${item.status === "Critical" ? "bg-rose-50 text-rose-700 border border-rose-100" :
                          item.status === "Low" ? "bg-amber-50 text-amber-700 border border-amber-100" :
                            item.status === "Overstocked" ? "bg-yellow-50 text-yellow-700 border border-yellow-100" :
                              "bg-emerald-50 text-emerald-700 border border-emerald-100"
                          }`
                      }, item.status)
                    ),
                    React.createElement("td", { key: "act", className: "px-6 py-4" },
                      (item.status === "Critical" || item.status === "Low")
                        ? React.createElement("button", { className: "text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:underline" }, "Reorder")
                        : React.createElement("span", { className: "text-slate-300 text-[10px] uppercase tracking-widest font-black" }, "Monitor")
                    ),
                  ])
                )
            )
          ])
        ])
      ])
    ]),

    // TAB 2: WASTE & SHRINKAGE
    activeTab === 'waste' && React.createElement("div", { key: "tab-waste", className: "space-y-8 animate-in fade-in slide-in-from-bottom-4" }, [
      React.createElement("div", { key: "waste-content" }, [
        React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-8" }, [
          // Waste Log
          React.createElement(InventoryCard, { key: "log", title: "Recent Waste Log", subtitle: "Tracked impact on margins", tone: "amber" }, [
            React.createElement("div", { className: "space-y-3" },
              waste.map(w =>
                React.createElement("div", { key: w.id, className: "flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100" }, [
                  React.createElement("div", {}, [
                    React.createElement("p", { className: "font-bold text-sm text-slate-900" }, w.itemName),
                    React.createElement("p", { className: "text-xs text-slate-500" }, `${w.reason} • ${w.quantity} units`),
                  ]),
                  React.createElement("p", { className: "font-bold text-rose-600" }, `-£${w.value.toFixed(2)}`)
                ])
              )
            )
          ]),

          // Insights
          React.createElement("div", { key: "insights", className: "space-y-4" }, [
            React.createElement(InventoryCard, { title: "Waste Insight", subtitle: "Primary driver", tone: "amber" }, [
              React.createElement("p", { className: "text-sm text-amber-800 leading-relaxed" },
                "Fresh produce accounts for 52% of total waste this month. Tomatoes are the highest contributor due to spoilage."
              )
            ]),
            React.createElement(InventoryCard, { title: "Theft Alert", subtitle: "Last 7 days", tone: "neutral" }, [
              React.createElement("p", { className: "text-sm text-slate-600" }, "No significant theft anomalies detected in the last 7 days.")
            ])
          ])
        ])
      ])
    ]),

    // TAB 3: REORDERING
    activeTab === 'reordering' && React.createElement("div", { key: "tab-reordering", className: "space-y-8 animate-in fade-in slide-in-from-bottom-4" }, [
      React.createElement("div", { key: "reordering-content" }, [
        React.createElement(InventoryCard, { title: "Reordering & Suppliers", subtitle: "Suggested orders for today", tone: "indigo" }, [
          React.createElement("div", { className: "bg-indigo-900 text-white rounded-2xl p-8 shadow-xl" }, [
            React.createElement("div", { className: "flex justify-between items-center mb-6" }, [
              React.createElement("h4", { className: "text-2xl font-black" }, "Morning Order List"),
              React.createElement("button", { className: "bg-white text-indigo-900 px-6 py-2 rounded-lg font-black hover:bg-indigo-50 transition-colors text-xs uppercase tracking-widest" }, "Order All Items")
            ]),

            React.createElement("div", { className: "space-y-4" },
              items.filter(i => i.status === "Critical").map(item =>
                React.createElement("div", { key: `order_${item.id}`, className: "flex items-center justify-between bg-indigo-800/50 p-4 rounded-xl border border-indigo-700" }, [
                  React.createElement("div", {}, [
                    React.createElement("p", { className: "font-bold text-lg" }, item.name),
                    React.createElement("p", { className: "text-indigo-200 text-sm" },
                      `${suppliers.find(s => s.id === item.supplierId)?.name} • Lead time: ${suppliers.find(s => s.id === item.supplierId)?.leadTimeDays} days`
                    )
                  ]),
                  React.createElement("div", { className: "text-right" }, [
                    React.createElement("p", { className: "font-black text-xl" }, `${(item.minThreshold - item.currentQty) + 5} ${item.unit}`),
                    React.createElement("p", { className: "text-indigo-300 text-sm uppercase tracking-widest" }, "Suggested Qty")
                  ])
                ])
              )
            )
          ])
        ])
      ])
    ])

  ]);
}

function IntegrationDataViewer({ isOpen, onClose, appName, files }: { isOpen: boolean; onClose: () => void; appName: string | null; files: any[] }) {
  if (!isOpen || !appName) return null;

  const dataFields = [
    { label: "Total Sales", key: "total_sales", icon: "💰", format: (v: number) => `£${v?.toLocaleString()}` },
    { label: "Average Order Value", key: "aov", icon: "📈", format: (v: number) => `£${v?.toFixed(2)}` },
    { label: "Total Covers", key: "covers", icon: "👤", format: (v: number) => v?.toLocaleString() },
    { label: "Inventory Items", key: "inventory_items", icon: "📦", format: (v: number) => v?.toLocaleString() },
    { label: "Total Stock Level", key: "total_stock", icon: "📊", format: (v: number) => v?.toLocaleString() },
    { label: "Gross Margin", key: "gross_margin", icon: "📊", format: (v: number) => `${v}%` },
    { label: "Waste Rate", key: "waste_rate", icon: "🗑️", format: (v: number) => `${v}%` },
    { label: "Social Reach", key: "social_reach", icon: "📱", format: (v: number) => v?.toLocaleString() },
    { label: "Average Rating", key: "avg_rating", icon: "⭐", format: (v: number) => Number.isFinite(v) ? v.toFixed(1) : String(v) },
    { label: "Review Count", key: "review_count", icon: "📝", format: (v: number) => v?.toLocaleString() },
    { label: "Email Subscribers", key: "email_subscribers", icon: "✉️", format: (v: number) => v?.toLocaleString() },
    { label: "Ad Reach", key: "ad_reach", icon: "🎯", format: (v: number) => v?.toLocaleString() },
    { label: "Ad Impressions", key: "ad_impressions", icon: "👁️", format: (v: number) => v?.toLocaleString() },
    { label: "Ad Clicks", key: "ad_clicks", icon: "🖱️", format: (v: number) => v?.toLocaleString() },
    { label: "Ad Spend", key: "ad_spend", icon: "💷", format: (v: number) => `£${Number(v || 0).toFixed(2)}` },
    { label: "Xero Tenants", key: "tenants", icon: "🏢", format: (v: number) => v?.toLocaleString() },
  ];

  return React.createElement("div", {
    className: "fixed inset-0 z-[60] flex items-center justify-center p-4"
  }, [
    React.createElement("div", {
      key: "overlay",
      onClick: onClose,
      className: "absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
    }),
    React.createElement("div", {
      key: "modal",
      className: "relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
    }, [
      // Header
      React.createElement("div", { className: "p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50" }, [
        React.createElement("div", { className: "flex items-center gap-3" }, [
          React.createElement("div", { className: "w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white text-xl font-bold" }, appName.charAt(0)),
          React.createElement("div", {}, [
            React.createElement("h3", { className: "font-black text-gray-900" }, `${appName} Shared Data`),
            React.createElement("p", { className: "text-[10px] text-gray-500 font-bold uppercase tracking-widest" }, "Active API Connection")
          ])
        ]),
        React.createElement("button", { onClick: onClose, className: "w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-400 transition-colors" }, "✕")
      ]),

      // Content
      React.createElement("div", { className: "p-6 space-y-4" }, [
        React.createElement("div", { className: "p-4 bg-indigo-50 rounded-2xl border border-indigo-100 flex items-start gap-4 mb-4" }, [
          React.createElement("span", { className: "text-2xl" }, "🧠"),
          React.createElement("p", { className: "text-xs text-indigo-900 leading-relaxed font-medium" },
            `Findsy is currently pulling the following metrics from ${appName}. This data is updated automatically once per day.`
          )
        ]),

        React.createElement("div", { className: "grid grid-cols-1 gap-2" },
          (() => {
            const fieldsWithData = dataFields.filter(f => {
              const val = findMetricInFiles(f.key, files);
              return val !== undefined && val !== null;
            });

            if (fieldsWithData.length === 0) {
              return React.createElement("div", { className: "py-10 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200" }, [
                React.createElement("span", { className: "text-3xl mb-2 block" }, "📂"),
                React.createElement("p", { className: "text-sm font-bold text-gray-400" }, "No shared data found for this period."),
                React.createElement("p", { className: "text-[10px] text-gray-400 mt-1 max-w-[200px] mx-auto leading-relaxed" },
                  "Ensure your integration is connected and a successful sync has completed."
                )
              ]);
            }

            return fieldsWithData.map(f => {
              const val = findMetricInFiles(f.key, files);
              return React.createElement("div", { key: f.key, className: "flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl hover:border-indigo-200 transition-colors shadow-sm" }, [
                React.createElement("div", { className: "flex items-center gap-3" }, [
                  React.createElement("span", { className: "text-xl" }, f.icon),
                  React.createElement("span", { className: "text-sm font-bold text-gray-600" }, f.label)
                ]),
                React.createElement("span", { className: "text-lg font-black text-gray-900" }, f.format(val as number))
              ]);
            });
          })()
        ),

        (() => {
          const topItems = findRawMetricInFiles("top_items", files);
          if (!topItems || !Array.isArray(topItems) || topItems.length === 0) return null;

          return React.createElement("div", { className: "mt-6 animate-in slide-in-from-top-2 duration-500" }, [
            React.createElement("h4", { className: "text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1" }, "Top Stocked Items"),
            React.createElement("div", { className: "space-y-2" },
              topItems.map((item, idx) =>
                React.createElement("div", { key: idx, className: "flex items-center justify-between p-3 bg-gray-50/50 rounded-xl border border-gray-100" }, [
                  React.createElement("span", { className: "text-xs font-bold text-gray-700" }, item.name),
                  React.createElement("span", { className: "text-xs font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-lg" }, `${item.qty} in stock`)
                ])
              )
            )
          ]);
        })(),

        React.createElement("div", { className: "mt-6 p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-center" }, [
          React.createElement("div", {}, [
            React.createElement("p", { className: "text-[10px] text-gray-400 font-bold uppercase tracking-widest" }, "Last Successful Sync"),
            React.createElement("p", { className: "text-xs font-bold text-gray-900" }, new Date().toLocaleString())
          ]),
          React.createElement("button", { className: "px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-100 transition-all" }, "Refresh Now")
        ])
      ]),

      // Actions
      React.createElement("div", { className: "p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3" }, [
        React.createElement("button", { onClick: onClose, className: "px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-bold text-sm transition-all" }, "Close"),
        React.createElement("button", {
          onClick: () => alert("Integration settings would open here"),
          className: "px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-lg shadow-indigo-100 transition-all"
        }, "Connection Settings")
      ])
    ])
  ]);
}

// Main Findsy App Component

function FindsyApp() {
  const isOAuthReturn = !!new URLSearchParams(window.location.search).get('code');

  const [activeAgent, setActiveAgent] = useState<AgentId>(null);
  const [routerMode, setRouterMode] = useState(true);
  const [currentView, setCurrentView] = useState<"chat" | "dashboard" | "upload" | "actions" | "channels" | "notifications" | "settings" | "staffing" | "invoices" | "inventory" | "manual-inventory" | "manual-menu" | "manual-sales" | "manual-marketing" | "menu" | "sales" | "scenarios" | "marketing" | "int_pos" | "int_delivery" | "int_middleware" | "int_reservations" | "int_labour" | "int_inventory" | "int_reviews" | "int_accounting" | "int_marketing">(isOAuthReturn ? "int_pos" : "dashboard");
  const [manualInventoryData, setManualInventoryData] = useState<any>(null);
  const [manualMenuData, setManualMenuData] = useState<any>(null);
  const [manualSalesData, setManualSalesData] = useState<any>(null);
  const [manualMarketingData, setManualMarketingData] = useState<any>(null);
  const marketingData = useMemo(() => normalizeManualMarketingData(manualMarketingData), [manualMarketingData]);
  const [activeContext, setActiveContext] = useState<"home" | "dashboards" | "actions" | "teams" | "channels" | "settings" | "staffing" | "invoices" | "scenarios" | "integrations">(isOAuthReturn ? "integrations" : "home");
  const [decisions, setDecisions] = useState<any[]>([]);
  const [targets, setTargets] = useState<Target[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [targetNotifications, setTargetNotifications] = useState<TargetNotification[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [connectedApps, setConnectedApps] = useState<string[]>(JSON.parse(localStorage.getItem('findsy_connected_apps') || '[]'));
  const [externalAlert, setExternalAlert] = useState<{ id?: string; message: string; agentType?: AgentId } | null>(null);
  const [prefillInput, setPrefillInput] = useState<string | null>(null);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [pendingDecisionContext, setPendingDecisionContext] = useState("");
  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [kpiLayout, setKpiLayout] = useState<string[]>(JSON.parse(localStorage.getItem('findsy_kpi_layout') || '["total_sales", "gross_margin", "prime_cost", "food_cost_pct", "labor_cost_pct", "waste_rate", "inventory_value", "covers"]'));
  const [venueName, setVenueName] = useState<string>("");
  const [venueId, setVenueId] = useState<string>("");
  const [navLayout, setNavLayout] = useState<"top" | "left">(() => {
    try {
      return localStorage.getItem("findsy_nav_layout") === "left" ? "left" : "top";
    } catch {
      return "top";
    }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("findsy_nav_collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [sidebarDashOpen, setSidebarDashOpen] = useState(false);
  const [sidebarIntOpen, setSidebarIntOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState<"document" | "image">("document");
  // Integration modal state
  const [integrationModalOpen, setIntegrationModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<{ name: string; category: string } | null>(null);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [integrationForms, setIntegrationForms] = useState<Record<string, Record<string, string>>>(() => {
    try {
      return JSON.parse(localStorage.getItem("findsy_integration_forms") || "{}");
    } catch {
      return {};
    }
  });
  const integrationForm = selectedApp ? (integrationForms[selectedApp.name] || {}) : {};
  const updateIntegrationForm = (key: string, value: string) => {
    if (!selectedApp) return;
    setIntegrationForms((prev) => {
      const next = {
        ...prev,
        [selectedApp.name]: {
          ...(prev[selectedApp.name] || {}),
          [key]: value,
        },
      };
      try {
        localStorage.setItem("findsy_integration_forms", JSON.stringify(next));
      } catch { }
      return next;
    });
  };
  const mergeIntegrationFormForApp = (appName: string, patch: Record<string, string>) => {
    if (!appName) return;
    setIntegrationForms((prev) => {
      const next = {
        ...prev,
        [appName]: {
          ...(prev[appName] || {}),
          ...(patch || {}),
        },
      };
      try {
        localStorage.setItem("findsy_integration_forms", JSON.stringify(next));
      } catch { }
      return next;
    });
  };
  const [integrationError, setIntegrationError] = useState<string | null>(null);
  const [isDataViewerOpen, setIsDataViewerOpen] = useState(false);
  const [viewingAppName, setViewingAppName] = useState<string | null>(null);
  const [squareAuth, setSquareAuth] = useState<SquareAuth | null>(readSquareAuth());
  // OAuth State (demo-friendly; replace with server-side env vars in production)
  const [oauthConfig] = useState({
    clientId: "sq0idp-vrBh0EQYUiagAlLGe40KMg",
    clientSecret: "sq0csp-3VeZjZIPrx3sfYn8ExdTUPmWkOYDalq7EH8cgpFEKPs"
  });
  const [useManualToken, setUseManualToken] = useState(false);
  const [useManualIntegrationAuth, setUseManualIntegrationAuth] = useState(false);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);

  // Click outside listener for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle Profile Dropdown
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
      // Handle Notification Dropdown
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target as Node)) {
        setShowNotificationsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const [schedule, setSchedule] = useState("weekly"); // default weekly
  const [syncStatus, setSyncStatus] = useState<string | null>(null); // null, 'authenticating', 'locating', 'extracting', 'syncing', 'reviewing', 'complete'
  const [extractedMetrics, setExtractedMetrics] = useState<any>(null);

  type IntegrationFieldDef = {
    key: string;
    label: string;
    placeholder?: string;
    type?: "text" | "password";
    optional?: boolean;
  };

  const OAUTH_INTEGRATIONS = new Set([
    "Findsy",
    "Xero",
    "QuickBooks",
    "Mailchimp",
    "Meta Ads",
    "Google Ads",
    "Deputy",
  ]);

  const isOAuthIntegration = (appName: string) => OAUTH_INTEGRATIONS.has(appName);
  const INTEGRATION_OAUTH_PENDING_KEY = "findsy_integration_oauth_pending";

  const getIntegrationOAuthSchema = (appName: string): { fields: IntegrationFieldDef[] } | null => {
    switch (appName) {
      case "Findsy":
        return {
          fields: [
            { key: "clientId", label: "Client ID", placeholder: "Findsy app client id", type: "text" },
            { key: "clientSecret", label: "Client Secret", placeholder: "Findsy app client secret", type: "password" },
          ]
        };
      case "Xero":
        return {
          fields: [
            { key: "clientId", label: "Client ID", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxx", type: "text" },
            { key: "clientSecret", label: "Client Secret", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxx", type: "password" },
          ]
        };
      case "QuickBooks":
        return {
          fields: [
            { key: "clientId", label: "Client ID", placeholder: "ABCD1234...", type: "text" },
            { key: "clientSecret", label: "Client Secret", placeholder: "xxxxxxxx", type: "password" },
            { key: "environment", label: "Environment (Optional)", placeholder: "production | sandbox", type: "text", optional: true },
          ]
        };
      case "Mailchimp":
        return {
          fields: [
            { key: "clientId", label: "Client ID", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxx", type: "text" },
            { key: "clientSecret", label: "Client Secret", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxx", type: "password" },
          ]
        };
      case "Meta Ads":
        return {
          fields: [
            { key: "clientId", label: "App ID", placeholder: "1234567890", type: "text" },
            { key: "clientSecret", label: "App Secret", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxx", type: "password" },
            { key: "adAccountId", label: "Ad Account ID (Optional)", placeholder: "1234567890", type: "text", optional: true },
          ]
        };
      case "Google Ads":
        return {
          fields: [
            { key: "developerToken", label: "Developer Token", placeholder: "XXXXXX", type: "password" },
            { key: "customerId", label: "Customer ID", placeholder: "123-456-7890", type: "text" },
            { key: "loginCustomerId", label: "Login Customer ID (Optional)", placeholder: "Manager account id", type: "text", optional: true },
            { key: "clientId", label: "OAuth Client ID", placeholder: "xxxx.apps.googleusercontent.com", type: "text" },
            { key: "clientSecret", label: "OAuth Client Secret", placeholder: "XXXX", type: "password" },
          ]
        };
      case "Deputy":
        return {
          fields: [
            { key: "install", label: "Deputy Install", placeholder: "your-install-name", type: "text" },
            { key: "geo", label: "Geo", placeholder: "uk | us | eu | au", type: "text" },
            { key: "clientId", label: "Client ID", placeholder: "xxxxxxxx", type: "text" },
            { key: "clientSecret", label: "Client Secret", placeholder: "xxxxxxxx", type: "password" },
          ]
        };
      default:
        return null;
    }
  };

  const getIntegrationSchema = (appName: string): { endpoint: string; fields: IntegrationFieldDef[] } | null => {
    switch (appName) {
      case "Findsy":
        return {
          endpoint: "/api/findsy-reservations-sync",
          fields: [
            { key: "accessToken", label: "Findsy Access Token", placeholder: "eyJ...", type: "password" },
          ]
        };
      case "Google Reviews":
        return {
          endpoint: "/api/google-reviews-sync",
          fields: [
            { key: "apiKey", label: "Google Maps API Key", placeholder: "AIza...", type: "password" },
            { key: "placeId", label: "Place ID (Recommended)", placeholder: "ChIJ...", type: "text", optional: true },
            { key: "query", label: "Lookup Query (Optional)", placeholder: "Venue name + city", type: "text", optional: true }
          ]
        };
      case "Yelp":
        return {
          endpoint: "/api/yelp-sync",
          fields: [
            { key: "apiKey", label: "Yelp API Key", placeholder: "Bearer token", type: "password" },
            { key: "businessId", label: "Business ID / Yelp URL (Recommended)", placeholder: "my-restaurant-london", type: "text", optional: true },
            { key: "phone", label: "Phone (Optional)", placeholder: "+4420...", type: "text", optional: true },
            { key: "term", label: "Search Term (Optional)", placeholder: "Restaurant name", type: "text", optional: true },
            { key: "location", label: "Search Location (Optional)", placeholder: "London, UK", type: "text", optional: true },
          ]
        };
      case "Mailchimp":
        return {
          endpoint: "/api/mailchimp-sync",
          fields: [
            { key: "apiKey", label: "Mailchimp API Key", placeholder: "xxxxxxxx-us21", type: "password" },
            { key: "serverPrefix", label: "Server Prefix (Optional)", placeholder: "us21", type: "text", optional: true },
          ]
        };
      case "Meta Ads":
        return {
          endpoint: "/api/meta-ads-sync",
          fields: [
            { key: "accessToken", label: "Meta Access Token", placeholder: "EAAB...", type: "password" },
            { key: "adAccountId", label: "Ad Account ID (Optional)", placeholder: "1234567890", type: "text", optional: true },
          ]
        };
      case "Google Ads":
        return {
          endpoint: "/api/google-ads-sync",
          fields: [
            { key: "developerToken", label: "Developer Token", placeholder: "XXXXXX", type: "password" },
            { key: "clientId", label: "OAuth Client ID", placeholder: "xxxx.apps.googleusercontent.com", type: "text" },
            { key: "clientSecret", label: "OAuth Client Secret", placeholder: "XXXX", type: "password" },
            { key: "refreshToken", label: "OAuth Refresh Token", placeholder: "1//...", type: "password" },
            { key: "customerId", label: "Customer ID", placeholder: "123-456-7890", type: "text" },
            { key: "loginCustomerId", label: "Login Customer ID (Optional)", placeholder: "Manager account id", type: "text", optional: true },
          ]
        };
      case "Xero":
        return {
          endpoint: "/api/xero-sync",
          fields: [
            { key: "accessToken", label: "Xero Access Token", placeholder: "eyJ...", type: "password" },
            { key: "tenantId", label: "Tenant ID (Optional)", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", type: "text", optional: true },
          ]
        };
      case "QuickBooks":
        return {
          endpoint: "/api/quickbooks-sync",
          fields: [
            { key: "accessToken", label: "QuickBooks Access Token", placeholder: "eyJ...", type: "password" },
            { key: "realmId", label: "Realm ID", placeholder: "123146123146", type: "text" },
            { key: "environment", label: "Environment (Optional)", placeholder: "production | sandbox", type: "text", optional: true },
          ]
        };
      case "7shifts":
        return {
          endpoint: "/api/7shifts-sync",
          fields: [
            { key: "accessToken", label: "7shifts Access Token", placeholder: "eyJ...", type: "password" },
            { key: "companyGuid", label: "Company GUID", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", type: "text" },
          ]
        };
      case "Deputy":
        return {
          endpoint: "/api/deputy-sync",
          fields: [
            { key: "install", label: "Deputy Install", placeholder: "your-install-name", type: "text" },
            { key: "geo", label: "Geo", placeholder: "uk | us | eu | au", type: "text" },
            { key: "accessToken", label: "Deputy Access Token", placeholder: "eyJ...", type: "password" },
          ]
        };
      default:
        return null;
    }
  };

  function buildIntegrationRedirectUri() {
    // Keep it simple (and consistent with Square) to avoid preview/prod mismatch.
    return `${window.location.origin}/`;
  }

  function buildOAuthState(provider: string) {
    return `findsy_${provider}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  function buildIntegrationAuthorizeUrl(appName: string, redirectUri: string, state: string, form: Record<string, string>) {
    switch (appName) {
      case "Findsy": {
        const baseUrl = "https://enterprise.findsy.ai";
        const clientId = String(form.clientId || "").trim();
        const url = new URL("/oauth/authorize", baseUrl);
        url.searchParams.set("response_type", "code");
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("state", state);
        return url.toString();
      }
      case "Xero": {
        const clientId = String(form.clientId || "").trim();
        const scope = [
          "offline_access",
          "openid",
          "profile",
          "email",
          "accounting.settings",
          "accounting.transactions",
        ].join(" ");
        const url = new URL("https://login.xero.com/identity/connect/authorize");
        url.searchParams.set("response_type", "code");
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("scope", scope);
        url.searchParams.set("state", state);
        url.searchParams.set("prompt", "consent");
        return url.toString();
      }
      case "QuickBooks": {
        const clientId = String(form.clientId || "").trim();
        const scope = ["com.intuit.quickbooks.accounting", "openid", "profile", "email"].join(" ");
        const url = new URL("https://appcenter.intuit.com/connect/oauth2");
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("scope", scope);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("response_type", "code");
        url.searchParams.set("state", state);
        return url.toString();
      }
      case "Mailchimp": {
        const clientId = String(form.clientId || "").trim();
        const url = new URL("https://login.mailchimp.com/oauth2/authorize");
        url.searchParams.set("response_type", "code");
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("state", state);
        return url.toString();
      }
      case "Meta Ads": {
        const clientId = String(form.clientId || "").trim(); // app id
        const scope = ["ads_read", "read_insights", "public_profile", "email"].join(",");
        const url = new URL("https://www.facebook.com/v20.0/dialog/oauth");
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("response_type", "code");
        url.searchParams.set("state", state);
        url.searchParams.set("scope", scope);
        return url.toString();
      }
      case "Google Ads": {
        const clientId = String(form.clientId || "").trim();
        const scope = ["https://www.googleapis.com/auth/adwords"].join(" ");
        const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("response_type", "code");
        url.searchParams.set("scope", scope);
        url.searchParams.set("access_type", "offline");
        url.searchParams.set("include_granted_scopes", "true");
        url.searchParams.set("prompt", "consent");
        url.searchParams.set("state", state);
        return url.toString();
      }
      case "Deputy": {
        const clientId = String(form.clientId || "").trim();
        const url = new URL("https://once.deputy.com/my/oauth/login");
        url.searchParams.set("response_type", "code");
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("state", state);
        return url.toString();
      }
      default:
        return null;
    }
  }

  function startIntegrationOAuth() {
    if (!selectedApp) return;
    const appName = selectedApp.name;
    setIntegrationError(null);

    const oauthSchema = getIntegrationOAuthSchema(appName);
    if (!oauthSchema) {
      setIntegrationError("OAuth isn't configured for this integration yet.");
      return;
    }

    for (const f of oauthSchema.fields) {
      const v = String(integrationForm?.[f.key] || "").trim();
      if (!f.optional && !v) {
        setIntegrationError(`Missing: ${f.label}`);
        return;
      }
    }

    const redirectUri = buildIntegrationRedirectUri();
    const state = buildOAuthState(appName);
    const authorizeUrl = buildIntegrationAuthorizeUrl(appName, redirectUri, state, integrationForm || {});
    if (!authorizeUrl) {
      setIntegrationError("Couldn't build an authorization URL for this provider.");
      return;
    }

    // Persist config to survive redirect
    try {
      localStorage.setItem(INTEGRATION_OAUTH_PENDING_KEY, JSON.stringify({
        provider: appName,
        category: selectedApp.category,
        redirectUri,
        state,
        form: integrationForm || {},
        createdAt: new Date().toISOString(),
      }));
    } catch { }

    window.location.href = authorizeUrl;
  }

  const submitIntegration = async () => {
    if (!selectedApp) return;
    setIntegrationError(null);

    setSyncStatus('authenticating');
    await new Promise(r => setTimeout(r, 1500));
    setSyncStatus('locating');
    // Step 2: Extract Reports
    await new Promise(r => setTimeout(r, 1500));
    setSyncStatus('extracting');

    let results: any = null;

    if (selectedApp.name === "Square") {
      // Real API Call to our new Serverless Function
      try {
        const resp = await fetch('/api/square-sales', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken: credentials.password }) // Using password field for token
        });

        if (!resp.ok) throw new Error("Square Connection Failed");
        results = await resp.json();
        persistSquareAuth({
          accessToken: credentials.password,
          updatedAt: new Date().toISOString()
        });
      } catch (e) {
        setIntegrationError("Failed to connect to Square. Please check your access token.");
        setSyncStatus(null);
        return;
      }
    } else {
      try {
        const schema = getIntegrationSchema(selectedApp.name);
        if (!schema) throw new Error(`Integration is not configured for ${selectedApp.name}`);

        // Validate required fields
        for (const f of schema.fields) {
          const v = String(integrationForm?.[f.key] || "").trim();
          const hasValue = v.length > 0;
          if (!f.optional && !hasValue) {
            throw new Error(`Missing: ${f.label}`);
          }
        }

        // App-specific validation for "either/or" inputs
        if (selectedApp.name === "Google Reviews") {
          const placeId = String(integrationForm?.placeId || "").trim();
          const query = String(integrationForm?.query || "").trim();
          if (!placeId && !query) {
            throw new Error("Provide either Place ID or Lookup Query.");
          }
        }
        if (selectedApp.name === "Yelp") {
          const businessId = String(integrationForm?.businessId || "").trim();
          const phone = String(integrationForm?.phone || "").trim();
          const term = String(integrationForm?.term || "").trim();
          const location = String(integrationForm?.location || "").trim();
          if (!businessId && !phone && !(term && location)) {
            throw new Error("Provide either Business ID/URL, Phone, or Term + Location.");
          }
        }

        const resp = await fetch(schema.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(integrationForm || {})
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const msg = String((data && (data.error || data.message)) || "Connection failed");
          throw new Error(msg);
        }
        results = data;
      } catch (e: any) {
        setIntegrationError(String(e?.message || e || "Integration failed"));
        setSyncStatus(null);
        return;
      }
    }

    setExtractedMetrics(results);
    setSyncStatus('reviewing');
  };

  const confirmIntegration = async () => {
    if (!selectedApp || !extractedMetrics) return;

    console.log("[Findsy] Confirming integration for:", selectedApp.name);
    setSyncStatus('syncing');

    try {
      if (supabase) {
        // 1. Insert/Update integration record
        const { error: intError } = await supabase.from('integrations').upsert([{
          user_id: user.id,
          venue_id: venueId,
          platform_name: selectedApp.name,
          category: selectedApp.category,
          status: 'connected',
          sync_frequency: schedule,
          last_sync: new Date().toISOString(),
          total_sales: extractedMetrics.total_sales,
          aov: extractedMetrics.aov,
          inventory_items: extractedMetrics.inventory_items,
          total_stock: extractedMetrics.total_stock,
          top_items: extractedMetrics.top_items
        }], { onConflict: 'user_id,platform_name' });

        if (intError) console.error("[Findsy] Error updating integration record:", intError);

        // Sync extracted metrics
        for (const [key, val] of Object.entries(extractedMetrics)) {
          await supaUpdateManualMetric(user.id, venueId, key, val);
        }

        // Optional: update the Marketing dashboard (manual_marketing.json) when the integration provides
        // marketing/reputation-oriented data. This keeps the visible dashboard in sync with live sources.
        if (selectedApp.name === "Google Reviews" || selectedApp.name === "Yelp" || selectedApp.name === "Mailchimp" || selectedApp.name === "Meta Ads" || selectedApp.name === "Google Ads") {
          try {
            const formatCompact = (value: number) => {
              if (!Number.isFinite(value)) return "0";
              if (value >= 1000000) return `${(value / 1000000).toFixed(value >= 10000000 ? 0 : 1)}m`;
              if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
              return String(Math.round(value));
            };

            const current = normalizeManualMarketingData(manualMarketingData);
            const next = { ...current };
            next.kpis = { ...(current.kpis || {}) };
            next.platforms = Array.isArray(current.platforms) ? [...current.platforms] : [];
            next.reviews = Array.isArray(current.reviews) ? [...current.reviews] : [];
            next.reach = { ...(current.reach || {}) };

            const upsertPlatform = (platform: any) => {
              next.platforms = (next.platforms || []).filter((p: any) => p?.name !== platform?.name);
              next.platforms.unshift(platform);
              next.platforms = next.platforms.slice(0, 6);
            };

            const recentReviews = Array.isArray((extractedMetrics as any)?.recent_reviews) ? (extractedMetrics as any).recent_reviews : [];

            if (selectedApp.name === "Google Reviews") {
              const rating = Number((extractedMetrics as any)?.avg_rating || 0);
              const count = Number((extractedMetrics as any)?.review_count || 0);
              if (Number.isFinite(rating) && rating > 0) next.kpis.reputationScore = rating;
              upsertPlatform({ name: "Google", icon: "GG", status: "Connected", rating: rating ? rating.toFixed(1) : "", reviews: count ? String(count) : "" });
              if (recentReviews.length > 0) {
                next.reviews = [...recentReviews, ...next.reviews.filter((r: any) => r?.platform !== "Google")].slice(0, 12);
              }
            }

            if (selectedApp.name === "Yelp") {
              const rating = Number((extractedMetrics as any)?.avg_rating || 0);
              const count = Number((extractedMetrics as any)?.review_count || 0);
              if (!next.kpis.reputationScore && Number.isFinite(rating) && rating > 0) next.kpis.reputationScore = rating;
              upsertPlatform({ name: "Yelp", icon: "YP", status: "Connected", rating: rating ? rating.toFixed(1) : "", reviews: count ? String(count) : "" });
              if (recentReviews.length > 0) {
                next.reviews = [...recentReviews, ...next.reviews.filter((r: any) => r?.platform !== "Yelp")].slice(0, 12);
              }
            }

            if (selectedApp.name === "Mailchimp") {
              const subscribers = Number((extractedMetrics as any)?.email_subscribers || 0);
              upsertPlatform({ name: "Mailchimp", icon: "MC", status: "Connected", followers: subscribers ? formatCompact(subscribers) : "0", engagement: "" });
            }

            if (selectedApp.name === "Meta Ads") {
              const reach = Number((extractedMetrics as any)?.ad_reach || 0);
              const spend = Number((extractedMetrics as any)?.ad_spend || 0);
              if (Number.isFinite(reach) && reach > 0) {
                next.kpis.totalReach = Math.max(Number(next.kpis.totalReach || 0), reach);
              }
              upsertPlatform({ name: "Meta", icon: "MA", status: "Connected", followers: reach ? formatCompact(reach) : "", engagement: spend ? `£${formatCompact(spend)}` : "" });
            }

            if (selectedApp.name === "Google Ads") {
              const impressions = Number((extractedMetrics as any)?.ad_impressions || 0);
              const spend = Number((extractedMetrics as any)?.ad_spend || 0);
              if (Number.isFinite(impressions) && impressions > 0) {
                next.kpis.totalReach = Math.max(Number(next.kpis.totalReach || 0), impressions);
              }
              // If we have a daily series, use it as the reach trend.
              const daily = Array.isArray((extractedMetrics as any)?.daily) ? (extractedMetrics as any).daily : [];
              if (daily.length > 0) {
                next.reach.series = daily.map((d: any) => Number(d?.impressions || 0));
                const first = String(daily[0]?.date || "");
                const last = String(daily[daily.length - 1]?.date || "");
                const mid = String(daily[Math.floor(daily.length / 2)]?.date || "");
                next.reach.labels = [first, mid, last].filter(Boolean);
              }
              upsertPlatform({ name: "Google Ads", icon: "GA", status: "Connected", followers: impressions ? formatCompact(impressions) : "", engagement: spend ? `£${formatCompact(spend)}` : "" });
            }

            // Persist + update state
            setManualMarketingData(next);
            await supaSaveManualMarketing(user.id, venueId || "", next);
          } catch (e) {
            console.warn("[Findsy] Marketing integration merge failed:", e);
          }
        }

        const safeSales = Number((extractedMetrics as any)?.total_sales || 0);
        const metricCount = Object.keys(extractedMetrics || {}).length;
        const notif: TargetNotification = {
          id: `int_${Date.now()}`,
          userId: user.id,
          targetId: 'system',
          targetName: 'Integration Success',
          metric: selectedApp.name,
          message: Number.isFinite(safeSales) && safeSales > 0
            ? `Sync complete. Imported £${Math.round(safeSales).toLocaleString()} in sales from ${selectedApp.name}.`
            : `Sync complete. Imported ${metricCount} data points from ${selectedApp.name}.`,
          agentType: 'accountant',
          createdAt: new Date().toISOString(),
          read: false
        };
        await supaSaveNotification(notif);
        setTargetNotifications(prev => [notif, ...prev]);

        console.log("[Findsy] Integration confirmed. Refreshing states...");
        await fetchConnectedApps();
        const f = await supaFetchFilesMetadata(user.id);
        console.log("[Findsy] Files metadata refreshed. Count:", f?.length);
        setFiles(f);
        storeFiles(f);
      }
    } catch (e) {
      console.error("[Findsy] Sync error:", e);
    }

    setSyncStatus('complete');
    await new Promise(r => setTimeout(r, 2000));
    setIntegrationModalOpen(false);
    setSyncStatus(null);
    setExtractedMetrics(null);
    setCredentials({ username: "", password: "" });
    setSelectedApp(null);
  };
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);

  const persistSquareAuth = (auth: SquareAuth) => {
    setSquareAuth(auth);
    storeSquareAuth(auth);
  };

  const refreshSquareMetrics = async (opts?: { accessToken?: string; silent?: boolean; }) => {
    if (!user) return null;
    const token = opts?.accessToken || squareAuth?.accessToken || readSquareAuth()?.accessToken;
    if (!token) return null;

    try {
      const resp = await fetch('/api/square-sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken: token })
      });

      if (!resp.ok) throw new Error("Square refresh failed");
      const data = await resp.json();

      const metricsToUpdate: Record<string, number> = {
        total_sales: data.total_sales,
        aov: data.aov,
        covers: data.covers ?? data.tx_count,
        sales_pace: data.sales_pace
      };

      for (const [key, val] of Object.entries(metricsToUpdate)) {
        if (val !== undefined && val !== null && !Number.isNaN(val)) {
          await supaUpdateManualMetric(user.id, venueId, key, val);
        }
      }

      if (supabase) {
        await supabase.from('integrations').upsert([{
          user_id: user.id,
          venue_id: venueId,
          platform_name: "Square",
          category: "pos",
          status: "connected",
          sync_frequency: schedule,
          last_sync: new Date().toISOString(),
          total_sales: data.total_sales,
          aov: data.aov,
          inventory_items: data.inventory_items,
          total_stock: data.total_stock,
          top_items: data.top_items
        }], { onConflict: 'user_id,platform_name' });
      }

      const f = await supaFetchFilesMetadata(user.id);
      setFiles(f);
      storeFiles(f);
      storeSquareLastSync(new Date().toISOString());

      return data;
    } catch (e) {
      console.error("[Findsy] Square refresh error:", e);
      if (!opts?.silent) {
        alert("Square refresh failed. Please reconnect your integration.");
      }
      return null;
    }
  };


  // 1. Initial Session Load
  useEffect(() => {
    if (!supabase) {
      setAuthLoading(false);
      return;
    }

    console.log("[Findsy] Initializing Auth Session...");

    // Check current session
    supabase.auth.getSession().then(({ data: { session } }: any) => {
      const u = session?.user ?? null;
      console.log("[Findsy] getSession result:", u ? "User Authenticated" : "No Session");
      setUser(u);
      if (u) {
        setVenueName(u.user_metadata?.venue_name || "");
        fetchVenueName(u.id);
        supaFetchManualInventory(u.id).then(data => data && setManualInventoryData(data));
        supaFetchManualMenu(u.id).then(data => data && setManualMenuData(data));
        supaFetchManualSales(u.id).then(data => data && setManualSalesData(data));
        supaFetchManualMarketing(u.id).then(data => data && setManualMarketingData(data));
      }
      setAuthLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: any, session: any) => {
      console.log("[Findsy] Auth State Change:", event);
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        setVenueName(u.user_metadata?.venue_name || "");
        fetchVenueName(u.id);
        supaFetchManualInventory(u.id).then(data => data && setManualInventoryData(data));
        supaFetchManualMenu(u.id).then(data => data && setManualMenuData(data));
        supaFetchManualSales(u.id).then(data => data && setManualSalesData(data));
        supaFetchManualMarketing(u.id).then(data => data && setManualMarketingData(data));
      } else {
        setVenueName("");
        setVenueId("");
      }
      setAuthLoading(false);
    });

    fetchConnectedApps();
    return () => subscription.unsubscribe();
  }, []);

  // 2. Handle Square OAuth Callback (Runs only when user is available)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const savedConfig = localStorage.getItem('findsy_square_oauth_temp');

    if (code && savedConfig && user && !isProcessingOAuth) {
      console.log("[Findsy] Redirect detected. Starting integration for user:", user.email);
      setIsProcessingOAuth(true);

      const { clientId, clientSecret } = JSON.parse(savedConfig);

      // Set correct view
      setCurrentView("int_pos");
      setActiveContext("integrations");

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      handleSquareCallback(code, clientId, clientSecret);
    }
  }, [user, isProcessingOAuth]);

  // 2b. Handle OAuth callbacks for other self-serve integrations
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const error = params.get("error") || params.get("error_description");
    const pendingRaw = localStorage.getItem(INTEGRATION_OAUTH_PENDING_KEY);
    const squareSavedConfig = localStorage.getItem("findsy_square_oauth_temp");

    // If Square is mid-flow, let the Square handler own the redirect.
    if (squareSavedConfig) return;

    if ((!code && !error) || !pendingRaw || !user || isProcessingOAuth) return;

    let pending: any = null;
    try {
      pending = JSON.parse(pendingRaw);
    } catch {
      pending = null;
    }

    if (!pending?.provider || !pending?.category) {
      try { localStorage.removeItem(INTEGRATION_OAUTH_PENDING_KEY); } catch { }
      return;
    }

    const returnedState = String(params.get("state") || "").trim();
    if (pending?.state && returnedState && pending.state !== returnedState) {
      console.warn("[Findsy] OAuth state mismatch. Aborting.");
      setSelectedApp({ name: pending.provider, category: pending.category });
      setIntegrationModalOpen(true);
      setIntegrationError("Security check failed (state mismatch). Please try connecting again.");
      setSyncStatus(null);
      try { localStorage.removeItem(INTEGRATION_OAUTH_PENDING_KEY); } catch { }
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Put the UI back in Integrations context + open the modal for feedback.
    setIsProcessingOAuth(true);
    setCurrentView((`int_${pending.category}` as any));
    setActiveContext("integrations");
    setSelectedApp({ name: pending.provider, category: pending.category });
    setIntegrationModalOpen(true);
    setUseManualIntegrationAuth(false);

    // Clean URL (so refresh doesn't re-trigger)
    window.history.replaceState({}, document.title, window.location.pathname);

    if (error) {
      setIntegrationError(String(error));
      setSyncStatus(null);
      try { localStorage.removeItem(INTEGRATION_OAUTH_PENDING_KEY); } catch { }
      setIsProcessingOAuth(false);
      return;
    }

    if (code) {
      handleIntegrationOAuthCallback(String(code), pending, {
        realmId: String(params.get("realmId") || params.get("realm_id") || "").trim(),
      });
    }
  }, [user, isProcessingOAuth]);

  const handleSquareCallback = async (code: string, clientId: string, clientSecret: string) => {
    try {
      // 1. Open Modal & Show Progress
      setSelectedApp({ name: "Square", category: "pos" }); // Restore context
      setIntegrationModalOpen(true);
      setSyncStatus('authenticating');

      // Reconstruct the exact same URI logic for verification (Must match start logic!)
      const redirectUri = window.location.origin.includes('localhost')
        ? (window.location.origin + "/")
        : (window.location.origin + "/"); // Always use current origin to stay on the same preview/production instance

      const tokenResp = await fetch('/api/square-oauth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, clientId, clientSecret, redirectUri })
      });

      if (!tokenResp.ok) throw new Error("OAuth Token Exchange Failed");
      const { accessToken, refreshToken, expiresAt, merchantId } = await tokenResp.json();

      // 3. Proceed with Extraction using the new Token
      setSyncStatus('locating');
      console.log("AI targeting: https://app.squareup.com/dashboard/sales/reports/sales-trends");
      await new Promise(r => setTimeout(r, 1500)); // UI pacing

      setSyncStatus('extracting');

      const dataResp = await fetch('/api/square-sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken })
      });

      if (!dataResp.ok) throw new Error("Data Extraction Failed");
      const results = await dataResp.json();

      persistSquareAuth({
        accessToken,
        refreshToken,
        expiresAt,
        merchantId,
        updatedAt: new Date().toISOString()
      });

      setExtractedMetrics(results);
      setSyncStatus('reviewing');

      // Cleanup
      localStorage.removeItem('findsy_square_oauth_temp');

    } catch (e) {
      console.error(e);
      alert("Square Connection Failed: " + String(e));
      setIntegrationModalOpen(false);
      setSyncStatus(null);
    } finally {
      setIsProcessingOAuth(false);
    }
  };

  const handleIntegrationOAuthCallback = async (
    code: string,
    pending: any,
    extra?: { realmId?: string }
  ) => {
    const provider = String(pending?.provider || "").trim();
    const redirectUri = String(pending?.redirectUri || buildIntegrationRedirectUri()).trim();
    const form = (pending?.form && typeof pending.form === "object") ? pending.form : {};

    const postJson = async (endpoint: string, payload: any) => {
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = String((data && (data.error || data.message)) || `Request failed (${resp.status})`);
        throw new Error(msg);
      }
      return data;
    };

    try {
      setIntegrationError(null);
      setSyncStatus("authenticating");

      let oauthData: any = null;
      let accessToken = "";

      // 1) Exchange code for tokens
      if (provider === "Findsy") {
        oauthData = await postJson("/api/findsy-oauth", {
          code,
          clientId: String(form.clientId || "").trim(),
          clientSecret: String(form.clientSecret || "").trim(),
          redirectUri,
          baseUrl: "https://enterprise.findsy.ai",
        });
        accessToken = String(oauthData?.accessToken || "").trim();
        mergeIntegrationFormForApp("Findsy", {
          accessToken,
          refreshToken: String(oauthData?.refreshToken || "").trim(),
          expiresAt: String(oauthData?.expiresAt || "").trim(),
        });
      } else if (provider === "Xero") {
        oauthData = await postJson("/api/xero-oauth", {
          code,
          clientId: String(form.clientId || "").trim(),
          clientSecret: String(form.clientSecret || "").trim(),
          redirectUri,
        });
        accessToken = String(oauthData?.accessToken || "").trim();
        mergeIntegrationFormForApp("Xero", {
          accessToken,
          refreshToken: String(oauthData?.refreshToken || "").trim(),
          expiresAt: String(oauthData?.expiresAt || "").trim(),
        });
      } else if (provider === "QuickBooks") {
        oauthData = await postJson("/api/quickbooks-oauth", {
          code,
          clientId: String(form.clientId || "").trim(),
          clientSecret: String(form.clientSecret || "").trim(),
          redirectUri,
        });
        accessToken = String(oauthData?.accessToken || "").trim();
        const resolvedRealmId = String(extra?.realmId || "").trim();
        const envRaw = String(form.environment || "").trim().toLowerCase();
        const environment = envRaw === "sandbox" ? "sandbox" : "production";
        mergeIntegrationFormForApp("QuickBooks", {
          accessToken,
          refreshToken: String(oauthData?.refreshToken || "").trim(),
          realmId: resolvedRealmId,
          environment,
          expiresAt: String(oauthData?.expiresAt || "").trim(),
        });
      } else if (provider === "Mailchimp") {
        oauthData = await postJson("/api/mailchimp-oauth", {
          code,
          clientId: String(form.clientId || "").trim(),
          clientSecret: String(form.clientSecret || "").trim(),
          redirectUri,
        });
        accessToken = String(oauthData?.accessToken || "").trim();
        mergeIntegrationFormForApp("Mailchimp", {
          accessToken,
          dc: String(oauthData?.dc || "").trim(),
          apiEndpoint: String(oauthData?.apiEndpoint || "").trim(),
        });
      } else if (provider === "Meta Ads") {
        oauthData = await postJson("/api/meta-oauth", {
          code,
          clientId: String(form.clientId || "").trim(),
          clientSecret: String(form.clientSecret || "").trim(),
          redirectUri,
          exchangeLongLived: true,
        });
        accessToken = String(oauthData?.accessToken || "").trim();
        mergeIntegrationFormForApp("Meta Ads", {
          accessToken,
          adAccountId: String(form.adAccountId || "").trim(),
        });
      } else if (provider === "Google Ads") {
        oauthData = await postJson("/api/google-ads-oauth", {
          code,
          clientId: String(form.clientId || "").trim(),
          clientSecret: String(form.clientSecret || "").trim(),
          redirectUri,
        });
        accessToken = String(oauthData?.accessToken || "").trim();
        mergeIntegrationFormForApp("Google Ads", {
          accessToken,
          refreshToken: String(oauthData?.refreshToken || "").trim(),
        });
      } else if (provider === "Deputy") {
        oauthData = await postJson("/api/deputy-oauth", {
          code,
          clientId: String(form.clientId || "").trim(),
          clientSecret: String(form.clientSecret || "").trim(),
          redirectUri,
        });
        accessToken = String(oauthData?.accessToken || "").trim();
        mergeIntegrationFormForApp("Deputy", {
          accessToken,
          install: String(form.install || "").trim(),
          geo: String(form.geo || "").trim(),
        });
      } else {
        throw new Error("Unsupported OAuth provider.");
      }

      if (!accessToken) throw new Error("OAuth token exchange succeeded but no access token was returned.");

      setSyncStatus("locating");
      await new Promise((r) => setTimeout(r, 900));

      // 2) Pull a preview of shared data (no secrets)
      setSyncStatus("extracting");

      let results: any = null;
      if (provider === "Findsy") {
        results = await postJson("/api/findsy-reservations-sync", { accessToken, baseUrl: "https://enterprise.findsy.ai" });
      } else if (provider === "Xero") {
        results = await postJson("/api/xero-sync", { accessToken });
      } else if (provider === "QuickBooks") {
        const resolvedRealmId = String(extra?.realmId || "").trim();
        if (!resolvedRealmId) throw new Error("QuickBooks returned no realmId. Please reconnect.");
        const envRaw = String(form.environment || "").trim().toLowerCase();
        const environment = envRaw === "sandbox" ? "sandbox" : "production";
        results = await postJson("/api/quickbooks-sync", { accessToken, realmId: resolvedRealmId, environment });
      } else if (provider === "Mailchimp") {
        results = await postJson("/api/mailchimp-sync", {
          accessToken,
          dc: String(oauthData?.dc || "").trim(),
          apiEndpoint: String(oauthData?.apiEndpoint || "").trim(),
        });
      } else if (provider === "Meta Ads") {
        const adAccountId = String(form.adAccountId || "").trim();
        results = await postJson("/api/meta-ads-sync", { accessToken, ...(adAccountId ? { adAccountId } : {}) });
      } else if (provider === "Google Ads") {
        const loginCustomerId = String(form.loginCustomerId || "").trim();
        const developerToken = String(form.developerToken || "").trim();
        const customerId = String(form.customerId || "").trim();
        results = await postJson("/api/google-ads-sync", {
          developerToken,
          customerId,
          ...(loginCustomerId ? { loginCustomerId } : {}),
          accessToken,
        });
      } else if (provider === "Deputy") {
        const install = String(form.install || "").trim();
        const geo = String(form.geo || "").trim();
        results = await postJson("/api/deputy-sync", { accessToken, install, geo });
      } else {
        throw new Error("No sync handler for this provider.");
      }

      setExtractedMetrics(results);
      setSyncStatus("reviewing");
    } catch (e: any) {
      console.error("[Findsy] OAuth integration failed:", e);
      setIntegrationError(String(e?.message || e || "OAuth integration failed"));
      setSyncStatus(null);
    } finally {
      try { localStorage.removeItem(INTEGRATION_OAUTH_PENDING_KEY); } catch { }
      setIsProcessingOAuth(false);
    }
  };

  const handleViewSharedData = (appName: string) => {
    if (appName === "Square") {
      refreshSquareMetrics({ silent: true });
    }
    setViewingAppName(appName);
    setIsDataViewerOpen(true);
  };

  const startSquareOAuth = () => {
    if (!oauthConfig.clientId || !oauthConfig.clientSecret) {
      alert("Please enter your App ID and Secret to initiate the secure flow.");
      return;
    }

    // Save creds to survive redirect
    localStorage.setItem('findsy_square_oauth_temp', JSON.stringify(oauthConfig));

    // Redirect URI must match exactly what is registered in Square
    // and what will be used in handleSquareCallback.
    const redirectUri = window.location.origin.includes('localhost')
      ? (window.location.origin + "/")
      : (window.location.origin + "/");

    console.log("[Findsy] Initiating Square OAuth with redirect_uri:", redirectUri);

    console.log("Using Redirect URI:", redirectUri); // Debug for user to see what to paste in Square
    const scope = "MERCHANT_PROFILE_READ PAYMENTS_READ ITEMS_READ INVENTORY_READ";
    const authUrl = `https://connect.squareup.com/oauth2/authorize?client_id=${oauthConfig.clientId}&scope=${scope}&session=false&redirect_uri=${encodeURIComponent(redirectUri)}`;

    window.location.href = authUrl;
  };

  const fetchConnectedApps = async () => {
    if (!supabase || !user) return;
    console.log("[Findsy] Fetching connected apps...");

    try {
      const { data, error } = await supabase.from('integrations').select('platform_name');

      let discoveredApps: string[] = [];

      if (data && !error) {
        discoveredApps = data.map((i: any) => i.platform_name);
        console.log("[Findsy] Discovered apps from integrations table:", discoveredApps);
      } else {
        if (error) console.warn("[Findsy] Integrations table access error (might not exist yet):", error.message);
      }

      // Fallback: Check manual_data.json for integration markers
      const { data: fileData } = await supabase
        .from('file_metadata')
        .select('content')
        .eq('user_id', user.id)
        .eq('file_name', 'manual_data.json')
        .single();

      if (fileData?.content) {
        try {
          const content = JSON.parse(fileData.content);
          // If we have total_sales and it came from Square integration flow
          if (content.total_sales !== undefined && !discoveredApps.includes("Square")) {
            console.log("[Findsy] Detecting Square integration via manual_data fallback.");
            discoveredApps.push("Square");
          }
        } catch (e) { }
      }

      setConnectedApps([...new Set(discoveredApps)]);
      localStorage.setItem('findsy_connected_apps', JSON.stringify([...new Set(discoveredApps)]));
    } catch (err) {
      console.error("[Findsy] Critical error in fetchConnectedApps:", err);
      // Last-last resort: load from local storage
      const cached = localStorage.getItem('findsy_connected_apps');
      if (cached) setConnectedApps(JSON.parse(cached));
    }
  };

  const fetchVenueName = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('venue_id, venues(name)')
        .eq('id', userId)
        .single();

      if (data) {
        setVenueId(data.venue_id);
        if ((data as any).venues) {
          setVenueName((data as any).venues.name);
        }
      }
    } catch (e: any) {
      console.error("Error fetching venue details:", e);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setCurrentView("dashboard");
  };


  const checkAllTargets = async () => {
    const currentTargets = await supaFetchTargets();
    const currentNotifications = await supaFetchNotifications();

    let targetsChanged = false;
    let notifsChanged = false;

    for (const t of currentTargets) {
      const res = await evaluateTarget(t, files);
      t.lastCheckedAt = new Date().toISOString();

      if (res.value !== undefined && res.value !== t.lastValue) {
        t.lastValue = res.value;
        targetsChanged = true;
      }

      if (res.triggered) {
        if (t.status !== "alerting") {
          t.status = "alerting";
          targetsChanged = true;

          const hasUnread = currentNotifications.some(n => n.targetId === t.id && !n.read);
          if (!hasUnread) {
            const notif: TargetNotification = {
              id: `tn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              userId: t.userId,
              targetId: t.id,
              targetName: t.name,
              metric: t.metric,
              message: `Target '${t.name}' triggered: ${t.metric} is ${res.value}`,
              agentType: t.agentType,
              createdAt: new Date().toISOString(),
              read: false,
              value: res.value || 0,
              threshold: t.threshold,
              direction: t.direction,
            };
            await supaSaveNotification(notif);
            notifsChanged = true;
          }
        }
      } else if (t.status === "alerting") {
        t.status = "ok";
        targetsChanged = true;
      }

      if (targetsChanged) {
        await supaSaveTarget(t);
      }
    }

    if (targetsChanged || notifsChanged) {
      const [newT, newN] = await Promise.all([
        supaFetchTargets(),
        supaFetchNotifications()
      ]);
      setTargets(newT);
      setTargetNotifications(newN);
    }
  };

  useEffect(() => {
    // Initial check
    checkAllTargets();

    // Periodic check every 60s
    const interval = setInterval(checkAllTargets, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSaveDecision = (context: string) => {
    setPendingDecisionContext(context);
    setIsDecisionModalOpen(true);
  };

  const confirmSaveDecision = async (decision: string, expectedImpact: string) => {
    try {
      const result = await saveDecisionWorkflow({
        userId: user?.id || "user123",
        decision,
        context: pendingDecisionContext,
        expectedImpact,
        agentType: activeAgent,
      });

      setDecisions((prev) => [result, ...prev]);

      // Save to Supabase
      await supaSaveDecision(result);
      // alert("Decision saved successfully!");
    } catch (error) {
      console.error("Error saving decision:", error);
      alert("Failed to save decision");
    }
  };

  const handleFileUploaded = (fileRecord: any) => {
    console.log("File uploaded:", fileRecord);
    if (!user?.id) return;

    // Update local state immediately for instant feedback
    setFiles(prev => [fileRecord, ...prev]);

    (async () => {
      // 1) If this upload matches a known table structure, ingest it into the scoped manual dataset
      //    so dashboards/tiles update from a single source of truth.
      try {
        const scope = inferUploadScope(fileRecord);
        if (scope) {
          const match = findBestRequirementTable(fileRecord?.content || "", scope);
          if (match) {
            const currentDatasets = {
              inventory: manualInventoryData,
              menu: manualMenuData,
              sales: manualSalesData,
              marketing: manualMarketingData
            };

            const nextDatasets: any = { ...currentDatasets };

            if (scope === "inventory") {
              const next = applyIngestToInventory(manualInventoryData, match.requirementKey, match.table);
              if (next) {
                nextDatasets.inventory = next;
                setManualInventoryData(next);
                await supaSaveManualInventory(user.id, venueId || "", next);
              }
            } else if (scope === "menu") {
              const next = applyIngestToMenu(manualMenuData, match.requirementKey, match.table);
              if (next) {
                nextDatasets.menu = next;
                setManualMenuData(next);
                await supaSaveManualMenu(user.id, venueId || "", next);
              }
            } else if (scope === "sales") {
              const next = applyIngestToSales(manualSalesData, match.requirementKey, match.table);
              if (next) {
                nextDatasets.sales = next;
                setManualSalesData(next);
                await supaSaveManualSales(user.id, venueId || "", next);
              }
            } else if (scope === "marketing") {
              const next = applyIngestToMarketing(manualMarketingData, match.requirementKey, match.table);
              if (next) {
                nextDatasets.marketing = next;
                setManualMarketingData(next);
                await supaSaveManualMarketing(user.id, venueId || "", next);
              }
            }

            const kpiPatch = computeKpiPatchFromManualData(nextDatasets);
            await supaUpsertManualMetrics(user.id, venueId || "", kpiPatch);
          }
        }
      } catch (e) {
        console.warn("[Findsy] Upload ingestion failed (continuing):", e);
      }

      // Re-evaluate targets
      const currentTargets = getStoredTargets();
      if (currentTargets.length > 0) {
        const files = getStoredFiles();
        for (const t of currentTargets) {
          const res = await evaluateTarget(t, files);
          t.lastCheckedAt = new Date().toISOString();
          if (res.value !== undefined) t.lastValue = res.value;
          if (res.triggered) {
            if (t.status !== "alerting") {
              t.status = "alerting";
              const notif: TargetNotification = {
                id: `tn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                userId: t.userId,
                targetId: t.id,
                targetName: t.name,
                metric: t.metric,
                message: `Target '${t.name}' triggered: ${t.metric} is ${res.value}`,
                agentType: t.agentType,
                createdAt: new Date().toISOString(),
                read: false,
                value: res.value,
                threshold: t.threshold,
                direction: t.direction,
              };
              await supaSaveNotification(notif);
            }
          } else {
            t.status = "ok";
          }
          await supaSaveTarget(t);
        }
      }

      // Always refresh global state
      const [newTargets, newNotifs, newFiles] = await Promise.all([
        supaFetchTargets(),
        supaFetchNotifications(),
        supaFetchFilesMetadata(user.id)
      ]);
      setTargets(newTargets);
      setTargetNotifications(newNotifs);
      setFiles(newFiles);
      storeFiles(newFiles);
    })();
  };

  const handleFileDeleted = (fileId: number) => {
    console.log("File deleted:", fileId);
    // Update local state immediately
    setFiles(prev => prev.filter(f => f.id !== fileId));

    // Refresh fully to ensure consistency
    (async () => {
      try {
        const newFiles = await supaFetchFilesMetadata(user.id);
        setFiles(newFiles);
        storeFiles(newFiles);
      } catch (e) {
        console.error("Error refreshing files after delete", e);
      }
    })();
  };

  useEffect(() => {
    const initData = async () => {
      const [t, n, d, f, p] = await Promise.all([
        supaFetchTargets(),
        supaFetchNotifications(),
        supaFetchDecisions(),
        supaFetchFilesMetadata(user.id),
        supaFetchVenueProfile(user.id)
      ]);
      setTargets(t);
      setTargetNotifications(n);
      setDecisions(d);
      setFiles(f);
      if (p?.kpi_layout) {
        setKpiLayout(p.kpi_layout);
        localStorage.setItem('findsy_kpi_layout', JSON.stringify(p.kpi_layout));
      }
      await fetchConnectedApps();
      // Sync local storage for helper functions that still use it
      storeFiles(f);
    };
    if (user) initData();
  }, [user]);

  // Auto-refresh integration status when entering integrations view
  useEffect(() => {
    if (user && (activeContext === "integrations" || currentView.startsWith("int_"))) {
      fetchConnectedApps();
    }
  }, [activeContext, currentView, user]);

  // Auto-refresh Square data when connected and viewing relevant areas
  useEffect(() => {
    if (!user) return;
    if (!connectedApps.includes("Square")) return;
    const auth = squareAuth || readSquareAuth();
    if (!auth?.accessToken) return;

    const lastSync = readSquareLastSync();
    const elapsed = lastSync ? (Date.now() - new Date(lastSync).getTime()) : Infinity;
    const shouldSync = elapsed > 5 * 60 * 1000; // 5 min
    const inRelevantView = activeContext === "home"
      || activeContext === "integrations"
      || currentView === "dashboard"
      || currentView.startsWith("int_");

    if (shouldSync && inRelevantView) {
      refreshSquareMetrics({ silent: true });
    }
  }, [user, connectedApps, squareAuth, activeContext, currentView]);

  // Init Menu Items once
  useEffect(() => {
    // 4. Ensure KPI layout contains all active metrics
    const activeKeys = ["total_sales", "gross_margin", "prime_cost", "food_cost_pct", "labor_cost_pct", "waste_rate", "inventory_value", "covers"];
    const currentLayout = JSON.parse(localStorage.getItem('findsy_kpi_layout') || '[]');
    const needsUpdate = activeKeys.some(k => !currentLayout.includes(k)) || currentLayout.some((k: any) => !activeKeys.includes(k));

    if (needsUpdate || currentLayout.length === 0) {
      setKpiLayout(activeKeys);
      localStorage.setItem('findsy_kpi_layout', JSON.stringify(activeKeys));
    }
  }, []);

  useEffect(() => {
    if (manualMenuData?.items && Array.isArray(manualMenuData.items) && manualMenuData.items.length > 0) {
      const normalized = manualMenuData.items.map(normalizeMenuItemInput).filter(Boolean) as MenuItemInput[];
      setMenuItems(enrichMenuItems(normalized));
    } else {
      setMenuItems(generateMockMenu());
    }
  }, [manualMenuData]);

  useEffect(() => {
    try {
      localStorage.setItem("findsy_nav_layout", navLayout);
    } catch { }
  }, [navLayout]);

  useEffect(() => {
    try {
      localStorage.setItem("findsy_nav_collapsed", String(sidebarCollapsed));
    } catch { }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!sidebarCollapsed) {
      setSidebarDashOpen(false);
      setSidebarIntOpen(false);
    }
  }, [sidebarCollapsed]);

  const handleReorderKpis = (newOrder: string[]) => {
    setKpiLayout(newOrder);
    localStorage.setItem('findsy_kpi_layout', JSON.stringify(newOrder));
    if (user) {
      supaUpdateKpiLayout(user.id, newOrder);
    }
  };

  const createNewTarget = () => {
    setIsTargetModalOpen(true);
  };

  const confirmCreateTarget = async (t: {
    name: string;
    metric: string;
    threshold: number;
    direction: "above" | "below";
    agentType: AgentId;
  }) => {
    // If files exist, store tags
    const files = getStoredFiles();
    const fileScopeTags = files.length > 0 ? files.map((f: any) => f.tags || []).flat().slice(0, 3) : [];

    const target: Target = {
      id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      userId: user?.id || "user123",
      name: t.name,
      metric: t.metric,
      threshold: t.threshold,
      direction: t.direction,
      tolerance: undefined,
      agentType: t.agentType,
      fileScopeTags,
      createdAt: new Date().toISOString(),
      status: "ok",
    };

    const current = [...targets];
    current.unshift(target);
    setTargets(current);
    setIsTargetModalOpen(false);

    // Save to Supabase
    await supaSaveTarget(target);
  };

  const runCheckForTarget = async (t: Target) => {
    // Set loading state
    t.isChecking = true;
    setTargets(prev => prev.map(tt => tt.id === t.id ? { ...t } : tt));

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));

    const files = getStoredFiles();
    const res = await evaluateTarget(t, files);
    t.lastCheckedAt = new Date().toISOString();
    t.isChecking = false;
    if (res.value !== undefined) t.lastValue = res.value;

    // Save state to Supabase
    await supaSaveTarget(t);

    if (res.triggered) {
      t.status = "alerting";
      const notifs = await supaFetchNotifications();
      // Only create a notification if there's no unread for this target
      const existingUnread = notifs.find(n => n.targetId === t.id && !n.read);
      if (!existingUnread) {
        const notif: TargetNotification = {
          id: `tn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          userId: t.userId,
          targetId: t.id,
          targetName: t.name,
          metric: t.metric,
          message: `Target '${t.name}' triggered: ${t.metric} is ${res.value}`,
          agentType: t.agentType,
          createdAt: new Date().toISOString(),
          read: false,
          value: res.value,
          threshold: t.threshold,
          direction: t.direction,
        };

        await supaSaveNotification(notif);
        setTargetNotifications([notif, ...notifs]);
      }
    } else {
      t.status = "ok";
    }
    const all = targets.map(tt => tt.id === t.id ? t : tt);
    setTargets(all);
  };

  const handleKpiExplain = (kpi: Kpi) => {
    // Determine which agent to route to based on KPI
    let targetAgent: AgentId = kpi.agentType || activeAgent;
    const kpiTitle = kpi.title.toLowerCase();

    if (!kpi.agentType) {
      if (kpiTitle.includes("revenue") || kpiTitle.includes("margin")) {
        targetAgent = "accountant";
      } else if (kpiTitle.includes("inventory") || kpiTitle.includes("ingredient")) {
        targetAgent = "chef";
      } else if (kpiTitle.includes("campaign") || kpiTitle.includes("marketing")) {
        targetAgent = "marketing";
      } else if (kpiTitle.includes("orders") || kpiTitle.includes("operations") || kpiTitle.includes("covers") || kpiTitle.includes("aov") || kpiTitle.includes("order value") || kpiTitle.includes("traffic")) {
        targetAgent = "manager";
      }
    }

    // Construct the message - simpler format as per requirements
    const message = `Explain: ${kpi.title}`;

    // Switch to chat view and prefill its input
    // Open the widget instead of expanded view
    setIsChatWidgetOpen(true);
    setRouterMode(false);
    setActiveAgent(targetAgent);
    setPrefillInput(message);
  };

  const handleKpiManualFill = (kpi: Kpi) => {
    setIsChatWidgetOpen(true);
    setRouterMode(false);
    setActiveAgent(normalizeAgentId(kpi.agentType || 'chef'));
    setPrefillInput(`I want to manually update the data for '${kpi.title}'. Please ask me what the value is so I can provide it.`);
  };

  const renderSidebarButton = (label: string, active: boolean, onClick: () => void, icon: React.ReactNode) => (
    React.createElement("button", {
      onClick,
      title: label,
      className: `w-full ${sidebarCollapsed ? "justify-center px-2 py-2" : "justify-start px-3 py-2"} rounded-xl text-sm font-semibold transition-all flex items-center gap-3 ${active ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-50"}`
    }, [
      React.createElement("span", { key: "icon", className: "text-gray-500" }, icon),
      !sidebarCollapsed && React.createElement("span", { key: "label", className: "text-xs leading-snug whitespace-normal" }, label)
    ])
  );

  const leftSidebar = navLayout === "left"
    ? React.createElement("aside", {
      key: "sidebar",
      className: `fixed left-0 top-0 bottom-0 ${sidebarCollapsed ? "w-20 px-3" : "w-64 px-4"} bg-white border-r border-gray-200 py-6 z-40 overflow-y-auto no-scrollbar`,
      style: { scrollbarWidth: "none", msOverflowStyle: "none" }
    }, [
      React.createElement("div", { key: "logo", className: `flex items-center ${sidebarCollapsed ? "justify-center" : "gap-3"} px-2 mb-6` }, [
        React.createElement("img", { key: "logo-img", src: "/logo.png", alt: "Findsy", className: "h-7 w-auto" }),
        !sidebarCollapsed && React.createElement("span", { key: "logo-text", className: "text-lg font-black text-gray-900" }, "Findsy")
      ]),
      React.createElement("button", {
        key: "collapse",
        onClick: () => setSidebarCollapsed(!sidebarCollapsed),
        className: `mb-5 ${sidebarCollapsed ? "mx-auto" : "mr-auto"} flex items-center justify-center rounded-xl border border-gray-200 w-9 h-9 text-gray-500 hover:bg-gray-50`
      }, React.createElement(Menu, { size: 18 })),
      React.createElement("div", { key: "nav", className: "space-y-5" }, [
        React.createElement("div", { key: "overview", className: "space-y-2" }, [
          !sidebarCollapsed && React.createElement("div", { className: "px-2 text-[10px] uppercase tracking-widest text-gray-400 font-black" }, "Overview"),
          renderSidebarButton("Home", activeContext === "home", () => {
            setActiveContext("home");
            setCurrentView("dashboard");
          }, React.createElement(Home, { size: 18 }))
        ]),
        React.createElement("div", { key: "dashboards", className: "space-y-2" }, [
          !sidebarCollapsed && React.createElement("div", { className: "px-2 text-[10px] uppercase tracking-widest text-gray-400 font-black" }, "Dashboards"),
          sidebarCollapsed
            ? React.createElement(React.Fragment, null, [
              renderSidebarButton("Dashboards", ["inventory", "menu", "sales", "marketing"].includes(currentView), () => {
                setActiveContext("dashboards");
                setSidebarDashOpen(!sidebarDashOpen);
              }, React.createElement(LayoutDashboard, { size: 18 })),
              sidebarDashOpen && React.createElement("div", { className: "space-y-2" }, [
                renderSidebarButton("Inventory", currentView === "inventory", () => {
                  setActiveContext("dashboards");
                  setCurrentView("inventory");
                }, React.createElement(Boxes, { size: 16 })),
                renderSidebarButton("Menu Intelligence", currentView === "menu", () => {
                  setActiveContext("dashboards");
                  setCurrentView("menu");
                }, React.createElement(UtensilsCrossed, { size: 16 })),
                renderSidebarButton("Sales, Demand & Staffing", currentView === "sales", () => {
                  setActiveContext("dashboards");
                  setCurrentView("sales");
                }, React.createElement(BarChart3, { size: 16 })),
                renderSidebarButton("Marketing", currentView === "marketing", () => {
                  setActiveContext("dashboards");
                  setCurrentView("marketing");
                }, React.createElement(Megaphone, { size: 16 }))
              ])
            ])
            : React.createElement(React.Fragment, null, [
              renderSidebarButton("Inventory", currentView === "inventory", () => {
                setActiveContext("dashboards");
                setCurrentView("inventory");
              }, React.createElement(Boxes, { size: 18 })),
              renderSidebarButton("Menu Intelligence", currentView === "menu", () => {
                setActiveContext("dashboards");
                setCurrentView("menu");
              }, React.createElement(UtensilsCrossed, { size: 18 })),
              renderSidebarButton("Sales, Demand & Staffing", currentView === "sales", () => {
                setActiveContext("dashboards");
                setCurrentView("sales");
              }, React.createElement(BarChart3, { size: 18 })),
              renderSidebarButton("Marketing", currentView === "marketing", () => {
                setActiveContext("dashboards");
                setCurrentView("marketing");
              }, React.createElement(Megaphone, { size: 18 }))
            ])
        ]),
        React.createElement("div", { key: "ops", className: "space-y-2" }, [
          !sidebarCollapsed && React.createElement("div", { className: "px-2 text-[10px] uppercase tracking-widest text-gray-400 font-black" }, "Operations"),
          renderSidebarButton("Actions", currentView === "actions", () => {
            setActiveContext("actions");
            setCurrentView("actions");
          }, React.createElement(Target, { size: 18 })),
          renderSidebarButton("Simulation", currentView === "scenarios", () => {
            setActiveContext("scenarios");
            setCurrentView("scenarios");
          }, React.createElement(Beaker, { size: 18 })),
          renderSidebarButton("Staffing", currentView === "staffing", () => {
            setActiveContext("staffing");
            setCurrentView("staffing");
          }, React.createElement(Users, { size: 18 })),
          renderSidebarButton("Invoices", currentView === "invoices", () => {
            setActiveContext("invoices");
            setCurrentView("invoices");
          }, React.createElement(FileText, { size: 18 }))
        ]),
        React.createElement("div", { key: "integrations", className: "space-y-2" }, [
          sidebarCollapsed
            ? React.createElement(React.Fragment, null, [
              renderSidebarButton("Integrations", activeContext === "integrations" || currentView.startsWith("int_"), () => {
                setActiveContext("integrations");
                setSidebarIntOpen(!sidebarIntOpen);
                if (!currentView.startsWith("int_")) {
                  setCurrentView("int_pos");
                }
              }, React.createElement(Plug, { size: 18 })),
              sidebarIntOpen && React.createElement("div", { className: "space-y-2" }, [
                renderSidebarButton("POS / EPOS", currentView === "int_pos", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_pos");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Reservations", currentView === "int_reservations", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_reservations");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Labour", currentView === "int_labour", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_labour");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Reviews", currentView === "int_reviews", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_reviews");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Accounting", currentView === "int_accounting", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_accounting");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Marketing", currentView === "int_marketing", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_marketing");
                }, React.createElement(Plug, { size: 16 }))
              ])
            ])
            : React.createElement(React.Fragment, null, [
              renderSidebarButton("Integrations", activeContext === "integrations" || currentView.startsWith("int_"), () => {
                setActiveContext("integrations");
                setSidebarIntOpen(!sidebarIntOpen);
                if (!currentView.startsWith("int_")) {
                  setCurrentView("int_pos");
                }
              }, React.createElement(Plug, { size: 18 })),
              sidebarIntOpen && React.createElement("div", { className: "space-y-2" }, [
                renderSidebarButton("POS / EPOS", currentView === "int_pos", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_pos");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Reservations", currentView === "int_reservations", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_reservations");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Labour", currentView === "int_labour", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_labour");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Reviews", currentView === "int_reviews", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_reviews");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Accounting", currentView === "int_accounting", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_accounting");
                }, React.createElement(Plug, { size: 16 })),
                renderSidebarButton("Marketing", currentView === "int_marketing", () => {
                  setActiveContext("integrations");
                  setCurrentView("int_marketing");
                }, React.createElement(Plug, { size: 16 }))
              ])
            ])
        ]),
        React.createElement("div", { key: "settings", className: "space-y-2" }, [
          !sidebarCollapsed && React.createElement("div", { className: "px-2 text-[10px] uppercase tracking-widest text-gray-400 font-black" }, "Preferences"),
          renderSidebarButton("Settings", currentView === "settings", () => {
            setActiveContext("settings");
            setCurrentView("settings");
          }, React.createElement(Settings, { size: 18 }))
        ])
      ])
    ])
    : null;

  if (!supabase) {
    return React.createElement("div", { className: "min-h-screen flex items-center justify-center p-4 bg-gray-50 text-center" }, [
      React.createElement("div", { className: "bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-red-100" }, [
        React.createElement("div", { className: "text-4xl mb-4" }, "🔌"),
        React.createElement("h2", { className: "text-xl font-bold text-gray-900 mb-2" }, "Configuration Required"),
        React.createElement("p", { className: "text-gray-500 mb-6 text-sm" }, "Findsy is ready, but your Supabase environment variables are missing. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your Netlify dashboard."),
      ])
    ]);
  }

  if (authLoading) {
    return React.createElement("div", {
      className: "min-h-screen flex items-center justify-center bg-white",
    }, React.createElement("div", { className: "text-center" }, [
      React.createElement("div", { key: "spinner", className: "w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" }),
      React.createElement("p", { key: "text", className: "text-gray-500 font-medium animate-pulse" }, "Loading your command center...")
    ]));
  }

  if (!user) {
    return React.createElement(AuthView, { onAuthSuccess: (u) => setUser(u) });
  }

  return React.createElement("div", {
    className: "min-h-screen",
    style: {
      background: "radial-gradient(1400px 700px at 30% 10%, rgba(6,133,206,.15) 0%, rgba(255,255,255,0) 60%), radial-gradient(1000px 600px at 75% 40%, rgba(6,133,206,.08) 0%, rgba(255,255,255,0) 55%), linear-gradient(180deg, rgba(249,250,251,1) 0%, rgba(243,244,246,1) 100%)",
    }
  }, [
    navLayout === "left" && leftSidebar,
    React.createElement(
      "header",
      { key: "header", className: "bg-white shadow-sm" },
      React.createElement(
        "div",
        { className: navLayout === "left" ? (sidebarCollapsed ? "px-[48px] pl-[112px]" : "px-[48px] pl-[304px]") : "px-[48px]" },
        React.createElement("div", { className: "flex justify-between items-center py-4" }, [
          React.createElement("div", { key: "left-nav", className: "flex items-center gap-10" }, [
            navLayout === "top" && React.createElement(
              "h1",
              { key: "title", className: "text-2xl font-bold text-gray-900 flex items-center" },
              React.createElement("img", {
                key: "logo",
                src: "/logo.png",
                alt: "Findsy",
                className: "h-8 w-auto",
              })
            ),

            navLayout === "top" && React.createElement("div", { key: "main-nav", className: "hidden md:flex items-center gap-8" }, [
              // Home Tab
              React.createElement("button", {
                key: "home-tab",
                onClick: () => {
                  setActiveContext("home");
                  setCurrentView("dashboard");
                },
                className: `text-sm font-bold transition-all ${activeContext === "home" ? "text-indigo-600" : "text-gray-500 hover:text-gray-900"}`
              }, "Home"),

              // 1. Dashboards with Dropdown
              React.createElement("div", { key: "dash-group", className: "relative group py-4 flex items-center" }, [
                React.createElement("button", {
                  key: "dash-btn",
                  onClick: () => {
                    setActiveContext("dashboards");
                    if (activeContext !== "dashboards") setCurrentView("inventory");
                  },
                  className: `text-sm font-bold flex items-center gap-1 transition-all ${activeContext === "dashboards" ? "text-indigo-600" : "text-gray-500 hover:text-gray-900"}`
                }, ["Dashboards", React.createElement("span", { key: "arrow", className: "text-xs ml-1 opacity-50" }, "▼")]),

                // Dropdown Menu
                React.createElement("div", {
                  key: "dropdown",
                  className: "absolute top-full left-0 w-64 bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all transform origin-top z-50 mt-1"
                }, [
                  { id: "inventory", label: "Inventory" },
                  { id: "menu", label: "Menu Intelligence" },
                  { id: "sales", label: "Sales, Demand & Staffing" },
                  { id: "marketing", label: "Marketing" },
                ].map(item =>
                  React.createElement("button", {
                    key: item.id,
                    onClick: (e: React.MouseEvent) => {
                      e.stopPropagation();
                      setActiveContext("dashboards");
                      setCurrentView(item.id as any);
                    },
                    className: `w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center justify-between border-l-4 ${currentView === item.id ? "border-indigo-600 text-indigo-700 bg-indigo-50/50 font-semibold" : "border-transparent text-gray-700"}`
                  }, item.label)
                ))
              ]),

              // 2. Individual Tabs (Actions, Simulation, Staffing, Invoice Management)
              ...[["actions", "Actions"], ["scenarios", "Simulation"], ["staffing", "Staffing"], ["invoices", "Invoices"]].map(([id, label]) =>
                React.createElement("button", {
                  key: id,
                  onClick: () => {
                    setActiveContext(id as any);
                    if (id === "actions") setCurrentView("actions");
                    else if (id === "scenarios") setCurrentView("scenarios");
                    else if (id === "staffing") setCurrentView("staffing");
                    else if (id === "invoices") setCurrentView("invoices");
                  },
                  className: `text-sm font-bold transition-all ${activeContext === id ? "text-indigo-600" : "text-gray-500 hover:text-gray-900"}`
                }, label)
              ),

              // 3. Integrations with Dropdown
              React.createElement("div", { key: "int-group", className: "relative group py-4 flex items-center" }, [
                React.createElement("button", {
                  key: "int-btn",
                  onClick: () => {
                    setActiveContext("integrations");
                    if (activeContext !== "integrations") setCurrentView("int_pos");
                  },
                  className: `text-sm font-bold flex items-center gap-1 transition-all ${activeContext === "integrations" ? "text-indigo-600" : "text-gray-500 hover:text-gray-900"}`
                }, ["Integrations", React.createElement("span", { key: "arrow", className: "text-xs ml-1 opacity-50" }, "▼")]),

                // Dropdown Menu
                React.createElement("div", {
                  key: "dropdown",
                  className: "absolute top-full left-0 w-64 bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all transform origin-top z-50 mt-1"
                }, [
                  { id: "int_pos", label: "POS / EPOS" },
                  { id: "int_reservations", label: "Reservations" },
                  { id: "int_labour", label: "Labour" },
                  { id: "int_reviews", label: "Reviews" },
                  { id: "int_accounting", label: "Accounting" },
                  { id: "int_marketing", label: "Marketing" },
                ].map(item =>
                  React.createElement("button", {
                    key: item.id,
                    onClick: (e: React.MouseEvent) => {
                      e.stopPropagation();
                      setActiveContext("integrations");
                      setCurrentView(item.id as any);
                    },
                    className: `w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center justify-between border-l-4 ${currentView === item.id ? "border-indigo-600 text-indigo-700 bg-indigo-50/50 font-semibold" : "border-transparent text-gray-700"}`
                  }, item.label)
                ))
              ]),

              // 4. Settings Tab
              React.createElement("button", {
                key: "settings",
                onClick: () => {
                  setActiveContext("settings");
                  setCurrentView("settings");
                },
                className: `text-sm font-bold transition-all ${activeContext === "settings" ? "text-indigo-600" : "text-gray-500 hover:text-gray-900"}`
              }, "Settings")
            ]),
          ]),

          React.createElement("div", { key: "user-nav", className: "flex items-center gap-6" }, [
            // Select Agent in Header - Only shown when in full chat view
            currentView === "chat" && React.createElement(AgentSelector, {
              key: "agent-selector",
              activeAgent,
              onAgentChange: (agent: AgentId) => {
                setActiveAgent(agent);
                setCurrentView("chat");
              },
              routerMode,
              onRouterModeChange: (enabled: boolean) => {
                setRouterMode(enabled);
                setCurrentView("chat");
              },
            }),

            // Notifications Bell
            React.createElement("div", { key: "notifications", className: "relative", ref: notificationDropdownRef }, [
              React.createElement("button", {
                key: "bell-btn",
                onClick: () => setShowNotificationsDropdown(!showNotificationsDropdown),
                className: "relative p-2 rounded-full hover:bg-gray-100 transition-colors",
                style: { position: "relative" }
              }, [
                React.createElement(Bell, { key: "icon", size: 24, className: "text-gray-500" }),
                targetNotifications.filter(n => !n.read).length > 0 && React.createElement("span", {
                  key: "badge",
                  className: "absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center",
                  style: { fontSize: "10px" }
                }, targetNotifications.filter(n => !n.read).length)
              ]),


              // Dropdown
              showNotificationsDropdown && React.createElement("div", {
                key: "dropdown",
                className: "absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden",
                style: { maxHeight: "500px" }
              }, [
                // Header
                React.createElement("div", { key: "header", className: "px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50" }, [
                  React.createElement("h3", { key: "title", className: "font-bold text-gray-900" }, "Notifications"),
                  React.createElement("div", { key: "actions", className: "flex items-center gap-2" }, [
                    targetNotifications.filter(n => !n.read).length > 0 && React.createElement("button", {
                      key: "mark-all",
                      onClick: async () => {
                        const all = targetNotifications.map((n) => ({ ...n, read: true }));
                        setTargetNotifications(all);
                        const { error } = await supabase
                          .from('notifications')
                          .update({ read: true })
                          .eq('read', false);
                        if (error) console.error("Supabase: Error marking all read", error);
                      },
                      className: "text-xs text-indigo-600 hover:text-indigo-700 font-semibold"
                    }, "Mark all read"),
                    React.createElement("button", {
                      key: "close",
                      onClick: () => setShowNotificationsDropdown(false),
                      className: "text-gray-400 hover:text-gray-600"
                    }, "✕")
                  ])
                ]),

                // Notifications List
                React.createElement("div", { key: "list", className: "overflow-y-auto", style: { maxHeight: "400px" } },
                  targetNotifications.length === 0
                    ? React.createElement("div", { className: "p-8 text-center text-gray-500" }, [
                      React.createElement(Bell, { key: "icon", size: 40, className: "text-gray-300 block mb-2 mx-auto" }),
                      React.createElement("p", { key: "text", className: "text-sm" }, "No notifications yet")
                    ])
                    : targetNotifications.slice(0, 10).map((n) => {
                      const timeAgo = (() => {
                        const now = new Date();
                        const created = new Date(n.createdAt);
                        const diffMs = now.getTime() - created.getTime();
                        const diffMins = Math.floor(diffMs / 60000);
                        if (diffMins < 1) return 'just now';
                        if (diffMins < 60) return `${diffMins}m ago`;
                        const diffHours = Math.floor(diffMins / 60);
                        if (diffHours < 24) return `${diffHours}h ago`;
                        const diffDays = Math.floor(diffHours / 24);
                        return `${diffDays}d ago`;
                      })();

                      const agentEmoji = n.agentType === 'chef' ? '👨‍🍳'
                        : n.agentType === 'manager' ? '👔'
                          : n.agentType === 'accountant' ? '💼'
                            : '📢';

                      return React.createElement("div", {
                        key: n.id,
                        onClick: async () => {
                          const all = [...targetNotifications];
                          const idx = all.findIndex((x) => x.id === n.id);
                          if (idx >= 0 && !all[idx].read) {
                            all[idx].read = true;
                            setTargetNotifications(all);
                            await supabase.from('notifications').update({ read: true }).eq('id', n.id);
                          }
                          const msg = `ALERT: Our target '${n.targetName}' was missed.\n${n.metric} is ${n.value}, which is ${n.direction === 'above' ? 'above' : 'below'} the threshold of ${n.threshold}.\nWhat should we do?`;
                          setCurrentView("chat");
                          setActiveAgent(n.agentType);
                          setRouterMode(false);
                          setExternalAlert({ id: n.id, message: msg, agentType: n.agentType });
                          setShowNotificationsDropdown(false);
                        },
                        className: `px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${!n.read ? 'bg-indigo-50/50' : ''}`,
                      }, [
                        React.createElement("div", { key: "content", className: "flex gap-3" }, [
                          React.createElement("span", { key: "emoji", className: "text-2xl flex-shrink-0" }, agentEmoji),
                          React.createElement("div", { key: "details", className: "flex-1 min-w-0" }, [
                            React.createElement("div", { key: "header", className: "flex items-start justify-between gap-2 mb-1" }, [
                              React.createElement("h4", { key: "title", className: `text-sm font-bold text-gray-900 ${!n.read ? 'font-extrabold' : ''}` }, n.targetName),
                              !n.read && React.createElement("span", { key: "dot", className: "w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-1" })
                            ]),
                            React.createElement("p", { key: "message", className: "text-xs text-gray-600 mb-1 line-clamp-2" }, n.message),
                            React.createElement("span", { key: "time", className: "text-xs text-gray-400" }, timeAgo)
                          ])
                        ])
                      ]);
                    })
                ),

                // Footer
                targetNotifications.length > 10 && React.createElement("div", {
                  key: "footer",
                  className: "px-4 py-3 border-t border-gray-200 bg-gray-50 text-center"
                }, React.createElement("button", {
                  onClick: () => {
                    setCurrentView("notifications");
                    setShowNotificationsDropdown(false);
                  },
                  className: "text-sm text-indigo-600 hover:text-indigo-700 font-semibold"
                }, "View all notifications"))
              ])
            ]),

            // Profile Button
            React.createElement("div", { key: "profile", className: "relative", ref: profileDropdownRef }, [
              React.createElement("button", {
                key: "profile-btn",
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  setShowProfileDropdown(!showProfileDropdown);
                },
                className: "flex items-center gap-2 p-2 rounded-full hover:bg-gray-100 transition-colors",
              }, [
                React.createElement("div", {
                  key: "avatar",
                  className: "w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm"
                }, (venueName || "V").charAt(0).toUpperCase()),
                React.createElement(ChevronDown, {
                  key: "chevron",
                  size: 14,
                  className: "text-gray-400 hidden md:block"
                })
              ]),

              // Profile Dropdown
              showProfileDropdown && React.createElement("div", {
                key: "dropdown",
                className: "absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden",
              }, [
                // User Info Section
                React.createElement("div", { key: "user-section", className: "px-4 py-3 border-b border-gray-200 bg-gray-50" }, [
                  React.createElement("div", { key: "avatar-large", className: "flex items-center gap-3 mb-2" }, [
                    React.createElement("div", {
                      key: "avatar",
                      className: "w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-lg"
                    }, (venueName || "V").charAt(0).toUpperCase()),
                    React.createElement("div", { key: "info" }, [
                      React.createElement("h4", { key: "name", className: "font-bold text-gray-900 text-sm" }, venueName || "Your Venue"),
                      React.createElement("p", { key: "email", className: "text-xs text-gray-500" }, user?.email || "")
                    ])
                  ])
                ]),

                // Menu Items
                React.createElement("div", { key: "menu", className: "py-2" }, [
                  React.createElement("button", {
                    key: "settings",
                    onClick: () => {
                      setCurrentView("settings");
                      setShowProfileDropdown(false);
                    },
                    className: "w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                  }, [
                    React.createElement(Settings, { key: "icon", size: 18, className: "text-gray-500" }),
                    React.createElement("span", { key: "text", className: "text-sm font-medium text-gray-700" }, "Settings")
                  ]),
                  React.createElement("div", { key: "divider", className: "border-t border-gray-200 my-2" }),
                  React.createElement("button", {
                    key: "logout",
                    onClick: () => {
                      handleLogout();
                      setShowProfileDropdown(false);
                    },
                    className: "w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 text-red-600"
                  }, [
                    React.createElement(LogOut, { key: "icon", size: 18, className: "text-red-500" }),
                    React.createElement("span", { key: "text", className: "text-sm font-medium" }, "Log Out")
                  ])
                ])
              ])
            ])
          ]),
        ])
      )
    ),


    React.createElement(
      "div",
      { key: "main", className: navLayout === "left" ? (sidebarCollapsed ? "px-[48px] py-8 pl-[112px]" : "px-[48px] py-8 pl-[304px]") : "px-[48px] py-8" },
      React.createElement("div", { key: "main-content" },
        (currentView === "dashboard"
          ? React.createElement(DashboardView, {
            onKpiExplain: handleKpiExplain,
            onFillManual: handleKpiManualFill,
            uploadedFiles: files,
            onNavigate: setCurrentView,
            venueName: venueName,
            kpiLayout: kpiLayout,
            onReorderKpis: handleReorderKpis
          })
          : currentView === "inventory"
            ? React.createElement(InventoryView, {
              onNavigate: setCurrentView,
              uploadedFiles: files,
              manualData: manualInventoryData,
              setShowUploadModal: (show: boolean) => {
                if (show) setUploadMode("document");
                setShowUploadModal(show);
              }
            })
            : currentView === "manual-inventory"
              ? React.createElement(ManualInventoryView, {
                initialData: manualInventoryData,
                onSave: async (data) => {
                  setManualInventoryData(data);
                  if (user?.id) {
                    console.log("[Findsy] Saving manual inventory for user:", user.id);
                    await supaSaveManualInventory(user.id, venueId || "", data);
                    await supaUpsertManualMetrics(user.id, venueId || "", computeKpiPatchFromManualData({
                      inventory: data,
                      menu: manualMenuData,
                      sales: manualSalesData,
                      marketing: manualMarketingData
                    }));
                    const f = await supaFetchFilesMetadata(user.id);
                    setFiles(f);
                    storeFiles(f);
                  }
                  setCurrentView("inventory");
                },
                onCancel: () => setCurrentView("inventory")
              })
              : currentView === "manual-menu"
                ? React.createElement(ManualMenuView, {
                  initialData: manualMenuData,
                  onSave: async (data) => {
                    setManualMenuData(data);
                    if (user?.id) {
                      console.log("[Findsy] Saving manual menu for user:", user.id);
                      await supaSaveManualMenu(user.id, venueId || "", data);
                      await supaUpsertManualMetrics(user.id, venueId || "", computeKpiPatchFromManualData({
                        inventory: manualInventoryData,
                        menu: data,
                        sales: manualSalesData,
                        marketing: manualMarketingData
                      }));
                      const f = await supaFetchFilesMetadata(user.id);
                      setFiles(f);
                      storeFiles(f);
                    }
                    setCurrentView("menu");
                  },
                  onCancel: () => setCurrentView("menu")
                })
                : currentView === "manual-sales"
                  ? React.createElement(ManualSalesDemandView, {
                    initialData: manualSalesData,
                    onSave: async (data) => {
                      setManualSalesData(data);
                      if (user?.id) {
                        console.log("[Findsy] Saving manual sales for user:", user.id);
                        await supaSaveManualSales(user.id, venueId || "", data);
                        await supaUpsertManualMetrics(user.id, venueId || "", computeKpiPatchFromManualData({
                          inventory: manualInventoryData,
                          menu: manualMenuData,
                          sales: data,
                          marketing: manualMarketingData
                        }));
                        const f = await supaFetchFilesMetadata(user.id);
                        setFiles(f);
                        storeFiles(f);
                      }
                      setCurrentView("sales");
                    },
                    onCancel: () => setCurrentView("sales")
                  })
                  : currentView === "manual-marketing"
                    ? React.createElement(ManualMarketingView, {
                      initialData: manualMarketingData,
                      onSave: async (data) => {
                        setManualMarketingData(data);
                        if (user?.id) {
                          console.log("[Findsy] Saving manual marketing for user:", user.id);
                          await supaSaveManualMarketing(user.id, venueId || "", data);
                          await supaUpsertManualMetrics(user.id, venueId || "", computeKpiPatchFromManualData({
                            inventory: manualInventoryData,
                            menu: manualMenuData,
                            sales: manualSalesData,
                            marketing: data
                          }));
                          const f = await supaFetchFilesMetadata(user.id);
                          setFiles(f);
                          storeFiles(f);
                        }
                        setCurrentView("marketing");
                      },
                      onCancel: () => setCurrentView("marketing")
                    })
                    : currentView === "menu"
                      ? React.createElement(MenuIntelligenceView, {
                        menuItems,
                        onNavigate: setCurrentView,
                        onKpiExplain: handleKpiExplain,
                        onOpenUpload: () => {
                          setUploadMode("document");
                          setShowUploadModal(true);
                        }
                      })
                      : currentView === "sales"
                        ? React.createElement(SalesDemandView, {
                          onNavigate: setCurrentView,
                          onKpiExplain: handleKpiExplain,
                          manualData: manualSalesData,
                          onOpenUpload: () => {
                            setUploadMode("document");
                            setShowUploadModal(true);
                          }
                        })
                        : currentView === "upload"
                          ? React.createElement(
                            "div",
                            { key: "upload-view", className: "h-full" },
                            React.createElement(FileUpload, {
                              userId: user.id,
                              venueId: venueId,
                              onFileUploaded: handleFileUploaded,
                              onFileDeleted: handleFileDeleted,
                            })
                          )
                          : currentView === "actions"
                            ? React.createElement(ActionsInsightsView, { targets, decisions, runCheckForTarget, createNewTarget })
                            : currentView === "channels"
                              ? React.createElement(ChannelsView, { user, venueId })
                              : currentView === "staffing"
                                ? React.createElement(StaffingView, { user, venueId, onNavigate: setCurrentView })
                                : currentView === "invoices"
                                  ? React.createElement(InvoicesView, {
                                    onUploadDocument: () => {
                                      setUploadMode("document");
                                      setShowUploadModal(true);
                                    },
                                    onUploadImage: () => {
                                      setUploadMode("image");
                                      setShowUploadModal(true);
                                    }
                                  })
                                  : currentView === "scenarios"
                                    ? React.createElement(ScenarioView, {
                                      decisions,
                                      menuItems,
                                      onSaveMenuItem: (item: any) => setMenuItems(prev => [...prev, item]),
                                      onNavigate: setCurrentView
                                    })
                                    : currentView === "notifications"
                                      ? React.createElement(NotificationsView, {
                                        notifications: targetNotifications,
                                        onMarkAllRead: async () => {
                                          const all = targetNotifications.map((n) => ({ ...n, read: true }));
                                          setTargetNotifications(all);

                                          // Update in Supabase
                                          const { error } = await supabase
                                            .from('notifications')
                                            .update({ read: true })
                                            .eq('read', false);
                                          if (error) console.error("Supabase: Error marking all read", error);
                                        },
                                        onNotificationClick: async (n) => {
                                          const all = [...targetNotifications];
                                          const idx = all.findIndex((x) => x.id === n.id);
                                          if (idx >= 0 && !all[idx].read) {
                                            all[idx].read = true;
                                            setTargetNotifications(all);
                                            await supaMarkNotificationRead(n.id);
                                          }
                                          const msg = `ALERT: Our target '${n.targetName}' was missed.\n${n.metric} is ${n.value}, which is ${n.direction === 'above' ? 'above' : 'below'} the threshold of ${n.threshold}.\nWhat should we do?`;
                                          setCurrentView("chat");
                                          setActiveAgent(n.agentType);
                                          setRouterMode(false);
                                          setExternalAlert({ id: n.id, message: msg, agentType: n.agentType });
                                        }
                                      })
                                      : currentView === "marketing"
                                        ? React.createElement(MarketingView, {
                                          onNavigate: setCurrentView,
                                          data: marketingData,
                                          onOpenUpload: () => {
                                            setUploadMode("document");
                                            setShowUploadModal(true);
                                          }
                                        })
                                        : currentView.startsWith("int_")
                                          ? React.createElement(IntegrationsView, {
                                            category: currentView.replace("int_", "") as any,
                                            connectedApps: connectedApps,
                                            onIntegrate: (app, cat) => {
                                              setIntegrationError(null);
                                              setSyncStatus(null);
                                              setExtractedMetrics(null);
                                              setUseManualToken(false);
                                              setUseManualIntegrationAuth(false);
                                              setCredentials({ username: "", password: "" });
                                              setSelectedApp({ ...app, category: cat });
                                              setIntegrationModalOpen(true);
                                            },
                                            onViewData: (name) => {
                                              handleViewSharedData(name);
                                            }
                                          })
                                          : currentView === "settings"
                                            ? React.createElement(SettingsView, {
                                              user,
                                              venueName,
                                              onUpdate: () => fetchVenueName(user.id),
                                              navLayout,
                                              onNavLayoutChange: (layout: "top" | "left") => setNavLayout(layout)
                                            })
                                            : React.createElement(ChatInterface, {
                                              activeAgent,
                                              routerMode,
                                              onSaveDecision: handleSaveDecision,
                                              externalAlert: externalAlert,
                                              onClearExternalAlert: () => setExternalAlert(null),
                                              prefillInput: prefillInput,
                                              onClearPrefill: () => setPrefillInput(null),
                                              onAction: async (meta) => {
                                                if (meta.UPDATE_KPI) {
                                                  const { key, value } = meta.UPDATE_KPI;
                                                  await supaUpdateManualMetric(user.id, venueId, key, parseFloat(value));
                                                  const f = await supaFetchFilesMetadata(user.id);
                                                  setFiles(f);
                                                }
                                              }
                                            })
        )
      )
    ),

    React.createElement(DecisionModal, {
      key: "decision-modal",
      isOpen: isDecisionModalOpen,
      onClose: () => setIsDecisionModalOpen(false),
      onSave: confirmSaveDecision,
      initialContext: pendingDecisionContext,
    }),
    React.createElement(TargetModal, {
      key: "target-modal",
      isOpen: isTargetModalOpen,
      onClose: () => setIsTargetModalOpen(false),
      onSave: confirmCreateTarget,
    }),
    React.createElement(IntegrationDataViewer, {
      key: "data-viewer",
      isOpen: isDataViewerOpen,
      onClose: () => setIsDataViewerOpen(false),
      appName: viewingAppName,
      files: files
    }),

    // Floating Chat Widget Button
    !isChatWidgetOpen && currentView !== "chat" && React.createElement("button", {
      key: "chat-fab",
      onClick: () => setIsChatWidgetOpen(true),
      className: "fixed bottom-6 right-6 w-16 h-16 bg-white hover:bg-gray-50 text-indigo-600 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 z-50 border border-gray-100",
      style: { boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)" }
    }, [
      React.createElement("div", { key: "icon-wrap", className: "relative flex items-center justify-center" }, [
        React.createElement("img", {
          key: "logo",
          src: "/logo.png",
          className: "h-6 w-auto object-contain",
          alt: "Findsy"
        }),
        React.createElement("div", {
          key: "ai-tag",
          className: "absolute -bottom-4 -right-4 bg-indigo-600 w-7 h-7 rounded-full flex items-center justify-center text-sm shadow-md border-2 border-white"
        }, "🤖")
      ])
    ]),

    // Floating Chat Widget Panel
    isChatWidgetOpen && React.createElement("div", {
      key: "chat-widget",
      className: "fixed bottom-6 right-6 w-[420px] h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50",
      style: {
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        border: "1px solid rgba(0, 0, 0, 0.1)"
      }
    }, [
      // Header with Agent Selector
      React.createElement("div", {
        key: "widget-header",
        className: "bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-3 flex items-center justify-between"
      }, [
        // Agent Dropdown
        React.createElement("div", { key: "agent-dropdown", className: "relative group" }, [
          React.createElement("button", {
            key: "dropdown-btn",
            className: "flex items-center gap-2 text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-all"
          }, [
            React.createElement("div", {
              key: "agent-avatar",
              className: "w-8 h-8 bg-white rounded-full flex items-center justify-center text-lg"
            }, routerMode ? "🤖" : activeAgent === "chef" ? "👨‍🍳" : activeAgent === "manager" ? "📋" : activeAgent === "accountant" ? "💰" : activeAgent === "marketing" ? "📢" : "💬"),
            React.createElement("div", { key: "agent-info", className: "text-left" }, [
              React.createElement("h3", { key: "name", className: "text-white font-bold text-xs leading-tight" },
                routerMode ? "Auto Router" : activeAgent === "chef" ? "Chef" : activeAgent === "manager" ? "Manager" : activeAgent === "accountant" ? "Accountant" : activeAgent === "marketing" ? "Marketing" : "Assistant"
              ),
              React.createElement("p", { key: "status", className: "text-indigo-200 text-[10px]" }, "Online")
            ]),
            React.createElement("span", { key: "chevron", className: "text-white text-xs ml-1" }, "▼")
          ]),

          // Dropdown Menu
          React.createElement("div", {
            key: "dropdown-menu",
            className: "absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl overflow-hidden invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all z-50"
          }, [
            // Router Option
            React.createElement("button", {
              key: "router",
              onClick: () => {
                setRouterMode(true);
                setActiveAgent(null);
              },
              className: `w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-l-4 ${routerMode ? "border-emerald-500 bg-emerald-50" : "border-transparent"}`
            }, [
              React.createElement("span", { key: "icon", className: "text-2xl" }, "🤖"),
              React.createElement("div", { key: "info" }, [
                React.createElement("div", { key: "name", className: "font-bold text-sm text-gray-900" }, "Auto Router"),
                React.createElement("div", { key: "desc", className: "text-xs text-gray-500" }, "AI selects best agent(s)")
              ])
            ]),

            // Individual Agents
            ...[
              { id: "chef", name: "Chef / Inventory", icon: "👨‍🍳", desc: "Recipes, costs & suppliers" },
              { id: "manager", name: "General Manager", icon: "📋", desc: "Operations & staffing" },
              { id: "accountant", name: "Accountant", icon: "💰", desc: "Finances & reporting" },
              { id: "marketing", name: "Marketing", icon: "📢", desc: "Campaigns & reviews" }
            ].map(agent =>
              React.createElement("button", {
                key: agent.id,
                onClick: () => {
                  setActiveAgent(agent.id as AgentId);
                  setRouterMode(false);
                },
                className: `w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-l-4 ${!routerMode && activeAgent === agent.id ? "border-indigo-500 bg-indigo-50" : "border-transparent"}`
              }, [
                React.createElement("span", { key: "icon", className: "text-2xl" }, agent.icon),
                React.createElement("div", { key: "info" }, [
                  React.createElement("div", { key: "name", className: "font-bold text-sm text-gray-900" }, agent.name),
                  React.createElement("div", { key: "desc", className: "text-xs text-gray-500" }, agent.desc)
                ])
              ])
            )
          ])
        ]),

        React.createElement("div", { key: "header-actions", className: "flex items-center gap-1" }, [
          React.createElement("button", {
            key: "expand-btn",
            onClick: () => {
              setIsChatWidgetOpen(false);
              setCurrentView("chat");
            },
            className: "text-white hover:bg-white/20 rounded-lg px-3 py-1.5 text-base transition-all",
            title: "Open Full Screen Chat"
          }, "⤢"),
          React.createElement("button", {
            key: "close-btn",
            onClick: () => setIsChatWidgetOpen(false),
            className: "text-white hover:bg-white/20 rounded-lg px-3 py-1 text-xl transition-all"
          }, "×")
        ])
      ]),

      // Chat Content - Use the existing chat interface
      React.createElement("div", {
        key: "widget-content",
        className: "flex-1 overflow-hidden"
      },
        React.createElement(ChatInterface, {
          activeAgent,
          routerMode,
          onSaveDecision: handleSaveDecision,
          externalAlert: externalAlert,
          onClearExternalAlert: () => setExternalAlert(null),
          prefillInput: prefillInput,
          onClearPrefill: () => setPrefillInput(null),
          isWidget: true,
          onAction: async (meta) => {
            if (meta.UPDATE_KPI) {
              const { key, value } = meta.UPDATE_KPI;
              await supaUpdateManualMetric(user.id, venueId, key, parseFloat(value));
              const f = await supaFetchFilesMetadata(user.id);
              setFiles(f);
            }
          }
        })
      )
    ]),

    // Integration Modal
    integrationModalOpen && selectedApp && React.createElement("div", { key: "int-modal-overlay", className: "fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4" }, [
      React.createElement("div", { key: "int-modal-content", className: "bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl scale-100 animate-in fade-in zoom-in duration-200" }, [
        React.createElement("div", { className: "flex items-center gap-4 mb-6" }, [
          React.createElement("div", { className: "w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-2xl border border-indigo-100" }, "🔌"),
          React.createElement("div", {}, [
            React.createElement("h3", { className: "text-xl font-bold text-gray-900" }, `Integrate ${selectedApp.name}`),
            React.createElement("p", { className: "text-sm text-gray-500" }, "Connect your account to sync data into your dashboards")
          ])
        ]),

        syncStatus
          ? React.createElement("div", { className: "py-6 flex flex-col items-center text-center" }, [
            syncStatus === 'reviewing'
              ? React.createElement("div", { key: "review-view", className: "w-full space-y-6" }, [
                React.createElement("div", { className: "bg-indigo-50 p-6 rounded-2xl border border-indigo-100" }, [
                  React.createElement("div", { className: "flex items-start justify-between gap-3 mb-4" }, [
                    React.createElement("div", { className: "text-left" }, [
                      React.createElement("h4", { className: "text-sm font-black text-indigo-900" }, "Connection Preview"),
                      React.createElement("p", { className: "text-[10px] text-indigo-600 font-bold uppercase tracking-wider" }, `Source: ${selectedApp?.name}`)
                    ]),
                    React.createElement("span", { className: "text-[10px] font-black uppercase tracking-wider bg-white/60 border border-indigo-100 text-indigo-700 px-2 py-1 rounded-lg" }, "Ready")
                  ]),
                  React.createElement("div", { className: "grid grid-cols-1 gap-2" },
                    (() => {
                      const app = selectedApp?.name || "";
                      const m: any = extractedMetrics || {};
                      const rows: { label: string; value: string }[] = [];

                      const push = (label: string, value: any) => {
                        if (value === undefined || value === null) return;
                        const s = String(value).trim();
                        if (!s) return;
                        rows.push({ label, value: s });
                      };

                      const num = (v: any) => {
                        const n = Number(v);
                        return Number.isFinite(n) ? n : null;
                      };

                      const fmtMoney = (v: any) => {
                        const n = num(v);
                        return n === null ? "" : `£${Math.round(n).toLocaleString()}`;
                      };

                      const fmtInt = (v: any) => {
                        const n = num(v);
                        return n === null ? "" : Math.round(n).toLocaleString();
                      };

                      const fmtDec1 = (v: any) => {
                        const n = num(v);
                        return n === null ? "" : n.toFixed(1);
                      };

                      if (app === "Square") {
                        push("Total sales (7d)", fmtMoney(m.total_sales));
                        push("AOV", (() => {
                          const n = num(m.aov);
                          return n === null ? "" : `£${n.toFixed(2)}`;
                        })());
                        push("Covers", fmtInt(m.covers));
                        push("Sales pace", (() => {
                          const n = num(m.sales_pace);
                          return n === null ? "" : `${Math.round(n)}%`;
                        })());
                        push("Inventory items", fmtInt(m.inventory_items));
                        push("Total stock", fmtInt(m.total_stock));
                      } else if (app === "Google Reviews") {
                        push("Place", m.place_name);
                        push("Average rating", fmtDec1(m.avg_rating));
                        push("Total reviews", fmtInt(m.review_count));
                      } else if (app === "Yelp") {
                        push("Business", m.business_name);
                        push("Average rating", fmtDec1(m.avg_rating));
                        push("Total reviews", fmtInt(m.review_count));
                      } else if (app === "Mailchimp") {
                        push("Account", m.account_name);
                        push("Audiences", fmtInt(m.audiences));
                        push("Email subscribers", fmtInt(m.email_subscribers));
                      } else if (app === "Meta Ads") {
                        push("Ad account", m.ad_account_id);
                        push("Reach (30d)", fmtInt(m.ad_reach));
                        push("Impressions (30d)", fmtInt(m.ad_impressions));
                        push("Clicks (30d)", fmtInt(m.ad_clicks));
                        push("Spend (30d)", (() => {
                          const n = num(m.ad_spend);
                          return n === null ? "" : `£${n.toFixed(2)}`;
                        })());
                      } else if (app === "Google Ads") {
                        push("Customer ID", m.customer_id);
                        push("Impressions (30d)", fmtInt(m.ad_impressions));
                        push("Clicks (30d)", fmtInt(m.ad_clicks));
                        push("Spend (30d)", (() => {
                          const n = num(m.ad_spend);
                          return n === null ? "" : `£${n.toFixed(2)}`;
                        })());
                      } else if (app === "Xero") {
                        push("Tenants", fmtInt(m.tenants));
                        push("Organisation", m.organisation_name || m.tenant_name);
                      } else if (app === "QuickBooks") {
                        push("Company", m.company_name);
                        push("Country", m.country);
                      } else if (app === "7shifts") {
                        push("Company", m.company_name);
                        push("User", m.user_email || m.user_name);
                      } else if (app === "Deputy") {
                        push("User", m.user_email || m.user_name);
                        push("Region", `${m.deputy_install || ""}.${m.deputy_geo || ""}`.replace(/^\./, ""));
                      } else if (app === "Findsy") {
                        push("Reservations", fmtInt(m.reservations));
                        push("Covers", fmtInt(m.covers));
                        push("No-show rate", (() => {
                          const n = num(m.no_show_rate);
                          return n === null ? "" : `${n.toFixed(1)}%`;
                        })());
                        push("Status", "Connected");
                      } else {
                        push("Status", "Connected");
                      }

                      if (rows.length === 0) {
                        return React.createElement("div", { className: "bg-white p-3 rounded-xl border border-indigo-50 text-xs text-gray-600 font-medium" }, "No preview data returned.");
                      }

                      return rows.map((r) =>
                        React.createElement("div", { key: r.label, className: "bg-white p-3 rounded-xl border border-indigo-50 flex items-center justify-between" }, [
                          React.createElement("span", { className: "text-[10px] font-black uppercase tracking-tighter text-gray-400" }, r.label),
                          React.createElement("span", { className: "text-sm font-black text-gray-900 text-right" }, r.value)
                        ])
                      );
                    })()
                  ),

                  // Show Top Items preview in review if available
                  extractedMetrics?.top_items && Array.isArray(extractedMetrics.top_items) && extractedMetrics.top_items.length > 0 &&
                  React.createElement("div", { className: "mt-4 pt-4 border-t border-indigo-100" }, [
                    React.createElement("h5", { className: "text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-2" }, "Inventory Preview"),
                    React.createElement("div", { className: "space-y-1.5" },
                      extractedMetrics.top_items.slice(0, 3).map((item: any, idx: number) =>
                        React.createElement("div", { key: idx, className: "flex justify-between text-[10px] bg-white/50 p-1.5 rounded-lg" }, [
                          React.createElement("span", { className: "font-bold text-gray-600 truncate max-w-[140px]" }, item.name),
                          React.createElement("span", { className: "font-black text-indigo-600" }, `${item.qty} pcs`)
                        ])
                      )
                    )
                  ]),

                  // Show reviews preview when available
                  extractedMetrics?.recent_reviews && Array.isArray(extractedMetrics.recent_reviews) && extractedMetrics.recent_reviews.length > 0 &&
                  React.createElement("div", { className: "mt-4 pt-4 border-t border-indigo-100" }, [
                    React.createElement("h5", { className: "text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-2" }, "Recent Reviews"),
                    React.createElement("div", { className: "space-y-1.5" },
                      extractedMetrics.recent_reviews.slice(0, 3).map((r: any, idx: number) =>
                        React.createElement("div", { key: idx, className: "bg-white/50 border border-indigo-50 rounded-lg p-2" }, [
                          React.createElement("div", { className: "flex items-center justify-between gap-3" }, [
                            React.createElement("span", { className: "text-[10px] font-black text-gray-700 truncate" }, `${r.platform || ""} • ${r.user || "Anonymous"}`.trim()),
                            React.createElement("span", { className: "text-[10px] font-black text-indigo-700" }, (r.rating !== undefined && r.rating !== null) ? `${r.rating}/5` : "")
                          ]),
                          React.createElement("div", { className: "text-[10px] text-gray-600 mt-1 line-clamp-2" }, r.text || "")
                        ])
                      )
                    )
                  ])
                ]),
                React.createElement("div", { className: "flex flex-col gap-3" }, [
                  React.createElement("button", {
                    onClick: confirmIntegration,
                    className: "w-full py-4 bg-indigo-600 text-white rounded-xl font-black text-sm shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                  }, ["Add to Dashboards", React.createElement("span", {}, "→")]),
                  React.createElement("button", {
                    onClick: () => { setSyncStatus(null); setExtractedMetrics(null); },
                    className: "w-full py-2 text-gray-400 text-xs font-bold hover:text-gray-600 transition-all"
                  }, "Cancel Extraction")
                ])
              ])
              : React.createElement("div", { key: "progress-view", className: "py-6" }, [
                React.createElement("div", { className: "w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-6 relative mx-auto" }, [
                  React.createElement("div", { className: "absolute inset-0 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" }),
                  syncStatus === 'complete' ? "✅" : "🤖"
                ]),
                React.createElement("h3", { className: "text-lg font-bold text-gray-900 mb-2" },
                  syncStatus === 'authenticating' ? "Authenticating..." :
                    syncStatus === 'locating' ? (selectedApp?.name === 'Square' ? "Scanning Square Sales Trends..." : "Locating Financial Reports...") :
                      syncStatus === 'extracting' ? "Fetching data..." :
                        syncStatus === 'syncing' ? "Saving to Findsy..." :
                          "Sync Complete!"
                ),
                React.createElement("p", { className: "text-sm text-gray-500 max-w-[240px] mx-auto" },
                  syncStatus === 'authenticating' ? "Securely authenticating with your account." :
                    syncStatus === 'locating' ? (selectedApp?.name === 'Square' ? "Preparing your Square reports connection." : "Preparing your data connection.") :
                      syncStatus === 'extracting' ? "Normalizing data for dashboard metrics." :
                        syncStatus === 'syncing' ? "Finalizing data transfer to your dashboards." :
                          "Your dashboard is now live with real-time data."
                )
              ])
          ])
          : React.createElement("div", { className: "space-y-4 mb-8" }, [
            integrationError && React.createElement("div", { className: "p-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-xs font-bold" }, integrationError),
            React.createElement("div", {}, [
              selectedApp.name === "Square"
                ? React.createElement("div", { className: "space-y-4" }, [
                  React.createElement("div", { className: "flex justify-end" },
                    React.createElement("button", {
                      onClick: () => setUseManualToken(!useManualToken),
                      className: "text-[10px] font-bold text-indigo-600 hover:underline"
                    }, useManualToken ? "Switch to OAuth" : "Use Manual Access Token (Dev)")
                  ),

                  useManualToken
                    ? React.createElement("div", {}, [
                      React.createElement("label", { className: "block text-xs font-bold text-gray-500 uppercase mb-1" }, "Square Access Token"),
                      React.createElement("input", {
                        type: "password",
                        value: credentials.password,
                        onChange: (e: any) => setCredentials({ ...credentials, password: e.target.value }),
                        className: "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none",
                        placeholder: "EAAA..."
                      }),
                      React.createElement("p", { className: "text-[10px] text-gray-400 mt-1" }, "Paste an access token to sync without OAuth (developer use only).")
                    ])
                    : React.createElement("div", { className: "space-y-3" }, [
                      React.createElement("div", { className: "bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col text-left gap-1" }, [
                        React.createElement("h4", { className: "text-sm font-bold text-gray-900" }, "Secure connection"),
                        React.createElement("p", { className: "text-xs text-gray-500" },
                          "You will be redirected to Square to authorize Findsy. We never see your password."
                        )
                      ])
                    ])
                ])
                : React.createElement("div", { className: "space-y-4" }, [
                  isOAuthIntegration(selectedApp.name) && React.createElement("div", { className: "flex justify-end" },
                    React.createElement("button", {
                      onClick: () => setUseManualIntegrationAuth(!useManualIntegrationAuth),
                      className: "text-[10px] font-bold text-indigo-600 hover:underline"
                    }, useManualIntegrationAuth ? "Switch to OAuth" : "Use Manual Credentials (Dev)")
                  ),

                  (() => {
                    const schema: { fields: IntegrationFieldDef[] } | null =
                      (isOAuthIntegration(selectedApp.name) && !useManualIntegrationAuth)
                        ? getIntegrationOAuthSchema(selectedApp.name)
                        : getIntegrationSchema(selectedApp.name);

                    if (!schema) {
                      return React.createElement("div", { className: "p-4 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600 font-medium" }, "This integration isn't configured yet.");
                    }

                    return React.createElement("div", { className: "space-y-3" },
                      schema.fields.map((f) =>
                        React.createElement("div", { key: f.key }, [
                          React.createElement("label", { className: "block text-xs font-bold text-gray-500 uppercase mb-1" }, f.label),
                          React.createElement("input", {
                            type: f.type === "password" ? "password" : "text",
                            value: integrationForm?.[f.key] || "",
                            onChange: (e: any) => updateIntegrationForm(f.key, e.target.value),
                            className: "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all placeholder:text-gray-400",
                            placeholder: f.placeholder || ""
                          })
                        ])
                      )
                    );
                  })(),

                  (isOAuthIntegration(selectedApp.name) && !useManualIntegrationAuth) && React.createElement("div", { className: "bg-gray-50 p-3 rounded-xl border border-gray-100" }, [
                    React.createElement("div", { className: "text-[10px] font-black uppercase tracking-widest text-gray-500" }, "Redirect URI"),
                    React.createElement("div", { className: "mt-1 text-[11px] font-mono text-gray-700 break-all" }, buildIntegrationRedirectUri())
                  ])
                ])
            ]),
            React.createElement("div", {}, [
              React.createElement("label", { className: "block text-xs font-bold text-gray-500 uppercase mb-1" }, "Data Sync Frequency"),
              React.createElement("select", {
                value: schedule,
                onChange: (e: any) => setSchedule(e.target.value),
                className: "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
              }, [
                React.createElement("option", { value: "daily" }, "Daily (Recommended)"),
                React.createElement("option", { value: "weekly" }, "Weekly"),
                React.createElement("option", { value: "monthly" }, "Monthly")
              ])
            ])
          ]),

        !syncStatus && React.createElement("div", { className: "flex justify-end gap-3" }, [
          React.createElement("button", {
            onClick: () => setIntegrationModalOpen(false),
            className: "px-5 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
          }, "Cancel"),
          selectedApp.name === "Square" && !useManualToken
            ? React.createElement("button", {
              onClick: startSquareOAuth,
              className: "px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg transition-all flex items-center gap-2"
            }, ["Log in with Square", React.createElement("span", {}, "↗")])
            : (isOAuthIntegration(selectedApp.name) && !useManualIntegrationAuth)
              ? React.createElement("button", {
                onClick: startIntegrationOAuth,
                className: "px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg transition-all flex items-center gap-2"
              }, [`Log in with ${selectedApp.name}`, React.createElement("span", {}, "↗")])
              : React.createElement("button", {
                onClick: submitIntegration,
                className: "px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg shadow-indigo-200 transition-all flex items-center gap-2"
              }, ["Test Connection", React.createElement("span", {}, "→")])
        ])
      ])
    ]),

    showUploadModal && React.createElement("div", {
      key: "upload-modal-overlay",
      className: "fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in",
      style: { background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }
    }, [
      React.createElement("div", {
        key: "modal",
        className: "bg-white w-full max-w-5xl rounded-3xl overflow-hidden shadow-2xl relative"
      }, [
        React.createElement("div", { className: "p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50" }, [
          React.createElement("h2", { className: "text-2xl font-black text-gray-900" },
            uploadMode === "image" ? "Scan Invoice Image" : "Upload Documents"
          ),
          React.createElement("button", { onClick: () => setShowUploadModal(false), className: "text-2xl text-gray-400 hover:text-gray-600" }, "×")
        ]),
        React.createElement("div", { className: "p-8 max-h-[80vh] overflow-y-auto" }, [
          React.createElement(FileUpload, {
            userId: user?.id || "user123",
            venueId: venueId,
            onFileUploaded: handleFileUploaded,
            onFileDeleted: handleFileDeleted,
            mode: uploadMode,
            context: currentView,
          })
        ])
      ])
    ])
  ]);
}



// Route to multiple agents (2-3)
async function routeToMultipleAgents(
  input: string,
  availableAgents: AgentId[] = Object.keys(AGENT_PERSONAS) as AgentId[]
): Promise<AgentId[]> {
  const system = `You are a multi-agent routing analyzer for Findsy. Analyze the user's question and determine which 2-3 agents should collaborate to answer it.

Available agents: ${availableAgents.join(", ")}

Agent capabilities:
- chef: recipes, ingredients, food costs, menu pricing, kitchen operations
- manager: staffing, operations, compliance, workflows, service
- accountant: finances, margins, taxes, cash flow, break-even analysis
- marketing: promotions, social media, customer acquisition, branding

Respond with a comma-separated list of 2-3 agent names that best address this question. Example: "chef, accountant" or "marketing, manager". Do NOT return more than 3 agents.`;

  const assistant = await openaiChat([
    { role: "system", content: system },
    { role: "user", content: input },
  ]);

  const agentNames = String(assistant).split(",").map((s) => s.trim().toLowerCase());
  // Deduplicate and validate
  const uniqueAgents = Array.from(new Set(agentNames.map((name) => {
    try {
      return normalizeAgentId(name);
    } catch {
      return null;
    }
  }))).filter(Boolean) as AgentId[];

  // Return 1-3 agents; default to single if routing fails
  return uniqueAgents.length > 0 ? uniqueAgents.slice(0, 3) : ["chef"];
}

async function retrieveRelevantFiles(query: string, agentScope: AgentId) {
  console.log(`Retrieving files for agent: ${agentScope}, query: ${query}`);

  const allFiles = getStoredFiles();
  console.log(`Total files in storage: ${allFiles.length}`, allFiles);

  if (allFiles.length === 0 || !agentScope) return [];

  const agent = AGENT_PERSONAS[agentScope as ValidAgentId];
  const relevantFiles = allFiles.filter((file: any) =>
    file.tags?.some((tag: string) => (agent.allowedTags as readonly string[]).includes(tag))
  );

  console.log(`Relevant files for ${agentScope}:`, relevantFiles);

  if (relevantFiles.length === 0) return [];

  const queryLower = query.toLowerCase();
  const queryTerms = queryLower.split(" ").filter((t) => t.length > 2);

  const scoredFiles = relevantFiles.map((file: any) => {
    const contentLower = String(file.content || "").toLowerCase();
    let score = 0;

    queryTerms.forEach((term) => {
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = (contentLower.match(new RegExp(escapedTerm, "g")) || []).length;
      score += matches;
    });

    if (String(file.fileName || "").toLowerCase().includes(queryLower)) score += 10;

    return {
      ...file,
      relevanceScore: score,
      text: file.content,
      metadata: { fileName: file.fileName, tags: file.tags, uploadedAt: file.uploadedAt },
    };
  });

  const topFiles = scoredFiles.sort((a: any, b: any) => b.relevanceScore - a.relevanceScore).slice(0, 5);
  console.log(`Top relevant files for ${agentScope}:`, topFiles);

  return topFiles;
}

async function findsyWorkflow({
  input,
  agentType,
  conversationHistory = [],
  routerMode = false,
  env,
  tone,
  responseLength,
}: {
  input: string;
  agentType: AgentId;
  conversationHistory?: any[];
  routerMode?: boolean;
  env: { LANGBASE_API_KEY?: string };
  // Optional tone only applies to the marketing agent
  tone?: "calm" | "warm" | "firm";
  responseLength?: "short" | "normal";
}) {
  // Router mode: select multiple agents (2-3) based on question domain
  // Manual mode: use the single selected agent
  const selectedAgents: AgentId[] = routerMode
    ? await routeToMultipleAgents(input)
    : [normalizeAgentId(agentType)];

  // If multiple agents were routed, run workflows in parallel and combine results
  if (selectedAgents.length > 1) {
    const parallelResults = await Promise.all(
      selectedAgents.map((agentId) =>
        runSingleAgentWorkflow(input, agentId, conversationHistory, env, tone, responseLength)
      )
    );

    // Filter out non-substantive responses (mostly questions or deferring to other agents)
    // Keep responses that are direct advice and actionable
    const substantiveResults = parallelResults.filter((r) => {
      const response = String(r.response || "").toLowerCase();
      const hasQuestion = response.includes("can you") || response.includes("could you") ||
        response.includes("please provide") || response.includes("please share") ||
        response.includes("please upload") || response.includes("i need");
      // Only include if response is substantive (not mostly asking for info)
      const isSubstantive = response.length > 100 && !hasQuestion;
      return isSubstantive;
    });

    // If all responses were filtered out, include all but mark them
    const resultsToUse = substantiveResults.length > 0 ? substantiveResults : parallelResults;

    // Format responses with clear separation and agent headers
    const formattedResponses = resultsToUse.map((r, idx) =>
      `${r.agentName}:\n${r.response}${idx < resultsToUse.length - 1 ? "\n" : ""}`
    ).join("\n\n");

    // Return combined results with multiple agents
    return {
      response: formattedResponses,
      agent: "multiple",
      agentName: selectedAgents.map((a) => AGENT_PERSONAS[a as ValidAgentId].name).join(" + "),
      agentIds: selectedAgents,
      filesUsed: parallelResults.reduce((sum, r) => sum + r.filesUsed, 0),
      citations: parallelResults.flatMap((r) => r.citations),
      actionMeta: parallelResults.find((r) => r.actionMeta)?.actionMeta || null,
    };
  }

  // Single agent workflow
  return runSingleAgentWorkflow(input, selectedAgents[0], conversationHistory, env, tone, responseLength);
}

// Helper: synthesize multi-agent responses into unified, coordinated format
// Helper: run workflow for a single agent
async function runSingleAgentWorkflow(
  input: string,
  selectedAgent: AgentId,
  conversationHistory: any[],
  _env: { LANGBASE_API_KEY?: string },
  tone?: "calm" | "warm" | "firm",
  responseLength?: "short" | "normal"
) {
  void _env;
  const relevantFiles = await retrieveRelevantFiles(input, selectedAgent);

  const agent = AGENT_PERSONAS[selectedAgent as ValidAgentId];

  const MAX_FILE_CHARS = 4000;
  let fileContext = "";
  if (relevantFiles.length > 0) {
    fileContext =
      `\n\nUPLOADED FILES AVAILABLE (${relevantFiles.length}). Use them when relevant.\n` +
      relevantFiles
        .map((file: any, index: number) => {
          const content = String(file.content || "");
          const snippet =
            content.length > MAX_FILE_CHARS
              ? content.slice(0, MAX_FILE_CHARS) + "\n[TRUNCATED]"
              : content;
          return `FILE ${index + 1}: ${file.fileName} (Tags: ${file.tags.join(
            ", "
          )})\nContent:\n${snippet}\n---`;
        })
        .join("\n\n");
  } else {
    fileContext =
      "\n\nNO RELEVANT FILES FOUND for this agent. The user may need to upload documents such as:\n- Menu items and recipes\n- Ingredient costs and supplier invoices\n- Sales reports\n- Staff schedules\n- Compliance documents";
  }

  const conversationContext =
    conversationHistory.length > 0
      ? `\n\nPREVIOUS CONVERSATION:\n${conversationHistory
        .map((msg: any) => `${msg.role}: ${msg.content}`)
        .join("\n")}`
      : "";

  // If this is the marketing agent and a tone is supplied, include explicit tone guidance
  const toneInstruction =
    selectedAgent === "marketing" && tone
      ? `\n\nTONE INSTRUCTION: When writing public review replies, match the selected tone exactly: calm = apologetic, steady, professional; warm = empathetic, friendly, reassuring; firm = polite, confident, sets expectations. Use the user's selected tone.`
      : "";

  // Length instruction (only for 'short')
  const lengthInstruction =
    responseLength === "short"
      ? "\n\nCRITICAL LENGTH INSTRUCTION: Your response must be extremely brief (max 2 sentences). Be direct and blunt. Skip any polite filler, reasoning, or multiple sections. Word count target: < 30 words."
      : "";

  const responseFormat = responseLength === "short"
    ? "RESPONSE FORMAT:\n- A single direct recommendation\n- (Optional) One short metric or reason\n- NO headers, NO next steps, NO intro/outro."
    : "RESPONSE FORMAT:\n1. Direct answer to the user's question\n2. Show calculations/reasoning when relevant\n3. Cite specific files if used: [FileName: specific data]\n4. End with \"NEXT STEPS:\" and 2-3 actionable items\n5. Keep your response focused and practical (not exploratory)";

  // In multi-agent mode, instruct agents to provide answers based on available context
  const multiAgentInstruction = conversationHistory.length === 0 && selectedAgent !== "marketing"
    ? "\n\nIMPORTANT: You are responding as part of a multi-agent team. Provide your best actionable advice based on the available context. Do NOT ask the user for more information or defer to another agent. Give a direct, confident answer in your domain."
    : "";

  const systemPrompt = `${agent.instructions}${toneInstruction}${lengthInstruction}${fileContext}${conversationContext}${multiAgentInstruction}\n\nIMPORTANT INSTRUCTIONS:\n- If you have uploaded files available, USE THEM to provide specific, data-driven advice\n- Always cite the specific file name and data when referencing uploaded information\n- If no relevant files are available, still provide practical, actionable advice based on industry best practices\n- Provide direct, confident advice based on the available information\n- If the user provides a specific numeric value for a KPI (like revenue, covers, pace, food cost, labor, waste), you MUST include an ACTION block at the very end of your response.\n  Format:\n  ACTION: UPDATE_KPI\n  key: metric_key\n  value: 5000\n  Keys: total_sales, covers, aov, sales_pace, food_cost_pct, labor_cost_pct, waste_rate, inventory_value.\n- Do NOT use markdown formatting like ** or __ or # — use plain text only\n- Do NOT ask the user for more information; provide your best answer with what you have\n\n${responseFormat}\n\nExample assistant reply (standard):\nShort: "Raise the price to £10.50 and test for 2 weeks."\nSupport: "Based on your £2.50 cost, this gives you 38% margin, which is healthy for this dish."`;

  console.log(`[runSingleAgentWorkflow] Agent: ${selectedAgent}, Length: ${responseLength}, Tone: ${tone}`);

  // Call the LLM
  const assistantOutput = await openaiChat([
    { role: "system", content: systemPrompt },
    { role: "user", content: input },
  ], "gpt-4o-mini", 0.7);

  // Detect an ACTION block appended by the assistant (used for UI actions)
  let actionMeta: any = null;
  let outputWithoutAction = String(assistantOutput || "");
  const actionStart = outputWithoutAction.indexOf("ACTION:");
  if (actionStart !== -1) {
    const actionBlock = outputWithoutAction.slice(actionStart);
    const actionLines = actionBlock.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (actionLines.length > 0) {
      const actionType = actionLines[0].replace("ACTION:", "").trim();
      const meta: any = {};
      actionLines.slice(1).forEach((l) => {
        const parts = l.split(":");
        if (parts.length >= 2) {
          meta[parts[0].trim()] = parts.slice(1).join(":").trim();
        }
      });
      actionMeta = { [actionType]: meta };
    }
    outputWithoutAction = outputWithoutAction.slice(0, actionStart).trim();
  }

  const cleanedOutput = sanitizeOutput(outputWithoutAction);

  return {
    response: cleanedOutput,
    agent: selectedAgent,
    agentName: agent.name,
    filesUsed: relevantFiles.length,
    citations: relevantFiles.map((file: any) => ({
      fileName: file.fileName,
      snippet: String(file.content || "").substring(0, 150) + "...",
      tags: file.tags,
    })),
    // Additional metadata for UI actions (e.g., review replies)
    actionMeta,
  };
}

// Decision saving workflow (kept simple)
async function saveDecisionWorkflow({
  userId,
  decision,
  context,
  expectedImpact,
  agentType,
  targetId,
}: {
  userId: string;
  decision: string;
  context: string;
  expectedImpact: string;
  agentType: AgentId;
  targetId?: string;
}) {
  const decisionRecord = {
    id: `decision_${Date.now()}`,
    userId,
    agentType,
    decision,
    context,
    expectedImpact,
    targetId: targetId || null,
    timestamp: new Date().toISOString(),
    status: "active",
  };

  console.log("Decision saved:", decisionRecord);
  return decisionRecord;
}

async function main(event: any, env: any) {
  try {
    console.log("Event received:", event);

    // UI render request
    if (event && (event.type === "ui" || event.method === "GET" || !event.json)) {
      return React.createElement(FindsyApp);
    }

    // API requests
    let payload: any;
    try {
      payload = await event.json();
    } catch (jsonError) {
      console.error("JSON parsing error:", jsonError);
      payload = {
        action: "chat",
        input:
          event.input ||
          event.query ||
          "Hello! I'm looking for help with my food vendor business.",
        agentType: "chef",
        userId: "user123",
        conversationHistory: [],
        routerMode: false,
      };
    }

    console.log("Payload:", payload);

    const action = payload.action || "chat";

    switch (action) {
      case "chat": {
        const result = await findsyWorkflow({
          input:
            payload.input ||
            "Hello! I'm looking for help with my food vendor business.",
          agentType: normalizeAgentId(payload.agentType),
          routerMode: !!payload.routerMode,
          env,
          tone: payload.tone,
        });
        return result;
      }

      case "saveDecision": {
        const decision = await saveDecisionWorkflow({
          userId: payload.userId || "user123",
          decision: payload.decision || "No decision specified",
          context: payload.context || "No context provided",
          expectedImpact: payload.expectedImpact || "No impact specified",
          agentType: normalizeAgentId(payload.agentType),
        });
        return decision;
      }

      default: {
        console.log(`Unknown action: ${action}, defaulting to chat`);
        const defaultResult = await findsyWorkflow({
          input:
            payload.input ||
            "Hello! I'm looking for help with my food vendor business.",
          agentType: normalizeAgentId(payload.agentType),
          routerMode: !!payload.routerMode,
          env,
          tone: payload.tone,
        });
        return defaultResult;
      }
    }
  } catch (error: any) {
    console.error("Main function error:", error);

    return {
      response:
        "Welcome to Findsy! I'm here to help you with your food vendor business. You can ask me about recipes, costs, operations, finances, or marketing. What would you like to know?",
      agent: "chef",
      agentName: "Chef / Inventory Agent",
      filesUsed: 0,
      citations: [],
      error: error?.message || "unknown error",
    };
  }
}

export default main;
